// ── Module Access Rules schema (mirrors tenant-funcs/_shared.js) ────────────
// Used to render the Access Rules section in per-tenant module cards.
const MODULE_ACCESS_RULES = {
  employees: {
    exportPermission:    { type:'role', default:'admin',
      label:'Export employee records',
      desc:'Minimum role to export employee data as CSV' },
    sensitiveFields:     { type:'role', default:'admin',
      label:'View sensitive fields',
      desc:'Who can see Salary, National ID, and Passport details' },
    redundancyAuthority: { type:'role', default:'admin',
      label:'Make redundant / reinstate',
      desc:'Who can mark employees as redundant or reinstate them' },
  },
  leave: {
    approvalAuthority:    { type:'role', default:'manager',
      label:'Approve leave requests',
      desc:'Minimum role that can approve or reject leave' },
    balanceEditing:       { type:'role', default:'admin',
      label:'Edit leave balances',
      desc:'Who can manually adjust leave balances' },
    allowBackdated:       { type:'bool', default:false,
      label:'Allow backdated requests',
      desc:'Whether leave can be submitted for past dates' },
    maxSingleRequestDays: { type:'number', default:30,
      label:'Max single request (days)',
      desc:'Maximum days per leave request (0 = unlimited)' },
  },
  discipline: {
    caseCreation:     { type:'role', default:'admin',
      label:'Open discipline cases',
      desc:'Who can open a new discipline case' },
    caseVisibility:   { type:'role', default:'admin',
      label:'View discipline cases',
      desc:'Who can see discipline records' },
    outcomeRecording: { type:'role', default:'admin',
      label:'Record case outcomes',
      desc:'Who can close a case and record the outcome' },
  },
  performance: {
    appraisalCreation: { type:'role', default:'manager',
      label:'Create appraisals',
      desc:'Who can open a new appraisal for an employee' },
    allowSelfRating:   { type:'bool', default:false,
      label:'Allow self-rating',
      desc:'Whether employees can submit their own appraisal rating' },
    ratingVisibility:  { type:'role', default:'admin',
      label:'View ratings',
      desc:'Who can see completed appraisal ratings' },
    kpiEditing:        { type:'role', default:'manager',
      label:'Edit KPI targets',
      desc:'Who can set or change KPI targets' },
  },
  training: {
    selfNomination:     { type:'bool', default:false,
      label:'Allow self-nomination',
      desc:'Whether employees can nominate themselves for training' },
    shortlistAuthority: { type:'role', default:'admin',
      label:'Shortlist nominations',
      desc:'Who can shortlist or reject training nominations' },
    approvalAuthority:  { type:'role', default:'admin',
      label:'Approve nominations',
      desc:'Who can give final approval for training nominations' },
    budgetVisibility:   { type:'role', default:'admin',
      label:'View training budget',
      desc:'Who can see training cost and budget figures' },
  },
  messaging: {
    broadcastAuthority: { type:'role', default:'admin',
      label:'Send broadcast messages',
      desc:'Who can send messages to groups of employees' },
    smsAuthority:       { type:'role', default:'admin',
      label:'Send SMS / WhatsApp',
      desc:'Minimum role required to send SMS or WhatsApp' },
    requireMessageLog:  { type:'bool', default:true,
      label:'Require message logging',
      desc:'All sent messages are permanently logged for audit' },
  },
  documents: {
    deleteAuthority:    { type:'role', default:'admin',
      label:'Delete documents',
      desc:'Who can permanently delete documents' },
    confidentialAccess: { type:'role', default:'admin',
      label:'Access confidential documents',
      desc:'Who can view or upload documents flagged as confidential' },
    templateEditing:    { type:'role', default:'admin',
      label:'Edit document templates',
      desc:'Who can create or modify document templates' },
  },
  recruitment: {
    vacancyPosting:  { type:'role', default:'admin',
      label:'Post vacancies',
      desc:'Who can create and publish job advertisements' },
    applicantAccess: { type:'role', default:'admin',
      label:'View applicant data',
      desc:'Who can see applicant records and personal details' },
    offerAuthority:  { type:'role', default:'admin',
      label:'Generate offer letters',
      desc:'Who can issue employment offer letters' },
  },
  succession: {
    editAccess:          { type:'role', default:'admin',
      label:'Edit succession plans',
      desc:'Who can modify succession pipelines and candidates' },
    candidateVisibility: { type:'role', default:'admin',
      label:'View succession candidates',
      desc:'Who can see who is on the succession list' },
  },
  // organisation access rules merged into employees — org structure is a
  // foundational prerequisite and not independently toggleable.
  employees: {
    structureEditing: { type:'role', default:'admin',
      label:'Edit org structure',
      desc:'Who can create, rename, or delete divisions and jobs' },
    gradeEditing:     { type:'role', default:'admin',
      label:'Edit job grades',
      desc:'Who can change a job\'s pay grade' },
  },
};

const ROLE_OPTIONS = [
  { value: "admin",    label: "Admin — full tenant access" },
  { value: "manager",  label: "Manager — self + direct reports" },
  { value: "employee", label: "Employee — self-service only" },
  { value: "custom",   label: "Custom role (select below)" },
];

// Built-in role capability reference — used in User Management UI
const ROLE_CAPABILITIES = {
  admin: {
    label: "Admin",
    icon: "🔑",
    color: "var(--purple)",
    description: "Full access to all tenant modules and all employee records",
    viewScope: "all",
    canApprove: true,
    canExport: true,
    canManageUsers: true,
  },
  manager: {
    label: "Manager",
    icon: "👔",
    color: "var(--blue)",
    description: "View and manage self plus direct reports only",
    viewScope: "self + direct reports",
    canApprove: true,
    canExport: false,
    canManageUsers: false,
  },
  employee: {
    label: "Employee",
    icon: "👤",
    color: "var(--accent)",
    description: "View and update own profile, submit personal requests",
    viewScope: "self only",
    canApprove: false,
    canExport: false,
    canManageUsers: false,
  },
  custom: {
    label: "Custom",
    icon: "🎭",
    color: "var(--amber)",
    description: "Module-scoped permissions defined in the tenant's custom role",
    viewScope: "per role definition",
    canApprove: "per role",
    canExport: "per role",
    canManageUsers: false,
  },
};

// ══════════════════════════════════════════════════════════════
// DATA MODEL
// ══════════════════════════════════════════════════════════════

const MODULES = [
  // ── People ──────────────────────────────────────────────────────────────────
  { id:'employees',         name:'Employees',               icon:'👥', desc:'Employee directory, records and org structure',            color:'var(--accent)', group:'People',
    children:['leave','discipline','succession','tasks','ohs','compliance','rostering'],
    features:[
      { id:'directory',     name:'Employee Directory',    desc:'View and search employee records'            },
      { id:'onboarding',    name:'Onboarding',            desc:'Onboarding checklists and activities'        },
      { id:'documents',     name:'Employee Documents',    desc:'Store contracts and personal documents'      },
      { id:'leave-history', name:'Leave History',         desc:'Leave balance and history per employee'      },
      { id:'divisions',     name:'Divisions',             desc:'Manage organisational divisions'             },
      { id:'jobs',          name:'Jobs',                  desc:'Define roles and job requirements'           },
      { id:'structure',     name:'Structure Chart',       desc:'Visual org structure view'                   },
      { id:'mobile-clock-in', name:'Mobile Clock-In',     desc:'Employee clock-in via mobile app, restricted to a customizable geofence radius around each office (mobile app only — not available on web)' },
    ],
    alwaysOn: true },
  { id:'leave',             name:'Leave',                   icon:'📅', desc:'Leave records and approvals',                              color:'var(--blue)',   group:'People',    parent:'employees',
    features:[
      { id:'requests',  name:'Leave Requests',  desc:'Submit and approve leave requests'   },
      { id:'balances',  name:'Leave Balances',  desc:'Track and adjust leave entitlements' },
      { id:'calendar',  name:'Leave Calendar',  desc:'Team leave calendar view'            },
      { id:'reports',   name:'Leave Reports',   desc:'Leave utilisation analytics'         },
    ]},
  { id:'discipline',        name:'Discipline',              icon:'⚖️', desc:'Disciplinary cases and records',                           color:'var(--red)',    group:'People',    parent:'employees',
    features:[
      { id:'cases',    name:'Discipline Cases',   desc:'Open and manage disciplinary cases'   },
      { id:'outcomes', name:'Case Outcomes',      desc:'Record outcomes and appeal decisions' },
      { id:'reports',  name:'Discipline Reports', desc:'Disciplinary analytics and trends'   },
    ]},
  { id:'succession',        name:'Succession',              icon:'🏛', desc:'Succession planning and org chart',                        color:'var(--cyan)',   group:'People',    parent:'employees',
    features:[
      { id:'pipeline', name:'Succession Pipeline', desc:'Identify and track succession candidates' },
      { id:'orgchart', name:'Org Chart',            desc:'Interactive organisational chart'         },
    ]},
  { id:'tasks',             name:'Tasks',                   icon:'✅', desc:'Task management and assignments',                          color:'var(--accent)', group:'People',    parent:'employees',
    features:[
      { id:'assign',  name:'Task Assignment',   desc:'Assign and track tasks to employees'  },
      { id:'notify',  name:'SMS Notifications', desc:'Notify assignees via SMS or WhatsApp' },
      { id:'reports', name:'Task Reports',      desc:'Task completion analytics'           },
    ]},
  { id:'ohs',               name:'Occupational Health & Safety', icon:'🦺', desc:'Incident reporting, corrective actions, and hazard inspections', color:'var(--purple)', group:'People',    parent:'employees',
    features:[
      { id:'incidents',          name:'Incident Reporting',   desc:'Report and manage workplace incidents and near misses' },
      { id:'corrective-actions', name:'Corrective Actions',   desc:'Track remediation actions raised from incidents'       },
      { id:'inspections',        name:'Hazard Inspections',   desc:'Record hazard inspections and findings'                },
    ]},
  { id:'compliance',        name:'Compliance',              icon:'🛡️', desc:'Regulatory requirements for jobs and positions, employee records and expiry alerts', color:'var(--blue)', group:'People',    parent:'employees',
    features:[
      { id:'requirements', name:'Requirements Library', desc:'Define licences, registrations and certifications and the jobs/positions that need them' },
      { id:'records',      name:'Employee Records',     desc:'Track each employee\'s evidence, issue and expiry dates'                               },
      { id:'alerts',       name:'Expiry Alerts',        desc:'Reminders before expiry and escalation after lapse'                                     },
      { id:'reports',      name:'Compliance Reports',   desc:'Organisation-wide compliance status'                                                    },
    ]},
  { id:'rostering',         name:'Rostering',               icon:'🗓️', desc:'Business units build shift rosters and submit attendance to payroll', color:'var(--cyan)', group:'People',    parent:'employees',
    features:[
      { id:'shifts',     name:'Shift Templates',       desc:'Each business unit defines the shifts it works'                        },
      { id:'rosters',    name:'Roster Grid',           desc:'Build per-pay-period rosters and publish them to staff'                },
      { id:'attendance', name:'Attendance Submission', desc:'Upload time & attendance files, check against the roster, send to payroll' },
    ]},
  // ── Development ─────────────────────────────────────────────────────────────
  { id:'training',          name:'Training',                icon:'🎓', desc:'Training plans, nominations, and completion',              color:'var(--amber)', group:'Development',
    children:['providers'],
    features:[
      { id:'nominations', name:'Nominations',         desc:'Employee nomination workflow'          },
      { id:'plans',       name:'Annual Plans',        desc:'Training budgets and annual plans'     },
      { id:'shortlist',   name:'Shortlisting',        desc:'Review and shortlist nominations'      },
      { id:'completion',  name:'Completion Register', desc:'Track and record training completions' },
      { id:'reports',     name:'Training Reports',    desc:'Analytics and cost tracking'           },
    ]},
  { id:'providers',         name:'Education Providers',     icon:'🏫', desc:'External training providers and course catalogue',         color:'var(--amber)', group:'Development', parent:'training',
    features:[
      { id:'directory',  name:'Provider Directory',  desc:'Manage provider profiles and MOUs'      },
      { id:'programmes', name:'Programme Catalogue', desc:'Browse and manage training programmes'  },
    ]},
  { id:'performance',       name:'Performance Management',  icon:'📈', desc:'Appraisals, KPIs and reviews',                            color:'var(--cyan)',   group:'Development',
    features:[
      { id:'reviews',  name:'Performance Reviews', desc:'Annual appraisal cycles and ratings'        },
      { id:'kpis',     name:'KPI Tracking',        desc:'Set and track key performance indicators'   },
      { id:'goals',    name:'Goal Setting',         desc:'Personal and team OKRs'                     },
      { id:'feedback', name:'360° Feedback',        desc:'Peer and manager feedback collection'       },
      { id:'reports',  name:'Performance Reports',  desc:'Appraisal analytics and dashboards'         },
    ]},
  { id:'competency',        name:'Competency Framework',    icon:'🎯', desc:'Competency library, proficiency assessments and gap analysis', color:'var(--purple)', group:'Development',
    features:[
      { id:'library',    name:'Competency Library',  desc:'Define and manage the organisation competency catalogue'  },
      { id:'assessment', name:'Assessments',         desc:'Rate employee proficiency against competency levels'      },
      { id:'gaps',       name:'Gap Analysis',        desc:'Identify and prioritise competency gaps for development'  },
      { id:'matrix',     name:'Role Matrix',         desc:'Competency × employee matrix view across roles'          },
    ]},
  // ── Operations ──────────────────────────────────────────────────────────────
  { id:'documents',         name:'Documents',               icon:'📄', desc:'Document storage and management',                          color:'var(--muted)', group:'Operations',
    features:[
      { id:'storage',   name:'Document Storage',    desc:'Upload and organise documents'             },
      { id:'contracts', name:'Contract Management', desc:'Track contract expiry and renewals'        },
      { id:'templates', name:'Document Templates',  desc:'Standardised letter and form templates'    },
    ]},
  { id:'messaging',         name:'Messaging',               icon:'💬', desc:'Internal messaging via SMS, WhatsApp and broadcast',       color:'var(--cyan)',   group:'Operations',
    features:[
      { id:'sms',       name:'SMS',       desc:'Send SMS messages to employees'             },
      { id:'whatsapp',  name:'WhatsApp',  desc:'Send WhatsApp messages to employees'        },
      { id:'broadcast', name:'Broadcast', desc:'Broadcast messages to groups'               },
    ]},
  { id:'recruitment',       name:'Recruitment',             icon:'🔍', desc:'Job postings and applicant tracking',                      color:'var(--blue)',   group:'Administration',
    features:[
      { id:'postings',   name:'Job Postings',       desc:'Create and publish job advertisements'    },
      { id:'applicants', name:'Applicant Tracking', desc:'Manage applications and interview stages' },
      { id:'onboarding', name:'Hire Onboarding',    desc:'Automate new hire onboarding steps'       },
    ]},
  { id:'settings',          name:'Settings',                icon:'⚙️', desc:'System configuration and preferences',                    color:'var(--muted)', group:'System', system:true, alwaysOn:true,
    features:[
      { id:'general',    name:'General Settings',   desc:'Organisation info and preferences'             },
      { id:'benefits',   name:'Benefits Config',    desc:'Configure employee benefits and allowances'    },
      { id:'onboarding', name:'Onboarding Config',  desc:'Customise onboarding activity checklists'      },
    ]},
  // ── Administration / Compensation ────────────────────────────────────────────
  { id:'compensation',      name:'Compensation',            icon:'💳', desc:'Pay scale, payroll and employee benefits management',      color:'var(--green)',  group:'Administration',
    children:['payscale','payroll','benefits'],
    features:[] },
  { id:'payscale',          name:'Pay Scale',               icon:'📊', desc:'Grade-based salary tiers and pay comparisons',             color:'var(--yellow)', group:'Administration', parent:'compensation',
    features:[
      { id:'grades',      name:'Grades',      desc:'View grade pay ranges'                    },
      { id:'tiers',       name:'Tiers',       desc:'Tier-based pay structure'                 },
      { id:'comparison',  name:'Comparison',  desc:'Compare employee pay to scale'            },
    ]},
  { id:'payroll',           name:'Payroll',                 icon:'💰', desc:'Pay runs, payslips, tax and superannuation',               color:'var(--green)',  group:'Administration', parent:'compensation',
    features:[
      { id:'periods',   name:'Pay Periods',   desc:'Create and manage pay periods'             },
      { id:'runs',      name:'Payroll Runs',  desc:'Execute and approve payroll runs'          },
      { id:'payslips',  name:'Payslips',      desc:'Generate and view employee payslips'       },
      { id:'reports',   name:'Reports',       desc:'Payroll reports and summaries'             },
      { id:'settings',  name:'Settings',      desc:'Tax brackets and superannuation config'    },
    ]},
  { id:'benefits',          name:'Benefits',                icon:'🏥', desc:'Employee benefits configuration and reporting',            color:'var(--cyan)',   group:'Administration', parent:'compensation',
    features:[
      { id:'config',      name:'Benefits Config',   desc:'Define allowances and benefit types'      },
      { id:'allowances',  name:'Allowances',         desc:'Manage recurring employee allowances'     },
      { id:'reports',     name:'Reports',            desc:'Benefits expenditure reports'             },
    ]},
  // ── Government ──────────────────────────────────────────────────────────────
  { id:'public-service-ref',name:'Public Service Reference',icon:'🏛️', desc:'Public service regulations, codes, and reference data',   color:'var(--blue)',   group:'Government',
    children:['gesi-compliance'],
    features:[
      { id:'general-orders', name:'General Orders',  desc:'PNG General Orders reference library'              },
      { id:'regulations',    name:'Regulations',     desc:'Public service regulations and codes'              },
      { id:'gesi-policy',    name:'GESI Policy',     desc:'Gender Equality & Social Inclusion policy framework' },
      { id:'gesi-standards', name:'GESI Standards',  desc:'GESI implementation standards and benchmarks'      },
      { id:'gesi-tools',     name:'GESI Tools',      desc:'Assessment tools, checklists and templates'        },
    ]},
  { id:'gesi-compliance',   name:'GESI Compliance',         icon:'⚖️', desc:'Gender Equality & Social Inclusion reporting and targets',  color:'var(--red)',    group:'Government', parent:'public-service-ref',
    features:[
      { id:'reporting',   name:'GESI Reporting',  desc:'Aggregate scorecard metrics'              },
      { id:'targets',     name:'Target Setting',  desc:'Configure GESI policy targets'            },
      { id:'benchmarks',  name:'Benchmarks',      desc:'PNG policy benchmark comparison'          },
      { id:'export',      name:'Export',          desc:'CSV export of GESI report'                },
    ]},
  // ── System ──────────────────────────────────────────────────────────────────
  { id:'ai',                name:'AI Assistant',            icon:'🤖', desc:'HR AI assistant — knowledge base chat powered by Gemini',  color:'var(--purple)', group:'System', system:true,
    features:[
      { id:'assistant',      name:'AI Chat',          desc:'Conversational HR assistant'                    },
      { id:'knowledge-base', name:'Knowledge Base',   desc:'Custom document knowledge base for RAG queries' },
    ]},
];

const HR_MODULES = MODULES.filter(m => !m.system);
const SYSTEM_MODULES = MODULES.filter(m => m.system);

// ── Module state helpers (work with both legacy array and new object format) ──
function modulesEnabled(t) {
  const mods = t.modules || {};
  const validIds = new Set(MODULES.map(m => m.id));
  return Object.entries(mods).filter(([k,v]) => validIds.has(k) && v && v.enabled).map(([k]) => k);
}
function hrModulesEnabled(t) {
  const hrIds = new Set(HR_MODULES.map(m => m.id));
  return modulesEnabled(t).filter(id => hrIds.has(id));
}
function isModEnabled(t, modId) {
  const mod = (t.modules || {})[modId];
  return !!(mod && mod.enabled);
}
function isFeatureEnabled(t, modId, featId) {
  const mod = (t.modules || {})[modId];
  if (!mod || !mod.enabled) return false;
  if (!mod.features || Object.keys(mod.features).length === 0) return true;
  return mod.features[featId] !== false;
}
function enabledFeatureCount(t, modId) {
  const mod = MODULES.find(m => m.id === modId);
  if (!mod) return 0;
  return mod.features.filter(f => isFeatureEnabled(t, modId, f.id)).length;
}

// Plans are loaded from Firestore via HRMGMT.listPlans() on boot.
// state.plans is the live source of truth for all plan data.
// This helper returns plan config from state, falling back to a safe default.
function getPlanConfig(planId) {
  return state.plans[planId] || {
    id: planId,
    label: planId,
    employeeLimit: null,
    storageLimitMb: 5 * 1024,
    modules: ['employees','leave','tasks','settings'],
    color: 'blue',
  };
}

let state = {
  tenants: [],

  plans: {},

  globalRules: {
    requireMfa: true,
    allowGuestAccess: false,
    enforceDataRetention: true,
    dataRetentionYears: 7,
    allowExport: true,
    allowApiAccess: true,
    sandboxMode: false,
    autoProvision: false,
    defaultPlan: 'core',
    // plan limits are managed via state.plans — not stored in globalRules
    allowedDomains: [],
    blockedEmails: [],
    blockedDomains: [],
    blockedIps: [],
    // Messaging platform defaults
    messagingEnabled: true,
    defaultRequireMessageLog: true,
    allowedChannels: ['email','sms','whatsapp'],
  },

  messagingService: null,  // loaded from config/messagingService

  activityLog: [],
};

// ══════════════════════════════════════════════════════════════
// NAVIGATION
// ══════════════════════════════════════════════════════════════

function showPage(id, btn) {
  document.querySelectorAll('.page').forEach(p => p.classList.remove('active'));
  document.querySelectorAll('.nav-btn').forEach(b => b.classList.remove('active'));
  document.getElementById('page-'+id).classList.add('active');
  if(btn) btn.classList.add('active');
  const labels = {dashboard:'Dashboard',tenants:'Tenants','tenant-users':'Tenant Users',metrics:'Metrics',modules:'Module Management','platform-controls':'Platform Controls','ps-reference':'PS Reference Library','ai-knowledge':'AI Knowledge Base',messages:'Messages',storage:'Storage',activity:'Activity Log','email-templates':'Email Templates','system-settings':'System Settings','support':'Support Tickets','user-mgmt':'User Management',billing:'Billing'};
  document.getElementById('tb-page').textContent = labels[id] || id;
  if(id==='dashboard') renderDashboard();
  if(id==='tenants') renderTenantsTable();
  if(id==='tenant-users') loadAllTenantUsers();
  if(id==='metrics') renderMetricsPage();
  if(id==='modules') { renderModulesPage(); modLoadSchemaState(); }
  if(id==='platform-controls') renderPlatformControls();
  if(id==='ps-reference') renderPSRefLibrary();
  if(id==='ai-knowledge') aiKbLoad();
  if(id==='activity') renderActivityLog();
  if(id==='billing')  initBillingPage();
  if(id==='user-mgmt') renderUserMgmt();
  if(id==='support') renderSupportTickets();
  if(id==='messages') renderMessagesPage();
  if(id==='storage') renderStoragePage();
  if(id==='system-settings') loadDomainRequests();
  if(id==='email-templates') renderEmailTemplatesPage();
}

// modTab removed — module management now uses modSetTab()

// ══════════════════════════════════════════════════════════════
// DASHBOARD
// ══════════════════════════════════════════════════════════════

function renderDashboard() {
  const active = state.tenants.filter(t=>t.status==='active').length;
  const trial  = state.tenants.filter(t=>t.status==='trial').length;
  const suspended = state.tenants.filter(t=>t.status==='suspended').length;
  const totalEmp = state.tenants.reduce((s,t)=>s+t.employees,0);
  const totalUsers = state.tenants.reduce((s,t)=>s+t.activeUsers,0);

  document.getElementById('nb-total').textContent = state.tenants.length;
  document.getElementById('nb-tenants').textContent = state.tenants.length;

  document.getElementById('dash-metrics').innerHTML = `
    <div class="metric-card metric-accent"><div class="metric-label">Total Tenants</div><div class="metric-val">${state.tenants.length}</div><div class="metric-sub">${active} active · ${trial} trial</div></div>
    <div class="metric-card metric-blue"><div class="metric-label">Total Employees</div><div class="metric-val">${totalEmp.toLocaleString()}</div><div class="metric-sub">Across all tenants</div></div>
    <div class="metric-card"><div class="metric-label">Active Users</div><div class="metric-val">${totalUsers}</div><div class="metric-sub">Logged in last 24h</div></div>
    <div class="metric-card metric-amber"><div class="metric-label">Trial Tenants</div><div class="metric-val">${trial}</div><div class="metric-sub">Conversion pending</div></div>
    <div class="metric-card metric-red"><div class="metric-label">Suspended</div><div class="metric-val">${suspended}</div><div class="metric-sub">Action required</div></div>
    <div class="metric-card"><div class="metric-label">HR Modules</div><div class="metric-val">${HR_MODULES.length}</div><div class="metric-sub">Across all plans</div></div>
  `;

  renderTrialWarnings();

  document.getElementById('dash-tenant-list').innerHTML = state.tenants.map(t=>`
    <div style="display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid rgba(255,255,255,0.04);cursor:pointer;" onclick="openTenantDetail('${t.id}')">
      <div class="avatar" style="background:${t.color}22;color:${t.color};">${t.code.slice(0,2)}</div>
      <div style="flex:1;min-width:0;">
        <div style="font-size:0.76rem;font-weight:500;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${t.name}</div>
        <div style="font-size:0.63rem;color:var(--muted);">${t.employees} employees · ${t.lastActivity}</div>
      </div>
      <span class="badge badge-${t.status}">${t.status}</span>
    </div>
  `).join('') + '<div style="padding:10px 0 0;font-size:0.65rem;color:var(--muted);font-family:\'DM Mono\',monospace;text-align:center;">Click a tenant to manage →</div>';

  const modUsage = HR_MODULES.map(m => ({
    ...m,
    count: state.tenants.filter(t=>isModEnabled(t, m.id)).length,
  })).sort((a,b)=>b.count-a.count);

  document.getElementById('dash-modules').innerHTML = modUsage.slice(0,8).map(m => {
    const pct = Math.round(m.count / state.tenants.length * 100);
    return `
      <div style="margin-bottom:12px;">
        <div style="display:flex;justify-content:space-between;font-size:0.7rem;margin-bottom:4px;">
          <span style="color:var(--text);">${m.icon} ${m.name}</span>
          <span style="color:var(--muted);font-family:'DM Mono',monospace;">${m.count}/${state.tenants.length}</span>
        </div>
        <div class="usage-bar"><div class="usage-bar-fill" style="width:${pct}%;background:${m.color||'var(--accent)'};opacity:0.75;"></div></div>
      </div>
    `;
  }).join('');
}

function refreshDashboard() {
  renderDashboard();
  loadQuotaAlerts();
  toast('Dashboard refreshed', 'info');
}

// ── Quota alert banner on dashboard ──────────────────────────
async function loadQuotaAlerts() {
  try {
    const alerts = await HRMGMT.getQuotaAlerts(80);
    renderQuotaAlerts(alerts);
  } catch(e) {
    // silently fail — alerts are supplementary
  }
}

function renderQuotaAlerts(alerts) {
  const el = document.getElementById('dash-quota-alerts');
  if (!el) return;
  if (!alerts || alerts.length === 0) { el.innerHTML = ''; return; }

  el.innerHTML = `
    <div style="margin-bottom:20px;">
      <div style="font-family:'Syne',sans-serif;font-size:0.88rem;font-weight:700;margin-bottom:10px;display:flex;align-items:center;gap:8px;">
        <span style="color:var(--amber);">⚠</span> Quota Alerts
        <span style="font-size:0.6rem;font-family:'DM Mono',monospace;color:var(--muted);font-weight:400;">${alerts.length} tenant${alerts.length!==1?'s':''} need attention</span>
      </div>
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${alerts.map(a => {
          const isCrit = a.severity === 'critical';
          const color = isCrit ? 'var(--red)' : 'var(--amber)';
          const dim   = isCrit ? 'var(--red-dim)' : 'var(--amber-dim)';
          const label = a.type === 'employees'
            ? `${a.used} / ${a.limit} employees (${a.pct}%)`
            : `${(a.usedMb/1024).toFixed(1)} / ${(a.limitMb/1024).toFixed(0)} GB storage (${a.pct}%)`;
          return `
            <div style="display:flex;align-items:center;justify-content:space-between;padding:9px 14px;background:${dim};border:1px solid ${color}33;border-radius:7px;">
              <div style="display:flex;align-items:center;gap:10px;">
                <span style="font-size:0.68rem;font-weight:700;color:${color};font-family:'DM Mono',monospace;">${isCrit?'CRITICAL':'WARNING'}</span>
                <span style="font-size:0.75rem;color:var(--text);">${a.tenantName}</span>
                <span style="font-size:0.68rem;color:var(--muted);">${label}</span>
              </div>
              <button class="btn btn-xs btn-ghost" onclick="openTenantDetail('${a.tenantId}');showPage('tenants',document.querySelector('.nav-btn[onclick*=tenants]'))">View</button>
            </div>
          `;
        }).join('')}
      </div>
    </div>
  `;
}

// ── Trial expiry warnings ─────────────────────────────────────
function renderTrialWarnings() {
  const el = document.getElementById('dash-trial-warnings');
  if (!el) return;

  const today = new Date();
  const trials = state.tenants.filter(t => t.status === 'trial' && t.trialEndDate);
  const warnings = trials.map(t => {
    const end = new Date(t.trialEndDate);
    const daysLeft = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return {...t, daysLeft};
  }).filter(t => t.daysLeft <= 14).sort((a,b) => a.daysLeft - b.daysLeft);

  if (warnings.length === 0) { el.innerHTML = ''; return; }

  el.innerHTML = `
    <div style="margin-bottom:20px;">
      <div style="font-family:'Syne',sans-serif;font-size:0.88rem;font-weight:700;margin-bottom:10px;display:flex;align-items:center;gap:8px;">
        <span style="color:var(--blue);">⏱</span> Trial Expiry
        <span style="font-size:0.6rem;font-family:'DM Mono',monospace;color:var(--muted);font-weight:400;">${warnings.length} trial${warnings.length!==1?'s':''} expiring within 14 days</span>
      </div>
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${warnings.map(t => {
          const urgent = t.daysLeft <= 3;
          const color = urgent ? 'var(--red)' : 'var(--blue)';
          const dim   = urgent ? 'var(--red-dim)' : 'var(--blue-dim)';
          const label = t.daysLeft <= 0 ? 'Expired' : t.daysLeft === 1 ? 'Expires tomorrow' : `Expires in ${t.daysLeft} days`;
          return `
            <div style="display:flex;align-items:center;justify-content:space-between;padding:9px 14px;background:${dim};border:1px solid ${color}33;border-radius:7px;">
              <div style="display:flex;align-items:center;gap:10px;">
                <span style="font-size:0.75rem;color:var(--text);">${t.name}</span>
                <span style="font-size:0.65rem;font-family:'DM Mono',monospace;color:${color};">${label}</span>
                <span style="font-size:0.65rem;color:var(--muted);">${t.trialEndDate}</span>
              </div>
              <div style="display:flex;gap:6px;">
                <button class="btn btn-xs btn-ghost" onclick="openTenantDetail('${t.id}');showPage('tenants',document.querySelector('.nav-btn[onclick*=tenants]'))">Manage</button>
                <button class="btn btn-xs" style="background:var(--accent-dim);color:var(--accent);border:1px solid rgba(74,222,128,0.25);" onclick="reactivateTenant('${t.id}')">Convert</button>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    </div>
  `;
}

// ══════════════════════════════════════════════════════════════
// TENANTS TABLE
// ══════════════════════════════════════════════════════════════

function renderTenantsTable() {
  showTenantsListView();
  const q = (document.getElementById('tenant-search')?.value||'').toLowerCase();
  const sf = document.getElementById('tenant-status-filter')?.value||'';
  const filtered = state.tenants.filter(t =>
    (!q || t.name.toLowerCase().includes(q) || t.code.toLowerCase().includes(q)) &&
    (!sf || t.status===sf)
  );

  document.getElementById('tenant-tbody').innerHTML = filtered.map(t => `
    <tr>
      <td>
        <div style="display:flex;align-items:center;gap:9px;">
          <div class="avatar" style="background:${t.color}22;color:${t.color};">${t.code.slice(0,2)}</div>
          <div>
            <div style="font-weight:500;">${t.name}</div>
            <div style="font-size:0.63rem;color:var(--muted);font-family:'DM Mono',monospace;">${t.code}</div>
          </div>
        </div>
      </td>
      <td>
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="plan-badge plan-${t.plan}">${getPlanConfig(t.plan).label||t.plan}</span>
          ${t.isGovernment ? `<span style="font-size:0.58rem;padding:2px 6px;border-radius:4px;background:rgba(96,165,250,0.12);color:var(--blue);border:1px solid rgba(96,165,250,0.25);">🏛 Govt</span>` : ''}
        </div>
      </td>
      <td><span class="badge badge-${t.status}">${t.status}</span></td>
      <td>
        <div>${t.employees}<span style="font-size:0.63rem;color:var(--muted);"> / ${t.employeeLimit}</span></div>
        <div class="usage-bar" style="margin-top:4px;width:80px;"><div class="usage-bar-fill" style="width:${Math.min(100,Math.round(t.employees/t.employeeLimit*100))}%;background:var(--accent);"></div></div>
      </td>
      <td>
        <span style="font-size:0.75rem;">${hrModulesEnabled(t).length}</span>
        <span style="font-size:0.63rem;color:var(--muted);"> / ${HR_MODULES.length}</span>
      </td>
      <td style="font-family:'DM Mono',monospace;font-size:0.68rem;color:var(--muted);">${t.createdAt}</td>
      <td>
        <div style="display:flex;gap:6px;">
          <button class="btn btn-ghost btn-xs" onclick="openTenantDetail('${t.id}')">Manage</button>
          ${t.status==='suspended'
            ? `<button class="btn btn-xs" style="background:var(--accent-dim);color:var(--accent);border:1px solid rgba(74,222,128,0.25);" onclick="reactivateTenant('${t.id}')">Reactivate</button>`
            : t.status!=='inactive'
            ? `<button class="btn btn-danger btn-xs" onclick="suspendTenant('${t.id}')">Suspend</button>`
            : ''}
          <button class="btn btn-xs" style="background:transparent;border:1px solid rgba(248,113,113,0.2);color:var(--red);" onclick="confirmDeleteTenant('${t.id}')" title="Delete tenant">
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M6 4V2h4v2M5 4v9a1 1 0 001 1h4a1 1 0 001-1V4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </button>
        </div>
      </td>
    </tr>
  `).join('') || '<tr><td colspan="7" style="text-align:center;color:var(--muted);padding:30px;">No tenants found</td></tr>';
}

function filterTenants() { renderTenantsTable(); }

function showTenantsListView() {
  document.getElementById('tenants-list-view').style.display='';
  document.getElementById('tenant-detail-view').style.display='none';
}

// ── TENANT DATA IMPORT WIZARD ─────────────────────────────────────────────────

var _importState = { tenantId: null, rows: [], mapping: {}, step: 1 };

var IMPORT_FIELDS = [
  { id:'firstName',  label:'First Name',   required:true  },
  { id:'lastName',   label:'Last Name',    required:true  },
  { id:'employeeId', label:'Employee ID',  required:true  },
  { id:'email',      label:'Email',        required:false },
  { id:'phone',      label:'Phone',        required:false },
  { id:'division',   label:'Division',     required:false },
  { id:'grade',      label:'Grade',        required:false },
  { id:'gender',     label:'Gender',       required:false },
  { id:'joinDate',   label:'Join Date',    required:false },
];

function openImportWizard(tenantId) {
  _importState = { tenantId, rows: [], mapping: {}, step: 1 };
  _showImportModal();
}

function _showImportModal() {
  var old = document.getElementById('import-wizard-overlay');
  if (old) old.remove();
  var overlay = document.createElement('div');
  overlay.id = 'import-wizard-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.75);display:flex;align-items:center;justify-content:center;z-index:9999;padding:20px;';
  overlay.innerHTML = `<div style="background:var(--bg2);border:1px solid var(--border);border-radius:12px;max-width:700px;width:100%;max-height:90vh;overflow-y:auto;">
    <div style="padding:20px 24px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;">
      <div>
        <div style="font-weight:700;font-size:1rem;">Import Employee Data</div>
        <div style="font-size:0.68rem;color:var(--muted);margin-top:2px;">Step ${_importState.step} of 3</div>
      </div>
      <button onclick="document.getElementById('import-wizard-overlay').remove()" style="background:none;border:none;cursor:pointer;color:var(--muted);font-size:1.2rem;">✕</button>
    </div>
    <div id="import-wizard-body" style="padding:20px 24px;">
      ${_renderImportStep()}
    </div>
  </div>`;
  document.body.appendChild(overlay);
}

function _renderImportStep() {
  if (_importState.step === 1) return _importStep1();
  if (_importState.step === 2) return _importStep2();
  return _importStep3();
}

function _importStep1() {
  return `<div>
    <div style="font-size:0.75rem;color:var(--text);margin-bottom:14px;">Upload a CSV file. The first row must be column headers.</div>
    <div style="border:2px dashed var(--border);border-radius:8px;padding:32px;text-align:center;margin-bottom:16px;">
      <div style="font-size:0.85rem;color:var(--muted);margin-bottom:12px;"><i class="fas fa-file-csv" style="font-size:2rem;color:var(--accent);margin-bottom:8px;display:block;"></i>Drop CSV file or click to browse</div>
      <input type="file" id="import-csv-file" accept=".csv" style="display:none;" onchange="_importHandleFile(this)">
      <button class="btn btn-sm" onclick="document.getElementById('import-csv-file').click()">Choose File</button>
    </div>
    <div id="import-preview" style="display:none;overflow-x:auto;margin-bottom:16px;"></div>
    <div style="display:flex;justify-content:flex-end;gap:8px;">
      <button class="btn btn-sm" onclick="document.getElementById('import-wizard-overlay').remove()">Cancel</button>
      <button class="btn btn-sm" id="import-next-1" style="background:var(--accent-dim);color:var(--accent);border:1px solid rgba(74,222,128,0.25);display:none;" onclick="_importGoToStep2()">Next: Map Columns →</button>
    </div>
  </div>`;
}

function _importHandleFile(input) {
  var file = input.files[0];
  if (!file) return;
  var reader = new FileReader();
  reader.onload = function(e) {
    var lines = e.target.result.split('\n').map(l=>l.trim()).filter(Boolean);
    if (lines.length < 2) { alert('CSV must have a header row and at least one data row.'); return; }
    var headers = lines[0].split(',').map(h=>h.trim().replace(/^"|"$/g,''));
    var rows = lines.slice(1).map(l => {
      var vals = l.split(',').map(v=>v.trim().replace(/^"|"$/g,''));
      var obj = {};
      headers.forEach((h,i) => { obj[h] = vals[i] || ''; });
      return obj;
    });
    _importState.rows = rows;
    _importState.headers = headers;
    // Auto-detect mapping
    _importState.mapping = {};
    IMPORT_FIELDS.forEach(f => {
      var match = headers.find(h => h.toLowerCase().replace(/[\s_-]/g,'') === f.id.toLowerCase());
      if (match) _importState.mapping[f.id] = match;
    });
    // Show preview
    var preview = document.getElementById('import-preview');
    if (preview) {
      var previewRows = rows.slice(0,5);
      var hdrs = headers.map(h=>`<th style="font-size:0.62rem;padding:4px 8px;white-space:nowrap;">${h}</th>`).join('');
      var rws = previewRows.map(r => `<tr>${headers.map(h=>`<td style="font-size:0.62rem;padding:4px 8px;">${r[h]||''}</td>`).join('')}</tr>`).join('');
      preview.innerHTML = `<div style="font-size:0.68rem;color:var(--muted);margin-bottom:6px;">Preview (first 5 rows of ${rows.length} total)</div><table style="border-collapse:collapse;min-width:100%;"><thead><tr style="background:var(--bg3);">${hdrs}</tr></thead><tbody>${rws}</tbody></table>`;
      preview.style.display = 'block';
    }
    var nextBtn = document.getElementById('import-next-1');
    if (nextBtn) nextBtn.style.display = 'inline-block';
  };
  reader.readAsText(file);
}

function _importGoToStep2() {
  _importState.step = 2;
  document.getElementById('import-wizard-body').innerHTML = _renderImportStep();
}

function _importStep2() {
  var selOpts = `<option value="">— Skip —</option>` + (_importState.headers||[]).map(h=>`<option value="${h}">${h}</option>`).join('');
  var rows = IMPORT_FIELDS.map(f => {
    var cur = _importState.mapping[f.id] || '';
    var opts = selOpts.replace(`value="${cur}"`, `value="${cur}" selected`);
    return `<div style="display:flex;align-items:center;gap:12px;margin-bottom:10px;">
      <div style="width:120px;font-size:0.72rem;font-weight:500;">${f.label}${f.required?` <span style="color:var(--red);">*</span>`:''}</div>
      <select style="flex:1;padding:5px 8px;font-size:0.72rem;" onchange="_importSetMapping('${f.id}',this.value)">${opts}</select>
    </div>`;
  }).join('');
  return `<div>
    <div style="font-size:0.75rem;color:var(--text);margin-bottom:14px;">Map your CSV columns to employee fields. Required fields are marked with <span style="color:var(--red);">*</span>.</div>
    ${rows}
    <div id="import-map-error" style="display:none;color:var(--red);font-size:0.7rem;margin-top:8px;"></div>
    <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
      <button class="btn btn-sm btn-ghost" onclick="_importGoBack()">← Back</button>
      <button class="btn btn-sm" style="background:var(--accent-dim);color:var(--accent);border:1px solid rgba(74,222,128,0.25);" onclick="_importGoToStep3()">Next: Confirm →</button>
    </div>
  </div>`;
}

function _importSetMapping(fieldId, csvHeader) {
  _importState.mapping[fieldId] = csvHeader;
}

function _importGoBack() {
  _importState.step = Math.max(1, _importState.step - 1);
  document.getElementById('import-wizard-body').innerHTML = _renderImportStep();
}

function _importGoToStep3() {
  // Validate required mappings
  var missing = IMPORT_FIELDS.filter(f => f.required && !_importState.mapping[f.id]);
  var errEl = document.getElementById('import-map-error');
  if (missing.length) {
    if (errEl) { errEl.textContent = 'Required field(s) not mapped: ' + missing.map(f=>f.label).join(', '); errEl.style.display='block'; }
    return;
  }
  _importState.step = 3;
  document.getElementById('import-wizard-body').innerHTML = _renderImportStep();
}

function _importStep3() {
  // Build preview of what will be imported
  var total = _importState.rows.length;
  var mapped = _importState.rows.map(r => {
    var out = {};
    IMPORT_FIELDS.forEach(f => { if (_importState.mapping[f.id]) out[f.id] = r[_importState.mapping[f.id]] || ''; });
    return out;
  });
  var preview = mapped.slice(0,5).map(r => `<tr>${IMPORT_FIELDS.slice(0,5).map(f=>`<td style="font-size:0.62rem;padding:4px 8px;">${r[f.id]||'—'}</td>`).join('')}</tr>`).join('');
  var hdrs = IMPORT_FIELDS.slice(0,5).map(f=>`<th style="font-size:0.62rem;padding:4px 8px;">${f.label}</th>`).join('');
  return `<div>
    <div style="padding:12px 16px;border-radius:8px;background:rgba(74,222,128,0.07);border:1px solid rgba(74,222,128,0.2);margin-bottom:16px;font-size:0.75rem;">
      Ready to import <strong style="color:var(--accent);">${total} employee${total!==1?'s':''}</strong> into this tenant.
    </div>
    <div style="overflow-x:auto;margin-bottom:16px;">
      <div style="font-size:0.68rem;color:var(--muted);margin-bottom:6px;">Preview (first 5 rows)</div>
      <table style="border-collapse:collapse;min-width:100%;"><thead><tr style="background:var(--bg3);">${hdrs}</tr></thead><tbody>${preview}</tbody></table>
    </div>
    <div id="import-progress" style="display:none;margin-bottom:12px;">
      <div style="background:var(--bg3);border-radius:4px;height:8px;overflow:hidden;">
        <div id="import-prog-bar" style="width:0%;height:100%;background:var(--accent);transition:width 0.3s;"></div>
      </div>
      <div id="import-prog-label" style="font-size:0.68rem;color:var(--muted);margin-top:4px;"></div>
    </div>
    <div id="import-result" style="display:none;"></div>
    <div id="import-step3-actions" style="display:flex;justify-content:flex-end;gap:8px;">
      <button class="btn btn-sm btn-ghost" onclick="_importGoBack()">← Back</button>
      <button class="btn btn-sm" id="import-run-btn" style="background:var(--accent-dim);color:var(--accent);border:1px solid rgba(74,222,128,0.25);font-weight:600;" onclick="_importRunImport()">
        <i class="fas fa-play" style="margin-right:5px;"></i>Run Import
      </button>
    </div>
  </div>`;
}

async function _importRunImport() {
  var btn = document.getElementById('import-run-btn');
  var prog = document.getElementById('import-progress');
  var progBar = document.getElementById('import-prog-bar');
  var progLabel = document.getElementById('import-prog-label');
  var resultEl = document.getElementById('import-result');
  var actions = document.getElementById('import-step3-actions');

  if (btn) btn.disabled = true;
  if (prog) prog.style.display = 'block';
  if (progLabel) progLabel.textContent = 'Preparing import…';

  // Map rows to employee objects
  var employees = _importState.rows.map(r => {
    var out = {};
    IMPORT_FIELDS.forEach(f => { if (_importState.mapping[f.id]) out[f.id] = r[_importState.mapping[f.id]] || ''; });
    return out;
  });

  // Split into batches of 200
  var BATCH = 200;
  var totalImported = 0;
  var allErrors = [];

  try {
    for (var i = 0; i < employees.length; i += BATCH) {
      var chunk = employees.slice(i, i + BATCH);
      var pct = Math.round((i / employees.length) * 100);
      if (progBar) progBar.style.width = pct + '%';
      if (progLabel) progLabel.textContent = `Importing rows ${i+1}–${Math.min(i+BATCH, employees.length)} of ${employees.length}…`;
      var res = await HRMGMT.adminBatchImport({ tenantId: _importState.tenantId, employees: chunk });
      totalImported += res.imported || 0;
      if (res.errors && res.errors.length) allErrors = allErrors.concat(res.errors);
    }
    if (progBar) progBar.style.width = '100%';
    if (progLabel) progLabel.textContent = 'Done.';

    var errHtml = allErrors.length
      ? `<div style="margin-top:10px;max-height:120px;overflow-y:auto;font-size:0.65rem;color:var(--muted);">${allErrors.slice(0,20).map(e=>`<div>Row ${e.index+1} (${e.name}): ${e.error}</div>`).join('')}${allErrors.length>20?`<div>…and ${allErrors.length-20} more</div>`:''}</div>` : '';
    if (resultEl) {
      resultEl.style.display = 'block';
      resultEl.innerHTML = `<div style="padding:12px 16px;border-radius:8px;background:rgba(74,222,128,0.07);border:1px solid rgba(74,222,128,0.2);font-size:0.75rem;">
        ✅ <strong>Import complete.</strong> ${totalImported} employee${totalImported!==1?'s':''} imported${allErrors.length?`, ${allErrors.length} skipped`:''}. ${errHtml}
      </div>`;
    }
    if (actions) actions.innerHTML = `<button class="btn btn-sm" onclick="document.getElementById('import-wizard-overlay').remove()">Close</button>`;
  } catch (err) {
    if (resultEl) {
      resultEl.style.display = 'block';
      resultEl.innerHTML = `<div style="padding:12px 16px;border-radius:8px;background:rgba(248,113,113,0.07);border:1px solid rgba(248,113,113,0.2);font-size:0.75rem;color:var(--red);">
        Import failed: ${err.message || 'Unknown error'}
      </div>`;
    }
    if (btn) { btn.disabled = false; btn.textContent = 'Retry'; }
  }
}

async function suspendTenant(id) {
  const t = state.tenants.find(x=>x.id===id);
  if(!t) return;
  try {
    await HRMGMT.suspendTenant(id);
    t.status = 'suspended';
    logActivity('tenant', `Tenant "${t.name}" suspended`, 'var(--red)');
    renderTenantsTable();
    renderDashboard();
    toast(`${t.name} has been suspended`, 'error');
  } catch(err) {
    toast(err.message || 'Failed to suspend tenant', 'error');
  }
}

async function reactivateTenant(id) {
  const t = state.tenants.find(x=>x.id===id);
  if(!t) return;
  try {
    await HRMGMT.reactivateTenant(id);
    t.status = 'active';
    logActivity('tenant', `Tenant "${t.name}" reactivated`, 'var(--accent)');
    renderTenantsTable();
    renderDashboard();
    toast(`${t.name} has been reactivated`, 'success');
  } catch(err) {
    toast(err.message || 'Failed to reactivate tenant', 'error');
  }
}

// ── accessTenantPortal ──────────────────────────────────────────────────────
// Opens the tenant's HR portal in a new tab, signed in as a dedicated
// "HR Support" account (full_admin rights, time-boxed, fully audited — see
// startSupportSession in functions/index.js) so a super-admin can assist or
// act on the tenant's behalf without ever knowing an admin's password.
const HR_APP_URL = 'https://portal.hr.bytebrew.online';

async function accessTenantPortal(id) {
  const t = state.tenants.find(x=>x.id===id);
  if(!t) return;
  // Open the tab synchronously, in direct response to the click — browsers
  // block window.open() as a popup once it happens after an await, since it
  // no longer looks like it came straight from the user gesture. We open a
  // blank tab now and navigate it once the token comes back.
  const tab = window.open('about:blank', '_blank', 'noopener');
  try {
    const { customToken, gcipTenantId } = await HRMGMT.startSupportSession(id);
    logActivity('tenant', `Support session started for "${t.name}"`, 'var(--accent)');
    const url = `${HR_APP_URL}/#support=${encodeURIComponent(customToken)}&gt=${encodeURIComponent(gcipTenantId)}`;
    if (tab) tab.location.href = url;
    else window.open(url, '_blank', 'noopener');
    toast(`Opening ${t.name}'s portal as HR Support…`, 'success');
  } catch(err) {
    if (tab) tab.close();
    toast(err.message || 'Failed to start support session', 'error');
  }
}

// ── resendAdminInvite ─────────────────────────────────────────────────────────
// Creates the admin GCIP account if it doesn't exist, then sends the
// password-reset / invite email. Idempotent — safe to call multiple times.
// Called from the Resend Invite button in renderTenantDetail.
async function resendAdminInvite(tenantId, email) {
  if (!email) { toast('No admin email on record for this tenant', 'error'); return; }
  const btn = document.querySelector(`[onclick*="resendAdminInvite('${tenantId}"]`);
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  try {
    const res = await HRMGMT.provisionTenantAdmin(tenantId, email);
    const msg = res.created
      ? `Admin account created and invite sent to ${email}`
      : `Invite email resent to ${email}`;
    toast(msg, 'success');
    logActivity('auth', msg, 'var(--cyan)');
  } catch (err) {
    toast(err.message || 'Failed to send invite email', 'error');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<i class="fas fa-envelope" style="margin-right:5px;"></i>Resend Invite';
    }
  }
}


// ── openTenantManage — unified tenant management modal ────────────────────────
// Escalating management actions in a single modal, ordered by severity:
//   Level 0: Set / Reset Domain  (blue  — identity, no data risk)
//   Level 1: Reset Tenant        (amber — wipes users + HR data, re-onboards)
//   Level 2: Clear All Data      (red   — full purge, slot preserved)
//   Level 3: Delete Tenant       (deep  — complete removal, no recovery)
async function openTenantManage(id) {
  const t = state.tenants.find(x => x.id === id);
  if (!t) return;

  const old = document.getElementById('tenant-manage-overlay');
  if (old) old.remove();

  const overlay = document.createElement('div');
  overlay.id = 'tenant-manage-overlay';
  overlay.style.cssText =
    'position:fixed;inset:0;background:#0a0c0f;' +
    'display:flex;align-items:flex-start;justify-content:center;z-index:9999;' +
    'padding:40px 16px;overflow-y:auto;';

  const currentDomains = (t.allowedDomains || []).join(', ') || t.emailDomain || '';
  const currentAdminEmail = t.adminEmail || '';

  // Level badge helper
  const lvl = (n, color, label) =>
    `<span style="font-size:0.55rem;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;` +
    `padding:2px 7px;border-radius:4px;background:${color}18;color:${color};border:1px solid ${color}30;` +
    `font-family:'DM Mono',monospace;">${label}</span>`;

  overlay.innerHTML = `
    <div id="tm-panel" style="background:var(--bg1);border:1px solid var(--border);
      border-radius:16px;width:540px;max-width:100%;
      box-shadow:0 40px 120px rgba(0,0,0,0.9);">

      <!-- ── Header ── -->
      <div style="padding:20px 24px;border-bottom:1px solid var(--border);
        display:flex;align-items:center;justify-content:space-between;
        border-radius:16px 16px 0 0;background:var(--bg2);">
        <div style="display:flex;align-items:center;gap:12px;">
          <div class="avatar" style="width:38px;height:38px;font-size:0.8rem;
            background:${t.color}22;color:${t.color};border-radius:9px;">${t.code.slice(0,2)}</div>
          <div>
            <div style="font-family:'Syne',sans-serif;font-size:0.95rem;font-weight:700;">${t.name}</div>
            <div style="font-size:0.63rem;color:var(--muted);font-family:'DM Mono',monospace;margin-top:1px;">
              ${t.code} · ${t.orgType || '—'} · <span class="badge badge-${t.status}" style="font-size:0.58rem;padding:1px 7px;">${t.status}</span>
            </div>
          </div>
        </div>
        <button onclick="document.getElementById('tenant-manage-overlay').remove()"
          style="background:var(--bg3);border:1px solid var(--border);color:var(--muted);cursor:pointer;
          width:30px;height:30px;border-radius:7px;display:flex;align-items:center;justify-content:center;
          font-size:1rem;line-height:1;transition:all 0.15s;" onmouseover="this.style.color='var(--text)'"
          onmouseout="this.style.color='var(--muted)'">&times;</button>
      </div>

      <!-- ── Escalation note ── -->
      <div style="padding:14px 24px;border-bottom:1px solid var(--border);background:var(--bg2);
        display:flex;align-items:center;gap:10px;">
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;opacity:0.4;">
          <circle cx="8" cy="8" r="6" stroke="var(--text)" stroke-width="1.4"/>
          <path d="M8 7v4M8 5.5v.5" stroke="var(--text)" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <span style="font-size:0.65rem;color:var(--muted);line-height:1.5;">
          Actions below escalate in severity. Each level requires explicit confirmation.
          Domain management is non-destructive. Deletion is permanent and irreversible.
        </span>
      </div>

      <div style="padding:16px 20px;display:flex;flex-direction:column;gap:10px;">

        <!-- ══ LEVEL 0: Domain ════════════════════════════════════════════ -->
        <div style="border:1px solid rgba(96,165,250,0.25);border-radius:10px;overflow:hidden;">
          <button onclick="tmToggle('domain')"
            style="width:100%;padding:13px 16px;background:rgba(96,165,250,0.04);border:none;
            display:flex;align-items:center;justify-content:space-between;cursor:pointer;text-align:left;">
            <div style="display:flex;align-items:center;gap:11px;">
              <div style="width:32px;height:32px;border-radius:8px;background:rgba(96,165,250,0.10);
                border:1px solid rgba(96,165,250,0.22);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6" stroke="var(--blue)" stroke-width="1.4"/>
                  <path d="M8 2c-1.5 2-2 3.7-2 6s.5 4 2 6M8 2c1.5 2 2 3.7 2 6s-.5 4-2 6M2 8h12" stroke="var(--blue)" stroke-width="1.3"/>
                </svg>
              </div>
              <div style="text-align:left;">
                <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px;">
                  <span style="font-size:0.82rem;font-weight:600;color:var(--text);">Set / Reset Domain</span>
                  ${lvl(0,'#60a5fa','Non-destructive')}
                </div>
                <div style="font-size:0.63rem;color:var(--muted);">
                  Control which email domains can log in to this tenant
                  ${currentDomains ? `· <span style="font-family:'DM Mono',monospace;color:var(--blue);">${currentDomains}</span>` : '· <em>no domains set</em>'}
                </div>
              </div>
            </div>
            <svg id="tm-chevron-domain" width="13" height="13" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;transition:transform 0.2s;color:var(--muted);">
              <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
          </button>
          <div id="tm-body-domain" style="display:none;padding:16px;border-top:1px solid rgba(96,165,250,0.15);background:rgba(96,165,250,0.02);">
            <p style="font-size:0.71rem;color:var(--muted);margin-bottom:14px;line-height:1.6;">
              The login page resolves tenants by matching the user's email domain against this list.
              Separate multiple domains with commas. Leave blank to allow any domain (not recommended).
            </p>
            <label style="font-size:0.6rem;font-weight:700;color:var(--muted);text-transform:uppercase;
              letter-spacing:0.10em;font-family:'DM Mono',monospace;display:block;margin-bottom:6px;">Allowed Domains</label>
            <div style="display:flex;gap:0;margin-bottom:14px;">
              <span style="padding:0 11px;height:38px;display:flex;align-items:center;background:var(--bg3);
                border:1px solid var(--border);border-right:none;border-radius:7px 0 0 7px;
                font-size:0.75rem;color:var(--muted);font-family:'DM Mono',monospace;">@</span>
              <input class="form-input" id="tm-domains-input" style="border-radius:0 7px 7px 0;flex:1;"
                placeholder="works.gov.pg, casa.gov.pg" value="${currentDomains}"/>
            </div>
            <div id="tm-domain-err" style="display:none;font-size:0.72rem;color:var(--red);margin-bottom:10px;
              padding:9px 12px;background:rgba(248,113,113,0.07);border-radius:7px;border:1px solid rgba(248,113,113,0.2);"></div>
            <button id="tm-domain-btn" onclick="tmSaveDomain('${t.id}')"
              style="background:rgba(96,165,250,0.10);border:1px solid rgba(96,165,250,0.30);color:var(--blue);
              border-radius:7px;padding:9px 18px;font-size:0.78rem;font-weight:600;cursor:pointer;
              font-family:'DM Sans',sans-serif;transition:all 0.15s;">
              Save Domain Settings
            </button>
          </div>
        </div>

        <!-- ══ LEVEL 1: Reset Tenant ══════════════════════════════════════ -->
        <div style="border:1px solid rgba(251,191,36,0.25);border-radius:10px;overflow:hidden;">
          <button onclick="tmToggle('reset')"
            style="width:100%;padding:13px 16px;background:rgba(251,191,36,0.04);border:none;
            display:flex;align-items:center;justify-content:space-between;cursor:pointer;text-align:left;">
            <div style="display:flex;align-items:center;gap:11px;">
              <div style="width:32px;height:32px;border-radius:8px;background:rgba(251,191,36,0.10);
                border:1px solid rgba(251,191,36,0.22);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M2.5 8A5.5 5.5 0 008 13.5M13.5 8A5.5 5.5 0 008 2.5a5.5 5.5 0 00-4 1.73" stroke="var(--amber)" stroke-width="1.4" stroke-linecap="round"/>
                  <path d="M3.5 2v2.5H6" stroke="var(--amber)" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div style="text-align:left;">
                <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px;">
                  <span style="font-size:0.82rem;font-weight:600;color:var(--amber);">Reset Tenant</span>
                  ${lvl(1,'#fbbf24','Recoverable')}
                </div>
                <div style="font-size:0.63rem;color:var(--muted);">New admin · All users removed · HR data wiped · Re-onboard from scratch</div>
              </div>
            </div>
            <svg id="tm-chevron-reset" width="13" height="13" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;transition:transform 0.2s;color:var(--muted);">
              <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
          </button>
          <div id="tm-body-reset" style="display:none;padding:16px;border-top:1px solid rgba(251,191,36,0.15);background:rgba(251,191,36,0.02);">
            <div style="display:flex;gap:8px;margin-bottom:14px;padding:10px 12px;
              background:rgba(251,191,36,0.06);border-radius:8px;border:1px solid rgba(251,191,36,0.15);">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;margin-top:1px;">
                <path d="M8 2a6 6 0 100 12A6 6 0 008 2zM8 7v4M8 5.5v.01" stroke="var(--amber)" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <div style="font-size:0.70rem;color:var(--muted);line-height:1.6;">
                Wipes <strong style="color:var(--text);">all auth accounts</strong> and
                <strong style="color:var(--text);">all HR data</strong> for this tenant.
                Creates a fresh admin account and sends an invite email.
                The tenant <strong style="color:var(--text);">code, plan, and GCIP tenant</strong> are preserved.
              </div>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
              <div>
                <label style="font-size:0.6rem;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:0.10em;font-family:'DM Mono',monospace;display:block;margin-bottom:5px;">Admin Email *</label>
                <input class="form-input" id="tm-reset-email" placeholder="admin@org.gov.pg" value="${currentAdminEmail}"/>
              </div>
              <div>
                <label style="font-size:0.6rem;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:0.10em;font-family:'DM Mono',monospace;display:block;margin-bottom:5px;">First Name *</label>
                <input class="form-input" id="tm-reset-first" placeholder="First name" value="${(t.adminProfile && t.adminProfile.firstName) || ''}"/>
              </div>
              <div>
                <label style="font-size:0.6rem;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:0.10em;font-family:'DM Mono',monospace;display:block;margin-bottom:5px;">Last Name *</label>
                <input class="form-input" id="tm-reset-last" placeholder="Last name" value="${(t.adminProfile && t.adminProfile.lastName) || ''}"/>
              </div>
              <div>
                <label style="font-size:0.6rem;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:0.10em;font-family:'DM Mono',monospace;display:block;margin-bottom:5px;">Phone</label>
                <input class="form-input" id="tm-reset-phone" placeholder="+675 ..." value="${(t.adminProfile && t.adminProfile.phone) || ''}"/>
              </div>
            </div>
            <div id="tm-reset-err" style="display:none;font-size:0.72rem;color:var(--amber);margin-bottom:10px;
              padding:9px 12px;background:rgba(251,191,36,0.07);border-radius:7px;border:1px solid rgba(251,191,36,0.2);"></div>
            <button id="tm-reset-btn" onclick="tmDoReset('${t.id}')"
              style="width:100%;background:rgba(251,191,36,0.10);border:1px solid rgba(251,191,36,0.35);
              color:var(--amber);border-radius:7px;padding:10px 16px;font-size:0.8rem;font-weight:700;
              cursor:pointer;font-family:'DM Sans',sans-serif;transition:all 0.15s;display:flex;align-items:center;justify-content:center;gap:7px;">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M2.5 8A5.5 5.5 0 008 13.5M13.5 8A5.5 5.5 0 008 2.5a5.5 5.5 0 00-4 1.73" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M3.5 2v2.5H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              Reset Tenant &amp; Send Invite
            </button>
          </div>
        </div>

        <!-- ══ LEVEL 2: Clear All Data ════════════════════════════════════ -->
        <div style="border:1px solid rgba(248,113,113,0.28);border-radius:10px;overflow:hidden;">
          <button onclick="tmToggle('purge')"
            style="width:100%;padding:13px 16px;background:rgba(248,113,113,0.04);border:none;
            display:flex;align-items:center;justify-content:space-between;cursor:pointer;text-align:left;">
            <div style="display:flex;align-items:center;gap:11px;">
              <div style="width:32px;height:32px;border-radius:8px;background:rgba(248,113,113,0.10);
                border:1px solid rgba(248,113,113,0.25);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M2 4h12M6 4V2h4v2M5 4v9a1 1 0 001 1h4a1 1 0 001-1V4" stroke="var(--red)" stroke-width="1.4" stroke-linecap="round"/>
                  <path d="M7 7v4M9 7v4" stroke="var(--red)" stroke-width="1.3" stroke-linecap="round"/>
                </svg>
              </div>
              <div style="text-align:left;">
                <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px;">
                  <span style="font-size:0.82rem;font-weight:600;color:var(--red);">Clear All Data</span>
                  ${lvl(2,'#f87171','Destructive')}
                </div>
                <div style="font-size:0.63rem;color:var(--muted);">Full purge of every HR record and auth user — tenant slot preserved for re-join</div>
              </div>
            </div>
            <svg id="tm-chevron-purge" width="13" height="13" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;transition:transform 0.2s;color:var(--muted);">
              <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
          </button>
          <div id="tm-body-purge" style="display:none;padding:16px;border-top:1px solid rgba(248,113,113,0.15);background:rgba(248,113,113,0.02);">
            <div style="padding:12px 14px;background:rgba(248,113,113,0.06);border:1px solid rgba(248,113,113,0.18);
              border-radius:8px;margin-bottom:14px;">
              <div style="font-size:0.65rem;font-weight:700;color:var(--red);text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">What gets deleted</div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:3px 12px;font-size:0.67rem;color:var(--muted);">
                <span>✗ All auth accounts</span><span>✗ All employee records</span>
                <span>✗ Leave &amp; training data</span><span>✗ Appraisals &amp; KPIs</span>
                <span>✗ Tasks &amp; discipline</span><span>✗ Documents &amp; pay scale</span>
                <span>✗ Roles &amp; access rules</span><span>✗ Onboarding checklist</span>
              </div>
              <div style="margin-top:10px;padding-top:8px;border-top:1px solid rgba(248,113,113,0.15);font-size:0.67rem;">
                <span style="color:var(--accent);">✓ Tenant code preserved</span>
                <span style="color:var(--muted);margin:0 8px;">·</span>
                <span style="color:var(--accent);">✓ Plan retained</span>
                <span style="color:var(--muted);margin:0 8px;">·</span>
                <span style="color:var(--accent);">✓ Can re-join as new org</span>
              </div>
            </div>
            <label style="font-size:0.6rem;font-weight:700;color:var(--muted);text-transform:uppercase;
              letter-spacing:0.10em;font-family:'DM Mono',monospace;display:block;margin-bottom:5px;">
              Type <strong style="color:var(--text);">${t.code}</strong> to unlock
            </label>
            <input class="form-input" id="tm-purge-code" placeholder="${t.code}" autocomplete="off"
              style="border-color:rgba(248,113,113,0.3);margin-bottom:10px;"
              oninput="tmCheckPurge('${t.code}')"/>
            <div id="tm-purge-err" style="display:none;font-size:0.72rem;color:var(--red);margin-bottom:10px;
              padding:9px 12px;background:rgba(248,113,113,0.07);border-radius:7px;border:1px solid rgba(248,113,113,0.2);"></div>
            <button id="tm-purge-btn" disabled onclick="tmDoPurge('${t.id}','${t.code}')"
              style="width:100%;background:rgba(248,113,113,0.06);border:1px solid rgba(248,113,113,0.25);
              color:var(--red);border-radius:7px;padding:10px 16px;font-size:0.8rem;font-weight:700;
              cursor:pointer;font-family:'DM Sans',sans-serif;opacity:0.45;transition:all 0.15s;
              display:flex;align-items:center;justify-content:center;gap:7px;">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 2a6 6 0 100 12A6 6 0 008 2zM8 7v4M8 5.5v.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              Permanently Clear All Data
            </button>
          </div>
        </div>

        <!-- ══ SUSPEND / REACTIVATE ══════════════════════════════════════ -->
        <div style="border:1px solid var(--border);border-radius:10px;overflow:hidden;">
          <button onclick="tmToggle('suspend')"
            style="width:100%;padding:13px 16px;background:var(--bg3);border:none;
            display:flex;align-items:center;justify-content:space-between;cursor:pointer;text-align:left;">
            <div style="display:flex;align-items:center;gap:11px;">
              <div style="width:32px;height:32px;border-radius:8px;background:rgba(251,191,36,0.08);
                border:1px solid rgba(251,191,36,0.18);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6" stroke="var(--amber)" stroke-width="1.4"/>
                  <path d="M6 6h.01M10 6h.01M6 10h4" stroke="var(--amber)" stroke-width="1.4" stroke-linecap="round"/>
                </svg>
              </div>
              <div style="text-align:left;">
                <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px;">
                  <span style="font-size:0.82rem;font-weight:600;color:var(--text);">${t.status === 'suspended' ? 'Reactivate Tenant' : 'Suspend Tenant'}</span>
                  <span style="font-size:0.55rem;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;padding:2px 7px;border-radius:4px;background:${t.status === 'suspended' ? 'rgba(74,222,128,0.10)' : 'rgba(251,191,36,0.10)'};color:${t.status === 'suspended' ? 'var(--accent)' : 'var(--amber)'};border:1px solid ${t.status === 'suspended' ? 'rgba(74,222,128,0.25)' : 'rgba(251,191,36,0.25)'};font-family:'DM Mono',monospace;">${t.status === 'suspended' ? 'Restore access' : 'Revoke access'}</span>
                </div>
                <div style="font-size:0.63rem;color:var(--muted);">${t.status === 'suspended' ? 'Restore tenant access — users can log in again' : 'Block all logins immediately — data is untouched'}</div>
              </div>
            </div>
            <svg id="tm-chevron-suspend" width="13" height="13" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;transition:transform 0.2s;color:var(--muted);">
              <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
          </button>
          <div id="tm-body-suspend" style="display:none;padding:16px;border-top:1px solid var(--border);background:var(--bg3);">
            ${t.status === 'suspended' ? `
              <div style="font-size:0.71rem;color:var(--muted);margin-bottom:14px;line-height:1.6;
                padding:10px 12px;background:rgba(74,222,128,0.04);border:1px solid rgba(74,222,128,0.15);border-radius:8px;">
                Reactivating will restore full access for all users in this tenant immediately.
                No data is affected.
              </div>
              <button onclick="reactivateTenant('${t.id}');document.getElementById('tenant-manage-overlay').remove();openTenantDetail('${t.id}');"
                style="width:100%;background:rgba(74,222,128,0.10);border:1px solid rgba(74,222,128,0.28);
                color:var(--accent);border-radius:7px;padding:10px 16px;font-size:0.8rem;font-weight:700;
                cursor:pointer;font-family:'DM Sans',sans-serif;transition:all 0.15s;
                display:flex;align-items:center;justify-content:center;gap:7px;">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M2.5 8A5.5 5.5 0 008 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M13.5 8A5.5 5.5 0 008 2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                Reactivate Tenant
              </button>
            ` : `
              <div style="font-size:0.71rem;color:var(--muted);margin-bottom:14px;line-height:1.6;
                padding:10px 12px;background:rgba(251,191,36,0.04);border:1px solid rgba(251,191,36,0.15);border-radius:8px;">
                Suspending blocks all logins immediately. Existing sessions will be invalidated.
                All data remains intact and the tenant can be reactivated at any time.
              </div>
              <button onclick="suspendTenant('${t.id}');document.getElementById('tenant-manage-overlay').remove();openTenantDetail('${t.id}');"
                style="width:100%;background:rgba(251,191,36,0.08);border:1px solid rgba(251,191,36,0.28);
                color:var(--amber);border-radius:7px;padding:10px 16px;font-size:0.8rem;font-weight:700;
                cursor:pointer;font-family:'DM Sans',sans-serif;transition:all 0.15s;
                display:flex;align-items:center;justify-content:center;gap:7px;">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5"/><path d="M6 6h.01M10 6h.01M6 10h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
                Suspend Tenant
              </button>
            `}
          </div>
        </div>

        <!-- ══ LEVEL 3: Delete Tenant ═════════════════════════════════════ -->
        <div style="border:1px solid rgba(248,113,113,0.15);border-radius:10px;overflow:hidden;opacity:0.9;">
          <button onclick="tmToggle('delete')"
            style="width:100%;padding:13px 16px;background:rgba(248,113,113,0.02);border:none;
            display:flex;align-items:center;justify-content:space-between;cursor:pointer;text-align:left;">
            <div style="display:flex;align-items:center;gap:11px;">
              <div style="width:32px;height:32px;border-radius:8px;background:rgba(248,113,113,0.07);
                border:1px solid rgba(248,113,113,0.15);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M3 6h14M8 6V4h4v2M9 10v5M11 10v5" stroke="rgba(248,113,113,0.65)" stroke-width="1.6" stroke-linecap="round"/>
                  <rect x="4" y="6" width="12" height="11" rx="2" stroke="rgba(248,113,113,0.65)" stroke-width="1.4"/>
                </svg>
              </div>
              <div style="text-align:left;">
                <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px;">
                  <span style="font-size:0.82rem;font-weight:600;color:rgba(248,113,113,0.75);">Delete Tenant</span>
                  ${lvl(3,'#f87171','Permanent · No recovery')}
                </div>
                <div style="font-size:0.63rem;color:var(--muted);">Complete removal — GCIP tenant, all data, and slot gone forever</div>
              </div>
            </div>
            <svg id="tm-chevron-delete" width="13" height="13" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;transition:transform 0.2s;color:var(--muted);">
              <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            </svg>
          </button>
          <div id="tm-body-delete" style="display:none;padding:16px;border-top:1px solid rgba(248,113,113,0.12);background:rgba(248,113,113,0.015);">
            <div style="padding:10px 14px;background:rgba(248,113,113,0.04);border:1px solid rgba(248,113,113,0.12);
              border-radius:8px;font-size:0.71rem;color:var(--muted);margin-bottom:14px;line-height:1.6;">
              Permanently removes <strong style="color:var(--text);">${t.name}</strong> from the platform entirely.
              The GCIP auth tenant, all Firestore documents, and the tenant slot are deleted.
              The code <strong style="font-family:'DM Mono',monospace;color:var(--text);">${t.code}</strong> becomes
              available for re-registration. <strong style="color:var(--red);">No recovery is possible.</strong>
            </div>
            <label style="font-size:0.6rem;font-weight:700;color:var(--muted);text-transform:uppercase;
              letter-spacing:0.10em;font-family:'DM Mono',monospace;display:block;margin-bottom:5px;">
              Type <strong style="color:var(--text);">${t.name}</strong> exactly to confirm
            </label>
            <input class="form-input" id="tm-delete-name" placeholder="${t.name}" autocomplete="off"
              style="border-color:rgba(248,113,113,0.2);margin-bottom:10px;"
              oninput="tmCheckDelete('${t.name.replace(/'/g, "\\'")}')"/>
            <div id="tm-delete-err" style="display:none;font-size:0.72rem;color:var(--red);margin-bottom:10px;
              padding:9px 12px;background:rgba(248,113,113,0.07);border-radius:7px;border:1px solid rgba(248,113,113,0.2);"></div>
            <button id="tm-delete-btn" disabled onclick="tmDoDelete('${t.id}','${t.name.replace(/'/g, "\\'")}')"
              style="width:100%;background:transparent;border:1px solid rgba(248,113,113,0.28);
              color:rgba(248,113,113,0.7);border-radius:7px;padding:10px 16px;font-size:0.8rem;font-weight:600;
              cursor:pointer;font-family:'DM Sans',sans-serif;opacity:0.45;transition:all 0.15s;
              display:flex;align-items:center;justify-content:center;gap:7px;">
              <svg width="13" height="13" viewBox="0 0 20 20" fill="none"><path d="M3 6h14M8 6V4h4v2M9 10v5M11 10v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><rect x="4" y="6" width="12" height="11" rx="2" stroke="currentColor" stroke-width="1.4"/></svg>
              Delete Permanently
            </button>
          </div>
        </div>

      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
}

// ── Accordion toggle ──────────────────────────────────────────────────────────
function tmToggle(section) {
  const sections = ['domain', 'reset', 'purge', 'suspend', 'delete'];
  sections.forEach(s => {
    const body = document.getElementById('tm-body-' + s);
    const chevron = document.getElementById('tm-chevron-' + s);
    if (!body) return;
    if (s === section) {
      const isOpen = body.style.display !== 'none';
      body.style.display = isOpen ? 'none' : 'block';
      if (chevron) chevron.style.transform = isOpen ? '' : 'rotate(180deg)';
    } else {
      body.style.display = 'none';
      if (chevron) chevron.style.transform = '';
    }
  });
}

// ── Level 1: Save domain ──────────────────────────────────────────────────────
async function tmSaveDomain(id) {
  const t = state.tenants.find(x => x.id === id);
  if (!t) return;
  const raw = document.getElementById('tm-domains-input')?.value || '';
  const errDiv = document.getElementById('tm-domain-err');
  const btn = document.getElementById('tm-domain-btn');
  errDiv.style.display = 'none';

  // Parse comma-separated list
  const domains = raw.split(',').map(d => d.trim().toLowerCase().replace(/^@/, '')).filter(Boolean);
  const domainRe = /^[a-z0-9]([a-z0-9\-]*[a-z0-9])?(\.[a-z]{2,})+$/;
  const invalid = domains.filter(d => !domainRe.test(d));
  if (invalid.length) {
    errDiv.textContent = 'Invalid domain(s): ' + invalid.join(', ');
    errDiv.style.display = 'block';
    return;
  }

  btn.disabled = true;
  btn.textContent = 'Saving…';
  try {
    await HRMGMT.saveTenantConfig(id, {
      allowedDomains: domains,
      emailDomain: domains[0] || '',
    });
    t.allowedDomains = domains;
    t.emailDomain = domains[0] || '';
    toast('Domain settings saved — login will now resolve to this tenant for: ' + (domains.join(', ') || 'any domain'), 'success');
    btn.textContent = '✓ Saved';
    setTimeout(() => { btn.textContent = 'Save Domain Settings'; btn.disabled = false; }, 2000);
  } catch (err) {
    errDiv.textContent = err.message || 'Failed to save domain settings';
    errDiv.style.display = 'block';
    btn.disabled = false;
    btn.textContent = 'Save Domain Settings';
  }
}

// ── Level 2: Reset tenant ─────────────────────────────────────────────────────
async function tmDoReset(id) {
  const t = state.tenants.find(x => x.id === id);
  if (!t) return;
  const email = document.getElementById('tm-reset-email')?.value.trim();
  const first  = document.getElementById('tm-reset-first')?.value.trim();
  const last   = document.getElementById('tm-reset-last')?.value.trim();
  const phone  = document.getElementById('tm-reset-phone')?.value.trim();
  const errDiv = document.getElementById('tm-reset-err');
  const btn    = document.getElementById('tm-reset-btn');
  errDiv.style.display = 'none';

  if (!email || !first || !last) {
    errDiv.textContent = 'Admin email, first name and last name are required.';
    errDiv.style.display = 'block'; return;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errDiv.textContent = 'Enter a valid email address.';
    errDiv.style.display = 'block'; return;
  }

  btn.disabled = true;
  btn.textContent = 'Resetting…';
  try {
    const res = await HRMGMT.resetTenant(id, {
      adminEmail: email,
      adminFirstName: first,
      adminLastName: last,
      ...(phone && { adminPhone: phone }),
    });

    const idx = state.tenants.findIndex(x => x.id === id);
    if (idx !== -1) {
      state.tenants[idx] = {
        ...state.tenants[idx],
        adminEmail: email,
        adminProfile: { email, firstName: first, lastName: last, ...(phone && { phone }) },
        employees: 0, activeUsers: 0, storageUsedMb: 0,
        status: 'active', lastActivity: 'Just reset',
      };
    }
    logActivity('tenant', `Tenant "${t.name}" (${t.code}) reset. New admin: ${email}. ${res.usersSuspended || 0} non-admin user(s) suspended. ${res.docsArchived || 0} HR docs archived (${res.archiveKey || 'n/a'}).`, 'var(--amber)');
    document.getElementById('tenant-manage-overlay').remove();
    openTenantDetail(id);
    toast(`Tenant reset — invite sent to ${email}`, 'success');
  } catch (err) {
    errDiv.textContent = err.message || 'Reset failed';
    errDiv.style.display = 'block';
    btn.disabled = false;
    btn.textContent = '↺ Reset Tenant & Send Invite';
  }
}

// ── Level 3: Purge unlock check ───────────────────────────────────────────────
function tmCheckPurge(code) {
  const val = document.getElementById('tm-purge-code')?.value.trim().toUpperCase();
  const btn = document.getElementById('tm-purge-btn');
  if (!btn) return;
  const ok = val === code.toUpperCase();
  btn.disabled = !ok;
  btn.style.opacity = ok ? '1' : '0.45';
}

// ── Level 3: Do purge ─────────────────────────────────────────────────────────
async function tmDoPurge(id, code) {
  const t = state.tenants.find(x => x.id === id);
  if (!t) return;
  const btn    = document.getElementById('tm-purge-btn');
  const errDiv = document.getElementById('tm-purge-err');
  errDiv.style.display = 'none';
  btn.disabled = true;
  btn.textContent = 'Clearing all data…';
  try {
    const res = await HRMGMT.purgeTenantData(id, code);
    const idx = state.tenants.findIndex(x => x.id === id);
    if (idx !== -1) {
      state.tenants[idx] = {
        ...state.tenants[idx],
        status: 'active',
        employees: 0, activeUsers: 0, storageUsedMb: 0,
      };
    }
    logActivity('tenant', `ALL DATA PURGED for "${t.name}" (${t.code}). ${res.docsDeleted} HR docs deleted. ${res.usersSuspended} non-admin account(s) suspended pending re-onboarding.`, 'var(--red)');
    document.getElementById('tenant-manage-overlay').remove();
    openTenantDetail(id);
    toast(`All data cleared for ${t.name} — ${res.usersSuspended} non-admin user(s) suspended, admins can log in now`, 'success');
  } catch (err) {
    errDiv.textContent = err.message || 'Purge failed';
    errDiv.style.display = 'block';
    btn.disabled = false;
    btn.textContent = '⚠ Permanently Clear All Data';
    tmCheckPurge(code);
  }
}

// ── Level 4: Delete unlock check ──────────────────────────────────────────────
function tmCheckDelete(name) {
  const val = document.getElementById('tm-delete-name')?.value.trim();
  const btn = document.getElementById('tm-delete-btn');
  if (!btn) return;
  const ok = val === name;
  btn.disabled = !ok;
  btn.style.opacity = ok ? '1' : '0.45';
}

// ── Level 4: Do delete ────────────────────────────────────────────────────────
async function tmDoDelete(id, name) {
  const t = state.tenants.find(x => x.id === id);
  if (!t) return;
  const btn    = document.getElementById('tm-delete-btn');
  const errDiv = document.getElementById('tm-delete-err');
  errDiv.style.display = 'none';
  btn.disabled = true;
  btn.textContent = 'Deleting…';
  try {
    await HRMGMT.deleteTenant(id);
    state.tenants = state.tenants.filter(x => x.id !== id);
    logActivity('tenant', `Tenant "${t.name}" (${t.code}) permanently deleted`, 'var(--red)');
    document.getElementById('tenant-manage-overlay').remove();
    showTenantsListView();
    renderTenantsTable();
    renderDashboard();
    document.getElementById('nb-total').textContent = state.tenants.length;
    document.getElementById('nb-tenants').textContent = state.tenants.length;
    toast(`Tenant "${t.name}" permanently deleted`, 'error');
  } catch (err) {
    errDiv.textContent = err.message || 'Delete failed';
    errDiv.style.display = 'block';
    btn.disabled = false;
    btn.textContent = 'Delete Permanently';
    tmCheckDelete(name);
  }
}


// ══════════════════════════════════════════════════════════════
// TENANT DETAIL
// ══════════════════════════════════════════════════════════════

function openTenantDetail(id) {
  const t = state.tenants.find(x=>x.id===id);
  if(!t) return;
  document.getElementById('tenants-list-view').style.display='none';
  document.getElementById('tenant-detail-view').style.display='block';
  document.getElementById('tenant-detail-body').innerHTML = renderTenantDetail(t);
}

function renderTenantDetail(t) {
  const empPct = Math.min(100, Math.round(t.employees/t.employeeLimit*100));
  const storageLimitMb = getPlanConfig(t.plan).storageLimitMb || 5120;
  const storagePct = Math.min(100, Math.round(t.storageUsedMb/storageLimitMb*100));

  const _renderModCard = (m) => {
    const on = isModEnabled(t, m.id);
    const featCount = enabledFeatureCount(t, m.id);
    const featureRows = (m.features || []).map(f => {
      const fOn = isFeatureEnabled(t, m.id, f.id);
      return `<div class="mod-feat-row ${!on ? 'mod-feat-row-disabled' : ''}">
        <div class="mod-feat-info">
          <div class="mod-feat-name">${f.name}</div>
          <div class="mod-feat-desc">${f.desc}</div>
        </div>
        <label class="toggle toggle-sm">
          <input type="checkbox" ${fOn ? 'checked' : ''} ${!on ? 'disabled' : ''} onchange="toggleTenantFeature('${t.id}','${m.id}','${f.id}',this.checked)"/>
          <span class="toggle-slider"></span>
        </label>
      </div>`;
    }).join('');
    return `
      <div class="mod-perm-card ${on ? 'enabled' : 'disabled'}${m.parent ? ' child' : ''}" id="modcard-${t.id}-${m.id}">
        <div class="mod-perm-header">
          <div class="mod-perm-name">
            <div class="mod-perm-icon" style="background:${on ? m.color+'22' : 'rgba(107,114,128,0.1)'}">${m.icon}</div>
            ${m.name}
          </div>
          <div style="display:flex;align-items:center;gap:8px;">
            <span class="mod-feat-count" style="color:${on ? 'var(--muted)' : 'var(--bg3)'}">${featCount}/${m.features.length}</span>
            ${m.alwaysOn
              ? `<label class="toggle" title="This module cannot be disabled — it is a foundational requirement for all other HR modules."><input type="checkbox" checked disabled/><span class="toggle-slider" style="opacity:0.6;cursor:not-allowed;"></span></label>`
              : `<label class="toggle"><input type="checkbox" ${on ? 'checked' : ''} onchange="toggleTenantModule('${t.id}','${m.id}',this.checked)"/><span class="toggle-slider"></span></label>`
            }
          </div>
        </div>
        <div class="mod-perm-desc">${m.desc}</div>
        <div class="mod-feat-list">${featureRows}</div>
        ${(() => {
          const rules = MODULE_ACCESS_RULES[m.id];
          if (!rules || Object.keys(rules).length === 0) return '';
          const ruleRows = Object.entries(rules)
            .map(([ruleId, schema]) =>
              _renderAccessRuleRow(t, m.id, ruleId, schema))
            .join('');
          return `<div style="border-top:1px solid var(--border);
              margin-top:10px;padding-top:10px;">
            <div style="font-size:0.6rem;font-weight:600;color:var(--cyan);
              text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px;">
              Access Rules</div>
            ${ruleRows}
            <div style="display:flex;justify-content:flex-end;margin-top:10px;">
              <button class="btn btn-ghost btn-xs"
                id="btn-ar-save-${t.id}-${m.id}"
                onclick="saveAccessRules('${t.id}','${m.id}')">
                Save Rules</button>
            </div>
          </div>`;
        })()}
      </div>
    `;
  };
  const modCards = HR_MODULES.map(_renderModCard).join('') +
    `<div style="grid-column:1/-1;padding:14px 0 6px;border-top:1px solid var(--border);margin-top:4px;">
      <span style="font-size:0.6rem;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:0.08em;">System Modules</span>
    </div>` +
    SYSTEM_MODULES.map(_renderModCard).join('');

  return `
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;gap:16px;flex-wrap:wrap;">
      <div style="display:flex;align-items:center;gap:12px;">
        <div class="avatar" style="width:44px;height:44px;font-size:0.9rem;background:${t.color}22;color:${t.color};">${t.code.slice(0,2)}</div>
        <div>
          <div style="font-family:'Syne',sans-serif;font-size:1.3rem;font-weight:700;">${t.name}</div>
          <div style="font-size:0.68rem;color:var(--muted);font-family:'DM Mono',monospace;margin-top:2px;">${t.code} · ${t.orgType||t.sector||'—'}</div>
        </div>
        <span class="badge badge-${t.status}" style="font-size:0.68rem;padding:4px 10px;">${t.status}</span>
        <span class="plan-badge plan-${t.plan}">${getPlanConfig(t.plan).label||t.plan}</span>
        ${t.isGovernment ? `<span style="font-size:0.6rem;padding:3px 8px;border-radius:4px;background:rgba(96,165,250,0.12);color:var(--blue);border:1px solid rgba(96,165,250,0.25);">🏛 Government</span>` : ''}
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
        <button class="btn btn-ghost btn-sm" onclick="openEditTenantPlan('${t.id}')">Change Plan</button>
        ${(t.status!=='suspended' && t.status!=='inactive') ? `<button class="btn btn-ghost btn-sm" onclick="accessTenantPortal('${t.id}')"><i class="fas fa-user-shield" style="margin-right:5px;"></i>Access Tenant Portal</button>` : ''}
        ${t.adminEmail ? `<button class="btn btn-ghost btn-sm" onclick="resendAdminInvite('${t.id}','${t.adminEmail}')"><i class="fas fa-envelope" style="margin-right:5px;"></i>Resend Invite</button>` : ''}
        ${t.status==='suspended'
          ? `<button class="btn btn-sm" style="background:var(--accent-dim);color:var(--accent);border:1px solid rgba(74,222,128,0.25);" onclick="reactivateTenant('${t.id}');openTenantDetail('${t.id}')">Reactivate</button>`
          : `<button class="btn btn-danger btn-sm" onclick="suspendTenant('${t.id}');openTenantDetail('${t.id}')">Suspend</button>`
        }
        <div style="width:1px;height:20px;background:var(--border);margin:0 2px;"></div>
        <button class="btn btn-sm" onclick="openTenantManage('${t.id}')"
          style="background:rgba(248,113,113,0.07);border:1px solid rgba(248,113,113,0.28);
          color:var(--red);font-weight:600;display:flex;align-items:center;gap:6px;">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
            <path d="M8 2a6 6 0 100 12A6 6 0 008 2zM8 5v4M8 11h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          Manage Tenant
        </button>
      </div>
    </div>


    <div class="td-tab-bar">
    <div class="tab-bar" style="margin-bottom:0;">
      <button class="tab-btn active" onclick="tdTab('overview','${t.id}',this)">Overview</button>
      <button class="tab-btn" onclick="tdTab('modules','${t.id}',this)">Modules</button>
      <button class="tab-btn" onclick="tdTab('users','${t.id}',this)">Users</button>
      <button class="tab-btn" onclick="tdTab('roles','${t.id}',this)">Roles</button>
      <button class="tab-btn" onclick="tdTab('data','${t.id}',this)">Data</button>
      <button class="tab-btn" onclick="tdTab('onboarding','${t.id}',this)">Onboarding</button>
      <button class="tab-btn" onclick="tdTab('billing','${t.id}',this)">Billing</button>
      <button class="tab-btn" onclick="tdTab('audit','${t.id}',this)">Audit Log</button>
      <button class="tab-btn" onclick="tdTab('usage','${t.id}',this)">Usage</button>
      <button class="tab-btn" onclick="tdTab('periods','${t.id}',this)">Period Years</button>
    </div>
    </div>

    <div id="td-tab-overview-${t.id}" class="td-tab-pane">
    <div class="grid-3" style="margin-bottom:20px;">
      <div class="card card-pad">
        <div class="metric-label">Employees</div>
        <div style="font-family:'Syne',sans-serif;font-size:1.6rem;font-weight:700;color:var(--accent);">${t.employees}</div>
        <div class="usage-bar-wrap"><div class="usage-bar-label"><span>of ${t.employeeLimit} limit</span><span>${empPct}%</span></div><div class="usage-bar"><div class="usage-bar-fill" style="width:${empPct}%;background:var(--accent);"></div></div></div>
      </div>
      <div class="card card-pad">
        <div class="metric-label">Active Users</div>
        <div style="font-family:'Syne',sans-serif;font-size:1.6rem;font-weight:700;color:var(--blue);">${t.activeUsers}</div>
        <div class="metric-sub">Last activity: ${t.lastActivity}</div>
      </div>
      <div class="card card-pad">
        <div class="metric-label">Storage Used</div>
        <div style="font-family:'Syne',sans-serif;font-size:1.6rem;font-weight:700;color:var(--amber);">${(t.storageUsedMb/1024).toFixed(1)}<span style="font-size:1rem;"> GB</span></div>
        <div class="usage-bar-wrap"><div class="usage-bar-label"><span>of ${(storageLimitMb/1024).toFixed(0)} GB limit</span><span>${storagePct}%</span></div><div class="usage-bar"><div class="usage-bar-fill" style="width:${storagePct}%;background:${storagePct>80?'var(--red)':'var(--amber)'};"></div></div></div>
      </div>
    </div>

    <div class="grid-2" style="margin-bottom:20px;">
      <div class="card card-pad">
        <div style="font-family:'Syne',sans-serif;font-weight:700;margin-bottom:14px;font-size:0.88rem;">Tenant Configuration</div>
        ${(t.adminProfile && (t.adminProfile.firstName || t.adminProfile.lastName)) ? `
        <div class="form-group">
          <label class="form-label">Admin Name</label>
          <div style="display:flex;gap:8px;align-items:center;padding:8px 10px;background:var(--bg3);border:1px solid var(--border);border-radius:6px;">
            <div style="width:28px;height:28px;border-radius:50%;background:var(--blue-dim);border:1px solid rgba(96,165,250,0.25);display:flex;align-items:center;justify-content:center;font-size:0.65rem;font-weight:600;color:var(--blue);">${(t.adminProfile.firstName||'?')[0]}${(t.adminProfile.lastName||'')[0]||''}</div>
            <div>
              <div style="font-size:0.75rem;font-weight:500;">${t.adminProfile.firstName||''} ${t.adminProfile.lastName||''}</div>
              ${t.adminProfile.phone ? `<div style="font-size:0.62rem;color:var(--muted);">${t.adminProfile.phone}</div>` : ''}
              ${t.adminProfile.sex ? `<div style="font-size:0.6rem;color:var(--muted);text-transform:capitalize;">${t.adminProfile.sex}${t.adminProfile.dob ? ' · DOB: '+t.adminProfile.dob : ''}</div>` : ''}
            </div>
          </div>
        </div>` : ''}
        <div class="form-group"><label class="form-label">Admin Email</label><input class="form-input" value="${t.adminEmail}" id="td-email-${t.id}"/></div>
        ${t.adminProvisioningError ? `
        <div style="display:flex;align-items:flex-start;gap:10px;padding:10px 14px;background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.25);border-radius:8px;margin-bottom:12px;">
          <i class="fas fa-triangle-exclamation" style="color:var(--red);margin-top:2px;flex-shrink:0;"></i>
          <div>
            <div style="font-size:0.75rem;font-weight:600;color:var(--red);margin-bottom:2px;">Admin account provisioning failed</div>
            <div style="font-size:0.7rem;color:var(--muted);margin-bottom:8px;">${t.adminProvisioningError}</div>
            <button class="btn btn-ghost btn-xs" onclick="resendAdminInvite('${t.id}','${t.adminEmail}')">
              <i class="fas fa-rotate-right" style="margin-right:4px;"></i>Retry &amp; Resend Invite
            </button>
          </div>
        </div>` : ''}
        <div class="form-group"><label class="form-label">Employee Limit</label><input class="form-input" type="number" value="${t.employeeLimit}" id="td-limit-${t.id}"/></div>
        <div class="form-group">
          <label class="form-label">Staff Email Domain
            <span style="font-size:0.62rem;color:var(--muted);font-weight:400;margin-left:6px;">— required for staff account provisioning</span>
          </label>
          <div style="display:flex;align-items:center;gap:0;">
            <span style="padding:0 10px;height:36px;display:flex;align-items:center;background:var(--bg3);border:1px solid var(--border);border-right:none;border-radius:6px 0 0 6px;font-size:0.75rem;color:var(--muted);font-family:'DM Mono',monospace;">@</span>
            <input class="form-input" style="border-radius:0 6px 6px 0;" placeholder="e.g. works.gov.pg" value="${t.emailDomain||''}" id="td-domain-${t.id}"/>
          </div>
          <div style="font-size:0.62rem;color:var(--muted);margin-top:4px;">Staff accounts can only be created with emails matching this domain.</div>
        </div>
        <div class="form-group"><label class="form-label">Notes</label><textarea class="form-input" rows="2" id="td-notes-${t.id}">${t.notes}</textarea></div>
        <div style="display:flex;align-items:center;justify-content:space-between;padding:12px 14px;background:var(--bg-input,#151823);border:1px solid var(--border,#2a3045);border-radius:8px;margin-bottom:14px;">
          <div>
            <div style="font-size:0.78rem;font-weight:600;color:var(--text-primary,#e8eaf0);">Require phone verification (MFA)</div>
            <div style="font-size:0.68rem;color:var(--muted);margin-top:2px;">Users must verify a mobile number via SMS before accessing the HR system</div>
          </div>
          <label class="toggle" style="flex-shrink:0;margin-left:16px;">
            <input type="checkbox" id="td-mfa-${t.id}" ${t.mfaRequired ? 'checked' : ''} />
            <span class="toggle-slider"></span>
          </label>
        </div>
        <button class="btn btn-primary btn-sm" onclick="saveTenantConfig('${t.id}')">Save Configuration</button>
      </div>
      <div class="card card-pad">
        <div style="font-family:'Syne',sans-serif;font-weight:700;margin-bottom:14px;font-size:0.88rem;">Tenant Details</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;">
          <div class="info-pair"><div class="ik">Tenant ID</div><div class="iv" style="font-family:'DM Mono',monospace;font-size:0.68rem;">${t.id}</div></div>
          <div class="info-pair"><div class="ik">Short Code</div><div class="iv" style="font-family:'DM Mono',monospace;">${t.code}</div></div>
          <div class="info-pair"><div class="ik">Registered</div><div class="iv">${t.createdAt}</div></div>
          <div class="info-pair"><div class="ik">Billing Day</div><div class="iv"><span style="font-family:'DM Mono',monospace;font-size:0.78rem;color:var(--cyan);">${t.billingDay ? `Day ${t.billingDay} of each month` : '—'}</span></div></div>
          <div class="info-pair"><div class="ik">Org Type</div><div class="iv">${t.orgType||'—'} ${t.isGovernment?'<span style="font-size:0.6rem;padding:2px 6px;border-radius:4px;background:rgba(96,165,250,0.12);color:var(--blue);">Govt</span>':''}</div></div>
          <div class="info-pair"><div class="ik">Industry</div><div class="iv">${t.industry||'—'}</div></div>
          <div class="info-pair"><div class="ik">HR Modules Active</div><div class="iv">${hrModulesEnabled(t).length} / ${HR_MODULES.length}</div></div>
          <div class="info-pair"><div class="ik">Plan</div><div class="iv"><span class="plan-badge plan-${t.plan}">${getPlanConfig(t.plan).label||t.plan}</span></div></div>
        </div>
        ${t.referral ? `
        <div style="margin-top:14px;padding-top:12px;border-top:1px solid var(--border);">
          <div style="font-size:0.6rem;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:0.08em;font-family:'DM Mono',monospace;margin-bottom:8px;">Referral</div>
          <div style="font-size:0.72rem;color:var(--text);">${t.referral.name||'—'}${t.referral.source ? ` <span style="color:var(--muted);font-size:0.65rem;">· ${t.referral.source}</span>` : ''}</div>
          ${t.referral.notes ? `<div style="font-size:0.65rem;color:var(--muted);margin-top:2px;">${t.referral.notes}</div>` : ''}
        </div>` : ''}
      </div>
    </div>

    </div>

    <div id="td-tab-users-${t.id}" class="td-tab-pane" style="display:none;">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;flex-wrap:wrap;gap:8px;">
        <div class="sec-hdr" style="margin:0;">Registered Users</div>
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <select class="form-input btn-sm" id="tu-tab-role-${t.id}" onchange="filterTenantUsersTab('${t.id}')" style="width:130px;">
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="employee">Employee</option>
            <option value="full_admin">Full Admin</option>
          </select>
          <select class="form-input btn-sm" id="tu-tab-status-${t.id}" onchange="filterTenantUsersTab('${t.id}')" style="width:130px;">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          <input class="form-input btn-sm" id="tu-tab-search-${t.id}" placeholder="Search email…" oninput="filterTenantUsersTab('${t.id}')" style="width:180px;"/>
          <button class="btn btn-ghost btn-xs" onclick="loadTenantUsersTab('${t.id}')">↻ Refresh</button>
          <button class="btn btn-sm" style="background:rgba(96,165,250,0.1);color:var(--blue);border:1px solid rgba(96,165,250,0.25);" onclick="openProvisionAdminModal('${t.id}')"><i class="fas fa-user-plus" style="margin-right:5px;font-size:0.7rem;"></i>Provision Admin</button>
        </div>
      </div>
      <div id="td-users-${t.id}">
        <div style="color:var(--muted);font-size:0.75rem;padding:10px 0;">Loading users…</div>
      </div>
    </div>

    <div id="td-tab-modules-${t.id}" class="td-tab-pane">
      <div class="sec-hdr">Module Access</div>
      <div class="card card-pad" style="margin-bottom:8px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;">
          <div style="font-size:0.72rem;color:var(--muted);">${hrModulesEnabled(t).length} of ${HR_MODULES.length} HR modules enabled</div>
          <div style="display:flex;gap:8px;">
            <button class="btn btn-ghost btn-xs" onclick="setAllModules('${t.id}',true)">Enable All</button>
            <button class="btn btn-ghost btn-xs" onclick="setAllModules('${t.id}',false)">Disable All</button>
            <button class="btn btn-ghost btn-xs" onclick="setPlanModules('${t.id}')">Reset to Plan</button>
          </div>
        </div>
        <div class="mod-perm-grid" id="tenant-mods-${t.id}">${modCards}</div>
      </div>
    </div>

    <div id="td-tab-roles-${t.id}" class="td-tab-pane" style="display:none;">
      <div class="sec-hdr">Roles & Access Control</div>
      <div id="td-roles-${t.id}">
        <div style="color:var(--muted);font-size:0.75rem;padding:10px 0;">Loading roles…</div>
      </div>
    </div>

    <div id="td-tab-data-${t.id}" class="td-tab-pane" style="display:none;">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
        <div class="sec-hdr" style="margin-bottom:0;">Module Data <span style="font-size:0.62rem;font-family:'DM Mono',monospace;color:var(--muted);font-weight:400;">live counts from db-hr</span></div>
        <button class="btn btn-sm" style="background:rgba(74,222,128,0.08);border:1px solid rgba(74,222,128,0.25);color:var(--accent);" onclick="openImportWizard('${t.id}')">
          <i class="fas fa-file-import" style="margin-right:5px;"></i>Import Data
        </button>
      </div>
      <div id="td-data-${t.id}">
        <div style="color:var(--muted);font-size:0.75rem;">Loading module data…</div>
      </div>
    </div>

    <div id="td-tab-onboarding-${t.id}" class="td-tab-pane" style="display:none;">
      <div class="sec-hdr">Onboarding Checklist</div>
      <div class="card card-pad" id="td-onboarding-${t.id}">
        <div style="color:var(--muted);font-size:0.75rem;">Loading onboarding status…</div>
      </div>
    </div>

    <div id="td-tab-billing-${t.id}" class="td-tab-pane" style="display:none;">
      <div class="sec-hdr">Billing & Contract</div>
      <div class="card card-pad" id="td-billing-${t.id}">
        <div style="color:var(--muted);font-size:0.75rem;">Loading billing info…</div>
      </div>
    </div>

    <div id="td-tab-audit-${t.id}" class="td-tab-pane" style="display:none;">
      <div class="sec-hdr">Tenant Audit Log <span style="font-size:0.62rem;font-family:'DM Mono',monospace;color:var(--muted);font-weight:400;">from db-logs</span></div>
      <div class="card card-pad" id="td-audit-${t.id}">
        <div style="color:var(--muted);font-size:0.75rem;">Loading audit log…</div>
      </div>
    </div>

    <div id="td-tab-usage-${t.id}" class="td-tab-pane" style="display:none;">
      <div class="sec-hdr">Usage Analytics <span style="font-size:0.62rem;font-family:'DM Mono',monospace;color:var(--muted);font-weight:400;">last 30 days</span></div>
      <div id="td-usage-${t.id}">
        <div style="color:var(--muted);font-size:0.75rem;">Loading usage data…</div>
      </div>
    </div>

    <div id="td-tab-periods-${t.id}" class="td-tab-pane" style="display:none;">
      <div class="sec-hdr" style="display:flex;justify-content:space-between;align-items:center;">
        <span>Period Years</span>
        <button class="btn btn-ghost btn-xs" onclick="initTenantPeriodYears('${t.id}')">Initialise / Seed</button>
      </div>
      <div id="td-periods-${t.id}">
        <div style="color:var(--muted);font-size:0.75rem;">Loading period years…</div>
      </div>
    </div>
  `;
}


function _getAccessRuleVal(t, moduleId, ruleId) {
  const schema = (MODULE_ACCESS_RULES[moduleId] || {})[ruleId];
  const def = schema ? schema.default : null;
  const rules = (t._accessRules || {})[moduleId] || {};
  return ruleId in rules ? rules[ruleId] : def;
}

function _renderAccessRuleRow(t, moduleId, ruleId, schema) {
  const val = _getAccessRuleVal(t, moduleId, ruleId);
  const inputId = `ar-${t.id}-${moduleId}-${ruleId}`;

  let control = '';
  if (schema.type === 'role') {
    control = `<select class="form-input" style="padding:3px 8px;height:28px;`+
      `font-size:0.68rem;" id="${inputId}">` +
      ROLE_OPTIONS.map(o =>
        `<option value="${o.value}"${val===o.value?' selected':''}>${o.label}</option>`
      ).join('') + '</select>';
  } else if (schema.type === 'bool') {
    control = `<label class="toggle toggle-sm">` +
      `<input type="checkbox" id="${inputId}" ${val?'checked':''}/>` +
      `<span class="toggle-slider"></span></label>`;
  } else if (schema.type === 'number') {
    control = `<input type="number" min="0" class="form-input" ` +
      `id="${inputId}" value="${val??''}" ` +
      `style="width:80px;padding:3px 8px;height:28px;font-size:0.68rem;"/>`;
  }

  return `<div class="mod-feat-row" style="align-items:center;">` +
    `<div class="mod-feat-info">` +
      `<div class="mod-feat-name" style="color:var(--cyan);">${schema.label}</div>` +
      `<div class="mod-feat-desc">${schema.desc}</div>` +
    `</div>` +
    `<div style="flex-shrink:0;">${control}</div>` +
  `</div>`;
}

function _collectAccessRules(t, moduleId) {
  const schema = MODULE_ACCESS_RULES[moduleId] || {};
  const collected = {};
  for (const ruleId of Object.keys(schema)) {
    const s = schema[ruleId];
    const el = document.getElementById(`ar-${t.id}-${moduleId}-${ruleId}`);
    if (!el) continue;
    if (s.type === 'role')   collected[ruleId] = el.value;
    if (s.type === 'bool')   collected[ruleId] = el.checked;
    if (s.type === 'number') collected[ruleId] = parseInt(el.value) || 0;
  }
  return collected;
}

async function saveAccessRules(tenantId, moduleId) {
  const t = state.tenants.find(x => x.id === tenantId);
  if (!t) return;
  const rules = { [moduleId]: _collectAccessRules(t, moduleId) };
  const btn = document.getElementById(
    `btn-ar-save-${tenantId}-${moduleId}`
  );
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    await HRMGMT.saveModuleAccessRules(tenantId, rules);
    // Update local sub-collection cache on the tenant object
    if (!t._accessRules) t._accessRules = {};
    t._accessRules[moduleId] = {
      ...(t._accessRules[moduleId] || {}), ...rules[moduleId],
    };
    logActivity('module',
      `Access rules updated for "${moduleId}" on ${t.name}`,
      'var(--cyan)',
    );
    toast(`Access rules saved for ${moduleId}`, 'success');
  } catch(err) {
    toast(err.message || 'Failed to save access rules', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Save Rules'; }
  }
}

async function toggleTenantModule(tenantId, moduleId, enabled) {
  const t = state.tenants.find(x=>x.id===tenantId);
  if(!t) return;
  const modName = MODULES.find(m=>m.id===moduleId)?.name || moduleId;
  try {
    const res = await HRMGMT.toggleTenantModule(tenantId, moduleId, enabled);
    // sync local state from the server's returned module list
    if(res && res.modules) t.modules = res.modules;
    else {
      if (!t.modules) t.modules = {};
      if (!t.modules[moduleId]) t.modules[moduleId] = {enabled, features: {}};
      else t.modules[moduleId].enabled = enabled;
    }
    logActivity('module', `Module "${modName}" ${enabled?'enabled':'disabled'} for ${t.name}`, enabled?'var(--accent)':'var(--red)');
    toast(`${modName} ${enabled?'enabled':'disabled'} for ${t.name}`, enabled?'success':'info');
    const card = document.getElementById(`modcard-${tenantId}-${moduleId}`);
    if (card) {
      card.classList.toggle('enabled', enabled);
      card.classList.toggle('disabled', !enabled);
      // Refresh feature row disabled states
      card.querySelectorAll('.mod-feat-row').forEach(row => {
        row.classList.toggle('mod-feat-row-disabled', !enabled);
      });
      card.querySelectorAll('.mod-feat-list input[type=checkbox]').forEach(cb => {
        cb.disabled = !enabled;
      });
      const fc = card.querySelector('.mod-feat-count');
      if (fc) fc.style.color = enabled ? 'var(--muted)' : 'var(--bg3)';
      const icon = card.querySelector('.mod-perm-icon');
      const mod = MODULES.find(m => m.id === moduleId);
      if (icon && mod) icon.style.background = enabled ? mod.color + '22' : 'rgba(107,114,128,0.1)';
    }
    renderModulesPage();
    renderDashboard();
  } catch(err) {
    toast(err.message || `Failed to toggle ${modName}`, 'error');
    // revert the checkbox visually
    const cb = document.querySelector(`input[onchange*="${tenantId}"][onchange*="${moduleId}"]`);
    if(cb) cb.checked = !enabled;
  }
}

async function toggleTenantFeature(tenantId, moduleId, featureId, enabled) {
  const t = state.tenants.find(x => x.id === tenantId);
  if (!t) return;
  const mod = MODULES.find(m => m.id === moduleId);
  const feat = mod && mod.features.find(f => f.id === featureId);
  const featName = feat ? feat.name : featureId;
  try {
    const res = await HRMGMT.toggleTenantModule(tenantId, moduleId, enabled, featureId);
    if (res && res.modules) t.modules = res.modules;
    else {
      // Optimistic update
      if (!t.modules) t.modules = {};
      if (!t.modules[moduleId]) t.modules[moduleId] = { enabled: true, features: {} };
      t.modules[moduleId].features = t.modules[moduleId].features || {};
      t.modules[moduleId].features[featureId] = enabled;
      if (enabled) t.modules[moduleId].enabled = true;
    }
    toast(`${featName} ${enabled ? 'enabled' : 'disabled'}`, enabled ? 'success' : 'info');
    // Refresh just this module card
    const card = document.getElementById(`modcard-${tenantId}-${moduleId}`);
    if (card) {
      const featRows = card.querySelector('.mod-feat-list');
      const featCount = card.querySelector('.mod-feat-count');
      if (featRows && mod) {
        featRows.innerHTML = mod.features.map(f => {
          const fOn = isFeatureEnabled(t, moduleId, f.id);
          const modOn = isModEnabled(t, moduleId);
          return `<div class="mod-feat-row ${!modOn ? 'mod-feat-row-disabled' : ''}">
            <div class="mod-feat-info">
              <div class="mod-feat-name">${f.name}</div>
              <div class="mod-feat-desc">${f.desc}</div>
            </div>
            <label class="toggle toggle-sm">
              <input type="checkbox" ${fOn ? 'checked' : ''} ${!modOn ? 'disabled' : ''} onchange="toggleTenantFeature('${tenantId}','${moduleId}','${f.id}',this.checked)"/>
              <span class="toggle-slider"></span>
            </label>
          </div>`;
        }).join('');
      }
      if (featCount && mod) featCount.textContent = `${enabledFeatureCount(t, moduleId)}/${mod.features.length}`;
    }
    renderModulesPage();
    renderDashboard();
  } catch (err) {
    toast(err.message || `Failed to toggle ${featName}`, 'error');
    const cb = document.querySelector(`input[onchange*="${tenantId}"][onchange*="${moduleId}"][onchange*="${featureId}"]`);
    if (cb) cb.checked = !enabled;
  }
}

async function setAllModules(tenantId, enabled) {
  const t = state.tenants.find(x=>x.id===tenantId);
  if(!t) return;
  try {
    const res = await HRMGMT.setAllModules(tenantId, enabled);
    t.modules = res && res.modules ? res.modules : {};
    logActivity('module', `All modules ${enabled?'enabled':'disabled'} for ${t.name}`, 'var(--amber)');
    openTenantDetail(tenantId);
    toast(`All modules ${enabled?'enabled':'disabled'} for ${t.name}`, 'info');
  } catch(err) {
    toast(err.message || 'Failed to update modules', 'error');
  }
}

async function setPlanModules(tenantId) {
  const t = state.tenants.find(x=>x.id===tenantId);
  if(!t) return;
  try {
    const res = await HRMGMT.resetTenantModulesToPlan(tenantId);
    t.modules = res && res.modules ? res.modules : {};
    logActivity('module', `Modules reset to ${t.plan} plan defaults for ${t.name}`, 'var(--blue)');
    openTenantDetail(tenantId);
    toast(`Modules reset to ${t.plan} defaults for ${t.name}`, 'success');
  } catch(err) {
    toast(err.message || 'Failed to reset modules', 'error');
  }
}

async function saveTenantConfig(id) {
  const t = state.tenants.find(x=>x.id===id);
  if(!t) return;
  const email  = document.getElementById('td-email-'+id)?.value;
  const limit  = parseInt(document.getElementById('td-limit-'+id)?.value);
  const notes  = document.getElementById('td-notes-'+id)?.value;
  const domain = document.getElementById('td-domain-'+id)?.value.trim().toLowerCase().replace(/^@/,'');
  const mfaEl  = document.getElementById('td-mfa-'+id);
  const mfaRequired = mfaEl ? mfaEl.checked : undefined;
  if (domain && !/^[a-z0-9]([a-z0-9\-]*[a-z0-9])?(\.[a-z]{2,})+$/.test(domain)) {
    toast('Invalid email domain format — enter something like works.gov.pg', 'error'); return;
  }
  try {
    await HRMGMT.saveTenantConfig(id, {
      ...(email  !== undefined              && { adminEmail: email }),
      ...(!isNaN(limit) && limit > 0        && { employeeLimit: limit }),
      ...(notes  !== undefined              && { notes }),
      ...(domain !== undefined              && { emailDomain: domain }),
      ...(mfaRequired !== undefined         && { mfaRequired }),
    });
    if(email)                         t.adminEmail    = email;
    if(!isNaN(limit) && limit > 0)    t.employeeLimit  = limit;
    if(notes  !== undefined)          t.notes         = notes;
    if(domain !== undefined)          t.emailDomain   = domain;
    if(mfaRequired !== undefined)     t.mfaRequired   = mfaRequired;
    logActivity('tenant', `Tenant "${t.name}" configuration updated`, 'var(--blue)');
    toast('Tenant configuration saved', 'success');
  } catch(err) {
    toast(err.message || 'Failed to save configuration', 'error');
  }
}

async function openEditTenantPlan(id) {
  const t = state.tenants.find(x=>x.id===id);
  if(!t) return;
  // Build an inline plan-picker inside a small floating overlay
  const existing = document.getElementById('plan-picker-overlay');
  if(existing) existing.remove();

  const planOpts = Object.values(state.plans).map(p => {
    const empLabel = p.employeeLimit === null ? '∞ emp' : `${p.employeeLimit} emp`;
    const gbLabel  = `${(p.storageLimitMb/1024).toFixed(0)} GB`;
    return `<option value="${p.id}" ${t.plan===p.id?'selected':''}>${p.label} — ${empLabel}, ${gbLabel}</option>`;
  }).join('');

  const overlay = document.createElement('div');
  overlay.id = 'plan-picker-overlay';
  overlay.style.cssText = `
    position:fixed;inset:0;background:rgba(0,0,0,0.55);
    display:flex;align-items:center;justify-content:center;z-index:9999;
  `;
  overlay.innerHTML = `
    <div style="background:var(--bg2);border:1px solid var(--border);border-radius:12px;padding:28px;width:380px;box-shadow:0 24px 60px rgba(0,0,0,0.5);">
      <div style="font-family:'Syne',sans-serif;font-size:1rem;font-weight:700;margin-bottom:6px;">Change Plan</div>
      <div style="font-size:0.72rem;color:var(--muted);margin-bottom:18px;">${t.name} · currently on <b style="color:var(--text)">${t.plan}</b></div>
      <div class="form-group">
        <label class="form-label">New Plan</label>
        <select class="form-input" id="plan-picker-sel">
          ${planOpts}
        </select>
      </div>
      <div class="form-group" style="display:flex;align-items:center;gap:8px;margin-top:4px;">
        <label class="toggle"><input type="checkbox" id="plan-picker-reset"/><span class="toggle-slider"></span></label>
        <span style="font-size:0.72rem;color:var(--muted);">Reset modules to new plan defaults</span>
      </div>
      <div style="display:flex;gap:8px;margin-top:18px;">
        <button class="btn btn-primary" style="flex:1;" id="plan-picker-confirm">Apply</button>
        <button class="btn btn-ghost" id="plan-picker-cancel">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  document.getElementById('plan-picker-cancel').onclick = () => overlay.remove();
  overlay.addEventListener('click', e => { if(e.target===overlay) overlay.remove(); });

  document.getElementById('plan-picker-confirm').onclick = async () => {
    const newPlan = document.getElementById('plan-picker-sel').value;
    const resetMods = document.getElementById('plan-picker-reset').checked;
    const btn = document.getElementById('plan-picker-confirm');
    btn.disabled = true; btn.textContent = 'Saving…';
    try {
      await HRMGMT.changeTenantPlan(id, newPlan, resetMods);
      const old = t.plan;
      const newPlanConfig = getPlanConfig(newPlan);
      t.plan = newPlan;
      t.employeeLimit   = newPlanConfig.employeeLimit;
      t.storageLimitMb  = newPlanConfig.storageLimitMb;
      if (resetMods) {
        // Rebuild modules as object to match backend format (not flat array)
        const moduleObj = {};
        newPlanConfig.modules.forEach(mid => {
          moduleObj[mid] = { enabled: true, features: {} };
        });
        t.modules = moduleObj;
        t.enabledModuleIds = [...newPlanConfig.modules];
      }
      logActivity('tenant', `Tenant "${t.name}" plan changed from ${old} to ${t.plan}`, 'var(--purple)');
      overlay.remove();
      openTenantDetail(id);
      toast(`Plan updated to ${t.plan}`, 'success');
    } catch(err) {
      toast(err.message || 'Failed to change plan', 'error');
      btn.disabled = false; btn.textContent = 'Apply';
    }
  };
}

// ══════════════════════════════════════════════════════════════
// METRICS
// ══════════════════════════════════════════════════════════════

function renderMetricsPage() {
  const sel = document.getElementById('metrics-tenant-filter')?.value || '';
  const opts = state.tenants.map(t=>`<option value="${t.id}" ${sel===t.id?'selected':''}>${t.name}</option>`).join('');
  const el = document.getElementById('metrics-tenant-filter');
  if(el && !el.options.length) el.innerHTML = '<option value="">All Tenants</option>' + opts;

  const tenants = sel ? state.tenants.filter(t=>t.id===sel) : state.tenants;

  document.getElementById('metrics-content').innerHTML = `
    <div class="metrics-grid">
      ${tenants.map(t=>{
        const empPct = Math.min(100, Math.round(t.employees/t.employeeLimit*100));
        const storageLimitMb = getPlanConfig(t.plan).storageLimitMb || 5120;
        const storagePct = Math.min(100, Math.round(t.storageUsedMb/storageLimitMb*100));
        return `
          <div class="card card-pad" style="cursor:pointer;" onclick="openTenantDetail('${t.id}');showPage('tenants',document.querySelector('.nav-btn[onclick*=tenants]'))">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
              <div class="avatar" style="background:${t.color}22;color:${t.color};">${t.code.slice(0,2)}</div>
              <div>
                <div style="font-weight:600;font-size:0.78rem;">${t.name}</div>
                <span class="badge badge-${t.status}" style="font-size:0.58rem;">${t.status}</span>
              </div>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:12px;">
              <div><div style="font-size:0.6rem;color:var(--muted);">Employees</div><div style="font-family:'Syne',sans-serif;font-size:1.3rem;font-weight:700;color:var(--accent);">${t.employees}</div></div>
              <div><div style="font-size:0.6rem;color:var(--muted);">Active Users</div><div style="font-family:'Syne',sans-serif;font-size:1.3rem;font-weight:700;color:var(--blue);">${t.activeUsers}</div></div>
              <div><div style="font-size:0.6rem;color:var(--muted);">HR Modules</div><div style="font-family:'Syne',sans-serif;font-size:1.3rem;font-weight:700;">${hrModulesEnabled(t).length}<span style="font-size:0.7rem;color:var(--muted);">/${HR_MODULES.length}</span></div></div>
              <div><div style="font-size:0.6rem;color:var(--muted);">Plan</div><div style="margin-top:3px;"><span class="plan-badge plan-${t.plan}">${t.plan}</span></div></div>
            </div>
            <div style="font-size:0.62rem;color:var(--muted);margin-bottom:4px;">Employees (${empPct}%)</div>
            <div class="usage-bar" style="margin-bottom:8px;"><div class="usage-bar-fill" style="width:${empPct}%;background:var(--accent);"></div></div>
            <div style="font-size:0.62rem;color:var(--muted);margin-bottom:4px;">Storage (${storagePct}%)</div>
            <div class="usage-bar"><div class="usage-bar-fill" style="width:${storagePct}%;background:${storagePct>80?'var(--red)':'var(--amber)'};"></div></div>
          </div>
        `;
      }).join('')}
    </div>

    <div class="card card-pad" style="margin-top:16px;">
      <div style="font-family:'Syne',sans-serif;font-weight:700;margin-bottom:16px;font-size:0.88rem;">Module Adoption Across Tenants</div>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:14px;">
        ${HR_MODULES.map(m => {
          const count = tenants.filter(t=>isModEnabled(t, m.id)).length;
          const pct = tenants.length ? Math.round(count/tenants.length*100) : 0;
          return `
            <div>
              <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:5px;">
                <span style="font-size:0.72rem;color:var(--text);">${m.icon} ${m.name}</span>
                <span style="font-size:0.62rem;color:var(--muted);font-family:'DM Mono',monospace;">${count}/${tenants.length}</span>
              </div>
              <div class="usage-bar"><div class="usage-bar-fill" style="width:${pct}%;background:${m.color||'var(--accent)'};"></div></div>
            </div>
          `;
        }).join('')}
      </div>
    </div>
  `;
}

// ══════════════════════════════════════════════════════════════
// MODULE ACCESS MATRIX
// ══════════════════════════════════════════════════════════════

function renderModulesPage() {
  modInitSchemaState();
  modRenderRail();
}

async function applyBulkPreset() {
  const tid = document.getElementById('bulk-tenant-select')?.value;
  const preset = document.getElementById('bulk-preset-select')?.value;
  if(!tid || !preset) { toast('Select a tenant and preset first', 'error'); return; }
  const t = state.tenants.find(x=>x.id===tid);
  if(!t) return;
  try {
    const res = await HRMGMT.applyBulkPreset(tid, preset);
    if(res && res.modules) t.modules = res.modules;
    logActivity('module', `Bulk preset "${preset}" applied to ${t.name}`, 'var(--purple)');
    modRenderRail();
    toast(`Preset "${preset}" applied to ${t.name}`, 'success');
  } catch(err) {
    toast(err.message || 'Failed to apply preset', 'error');
  }
}


// ══════════════════════════════════════════════════════════════
// GLOBAL RULES
// ══════════════════════════════════════════════════════════════

function renderGlobalRules() {
  const gr = state.globalRules;

  const secEl = document.getElementById('global-security-rules');
  if (secEl) secEl.innerHTML = [
    { key:'requireMfa', label:'Require MFA for all admins', desc:'Force multi-factor authentication on all tenant admin accounts' },
    { key:'allowGuestAccess', label:'Allow guest / read-only access', desc:'Permit unauthenticated read access to public-facing reports' },
    { key:'allowApiAccess', label:'API access for tenants', desc:'Allow tenants to generate API keys for programmatic access' },
    { key:'sandboxMode', label:'Sandbox / demo mode', desc:'All write operations silently blocked — safe for demos' },
    { key:'autoProvision', label:'Self-service provisioning', desc:'Allow organisations to self-register without admin approval' },
  ].map(r => toggleRow(r.key, r.label, r.desc, gr[r.key])).join('');

  renderBlocklist();
}

// ── Blocklist helpers ────────────────────────────────────────────────────

const BLOCKLIST_VALIDATORS = {
  email:  { re: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, label: 'email address' },
  domain: { re: /^[a-z0-9]([a-z0-9\-]*[a-z0-9])?(\.[a-z]{2,})+$/, label: 'domain' },
  ip:     { re: /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/, label: 'IP or CIDR range' },
};

// Active tab and search state for blocklist
let _blTab = 'email';
let _blSearch = '';

function renderBlocklist() {
  const el = document.getElementById('global-blocklist');
  if (!el) return;
  const gr = state.globalRules;
  const emails  = gr.blockedEmails  || [];
  const domains = gr.blockedDomains || [];
  const ips     = gr.blockedIps     || [];
  const counts  = { email: emails.length, domain: domains.length, ip: ips.length };
  const allItems = { email: emails, domain: domains, ip: ips };

  const tabs = [
    { id: 'email',  label: 'Emails',  placeholder: 'user@example.com',  hint: 'Exact email addresses refused at sign-up and login.' },
    { id: 'domain', label: 'Domains', placeholder: 'example.com',       hint: 'All accounts from this domain will be refused — no @ needed.' },
    { id: 'ip',     label: 'IPs',     placeholder: '203.0.113.5 or /24', hint: 'IPv4 addresses or CIDR ranges blocked at sign-up.' },
  ];

  const items  = allItems[_blTab] || [];
  const search = _blSearch.toLowerCase();
  const filtered = search ? items.filter(v => v.includes(search)) : items;
  const tab = tabs.find(t => t.id === _blTab);

  el.innerHTML = `
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:14px;flex-wrap:wrap;">
      ${tabs.map(t => `
        <button onclick="blSetTab('${t.id}')" style="
          padding:5px 12px;border-radius:6px;font-size:0.72rem;font-weight:500;cursor:pointer;
          border:1px solid ${_blTab===t.id ? 'rgba(239,68,68,0.4)' : 'var(--border)'};
          background:${_blTab===t.id ? 'rgba(239,68,68,0.1)' : 'var(--bg3)'};
          color:${_blTab===t.id ? 'var(--red)' : 'var(--muted)'};transition:all 0.15s;">
          ${t.label}
          <span style="margin-left:5px;padding:1px 6px;border-radius:10px;font-size:0.62rem;
            background:${counts[t.id]>0 ? 'rgba(239,68,68,0.15)' : 'var(--bg2)'};
            color:${counts[t.id]>0 ? 'var(--red)' : 'var(--muted)'};">${counts[t.id]}</span>
        </button>
      `).join('')}
      <div style="margin-left:auto;display:flex;align-items:center;gap:6px;">
        <button onclick="blShowBulkPaste()" style="padding:5px 10px;border-radius:6px;font-size:0.7rem;font-weight:500;cursor:pointer;border:1px solid var(--border);background:var(--bg3);color:var(--muted);">
          ⊕ Bulk add
        </button>
      </div>
    </div>

    <div style="display:flex;gap:8px;margin-bottom:10px;">
      <div style="job:relative;flex:1;">
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style="job:absolute;left:10px;top:50%;transform:translateY(-50%);opacity:0.4;pointer-events:none;">
          <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" stroke-width="1.5"/>
          <path d="M10 10l3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <input class="form-input" id="bl-search" placeholder="Search ${tab.label.toLowerCase()}…" value="${_blSearch}"
          style="padding-left:30px;" oninput="blSetSearch(this.value)"/>
      </div>
    </div>

    <div style="border:1px solid var(--border);border-radius:8px;overflow:hidden;margin-bottom:12px;">
      ${filtered.length === 0 ? `
        <div style="padding:24px;text-align:center;color:var(--muted);font-size:0.75rem;">
          ${search ? 'No matches for "' + search + '"' : 'No ' + tab.label.toLowerCase() + ' blocked yet.'}
        </div>
      ` : `
        <div style="max-height:260px;overflow-y:auto;">
          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr style="border-bottom:1px solid var(--border);background:var(--bg3);">
                <th style="padding:7px 12px;font-size:0.6rem;font-weight:600;color:var(--muted);text-align:left;font-family:'DM Mono',monospace;text-transform:uppercase;letter-spacing:0.06em;">
                  ${tab.label} <span style="font-weight:400;">(${filtered.length}${search && filtered.length !== items.length ? ' of ' + items.length : ''})</span>
                </th>
                <th style="width:40px;"></th>
              </tr>
            </thead>
            <tbody>
              ${filtered.map(v => {
                const realIdx = items.indexOf(v);
                return `<tr style="border-bottom:1px solid rgba(255,255,255,0.03);" onmouseenter="this.style.background='rgba(239,68,68,0.04)'" onmouseleave="this.style.background=''">
                  <td style="padding:6px 12px;font-family:'DM Mono',monospace;font-size:0.72rem;color:var(--text);">${v}</td>
                  <td style="padding:6px 8px;text-align:right;">
                    <button onclick="removeBlockEntry('${_blTab}',${realIdx})" title="Remove"
                      style="background:none;border:none;color:var(--muted);cursor:pointer;padding:2px;display:flex;align-items:center;opacity:0.6;" onmouseenter="this.style.opacity=1;this.style.color='var(--red)'" onmouseleave="this.style.opacity=0.6;this.style.color='var(--muted)'">
                      <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                    </button>
                  </td>
                </tr>`;
              }).join('')}
            </tbody>
          </table>
        </div>
      `}
    </div>

    <div style="display:flex;gap:8px;">
      <input class="form-input" id="bl-input" placeholder="${tab.placeholder}" style="flex:1;font-family:'DM Mono',monospace;font-size:0.78rem;"
        onkeydown="if(event.key==='Enter'){event.preventDefault();addBlockEntry();}"/>
      <button class="btn btn-sm" style="background:rgba(239,68,68,0.1);border:1px solid rgba(239,68,68,0.3);color:var(--red);white-space:nowrap;"
        onclick="addBlockEntry()">Block</button>
    </div>
    <div style="margin-top:6px;font-size:0.67rem;color:var(--muted);">${tab.hint}</div>

    <div id="bl-bulk-panel" style="display:none;margin-top:14px;padding:14px;background:var(--bg3);border:1px solid var(--border);border-radius:8px;">
      <div style="font-size:0.72rem;font-weight:600;color:var(--text);margin-bottom:6px;">Bulk add ${tab.label.toLowerCase()} — one per line</div>
      <textarea class="form-input" id="bl-bulk-input" rows="5" placeholder="user@example.com&#10;another@domain.com&#10;bad@actor.net"
        style="font-family:'DM Mono',monospace;font-size:0.72rem;resize:vertical;width:100%;box-sizing:border-box;"></textarea>
      <div style="display:flex;gap:8px;margin-top:8px;justify-content:flex-end;">
        <button class="btn btn-ghost btn-sm" onclick="document.getElementById('bl-bulk-panel').style.display='none'">Cancel</button>
        <button class="btn btn-sm" style="background:rgba(239,68,68,0.1);border:1px solid rgba(239,68,68,0.3);color:var(--red);"
          onclick="blImportBulk()">Add all valid</button>
      </div>
    </div>
  `;
}

function blSetTab(type) {
  _blTab = type;
  _blSearch = '';
  renderBlocklist();
}

function blSetSearch(val) {
  _blSearch = val;
  renderBlocklist();
}

function blShowBulkPaste() {
  const p = document.getElementById('bl-bulk-panel');
  if (p) { p.style.display = p.style.display === 'none' ? '' : 'none'; }
}

function blImportBulk() {
  const textarea = document.getElementById('bl-bulk-input');
  if (!textarea) return;
  const key = 'blocked' + _blTab.charAt(0).toUpperCase() + _blTab.slice(1) + 's';
  const validator = BLOCKLIST_VALIDATORS[_blTab];
  if (!state.globalRules[key]) state.globalRules[key] = [];
  const existing = new Set(state.globalRules[key]);
  const lines = textarea.value.split(/[\r\n,;]+/).map(l => l.trim().toLowerCase().replace(/^@/, '')).filter(Boolean);
  let added = 0, skipped = 0, invalid = 0;
  lines.forEach(raw => {
    if (!validator.re.test(raw)) { invalid++; return; }
    if (existing.has(raw)) { skipped++; return; }
    state.globalRules[key].push(raw);
    existing.add(raw);
    added++;
  });
  textarea.value = '';
  document.getElementById('bl-bulk-panel').style.display = 'none';
  renderBlocklist();
  const parts = [];
  if (added)   parts.push(added + ' added');
  if (skipped) parts.push(skipped + ' duplicate' + (skipped > 1 ? 's' : '') + ' skipped');
  if (invalid) parts.push(invalid + ' invalid');
  toast(parts.join(', ') + ' — save to apply', added > 0 ? 'success' : 'info');
}

function addBlockEntry() {
  const key = 'blocked' + _blTab.charAt(0).toUpperCase() + _blTab.slice(1) + 's';
  const validator = BLOCKLIST_VALIDATORS[_blTab];
  const input = document.getElementById('bl-input');
  if (!input) return;
  const raw = input.value.trim().toLowerCase().replace(/^@/, '');
  if (!raw) { toast('Enter a value', 'error'); return; }
  if (!validator.re.test(raw)) { toast('Invalid ' + validator.label + ' format', 'error'); return; }
  if (!state.globalRules[key]) state.globalRules[key] = [];
  if (state.globalRules[key].includes(raw)) { toast(raw + ' is already blocked', 'info'); return; }
  state.globalRules[key].push(raw);
  input.value = '';
  renderBlocklist();
  toast(raw + ' blocked — save to apply', 'info');
}

function removeBlockEntry(type, index) {
  const key = 'blocked' + type.charAt(0).toUpperCase() + type.slice(1) + 's';
  if (!state.globalRules[key]) return;
  const removed = state.globalRules[key][index];
  state.globalRules[key].splice(index, 1);
  renderBlocklist();
  toast('Removed ' + removed + ' — save to apply', 'info');
}

function toggleRow(key, label, desc, val) {
  return `
    <div class="toggle-wrap">
      <div class="toggle-info"><div class="tl">${label}</div><div class="ts">${desc}</div></div>
      <label class="toggle"><input type="checkbox" ${val?'checked':''} onchange="state.globalRules['${key}']=this.checked"/><span class="toggle-slider"></span></label>
    </div>
  `;
}

async function saveGlobalRules() {
  try {
    await HRMGMT.saveGlobalRules({
      ...state.globalRules,
      blockedEmails:  state.globalRules.blockedEmails  || [],
      blockedDomains: state.globalRules.blockedDomains || [],
      blockedIps:     state.globalRules.blockedIps     || [],
    });
    logActivity('rule', 'Global rules updated', 'var(--amber)');
    toast('Global rules saved', 'success');
  } catch(err) {
    toast(err.message || 'Failed to save global rules', 'error');
  }
}

// ══════════════════════════════════════════════════════════════
// PLAN EDITOR
// ══════════════════════════════════════════════════════════════

function renderPlanEditor() {
  const el = document.getElementById('global-plan-editor');
  if (!el) return;

  const COLORS = { amber:'var(--amber)', purple:'var(--purple)', blue:'var(--blue)', teal:'var(--cyan)', green:'var(--accent)' };

  el.innerHTML = Object.values(state.plans).map(p => {
    const storageGb = (p.storageLimitMb / 1024).toFixed(0);
    const empDisplay = p.employeeLimit === null ? '∞ Unlimited' : p.employeeLimit;
    const badgeColor = COLORS[p.color] || 'var(--blue)';
    const hrModIds = new Set(HR_MODULES.map(m => m.id));
    const modCount = (p.modules || []).filter(id => hrModIds.has(id)).length;
    return `
      <div class="card card-pad" style="border-color:${badgeColor}33;margin-bottom:12px;">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;">
          <div style="display:flex;align-items:center;gap:10px;">
            <div style="width:10px;height:10px;border-radius:50%;background:${badgeColor};flex-shrink:0;"></div>
            <span style="font-family:'Syne',sans-serif;font-weight:700;font-size:0.9rem;">${p.label}</span>
            <span style="font-family:'DM Mono',monospace;font-size:0.62rem;color:var(--muted);">${p.id}</span>
          </div>
          <button class="btn btn-primary btn-sm" onclick="savePlanConfig('${p.id}')">Save Plan</button>
        </div>
        <div class="form-row-3">
          <div class="form-group">
            <label class="form-label">Employee Limit</label>
            <div style="display:flex;align-items:center;gap:8px;">
              <input class="form-input" type="number" id="plan-emp-${p.id}"
                value="${p.employeeLimit === null ? '' : p.employeeLimit}"
                placeholder="Leave blank = unlimited"
                style="flex:1;"/>
            </div>
            <div style="font-size:0.62rem;color:var(--muted);margin-top:4px;">Current: ${empDisplay}</div>
          </div>
          <div class="form-group">
            <label class="form-label">Storage Limit (GB)</label>
            <input class="form-input" type="number" id="plan-storage-${p.id}"
              value="${storageGb}" min="1"/>
            <div style="font-size:0.62rem;color:var(--muted);margin-top:4px;">Current: ${storageGb} GB</div>
          </div>
          <div class="form-group">
            <label class="form-label">Badge Colour</label>
            <select class="form-input" id="plan-color-${p.id}">
              ${['amber','purple','blue','teal','green'].map(c =>
                `<option value="${c}" ${p.color===c?'selected':''}>${c.charAt(0).toUpperCase()+c.slice(1)}</option>`
              ).join('')}
            </select>
          </div>
        </div>
        <label class="form-label" style="margin-top:4px;">Included HR Modules (${modCount} / ${HR_MODULES.length})</label>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:6px;margin-top:6px;">
          ${HR_MODULES.map(m => {
            const on = (p.modules || []).includes(m.id);
            return `
              <div style="display:flex;align-items:center;justify-content:space-between;padding:6px 10px;background:var(--bg3);border-radius:6px;border:1px solid var(--border);">
                <span style="font-size:0.7rem;">${m.icon} ${m.name}</span>
                <label class="toggle"><input type="checkbox" id="plan-mod-${p.id}-${m.id}" ${on?'checked':''}/>
                  <span class="toggle-slider"></span></label>
              </div>
            `;
          }).join('')}
        </div>
        <div style="margin-top:8px;padding:8px 10px;background:var(--bg3);border-radius:6px;border:1px solid var(--border);font-size:0.67rem;color:var(--muted);">
          ⚙️ System modules (Settings, AI Assistant) are managed per-tenant and not included in plan definitions.
        </div>

        <div style="border-top:1px solid var(--border);margin-top:18px;padding-top:16px;">
          <div style="font-size:0.72rem;font-weight:600;color:var(--text);margin-bottom:4px;">
            💰 Billing &amp; Cost
          </div>
          <div style="font-size:0.68rem;color:var(--muted);margin-bottom:12px;padding:8px 10px;background:rgba(96,165,250,0.06);border:1px solid rgba(96,165,250,0.15);border-radius:6px;">
            Tenants with <strong style="color:var(--text);">≤ 30 employees</strong> are charged the flat monthly rate only.
            Above 30, the per-employee rate applies to each <strong style="color:var(--text);">additional</strong> employee.
          </div>
          <div class="form-row-3">
            <div class="form-group">
              <label class="form-label">Flat Monthly Rate <span style="font-size:0.6rem;color:var(--muted);font-weight:400;">≤ 30 employees</span></label>
              <div style="display:flex;align-items:center;gap:0;">
                <span style="padding:0 10px;height:36px;display:flex;align-items:center;background:var(--bg3);border:1px solid var(--border);border-right:none;border-radius:6px 0 0 6px;font-size:0.75rem;color:var(--muted);font-family:'DM Mono',monospace;">PGK</span>
                <input class="form-input" type="number" min="0" step="0.01" id="plan-flat-rate-${p.id}"
                  value="${p.flatRate != null ? p.flatRate : ''}"
                  placeholder="0.00" style="border-radius:0 6px 6px 0;"/>
              </div>
              <div style="font-size:0.62rem;color:var(--muted);margin-top:4px;">Fixed charge when headcount is 30 or fewer</div>
            </div>
            <div class="form-group">
              <label class="form-label">Rate per Employee / Month <span style="font-size:0.6rem;color:var(--muted);font-weight:400;">&gt; 30 employees</span></label>
              <div style="display:flex;align-items:center;gap:0;">
                <span style="padding:0 10px;height:36px;display:flex;align-items:center;background:var(--bg3);border:1px solid var(--border);border-right:none;border-radius:6px 0 0 6px;font-size:0.75rem;color:var(--muted);font-family:'DM Mono',monospace;">PGK</span>
                <input class="form-input" type="number" min="0" step="0.01" id="plan-rate-per-emp-${p.id}"
                  value="${p.ratePerEmployee != null ? p.ratePerEmployee : ''}"
                  placeholder="0.00" style="border-radius:0 6px 6px 0;"/>
              </div>
              <div style="font-size:0.62rem;color:var(--muted);margin-top:4px;">Charged per employee above the 30-employee threshold</div>
            </div>
            <div class="form-group">
              <label class="form-label">Estimated Monthly Bill</label>
              <div style="height:36px;display:flex;align-items:center;padding:0 12px;background:var(--bg3);border:1px solid var(--border);border-radius:6px;font-size:0.76rem;font-family:'DM Mono',monospace;color:var(--cyan);" id="plan-bill-preview-${p.id}">
                ${(() => {
                  const flat = p.flatRate || 0;
                  const rate = p.ratePerEmployee || 0;
                  const ex = Math.max(0, (p.employeeLimit || 30) - 30);
                  const total = flat + (ex * rate);
                  return `K${total.toFixed(2)} <span style="font-size:0.6rem;color:var(--muted);margin-left:4px;">at limit</span>`;
                })()}
              </div>
              <div style="font-size:0.62rem;color:var(--muted);margin-top:4px;">PGK · Papua New Guinea Kina</div>
            </div>
          </div>
        </div>

        <div style="border-top:1px solid var(--border);margin-top:4px;padding-top:16px;">
          <div style="font-size:0.72rem;font-weight:600;color:var(--text);margin-bottom:12px;">
            📨 Messaging Channel Limits <span style="font-size:0.62rem;font-weight:400;color:var(--muted);margin-left:6px;">per tenant / month</span>
          </div>
          <div class="form-row-3">
            <div class="form-group">
              <label class="form-label">✉️ Email</label>
              <div style="display:flex;align-items:center;gap:6px;">
                <input class="form-input" type="number" min="0" id="plan-msg-email-${p.id}"
                  value="${p.messagingLimits?.email != null ? p.messagingLimits.email : ''}"
                  placeholder="Unlimited"/>
                <span style="font-size:0.65rem;color:var(--muted);white-space:nowrap;">msgs</span>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">📱 SMS</label>
              <div style="display:flex;align-items:center;gap:6px;">
                <input class="form-input" type="number" min="0" id="plan-msg-sms-${p.id}"
                  value="${p.messagingLimits?.sms != null ? p.messagingLimits.sms : ''}"
                  placeholder="Unlimited"/>
                <span style="font-size:0.65rem;color:var(--muted);white-space:nowrap;">msgs</span>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">💬 WhatsApp</label>
              <div style="display:flex;align-items:center;gap:6px;">
                <input class="form-input" type="number" min="0" id="plan-msg-whatsapp-${p.id}"
                  value="${p.messagingLimits?.whatsapp != null ? p.messagingLimits.whatsapp : ''}"
                  placeholder="Unlimited"/>
                <span style="font-size:0.65rem;color:var(--muted);white-space:nowrap;">msgs</span>
              </div>
            </div>
          </div>
          <div style="font-size:0.62rem;color:var(--muted);">Leave blank for unlimited. Tenants exceeding limits are throttled, not blocked.</div>
        </div>
      </div>
    `;
  }).join('');
}

async function savePlanConfig(planId) {
  const empVal     = document.getElementById(`plan-emp-${planId}`)?.value.trim();
  const storageGb  = parseFloat(document.getElementById(`plan-storage-${planId}`)?.value);
  const color      = document.getElementById(`plan-color-${planId}`)?.value;
  const modules    = HR_MODULES.map(m => m.id).filter(id =>
    document.getElementById(`plan-mod-${planId}-${id}`)?.checked
  );

  // Cost fields
  const flatRateVal       = document.getElementById(`plan-flat-rate-${planId}`)?.value.trim();
  const ratePerEmpVal     = document.getElementById(`plan-rate-per-emp-${planId}`)?.value.trim();
  const flatRate          = flatRateVal    !== '' ? parseFloat(flatRateVal)    : null;
  const ratePerEmployee   = ratePerEmpVal  !== '' ? parseFloat(ratePerEmpVal)  : null;

  // Messaging limit fields — null = unlimited
  const msgEmailVal    = document.getElementById(`plan-msg-email-${planId}`)?.value.trim();
  const msgSmsVal      = document.getElementById(`plan-msg-sms-${planId}`)?.value.trim();
  const msgWaVal       = document.getElementById(`plan-msg-whatsapp-${planId}`)?.value.trim();
  const messagingLimits = {
    email:    msgEmailVal    !== '' ? parseInt(msgEmailVal)    : null,
    sms:      msgSmsVal      !== '' ? parseInt(msgSmsVal)      : null,
    whatsapp: msgWaVal       !== '' ? parseInt(msgWaVal)       : null,
  };

  if (isNaN(storageGb) || storageGb <= 0) {
    toast('Enter a valid storage limit in GB', 'error'); return;
  }
  if (modules.length === 0) {
    toast('Select at least one module', 'error'); return;
  }

  const employeeLimit   = empVal === '' || empVal === null ? null : parseInt(empVal);
  const storageLimitMb  = Math.round(storageGb * 1024);
  const planData        = {
    label: state.plans[planId]?.label || planId,
    employeeLimit, storageLimitMb, modules, color,
    flatRate, ratePerEmployee, messagingLimits,
  };

  const btn = document.querySelector(`[onclick="savePlanConfig('${planId}')"]`);
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

  try {
    const res = await HRMGMT.savePlan(planId, planData);
    state.plans[planId] = { id: planId, ...planData };
    logActivity('rule', `Plan "${planData.label}" updated — ${res.tenantsUpdated} tenant(s) synced`, 'var(--cyan)');
    toast(`Plan "${planData.label}" saved — ${res.tenantsUpdated} tenant(s) updated`, 'success');
    renderPlanEditor();
  } catch(err) {
    toast(err.message || 'Failed to save plan', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Save Plan'; }
  }
}


// ══════════════════════════════════════════════════════════════
// ACTIVITY LOG
// ══════════════════════════════════════════════════════════════

// Tracks the last Firestore doc id seen for cursor-based pagination
let _logLastId = null;
let _logLoading = false;
const LOG_PAGE_SIZE = 50;

function _logEntryHTML(l) {
  const critBadge = l.critical
    ? '<span style="font-size:0.55rem;font-family:\'DM Mono\',monospace;' +
      'background:rgba(248,113,113,0.12);color:var(--red);border:1px solid ' +
      'rgba(248,113,113,0.3);border-radius:3px;' +
      'padding:1px 5px;margin-left:6px;' +
      'vertical-align:middle;">CRITICAL</span>'
    : '';
  return '<div class="log-entry">' +
    '<div class="log-dot" style="background:' + l.color + ';' +
    (l.critical
      ? 'box-shadow:0 0 0 3px rgba(248,113,113,0.2);'
      : '') + '"></div>' +
    '<div class="log-body">' +
    '<div class="lb">' + l.text + critBadge + '</div>' +
    '<div class="lt">' + l.user + ' · ' + l.time + '</div>' +
    '</div></div>';
}

function renderActivityLog() {
  _logLastId = null;
  const filter   = document.getElementById('log-filter')?.value   || '';
  const critical = document.getElementById('log-critical')?.value || '';
  const sort     = document.getElementById('log-sort')?.value     || 'desc';

  let logs = filter
    ? state.activityLog.filter(l => l.type === filter)
    : state.activityLog;

  if (critical === 'critical') logs = logs.filter(l => l.critical === true);
  if (critical === 'normal')   logs = logs.filter(l => !l.critical);

  if (sort === 'asc') logs = [...logs].reverse();

  document.getElementById('activity-log-list').innerHTML = logs.length
    ? logs.map(_logEntryHTML).join('')
    : '<div style="text-align:center;color:var(--muted);padding:30px;">' +
      'No events found</div>';

  const wrap = document.getElementById('log-load-more-wrap');
  if (wrap) wrap.style.display = logs.length >= LOG_PAGE_SIZE ? '' : 'none';
  if (logs.length) _logLastId = logs[logs.length - 1].id;
}

async function loadMoreActivityLog() {
  if(_logLoading) return;
  _logLoading = true;
  const btn = document.getElementById('log-load-more-btn');
  if(btn) { btn.disabled = true; btn.textContent = 'Loading…'; }

  try {
    const filter   = document.getElementById('log-filter')?.value   || '';
    const critical = document.getElementById('log-critical')?.value || '';
    const sort     = document.getElementById('log-sort')?.value     || 'desc';
    const opts = {
      limit: LOG_PAGE_SIZE,
      sort,
      ...(filter && { type: filter }),
      ...(critical === 'critical' && { critical: true }),
      ...(critical === 'normal'   && { critical: false }),
      ...(_logLastId && { startAfter: String(_logLastId) }),
    };
    const newEntries = await HRMGMT.getActivityLog(opts);

    if(newEntries && newEntries.length) {
      const formatted = newEntries.map(e => ({
        ...e,
        time: e.createdAt
          ? new Date(e.createdAt).toLocaleString('en-AU', {
              year:'numeric', month:'2-digit', day:'2-digit',
              hour:'2-digit', minute:'2-digit' })
          : '—',
      }));
      // Append to local state and DOM
      state.activityLog.push(...formatted);
      const list = document.getElementById('activity-log-list');
      list.insertAdjacentHTML(
        'beforeend', formatted.map(_logEntryHTML).join(''));
      _logLastId = formatted[formatted.length - 1].id;

      // Hide button if we got fewer than a full page (no more data)
      const wrap = document.getElementById('log-load-more-wrap');
      if(wrap) wrap.style.display =
        newEntries.length < LOG_PAGE_SIZE ? 'none' : '';
    } else {
      // No more entries
      const wrap = document.getElementById('log-load-more-wrap');
      if(wrap) wrap.style.display = 'none';
    }
  } catch(err) {
    toast(err.message || 'Failed to load more activity', 'error');
  } finally {
    _logLoading = false;
    if(btn) { btn.disabled = false; btn.textContent = 'Load more'; }
  }
}

function logActivity(type, text, color) {
  const colors = {
    tenant:'var(--accent)', module:'var(--blue)',
    rule:'var(--amber)', auth:'var(--purple)',
  };
  const user = (typeof HRMGMT !== 'undefined' && HRMGMT.getCurrentUser())
    ? HRMGMT.getCurrentUser().email
    : 'platform@hrms.io';
  state.activityLog.unshift({
    id: Date.now(), type, color: color || colors[type] || 'var(--muted)',
    text, user,
    time: new Date().toLocaleString('en-AU', {
      year:'numeric', month:'2-digit', day:'2-digit',
      hour:'2-digit', minute:'2-digit',
    }),
  });
}


// ══════════════════════════════════════════════════════════════
// BILLING — PAGE RENDERING AND INVOICE MANAGEMENT
// ══════════════════════════════════════════════════════════════


function initBillingPage() {
  // Show PNG current date in the batch run card
  const pngNow = new Date(Date.now() + 10 * 60 * 60 * 1000);
  const pngDateStr = pngNow.toISOString().split('T')[0];
  const pngDay = pngNow.getUTCDate();
  const pngDateEl = document.getElementById('batch-png-date');
  if (pngDateEl) {
    pngDateEl.innerHTML = `<strong>PNG date (GMT+10):</strong> ${pngDateStr} — day <strong>${pngDay}</strong>. Batch run will invoice tenants whose billing day = ${pngDay}.`;
  }

  // Populate month selectors (last 12 months) if not done yet
  ['batch-month', 'single-month'].forEach(function(selId) {
    var sel = document.getElementById(selId);
    if (!sel || sel.options.length > 1) return;
    var now = new Date();
    for (var i = 0; i < 12; i++) {
      var d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      var val = d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0');
      sel.appendChild(new Option(val, val));
    }
  });

  // Populate tenant selectors
  ['single-tenant', 'hist-tenant'].forEach(function(selId) {
    var sel = document.getElementById(selId);
    if (!sel || sel.options.length > 1) return;
    state.tenants.forEach(function(t) {
      sel.appendChild(new Option(t.name + ' (' + t.code + ')', t.id));
    });
  });

  // Show invoices tab by default
  billingTab(
    'invoices',
    document.querySelector('#page-billing .tab-btn'),
  );
}

function billingTab(id, btn) {
  document.querySelectorAll('#page-billing .tab-btn')
    .forEach(b => b.classList.remove('active'));
  if (btn) btn.classList.add('active');
  ['invoices', 'history', 'metrics'].forEach(t => {
    const el = document.getElementById('billing-' + t + '-pane');
    if (el) el.style.display = t === id ? '' : 'none';
  });
  if (id === 'invoices') renderInvoicesPane();
  if (id === 'history')  renderInvoiceHistory();
  if (id === 'metrics')  loadBillingMetrics();
}

// ── Status helpers ────────────────────────────────────────────
const INV_STATUS_COLOR = {
  draft:   'var(--muted)',
  sent:    'var(--blue)',
  partial: 'var(--amber)',
  paid:    'var(--accent)',
  overdue: 'var(--red)',
  void:    'var(--muted)',
};
const INV_STATUS_BG = {
  draft:   'rgba(107,114,128,0.12)',
  sent:    'rgba(96,165,250,0.12)',
  partial: 'rgba(251,191,36,0.12)',
  paid:    'rgba(74,222,128,0.12)',
  overdue: 'rgba(248,113,113,0.12)',
  void:    'rgba(107,114,128,0.08)',
};

function _invStatusBadge(status) {
  const c = INV_STATUS_COLOR[status] || 'var(--muted)';
  const bg = INV_STATUS_BG[status] || 'transparent';
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return '<span style="font-size:0.62rem;font-family:\'DM Mono\',monospace;' +
    'padding:2px 8px;border-radius:4px;background:' + bg + ';color:' + c + ';' +
    'border:1px solid ' + c + '40;">' + label + '</span>';
}

function _paidPct(inv) {
  if (inv.total <= 0) return 100;
  return Math.min(100, Math.round((inv.amountPaid / inv.total) * 100));
}

function _invoiceRowHtml(inv) {
  const pct   = _paidPct(inv);
  const color = INV_STATUS_COLOR[inv.status] || 'var(--muted)';
  return '<tr>' +
    '<td style="font-family:\'DM Mono\',monospace;font-size:0.68rem;">' +
      inv.billingMonth + '</td>' +
    '<td>' +
      '<div style="font-size:0.75rem;font-weight:600;">' +
        inv.tenantName + '</div>' +
      '<div style="font-size:0.62rem;color:var(--muted);">' +
        inv.tenantCode + ' · ' + (inv.plan||'') + '</div>' +
    '</td>' +
    '<td style="font-size:0.72rem;font-family:\'DM Mono\',monospace;">' +
      'K' + (inv.total||0).toFixed(2) + '</td>' +
    '<td>' +
      '<div style="display:flex;align-items:center;gap:8px;">' +
        '<div style="flex:1;height:4px;background:var(--bg3);' +
          'border-radius:2px;min-width:60px;overflow:hidden;">' +
          '<div style="height:100%;width:' + pct + '%;background:' +
            color + ';border-radius:2px;"></div></div>' +
        '<span style="font-size:0.62rem;color:var(--muted);' +
          'font-family:\'DM Mono\',monospace;white-space:nowrap;">' +
          'K' + (inv.amountPaid||0).toFixed(2) + '</span>' +
      '</div>' +
    '</td>' +
    '<td>' + _invStatusBadge(inv.status) + '</td>' +
    '<td style="font-size:0.65rem;color:var(--muted);">' +
      (inv.dueDate||'—') + '</td>' +
    '<td>' +
      '<div style="display:flex;gap:4px;justify-content:flex-end;">' +
        '<button class="btn btn-ghost btn-xs" ' +
          'onclick="openInvoiceDetail(\'' + inv.id + '\')" ' +
          'title="View / update">Detail</button>' +
      '</div>' +
    '</td>' +
  '</tr>';
}

// ── Invoices pane ─────────────────────────────────────────────
let _invList = [];
let _invLoading = false;
let _invLastId  = null;
let _invHasMore = false;

async function renderInvoicesPane() {
  const wrap = document.getElementById('billing-invoices-pane');
  if (!wrap) return;

  // Populate month selector with last 12 months
  const sel = document.getElementById('inv-month-filter');
  if (sel && sel.options.length <= 1) {
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0');
      sel.appendChild(new Option(val, val));
    }
  }

  await loadInvoices(true);
}

async function loadInvoices(reset) {
  if (_invLoading) return;
  _invLoading = true;
  const tbody = document.getElementById('inv-tbody');
  if (reset) {
    _invList = [];
    _invLastId = null;
    if (tbody) tbody.innerHTML = '<tr><td colspan="7">' +
      '<div style="padding:20px;text-align:center;' +
      'color:var(--muted);font-size:0.75rem;">' +
      '<i class="spin">⟳</i> Loading invoices…</div></td></tr>';
  }
  try {
    const month  = document.getElementById('inv-month-filter')?.value || '';
    const status = document.getElementById('inv-status-filter')?.value || '';
    const res = await HRMGMT.listInvoices({
      ...(month  && { billingMonth: month }),
      ...(status && { status }),
      limit: 50,
      ...(_invLastId && !reset ? { startAfter: _invLastId } : {}),
    });
    const invoices = (res && res.invoices) ? res.invoices : [];
    _invHasMore = res && res.hasMore;
    if (reset) _invList = invoices;
    else _invList.push(...invoices);
    _invLastId = invoices.length
      ? invoices[invoices.length - 1].id : _invLastId;

    // Render
    if (tbody) {
      if (_invList.length === 0) {
        tbody.innerHTML = '<tr><td colspan="7">' +
          '<div style="padding:30px;text-align:center;color:var(--muted);">' +
          'No invoices found.</div></td></tr>';
      } else {
        tbody.innerHTML = _invList.map(_invoiceRowHtml).join('');
      }
    }
    const moreBtn = document.getElementById('inv-load-more');
    if (moreBtn) moreBtn.style.display = _invHasMore ? '' : 'none';
  } catch(err) {
    if (tbody) tbody.innerHTML = '<tr><td colspan="7">' +
      '<div style="padding:20px;color:var(--red);font-size:0.75rem;">' +
        err.message + '</div></td></tr>';
  } finally {
    _invLoading = false;
  }
}

async function runBatchInvoices(dryRun) {
  const month = document.getElementById('batch-month')?.value || '';
  const btnRun = document.getElementById('btn-batch-run');
  const btnDry = document.getElementById('btn-batch-dryrun');
  [btnRun, btnDry].forEach(b => { if(b) b.disabled = true; });
  if (btnRun) btnRun.textContent = 'Generating…';
  if (btnDry) btnDry.textContent = '…';
  try {
    const payload = { skipExisting: true, dryRun: !!dryRun };
    if (month) payload.billingMonth = month;
    const res = await HRMGMT.generateBatchInvoices(payload);
    if (dryRun) {
      const list = res.preview || [];
      const pngDay = new Date(Date.now() + 36e6).getUTCDate();
      const msg = list.length === 0
        ? `Dry run: no tenants due today (PNG day ${pngDay}) — ${res.filteredOut||0} not due`
        : `Dry run — ${list.length} tenant(s) would be invoiced (PNG ${res.pngDate}):\n` +
          list.map(x => `• ${x.tenantName} [${x.plan}] billing day ${x.billingDay}`).join('\n');
      alert(msg);
    } else {
      toast(
        `Batch complete — ${res.created} created, ${res.skipped} skipped, ${res.filteredOut||0} not due today` +
        (res.errors && res.errors.length ? `, ${res.errors.length} errors` : '') +
        ` · PNG: ${res.pngDate}`,
        (res.errors && res.errors.length) ? 'info' : 'success',
      );
      logActivity('billing', `Batch invoices ${res.billingMonth} — ${res.created} created (PNG ${res.pngDate})`, 'var(--cyan)');
      await loadInvoices(true);
    }
  } catch(err) {
    toast(err.message || 'Batch failed', 'error');
  } finally {
    if (btnRun) { btnRun.disabled = false; btnRun.textContent = '▶ Run Batch'; }
    if (btnDry) { btnDry.disabled = false; btnDry.textContent = 'Dry Run'; }
  }
}

async function runSingleInvoice() {
  const tenantId = document.getElementById('single-tenant')?.value;
  const month    = document.getElementById('single-month')?.value;
  if (!tenantId) { toast('Select a tenant', 'error'); return; }
  if (!month)    { toast('Select a billing month', 'error'); return; }
  const btn = document.getElementById('btn-single-run');
  if (btn) { btn.disabled = true; btn.textContent = 'Generating…'; }
  try {
    const res = await HRMGMT.generateInvoice({ tenantId, billingMonth: month });
    const t = state.tenants.find(x => x.id === tenantId);
    toast(
      'Invoice generated for ' + (t ? t.name : tenantId) +
      ' — K' + res.total.toFixed(2), 'success',
    );
    logActivity('tenant',
      'Invoice ' + month + ' generated for ' + (t ? t.name : tenantId),
      'var(--cyan)',
    );
    loadInvoices(true);
  } catch(err) {
    toast(err.message || 'Failed to generate invoice', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Generate'; }
  }
}

// ── Invoice detail modal ──────────────────────────────────────
async function openInvoiceDetail(invoiceId) {
  const inv = _invList.find(x => x.id === invoiceId)
    || _histList.find(x => x.id === invoiceId);
  if (!inv) return;
  const pct = _paidPct(inv);
  const color = INV_STATUS_COLOR[inv.status] || 'var(--muted)';
  openModal(
    '<div class="modal-header-app">' +
      '<div><div class="modal-title-app">Invoice — ' +
        inv.billingMonth + '</div>' +
        '<div style="font-size:0.65rem;color:var(--muted);">' +
          inv.tenantName + ' · ' + (inv.plan||'') + '</div></div>' +
      '<button class="btn-ghost" style="margin-left:auto;" ' +
        'onclick="PME.closeModal()">' +
        '<i class="fa-solid fa-times"></i></button>' +
    '</div>' +
    '<div class="modal-body-app">' +
      '<div style="display:grid;grid-template-columns:1fr 1fr;' +
        'gap:12px;margin-bottom:18px;">' +
        _infoCell('Issue Date', inv.issueDate || '—') +
        _infoCell('Due Date', inv.dueDate || '—') +
        _infoCell('Total', 'K' + (inv.total||0).toFixed(2)) +
        _infoCell('Employees Billed', inv.empCount || 0) +
      '</div>' +
      '<div style="margin-bottom:14px;">' +
        '<div style="font-size:0.68rem;font-weight:600;color:var(--text);' +
          'margin-bottom:8px;">Line Items</div>' +
        '<table style="width:100%;border-collapse:collapse;' +
          'font-size:0.72rem;">' +
          '<thead><tr style="border-bottom:1px solid var(--border);">' +
            '<th style="text-align:left;padding:6px 0;color:var(--muted);' +
              'font-weight:500;">Description</th>' +
            '<th style="text-align:right;padding:6px 0;color:var(--muted);' +
              'font-weight:500;">Amount</th>' +
          '</tr></thead><tbody>' +
        (inv.lineItems||[]).map(function(li) {
          return '<tr style="border-bottom:1px solid ' +
            'rgba(255,255,255,0.04);">' +
            '<td style="padding:7px 0;">' + li.description + '</td>' +
            '<td style="padding:7px 0;text-align:right;' +
              'font-family:\'DM Mono\',monospace;">' +
              'K' + li.amount.toFixed(2) + '</td></tr>';
        }).join('') +
          '<tr><td style="padding:8px 0;font-weight:600;">Total</td>' +
            '<td style="padding:8px 0;text-align:right;font-weight:700;' +
              'font-family:\'DM Mono\',monospace;color:var(--accent);">' +
              'K' + (inv.total||0).toFixed(2) + '</td></tr>' +
          '</tbody></table>' +
      '</div>' +
      '<div style="margin-bottom:14px;">' +
        '<div style="display:flex;align-items:center;' +
          'justify-content:space-between;margin-bottom:6px;">' +
          '<div style="font-size:0.68rem;font-weight:600;">' +
            'Payment Progress</div>' +
          _invStatusBadge(inv.status) +
        '</div>' +
        '<div style="height:6px;background:var(--bg3);border-radius:3px;' +
          'overflow:hidden;margin-bottom:4px;">' +
          '<div style="height:100%;width:' + pct + '%;background:' +
            color + ';border-radius:3px;"></div></div>' +
        '<div style="font-size:0.62rem;color:var(--muted);">' +
          'K' + (inv.amountPaid||0).toFixed(2) + ' paid of ' +
          'K' + (inv.total||0).toFixed(2) + ' (' + pct + '%)' +
        '</div>' +
      '</div>' +
      '<div style="border-top:1px solid var(--border);padding-top:14px;">' +
        '<div style="font-size:0.68rem;font-weight:600;color:var(--text);' +
          'margin-bottom:10px;">Update Payment</div>' +
        '<div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">' +
          '<div><label class="form-label">Amount Paid (K)</label>' +
            '<input class="form-input" type="number" min="0" step="0.01" ' +
              'id="inv-detail-paid" value="' +
              (inv.amountPaid||0).toFixed(2) + '"/></div>' +
          '<div><label class="form-label">Status</label>' +
            '<select class="form-input" id="inv-detail-status">' +
              [
                'draft','sent','partial','paid','overdue','void',
              ].map(function(s) {
                return '<option value="' + s + '"' +
                  (inv.status === s ? ' selected' : '') + '>' +
                  s.charAt(0).toUpperCase() + s.slice(1) + '</option>';
              }).join('') +
            '</select></div>' +
          '<div style="grid-column:span 2;">' +
            '<label class="form-label">Notes</label>' +
            '<textarea class="form-input" id="inv-detail-notes" rows="2">' +
              (inv.notes||'') + '</textarea></div>' +
        '</div>' +
      '</div>' +
    '</div>' +
    '<div class="modal-footer-app">' +
      '<button class="btn-ghost" onclick="PME.closeModal()">Close</button>' +
      '<button class="btn-primary-app" ' +
        'onclick="PME.saveInvoiceUpdate(\'' + invoiceId + '\')">' +
        '<i class="fa-solid fa-save"></i>Save Update</button>' +
    '</div>'
  );
}

function _infoCell(label, value) {
  return '<div style="background:var(--bg3);border:1px solid var(--border);' +
    'border-radius:6px;padding:10px 12px;">' +
    '<div style="font-size:0.6rem;color:var(--muted);' +
      'text-transform:uppercase;letter-spacing:0.06em;' +
      'margin-bottom:3px;">' + label + '</div>' +
    '<div style="font-size:0.78rem;font-weight:600;">' + value + '</div>' +
  '</div>';
}

async function saveInvoiceUpdate(invoiceId) {
  const status     = document.getElementById('inv-detail-status')?.value;
  const amountPaid = parseFloat(
    document.getElementById('inv-detail-paid')?.value,
  ) || 0;
  const notes = document.getElementById('inv-detail-notes')?.value || '';
  closeModal();
  try {
    await HRMGMT.updateInvoiceStatus({ invoiceId, status, amountPaid, notes });
    toast('Invoice updated', 'success');
    // Update local cache
    const inv = _invList.find(x => x.id === invoiceId);
    if (inv) {
      inv.status = status;
      inv.amountPaid = amountPaid;
      inv.notes = notes;
    }
    const hinv = _histList.find(x => x.id === invoiceId);
    if (hinv) { hinv.status = status; hinv.amountPaid = amountPaid; }
    // Re-render table
    const tbody = document.getElementById('inv-tbody');
    if (tbody && _invList.length)
      tbody.innerHTML = _invList.map(_invoiceRowHtml).join('');
    const htbody = document.getElementById('hist-tbody');
    if (htbody && _histList.length)
      htbody.innerHTML = _histList.map(_invoiceRowHtml).join('');
  } catch(err) {
    toast(err.message || 'Failed to update invoice', 'error');
  }
}

// ── Invoice History pane ──────────────────────────────────────
let _histList = [];
let _histLoading = false;

async function renderInvoiceHistory() {
  const sel = document.getElementById('hist-tenant');
  if (sel && sel.options.length <= 1) {
    state.tenants.forEach(t => {
      sel.appendChild(new Option(t.name + ' (' + t.code + ')', t.id));
    });
  }
  await loadHistory(true);
}

async function loadHistory(reset) {
  if (_histLoading) return;
  _histLoading = true;
  const tbody = document.getElementById('hist-tbody');
  if (reset) {
    _histList = [];
    if (tbody) tbody.innerHTML = '<tr><td colspan="7">' +
      '<div style="padding:20px;text-align:center;color:var(--muted);">' +
      'Select a tenant to view their invoice history.</div></td></tr>';
  }
  try {
    const tenantId = document.getElementById('hist-tenant')?.value || '';
    const status   = document.getElementById('hist-status-filter')?.value || '';
    if (!tenantId) { _histLoading = false; return; }
    const res = await HRMGMT.listInvoices({
      tenantId,
      ...(status && { status }),
      limit: 100,
    });
    _histList = (res && res.invoices) ? res.invoices : [];
    if (tbody) {
      if (_histList.length === 0) {
        tbody.innerHTML = '<tr><td colspan="7">' +
          '<div style="padding:30px;text-align:center;color:var(--muted);">' +
          'No invoices found for this tenant.</div></td></tr>';
      } else {
        tbody.innerHTML = _histList.map(_invoiceRowHtml).join('');
      }
    }

    // Summary stats
    const total = _histList.reduce((s, i) => s + (i.total||0), 0);
    const paid  = _histList.reduce((s, i) => s + (i.amountPaid||0), 0);
    const outstanding = total - paid;
    const el = document.getElementById('hist-summary');
    if (el) el.innerHTML =
      '<div style="display:flex;gap:20px;flex-wrap:wrap;margin-bottom:14px;">' +
        _infoCell('Total Invoiced', 'K' + total.toFixed(2)) +
        _infoCell('Total Paid', 'K' + paid.toFixed(2)) +
        _infoCell('Outstanding', 'K' + outstanding.toFixed(2)) +
        _infoCell('Invoices', _histList.length) +
      '</div>';
  } catch(err) {
    if (tbody) tbody.innerHTML = '<tr><td colspan="7">' +
      '<div style="padding:20px;color:var(--red);font-size:0.75rem;">' +
        err.message + '</div></td></tr>';
  } finally {
    _histLoading = false;
  }
}

// ══════════════════════════════════════════════════════════════
// ADD TENANT MODAL
// ══════════════════════════════════════════════════════════════

// ── Show / hide IPA field based on org type ──
function onOrgTypeChange() {
  const type = document.getElementById('nt-org-type')?.value;
  const ipaGroup = document.getElementById('nt-ipa-group');
  if (ipaGroup) {
    // IPA required for business and SOE tenants
    const needsIpa = type === 'business' || type === 'soe';
    ipaGroup.style.display = needsIpa ? '' : 'none';
    if (!needsIpa) {
      const ipaEl = document.getElementById('nt-ipa');
      if (ipaEl) ipaEl.value = '';
    }
  }
}


function onStatusChange() {
  const status = document.getElementById('nt-status')?.value;
  const trialDates = document.getElementById('nt-trial-dates');
  if (trialDates) {
    trialDates.style.display = status === 'trial' ? '' : 'none';
    if (status === 'trial') {
      // Default trial start to today, end to +30 days
      const today = new Date();
      const end   = new Date(today);
      end.setDate(end.getDate() + 30);
      const fmt = d => d.toISOString().split('T')[0];
      const startEl = document.getElementById('nt-trial-start');
      const endEl   = document.getElementById('nt-trial-end');
      if (startEl && !startEl.value) startEl.value = fmt(today);
      if (endEl   && !endEl.value)   endEl.value   = fmt(end);
    }
  }
}

// ── Add-Tenant form pagination ─────────────────────────────────
const NT_TOTAL_PAGES = 4;
let ntCurrentPage = 1;

function ntGoToPage(page) {
  ntCurrentPage = page;
  for (let i = 1; i <= NT_TOTAL_PAGES; i++) {
    const el = document.getElementById('nt-page-' + i);
    if (el) el.style.display = i === page ? '' : 'none';
  }
  // Update step indicators
  document.querySelectorAll('.nt-step').forEach(s => {
    const n = parseInt(s.dataset.step);
    const numSpan = s.querySelector('.nt-step-num');
    if (n < page) {
      s.style.color = 'var(--muted)';
      if (numSpan) { numSpan.style.background = 'var(--accent)'; numSpan.style.color = '#fff'; numSpan.textContent = '✓'; }
    } else if (n === page) {
      s.style.color = 'var(--accent)';
      if (numSpan) { numSpan.style.background = 'var(--accent)'; numSpan.style.color = '#fff'; numSpan.textContent = String(n); }
    } else {
      s.style.color = 'var(--muted)';
      if (numSpan) { numSpan.style.background = 'var(--border)'; numSpan.style.color = 'var(--muted)'; numSpan.textContent = String(n); }
    }
  });
  // Update dot indicators
  document.querySelectorAll('.nt-dot').forEach(d => {
    d.style.background = parseInt(d.dataset.dot) === page ? 'var(--accent)' : 'var(--border)';
  });
  // Update back/cancel button
  const backBtn = document.getElementById('nt-btn-cancel-back');
  if (backBtn) backBtn.textContent = page === 1 ? 'Cancel' : '← Back';
  // Update next/submit button
  const nextBtn = document.getElementById('nt-btn-next');
  if (nextBtn) nextBtn.textContent = page === NT_TOTAL_PAGES ? 'Create Tenant' : 'Next →';
}

function ntShowClash(field, msg) {
  const el = document.getElementById('nt-' + field + '-clash');
  if (!el) return;
  el.textContent = msg;
  el.style.display = 'block';
  // Also highlight the input border
  const input = document.getElementById('nt-' + field);
  if (input) input.style.borderColor = 'rgba(251,191,36,0.6)';
}

function ntClearClash(field) {
  const el = document.getElementById('nt-' + field + '-clash');
  if (el) { el.style.display = 'none'; el.textContent = ''; }
  const input = document.getElementById('nt-' + field);
  if (input) input.style.borderColor = '';
}

function ntValidatePage(page) {
  if (page === 1) {
    const name = document.getElementById('nt-name')?.value.trim();
    const code = document.getElementById('nt-code')?.value.trim().toUpperCase();
    const orgType = document.getElementById('nt-org-type')?.value;
    const industry = document.getElementById('nt-industry')?.value;
    if (!name)     { toast('Organisation name is required', 'error'); return false; }
    if (!code)     { toast('Short code is required', 'error'); return false; }
    if (!orgType)  { toast('Select an organisation type', 'error'); return false; }
    if (!industry) { toast('Select an industry', 'error'); return false; }
    const needsIpa = orgType === 'business' || orgType === 'soe';
    if (needsIpa && !document.getElementById('nt-ipa')?.value.trim()) {
      toast('IPA registration number is required', 'error'); return false;
    }
    if (document.getElementById('nt-status')?.value === 'trial') {
      const ts = document.getElementById('nt-trial-start')?.value;
      const te = document.getElementById('nt-trial-end')?.value;
      if (!ts || !te) { toast('Trial start and end dates are required', 'error'); return false; }
      if (te <= ts)   { toast('Trial end date must be after start date', 'error'); return false; }
    }

    // ── Clash checks against existing tenants ──────────────────────────────
    // Normalise for comparison: lowercase, collapse whitespace
    const normName = name.toLowerCase().replace(/\s+/g, ' ');
    const normCode = code.toUpperCase();

    const nameClash = (state.tenants || []).find(
      t => t.name.toLowerCase().replace(/\s+/g, ' ') === normName
    );
    const codeClash = (state.tenants || []).find(
      t => t.code.toUpperCase() === normCode
    );

    // Show inline hints and block navigation if clashes found
    if (nameClash) {
      ntShowClash('name',
        `⚠ "${nameClash.name}" already exists (${nameClash.code} · ${nameClash.status}). ` +
        `Choose a different name or check the existing tenant.`
      );
    } else {
      ntClearClash('name');
    }

    if (codeClash) {
      ntShowClash('code',
        `⚠ Code "${codeClash.code}" is already in use by "${codeClash.name}". ` +
        `Choose a unique short code.`
      );
    } else {
      ntClearClash('code');
    }

    if (nameClash || codeClash) return false;
  }
  if (page === 2) {
    const first = document.getElementById('nt-admin-first')?.value.trim();
    const last  = document.getElementById('nt-admin-last')?.value.trim();
    const email = document.getElementById('nt-email')?.value.trim();
    if (!first) { toast('Admin first name is required', 'error'); return false; }
    if (!last)  { toast('Admin last name is required', 'error'); return false; }
    if (!email) { toast('Admin email is required', 'error'); return false; }

    // ── Domain collision check ─────────────────────────────────────────────
    // The backend seeds this email's domain as the tenant's allowedDomains.
    // Warn early if another tenant already owns that domain.
    const atIdx = email.indexOf('@');
    const emailDomain = atIdx > 0 ? email.substring(atIdx + 1).toLowerCase().trim() : '';
    if (emailDomain) {
      const domainClash = (state.tenants || []).find(t =>
        (t.allowedDomains || []).includes(emailDomain) ||
        (t.emailDomain || '').toLowerCase() === emailDomain
      );
      if (domainClash) {
        ntShowClash('email',
          `⚠ Domain "${emailDomain}" is already registered to "${domainClash.name}" ` +
          `(${domainClash.code} · ${domainClash.status}). ` +
          `Use a different admin email or contact support if this is intentional.`
        );
        return false;
      } else {
        ntClearClash('email');
      }

      // ── Blocklist pre-check ───────────────────────────────────────────────
      // Warn if the exact email or its domain is on the platform blocklist.
      // IP cannot be checked client-side; the backend enforces that at sign-up.
      const bl = state.globalRules || {};
      const blockedEmails  = bl.blockedEmails  || [];
      const blockedDomains = bl.blockedDomains || [];
      if (blockedEmails.includes(email.toLowerCase())) {
        ntShowClash('email',
          `⚠ "${email}" is on the platform blocklist and cannot be used as an admin email. ` +
          `Remove it from the Blocklist in Platform Controls first.`
        );
        return false;
      }
      if (emailDomain && blockedDomains.includes(emailDomain)) {
        ntShowClash('email',
          `⚠ Domain "${emailDomain}" is on the platform blocklist. ` +
          `Remove it from the Blocklist in Platform Controls first.`
        );
        return false;
      }
    }
  }
  if (page === 4) {
    const billingEmail = document.getElementById('nt-billing-email')?.value.trim();
    if (!billingEmail) { toast('Billing email is required', 'error'); return false; }
  }
  return true;
}

function ntBack() {
  if (ntCurrentPage === 1) {
    closeModal('modal-add-tenant');
  } else {
    ntGoToPage(ntCurrentPage - 1);
  }
}

function ntNext() {
  if (!ntValidatePage(ntCurrentPage)) return;
  if (ntCurrentPage === NT_TOTAL_PAGES) {
    submitAddTenant();
  } else {
    ntGoToPage(ntCurrentPage + 1);
  }
}

function openAddTenantModal() {
  // Reset conditional fields
  const orgType = document.getElementById('nt-org-type');
  if (orgType) orgType.value = 'government'; // default to Government
  const ipaGroup = document.getElementById('nt-ipa-group');
  if (ipaGroup) ipaGroup.style.display = 'none'; // government doesn't need IPA
  const trialDates = document.getElementById('nt-trial-dates');
  if (trialDates) trialDates.style.display = 'none';
  const statusSel = document.getElementById('nt-status');
  if (statusSel) statusSel.value = 'active';

  // Populate plan dropdown — free, startup, enterprise
  const planSel = document.getElementById('nt-plan');
  if (planSel) {
    planSel.innerHTML = Object.values(state.plans)
      .filter(p => ['core','enterprise'].includes(p.id))
      .map(p => `<option value="${p.id}">${p.label}</option>`)
      .join('');
    if (planSel.value === '') planSel.value = 'core';
  }

  // Clear all form fields
  ['nt-name','nt-code','nt-email','nt-notes','nt-ipa','nt-trial-start','nt-trial-end',
   'nt-admin-first','nt-admin-last','nt-admin-phone','nt-admin-dob',
   'nt-referral-name','nt-referral-notes',
   'nt-billing-name','nt-billing-email','nt-billing-phone','nt-billing-dept',
   'nt-billing-addr1','nt-billing-addr2','nt-billing-notes'].forEach(id => {
    const el = document.getElementById(id); if(el) el.value = '';
  });
  const sexEl = document.getElementById('nt-admin-sex'); if(sexEl) sexEl.value = '';
  const industryEl = document.getElementById('nt-industry'); if(industryEl) industryEl.value = '';
  const refSrcEl = document.getElementById('nt-referral-source'); if(refSrcEl) refSrcEl.value = '';
  const domainHint = document.getElementById('nt-domain-hint'); if(domainHint) domainHint.textContent = '';
  const billCountry = document.getElementById('nt-billing-country'); if(billCountry) billCountry.value = 'PG';

  // Clear any leftover clash hints from a previous open
  ntClearClash('name');
  ntClearClash('code');
  ntClearClash('email');

  // Reset pagination to page 1
  ntGoToPage(1);

  document.getElementById('modal-add-tenant').classList.add('open');
}

function onAdminEmailInput(emailVal) {
  ntClearClash('email');
  const hint = document.getElementById('nt-domain-hint');
  if (!hint) return;
  const atIdx = emailVal.indexOf('@');
  if (atIdx > 0 && atIdx < emailVal.length - 1) {
    const domain = emailVal.substring(atIdx + 1).toLowerCase().trim();
    hint.innerHTML = `<span style="color:var(--accent);">✓</span> Sign-up domain will be auto-set to <strong style="color:var(--text);">${domain}</strong>`;
  } else {
    hint.textContent = '';
  }
}

function closeModal(id) {
  document.getElementById(id).classList.remove('open');
}

async function submitAddTenant() {
  const name    = document.getElementById('nt-name').value.trim();
  const code    = document.getElementById('nt-code').value.trim().toUpperCase();
  const orgType = document.getElementById('nt-org-type').value;

  if (!name || !code)   { toast('Name and code are required', 'error'); return; }
  if (!orgType)         { toast('Select an organisation type', 'error'); return; }

  // Industry
  const industry = document.getElementById('nt-industry')?.value || '';
  if (!industry) { toast('Please select an industry', 'error'); return; }

  // Admin personal details
  const adminFirstName = document.getElementById('nt-admin-first')?.value.trim() || '';
  const adminLastName  = document.getElementById('nt-admin-last')?.value.trim() || '';
  if (!adminFirstName || !adminLastName) { toast('Admin first and last name are required', 'error'); return; }
  const adminDob   = document.getElementById('nt-admin-dob')?.value || '';
  const adminSex   = document.getElementById('nt-admin-sex')?.value || '';
  const adminPhone = document.getElementById('nt-admin-phone')?.value.trim() || '';

  // IPA number required for business and SOE tenants
  const needsIpa = orgType === 'business' || orgType === 'soe';
  const ipaNumber = needsIpa
    ? document.getElementById('nt-ipa')?.value.trim()
    : null;
  if (needsIpa && !ipaNumber) {
    toast('IPA registration number is required for ' + orgType + ' tenants', 'error');
    return;
  }

  // isGovernment — auto-true for government orgType only
  const isGovernment = ['government', 'government-dept', 'provincial-government', 'district-government'].includes(orgType);

  // Check for duplicate IPA number in existing tenants
  if (ipaNumber) {
    const duplicate = state.tenants.find(t => t.ipaNumber === ipaNumber);
    if (duplicate) {
      toast(`IPA number ${ipaNumber} is already registered to "${duplicate.name}"`, 'error');
      return;
    }
  }

  const plan       = document.getElementById('nt-plan').value;
  const status     = document.getElementById('nt-status').value;
  const adminEmail = document.getElementById('nt-email').value.trim();
  const notes      = document.getElementById('nt-notes').value.trim();

  if (!adminEmail) { toast('Admin email is required', 'error'); return; }

  // Auto-derive domain from admin email
  const atIdx = adminEmail.indexOf('@');
  const autoDomain = atIdx > 0 ? adminEmail.substring(atIdx + 1).toLowerCase().trim() : '';
  if (!autoDomain || !/^[a-z0-9]([a-z0-9\-]*[a-z0-9])?(\.[a-z]{2,})+$/.test(autoDomain)) {
    toast('Enter a valid admin email to auto-detect the sign-up domain', 'error');
    return;
  }

  // Referral details
  const referralName   = document.getElementById('nt-referral-name')?.value.trim() || '';
  const referralSource = document.getElementById('nt-referral-source')?.value || '';
  const referralNotes  = document.getElementById('nt-referral-notes')?.value.trim() || '';

  // Billing details
  const billingEmail   = document.getElementById('nt-billing-email')?.value.trim() || '';
  if (!billingEmail) { toast('Billing email is required', 'error'); return; }
  const billing = {
    contactName: document.getElementById('nt-billing-name')?.value.trim() || '',
    email:       billingEmail,
    phone:       document.getElementById('nt-billing-phone')?.value.trim() || '',
    department:  document.getElementById('nt-billing-dept')?.value.trim() || '',
    address1:    document.getElementById('nt-billing-addr1')?.value.trim() || '',
    address2:    document.getElementById('nt-billing-addr2')?.value.trim() || '',
    country:     document.getElementById('nt-billing-country')?.value || 'PG',
    notes:       document.getElementById('nt-billing-notes')?.value.trim() || '',
  };

  // Derive modules from the live plan config (state.plans, loaded on boot from
  // config/plans/{planId}) — this is the same source of truth the backend uses
  // (getPlanConfig), so core gets its curated default set and enterprise gets
  // every module. Falls back to omitting `modules` so the backend applies its
  // own PLAN_DEFAULTS if state.plans wasn't loaded for some reason.
  const planModules = state.plans[plan] && state.plans[plan].modules;
  let modules = Array.isArray(planModules) && planModules.length ? [...planModules] : null;
  // Government tenants always get public-service-ref (backend also enforces this)
  if (modules && isGovernment && !modules.includes('public-service-ref')) modules.push('public-service-ref');

  // Trial date validation
  let trialStartDate = null;
  let trialEndDate   = null;
  if (status === 'trial') {
    trialStartDate = document.getElementById('nt-trial-start')?.value;
    trialEndDate   = document.getElementById('nt-trial-end')?.value;
    if (!trialStartDate || !trialEndDate) {
      toast('Trial start and end dates are required', 'error'); return;
    }
    if (trialEndDate <= trialStartDate) {
      toast('Trial end date must be after start date', 'error'); return;
    }
  }

  const submitBtn = document.getElementById('nt-btn-next');
  if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Creating…'; }

  try {
    const payload = {
      name, code, plan, status, adminEmail, notes, orgType,
      isGovernment, industry, billing,
      ...(modules && { modules }),
      adminFirstName, adminLastName,
      ...(adminDob   && { adminDob }),
      ...(adminSex   && { adminSex }),
      ...(adminPhone && { adminPhone }),
      ...(ipaNumber      && { ipaNumber }),
      ...(trialStartDate && { trialStartDate }),
      ...(trialEndDate   && { trialEndDate }),
      ...(referralName   && { referralName }),
      ...(referralSource && { referralSource }),
      ...(referralNotes  && { referralNotes }),
    };

    const tenant = await HRMGMT.createTenant(payload);
    state.tenants.push(tenant);

    // Domain allow-list is now seeded on the tenant document by the backend
    // (from the admin email domain). No global saveGlobalRules call needed.

    logActivity('tenant', `Tenant "${name}" (${code}) created`, 'var(--accent)');
    closeModal('modal-add-tenant');
    renderTenantsTable();
    renderDashboard();
    document.getElementById('nb-total').textContent = state.tenants.length;
    document.getElementById('nb-tenants').textContent = state.tenants.length;
    toast(`Tenant "${name}" created successfully`, 'success');

    // Reset form fields
    ['nt-name','nt-email','nt-notes','nt-ipa','nt-trial-start','nt-trial-end',
     'nt-admin-first','nt-admin-last','nt-admin-phone','nt-admin-dob',
     'nt-referral-name','nt-referral-notes',
     'nt-billing-name','nt-billing-email','nt-billing-phone','nt-billing-dept',
     'nt-billing-addr1','nt-billing-addr2','nt-billing-notes'].forEach(id => {
      const el = document.getElementById(id); if(el) el.value = '';
    });
    document.getElementById('nt-code').value = '';
    document.getElementById('nt-org-type').value = 'government';
    document.getElementById('nt-ipa-group').style.display  = 'none';
    document.getElementById('nt-trial-dates').style.display = 'none';
    const domainHint = document.getElementById('nt-domain-hint'); if(domainHint) domainHint.textContent = '';
    ntGoToPage(1);
  } catch(err) {
    toast(err.message || 'Failed to create tenant', 'error');
  } finally {
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Create Tenant'; }
  }
}

// ══════════════════════════════════════════════════════════════
// USER MANAGEMENT — Claim assignment
// ══════════════════════════════════════════════════════════════

function renderUserMgmt() {
  // Populate tenant dropdown with current tenant list
  const sel = document.getElementById('tu-tenant');
  if(sel) {
    sel.innerHTML = '<option value="">— select tenant —</option>' +
      state.tenants.map(t => `<option value="${t.id}">${t.name} (${t.code})</option>`).join('');
  }
  // Populate role dropdowns with new role set
  ['ur-role','tu-role'].forEach(function(id) {
    const el = document.getElementById(id);
    if (!el) return;
    el.innerHTML = ROLE_OPTIONS.map(function(r) {
      return `<option value="${r.value}">${r.label}</option>`;
    }).join('');
  });
  // Wire custom role ID field show/hide
  ['ur-role','tu-role'].forEach(function(roleSelId) {
    const el = document.getElementById(roleSelId);
    if (!el) return;
    el.onchange = function() {
      const customRow = document.getElementById(roleSelId + '-custom-row');
      if (customRow) customRow.style.display = this.value === 'custom' ? '' : 'none';
    };
  });
}

async function submitSuperAdminClaim() {
  const uid    = document.getElementById('sa-uid')?.value.trim();
  const action = document.getElementById('sa-action')?.value;
  if(!uid) { toast('Enter a User UID', 'error'); return; }

  const btn = document.getElementById('btn-sa-apply');
  const grant = action === 'grant';

  try {
    if(btn) { btn.disabled = true; btn.textContent = 'Applying…'; }
    await HRMGMT.setSuperAdminClaim(uid, grant);
    logActivity('auth', `Super admin claim ${grant ? 'granted to' : 'revoked from'} ${uid}`, 'var(--purple)');
    toast(`Super admin ${grant ? 'granted to' : 'revoked from'} ${uid}`, grant ? 'success' : 'info');
    document.getElementById('sa-uid').value = '';
  } catch(err) {
    toast(err.message || 'Failed to set super admin claim', 'error');
  } finally {
    if(btn) { btn.disabled = false; btn.textContent = 'Apply claim'; }
  }
}

async function submitSetUserRole() {
  const uid  = document.getElementById('ur-uid')?.value.trim();
  const role = document.getElementById('ur-role')?.value;
  if(!uid)  { toast('Enter a User UID', 'error'); return; }
  if(!role) { toast('Select a role', 'error'); return; }
  const customRoleId = role === 'custom'
    ? document.getElementById('ur-role-custom-id')?.value.trim()
    : null;
  if (role === 'custom' && !customRoleId) {
    toast('Enter a custom role ID', 'error'); return;
  }
  const btn = document.getElementById('btn-ur-apply');
  const roleLabel = (ROLE_CAPABILITIES[role]?.label || role) +
    (customRoleId ? ' [' + customRoleId + ']' : '');
  try {
    if(btn) { btn.disabled = true; btn.textContent = 'Applying…'; }
    await HRMGMT.setUserRole(uid, role, customRoleId || undefined);
    logActivity('auth', `Role "${roleLabel}" assigned to ${uid}`, 'var(--cyan)');
    toast(`Role "${roleLabel}" assigned to ${uid}`, 'success');
    document.getElementById('ur-uid').value = '';
  } catch(err) {
    toast(err.message || 'Failed to set role', 'error');
  } finally {
    if(btn) { btn.disabled = false; btn.textContent = 'Apply role'; }
  }
}

async function submitTenantUserClaim() {
  const uid      = document.getElementById('tu-uid')?.value.trim();
  const tenantId = document.getElementById('tu-tenant')?.value;
  if(!uid)      { toast('Enter a User UID', 'error'); return; }
  if(!tenantId) { toast('Select a tenant', 'error'); return; }

  const t = state.tenants.find(x => x.id === tenantId);
  const gcipTenantId = t?.gcipTenantId;
  if(!gcipTenantId) { toast('Tenant has no GCIP ID — cannot assign', 'error'); return; }

  const btn = document.getElementById('btn-tu-assign');
  try {
    if(btn) { btn.disabled = true; btn.textContent = 'Assigning…'; }
    await HRMGMT.setTenantUserRole(uid, gcipTenantId, 'employee');
    logActivity('auth', `Tenant claim "${tenantId}" assigned to ${uid}`, 'var(--cyan)');
    toast(`User ${uid} assigned to tenant ${t?.name || tenantId}`, 'success');
    document.getElementById('tu-uid').value = '';
    document.getElementById('tu-tenant').value = '';
  } catch(err) {
    toast(err.message || 'Failed to assign tenant claim', 'error');
  } finally {
    if(btn) { btn.disabled = false; btn.textContent = 'Assign claim'; }
  }
}

async function revokeTenantUserClaim() {
  const uid = document.getElementById('tu-uid')?.value.trim();
  if(!uid) { toast('Enter a User UID to revoke', 'error'); return; }

  const btn = document.getElementById('btn-tu-revoke');
  try {
    if(btn) { btn.disabled = true; btn.textContent = 'Revoking…'; }
    await HRMGMT.setUserRole(uid, null, null);
    logActivity('auth', `System access revoked for user ${uid}`, 'var(--red)');
    toast(`System access revoked for user ${uid}`, 'info');
    document.getElementById('tu-uid').value = '';
  } catch(err) {
    toast(err.message || 'Failed to revoke tenant claim', 'error');
  } finally {
    if(btn) { btn.disabled = false; btn.textContent = 'Revoke'; }
  }
}

// ══════════════════════════════════════════════════════════════
// TOAST
// ══════════════════════════════════════════════════════════════

function toast(msg, type='info') {
  const icons = { success:'✓', error:'✕', info:'ℹ' };
  const el = document.createElement('div');
  el.className = `toast ${type}`;
  el.innerHTML = `<span style="font-size:0.85rem;">${icons[type]||'·'}</span>${msg}`;
  document.getElementById('toast-container').appendChild(el);
  setTimeout(() => { el.style.opacity='0'; el.style.transform='translateX(20px)'; el.style.transition='all 0.3s ease'; setTimeout(()=>el.remove(),300); }, 3000);
}

// ══════════════════════════════════════════════════════════════
// INIT
// ══════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════
// VIEW SWITCHER
// ══════════════════════════════════════════════════════════════

function showLoginView() {
  document.body.classList.remove('is-authenticated');
  setTimeout(() => { const el = document.getElementById('login-email'); if(el) el.focus(); }, 50);
}

function showPortalView(user) {
  document.body.classList.add('is-authenticated');
  const emailEl = document.getElementById('tb-user-email');
  if (emailEl) emailEl.textContent = user.email || 'admin';
}

// ══════════════════════════════════════════════════════════════
// LOGIN FORM
// ══════════════════════════════════════════════════════════════

function initLogin() {
  const emailEl   = document.getElementById('login-email');
  const passEl    = document.getElementById('login-password');
  const btnSignin = document.getElementById('btn-signin');
  const spinner   = document.getElementById('signin-spinner');
  const btnLabel  = document.getElementById('btn-label');
  const btnArrow  = document.getElementById('btn-arrow');
  const errBanner = document.getElementById('error-banner');
  const errText   = document.getElementById('error-text');
  const togglePw  = document.getElementById('toggle-pw');
  const eyeOpen   = document.getElementById('eye-open');
  const eyeClosed = document.getElementById('eye-closed');

  togglePw.addEventListener('click', () => {
    const hide = passEl.type === 'password';
    passEl.type = hide ? 'text' : 'password';
    eyeOpen.style.display   = hide ? 'none' : '';
    eyeClosed.style.display = hide ? ''     : 'none';
  });

  function showError(msg) { errText.textContent = msg; errBanner.classList.add('show'); }
  function clearError()   { errBanner.classList.remove('show'); }

  function setLoading(on) {
    btnSignin.disabled     = on;
    spinner.classList.toggle('show', on);
    btnLabel.textContent   = on ? 'Signing in\u2026' : 'Sign in';
    btnArrow.style.display = on ? 'none' : '';
  }

  function friendlyError(err) {
    const code = err && err.code ? err.code : '';
    const map = {
      'auth/invalid-email':             'Enter a valid email address.',
      'auth/user-not-found':            'No account found for this email.',
      'auth/wrong-password':            'Incorrect password. Please try again.',
      'auth/invalid-credential':        'Invalid email or password.',
      'auth/invalid-login-credentials': 'Invalid email or password.',
      'auth/too-many-requests':         'Too many attempts. Please wait and try again.',
      'auth/user-disabled':             'This account has been disabled.',
      'auth/network-request-failed':    'Network error. Check your connection.',
      'auth/operation-not-allowed':     'Email/password sign-in is not enabled in Firebase Console.',
    };
    if (map[code]) return map[code];
    return code ? 'Sign-in failed (' + code + ').' : 'Sign-in failed: ' + ((err && err.message) || 'Unknown error.');
  }

  async function doSignIn() {
    clearError();
    const email = emailEl.value.trim();
    const pass  = passEl.value;
    if (!email || !pass) { showError('Please enter your email and password.'); return; }
    setLoading(true);
    try {
      await HRMGMT.signIn(email, pass);
      // onAuthStateChanged fires automatically and calls showPortalView
    } catch (err) {
      setLoading(false);
      showError(friendlyError(err));
      passEl.value = '';
      passEl.focus();
    }
  }

  btnSignin.addEventListener('click', doSignIn);
  [emailEl, passEl].forEach(function(el) {
    el.addEventListener('keydown', function(e) { if (e.key === 'Enter') doSignIn(); });
  });
}

// ══════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════
// SYSTEM HEALTH CHECK & SETUP WIZARD
// ══════════════════════════════════════════════════════════════

const ALL_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain', 'text/csv',
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/zip', 'application/x-zip-compressed',
  'audio/mpeg', 'audio/wav', 'video/mp4',
];

// Wizard state
let wizState = {
  step: 0,
  missing: [],
  plans: {
    core: {
      label: 'Core', color: 'amber', employeeLimit: 30,
      storageLimitMb: 5120, flatRate: 0, ratePerEmployee: 0,
      modules: ['employees','leave','tasks','settings','discipline','documents'],
      messagingLimits: { email: 500, sms: 100, whatsapp: 0 },
    },
    enterprise: {
      label: 'Enterprise', color: 'purple', employeeLimit: null,
      storageLimitMb: 30720, flatRate: 0, ratePerEmployee: 0,
      modules: MODULES.map(m => m.id),
      messagingLimits: { email: null, sms: 2000, whatsapp: 500 },
    },
  },
  fileUpload: {
    maxFileSizeMb: 20,
    allowedExtensions: ['pdf','doc','docx','xls','xlsx','ppt','pptx','txt','csv','jpg','jpeg','png','gif','webp','zip'],
    allowedMimeTypes: [
      'application/pdf','application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain','text/csv','image/jpeg','image/png','image/gif','image/webp',
      'application/zip','application/x-zip-compressed',
    ],
    moduleOverrides: {
      documents: { maxFileSizeMb: 50 },
      employees: { allowedExtensions: ['pdf','jpg','jpeg','png'] },
      recruitment: { maxFileSizeMb: 10 },
    },
  },
  globalRules: {
    requireMfa: true, allowGuestAccess: false, allowApiAccess: true,
    sandboxMode: false, autoProvision: false, defaultPlan: 'core',
  },
};

const WIZ_STEPS = [
  { id: 'plans',       label: '📋 Plans',        icon: '📋' },
  { id: 'fileUpload',  label: '📁 File Uploads',  icon: '📁' },
  { id: 'moduleSchema',label: '🧩 Module Schema', icon: '🧩' },
  { id: 'globalRules', label: '🛡 Global Rules',  icon: '🛡' },
  { id: 'review',      label: '✓ Review',         icon: '✓'  },
];

async function runHealthCheck() {
  try {
    const result = await HRMGMT.systemHealthCheck();
    if (result.allOk) return; // all good, no wizard needed

    const missing = [];
    if (!result.checks.plans.ok) missing.push('plans');
    if (!result.checks.globalRules.ok) missing.push('globalRules');
    if (!result.checks.fileUpload.ok) missing.push('fileUpload');
    if (!result.checks.moduleSchema.ok) missing.push('moduleSchema');

    wizState.missing = missing;
    showWizard();
  } catch (err) {
    console.warn('Health check failed:', err.message);
  }
}

function showWizard() {
  const overlay = document.getElementById('setup-wizard-overlay');
  if (overlay) { overlay.style.display = 'flex'; }
  wizState.step = 0;
  wizRender();
}

function wizRender() {
  renderWizSteps();
  renderWizBody();
  renderWizFooter();
}

function renderWizSteps() {
  const el = document.getElementById('wiz-steps');
  if (!el) return;
  el.innerHTML = WIZ_STEPS.map((s, i) => {
    const active = i === wizState.step;
    const done   = i < wizState.step;
    return `<div style="display:flex;align-items:center;gap:0;">
      <div style="display:flex;align-items:center;gap:8px;padding:12px 16px;font-size:0.72rem;font-weight:${active?'700':'500'};
        color:${active?'var(--accent)':done?'var(--text)':'var(--muted)'};white-space:nowrap;cursor:${done?'pointer':'default'};border-bottom:2px solid ${active?'var(--accent)':'transparent'};transition:all 0.15s;"
        onclick="${done ? 'wizGoTo('+i+')' : ''}">
        <span style="width:20px;height:20px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:0.65rem;font-weight:700;flex-shrink:0;
          background:${active?'var(--accent)':done?'rgba(74,222,128,0.2)':'var(--border)'};
          color:${active?'#0a0d14':done?'var(--accent)':'var(--muted)'};">
          ${done ? '✓' : i+1}
        </span>
        ${s.label}
      </div>
      ${i < WIZ_STEPS.length-1 ? '<span style="color:var(--border);font-size:0.8rem;">›</span>' : ''}
    </div>`;
  }).join('');
}

function renderWizBody() {
  const el = document.getElementById('wiz-body');
  if (!el) return;
  const step = WIZ_STEPS[wizState.step];
  if (step.id === 'plans')        el.innerHTML = wizBodyPlans();
  else if (step.id === 'fileUpload')   el.innerHTML = wizBodyFileUpload();
  else if (step.id === 'moduleSchema') el.innerHTML = wizBodyModuleSchema();
  else if (step.id === 'globalRules')  el.innerHTML = wizBodyGlobalRules();
  else if (step.id === 'review')       el.innerHTML = wizBodyReview();
}

function renderWizFooter() {
  const back   = document.getElementById('wiz-btn-back');
  const next   = document.getElementById('wiz-btn-next');
  const finish = document.getElementById('wiz-btn-finish');
  const badge  = document.getElementById('wiz-missing-badge');
  if (back)   back.style.display   = wizState.step > 0 ? '' : 'none';
  if (next)   next.style.display   = wizState.step < WIZ_STEPS.length - 1 ? '' : 'none';
  if (finish) finish.style.display = wizState.step === WIZ_STEPS.length - 1 ? '' : 'none';
  if (badge)  badge.textContent    = wizState.missing.length
    ? 'Missing: ' + wizState.missing.join(', ')
    : '';
}

// ── Step bodies ───────────────────────────────────────────────

function wizBodyPlans() {
  return Object.entries(wizState.plans).map(([planId, p]) => {
    const modules = MODULES;
    return `
    <div style="border:1px solid var(--border);border-radius:10px;padding:18px;margin-bottom:16px;background:var(--bg3);">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px;">
        <span class="plan-badge plan-${planId}">${p.label}</span>
        <span style="font-size:0.68rem;color:var(--muted);font-family:'DM Mono',monospace;">${planId}</span>
      </div>
      <div class="form-row-3">
        <div class="form-group">
          <label class="form-label">Employee Limit</label>
          <input class="form-input" type="number" min="1" placeholder="blank = unlimited"
            value="${p.employeeLimit !== null ? p.employeeLimit : ''}"
            onchange="wizPlanSet('${planId}','employeeLimit',this.value===''?null:parseInt(this.value))"/>
        </div>
        <div class="form-group">
          <label class="form-label">Flat Monthly Rate (PGK)</label>
          <input class="form-input" type="number" min="0" step="0.01" placeholder="0.00"
            value="${p.flatRate || ''}"
            onchange="wizPlanSet('${planId}','flatRate',parseFloat(this.value)||0)"/>
        </div>
        <div class="form-group">
          <label class="form-label">Rate / Employee (PGK)</label>
          <input class="form-input" type="number" min="0" step="0.01" placeholder="0.00"
            value="${p.ratePerEmployee || ''}"
            onchange="wizPlanSet('${planId}','ratePerEmployee',parseFloat(this.value)||0)"/>
        </div>
      </div>
      <div class="form-row-3" style="margin-bottom:14px;">
        <div class="form-group">
          <label class="form-label">Storage Limit (GB)</label>
          <input class="form-input" type="number" min="1"
            value="${Math.round((p.storageLimitMb||5120)/1024)}"
            onchange="wizPlanSet('${planId}','storageLimitMb',parseInt(this.value)*1024)"/>
        </div>
        <div class="form-group">
          <label class="form-label">Badge Colour</label>
          <select class="form-input" onchange="wizPlanSet('${planId}','color',this.value)">
            ${['amber','purple','blue','teal','green'].map(c =>
              `<option value="${c}" ${p.color===c?'selected':''}>${c}</option>`).join('')}
          </select>
        </div>
      </div>
      <label class="form-label" style="margin-bottom:8px;">Included Modules</label>
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:5px;">
        ${modules.map(m => `
          <label style="display:flex;align-items:center;gap:7px;padding:5px 8px;background:var(--bg2);border:1px solid var(--border);border-radius:6px;cursor:pointer;font-size:0.7rem;">
            <input type="checkbox" ${(p.modules||[]).includes(m.id)?'checked':''}
              onchange="wizPlanToggleMod('${planId}','${m.id}',this.checked)"/>
            ${m.icon} ${m.name}
          </label>`).join('')}
      </div>
    </div>`;
  }).join('');
}

function wizBodyFileUpload() {
  const f = wizState.fileUpload;
  const extensions = ['pdf','doc','docx','xls','xlsx','ppt','pptx','txt','csv',
    'jpg','jpeg','png','gif','webp','zip','mp3','wav','mp4'];
  return `
    <div class="form-group" style="margin-bottom:20px;">
      <label class="form-label">Max File Size (MB)</label>
      <input class="form-input" type="number" min="1" max="500" style="max-width:160px;"
        value="${f.maxFileSizeMb}"
        onchange="wizState.fileUpload.maxFileSizeMb=parseInt(this.value)||20"/>
      <div style="font-size:0.62rem;color:var(--muted);margin-top:4px;">Platform-wide default — individual modules can override this.</div>
    </div>

    <label class="form-label" style="margin-bottom:10px;">Allowed File Extensions</label>
    <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(100px,1fr));gap:5px;margin-bottom:20px;">
      ${extensions.map(ext => `
        <label style="display:flex;align-items:center;gap:7px;padding:5px 8px;background:var(--bg3);border:1px solid var(--border);border-radius:6px;cursor:pointer;font-size:0.72rem;font-family:'DM Mono',monospace;">
          <input type="checkbox" ${f.allowedExtensions.includes(ext)?'checked':''}
            onchange="wizToggleExt('${ext}',this.checked)"/>
          .${ext}
        </label>`).join('')}
    </div>

    <div style="border-top:1px solid var(--border);padding-top:16px;">
      <div style="font-size:0.75rem;font-weight:600;margin-bottom:12px;">Module-specific Overrides</div>
      ${['documents','employees','recruitment'].map(mod => {
        const ov = f.moduleOverrides[mod] || {};
        return `
        <div style="padding:12px;background:var(--bg3);border:1px solid var(--border);border-radius:8px;margin-bottom:8px;">
          <div style="font-size:0.72rem;font-weight:600;margin-bottom:8px;color:var(--text);">
            ${MODULES.find(m=>m.id===mod)?.icon || '📦'} ${mod.charAt(0).toUpperCase()+mod.slice(1)}
          </div>
          <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
            <div style="display:flex;align-items:center;gap:8px;">
              <label class="form-label" style="margin:0;white-space:nowrap;">Max size (MB)</label>
              <input class="form-input btn-sm" type="number" min="1" style="width:80px;"
                placeholder="inherit" value="${ov.maxFileSizeMb||''}"
                onchange="wizSetModOverride('${mod}','maxFileSizeMb',parseInt(this.value)||null)"/>
            </div>
          </div>
        </div>`;
      }).join('')}
    </div>
  `;
}

function wizBodyModuleSchema() {
  return `
    <div style="background:rgba(74,222,128,0.06);border:1px solid rgba(74,222,128,0.2);border-radius:8px;padding:14px;margin-bottom:18px;">
      <div style="font-size:0.75rem;font-weight:600;color:var(--accent);margin-bottom:4px;">Auto-generated from MODULE_DEFS</div>
      <div style="font-size:0.7rem;color:var(--muted);">
        Module schema documents will be seeded automatically for all ${MODULES.length} defined modules.
        Each module gets a schema doc at <code style="font-family:'DM Mono',monospace;font-size:0.68rem;background:var(--bg3);padding:1px 5px;border-radius:3px;">platform/moduleDefs/defs/{moduleId}</code>
        containing its feature list and access rule definitions.
        You can refine these after setup via the Module Management page.
      </div>
    </div>
    <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:6px;">
      ${MODULES.map(m => `
        <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--bg3);border:1px solid var(--border);border-radius:6px;">
          <span style="font-size:1rem;">${m.icon}</span>
          <div>
            <div style="font-size:0.72rem;font-weight:500;">${m.name}</div>
            <div style="font-size:0.6rem;color:var(--muted);font-family:'DM Mono',monospace;">${m.id}</div>
          </div>
          <span style="margin-left:auto;font-size:0.6rem;color:var(--muted);">${(m.features||[]).length} feat</span>
        </div>`).join('')}
    </div>
  `;
}

function wizBodyGlobalRules() {
  const r = wizState.globalRules;
  const plans = Object.keys(wizState.plans);
  return `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;">
      ${[
        { key:'requireMfa',      label:'Require MFA for all admins',        desc:'Force 2FA on all tenant admin accounts' },
        { key:'allowGuestAccess',label:'Allow guest / read-only access',     desc:'Permit unauthenticated read access' },
        { key:'allowApiAccess',  label:'API access for tenants',             desc:'Allow tenants to generate API keys' },
        { key:'sandboxMode',     label:'Sandbox / demo mode',                desc:'All writes silently blocked — safe for demos' },
        { key:'autoProvision',   label:'Self-service provisioning',          desc:'Orgs can self-register without admin approval' },
      ].map(row => `
        <div style="display:flex;align-items:center;justify-content:space-between;padding:12px 14px;background:var(--bg3);border:1px solid var(--border);border-radius:8px;">
          <div>
            <div style="font-size:0.75rem;font-weight:500;">${row.label}</div>
            <div style="font-size:0.62rem;color:var(--muted);margin-top:2px;">${row.desc}</div>
          </div>
          <label class="toggle" style="flex-shrink:0;margin-left:14px;">
            <input type="checkbox" ${r[row.key]?'checked':''}
              onchange="wizState.globalRules['${row.key}']=this.checked"/>
            <span class="toggle-slider"></span>
          </label>
        </div>`).join('')}
    </div>
    <div class="form-group" style="margin-top:16px;max-width:280px;">
      <label class="form-label">Default Plan for New Tenants</label>
      <select class="form-input" onchange="wizState.globalRules.defaultPlan=this.value">
        ${plans.map(pid => `<option value="${pid}" ${r.defaultPlan===pid?'selected':''}>${wizState.plans[pid].label}</option>`).join('')}
      </select>
    </div>
  `;
}

function wizBodyReview() {
  const f  = wizState.fileUpload;
  const gr = wizState.globalRules;
  return `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:18px;">
      ${Object.entries(wizState.plans).map(([planId, p]) => `
        <div style="padding:14px;background:var(--bg3);border:1px solid var(--border);border-radius:8px;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
            <span class="plan-badge plan-${planId}">${p.label}</span>
          </div>
          <div style="font-size:0.7rem;color:var(--muted);display:grid;gap:4px;">
            <div>👥 Limit: <strong style="color:var(--text);">${p.employeeLimit===null?'Unlimited':p.employeeLimit}</strong></div>
            <div>💾 Storage: <strong style="color:var(--text);">${Math.round((p.storageLimitMb||0)/1024)} GB</strong></div>
            <div>💰 Flat rate: <strong style="color:var(--text);">K${(p.flatRate||0).toFixed(2)} / month</strong></div>
            <div>➕ Per employee: <strong style="color:var(--text);">K${(p.ratePerEmployee||0).toFixed(2)}</strong></div>
            <div>🧩 Modules: <strong style="color:var(--text);">${(p.modules||[]).length}</strong></div>
          </div>
        </div>`).join('')}
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:18px;">
      <div style="padding:14px;background:var(--bg3);border:1px solid var(--border);border-radius:8px;">
        <div style="font-size:0.72rem;font-weight:600;margin-bottom:8px;">📁 File Uploads</div>
        <div style="font-size:0.7rem;color:var(--muted);display:grid;gap:3px;">
          <div>Max size: <strong style="color:var(--text);">${f.maxFileSizeMb} MB</strong></div>
          <div>Extensions: <strong style="color:var(--text);">${(f.allowedExtensions||[]).length} types</strong></div>
        </div>
      </div>
      <div style="padding:14px;background:var(--bg3);border:1px solid var(--border);border-radius:8px;">
        <div style="font-size:0.72rem;font-weight:600;margin-bottom:8px;">🛡 Security</div>
        <div style="font-size:0.7rem;color:var(--muted);display:grid;gap:3px;">
          <div>MFA required: <strong style="color:var(--text);">${gr.requireMfa?'Yes':'No'}</strong></div>
          <div>API access: <strong style="color:var(--text);">${gr.allowApiAccess?'Enabled':'Disabled'}</strong></div>
          <div>Auto-provision: <strong style="color:var(--text);">${gr.autoProvision?'Yes':'No'}</strong></div>
          <div>Default plan: <strong style="color:var(--text);">${gr.defaultPlan}</strong></div>
        </div>
      </div>
    </div>

    <div style="background:rgba(74,222,128,0.06);border:1px solid rgba(74,222,128,0.2);border-radius:8px;padding:14px;">
      <div style="font-size:0.75rem;font-weight:600;color:var(--accent);margin-bottom:4px;">🧩 Module Schema</div>
      <div style="font-size:0.7rem;color:var(--muted);">
        ${MODULES.length} module schema documents will be seeded automatically.
      </div>
    </div>
  `;
}

// ── Wizard navigation ─────────────────────────────────────────

function wizCollectCurrentStep() {
  // All state is written live via oninput/onchange — nothing to collect here.
}

function wizNext() {
  wizCollectCurrentStep();
  if (wizState.step < WIZ_STEPS.length - 1) {
    wizState.step++;
    wizRender();
  }
}

function wizBack() {
  if (wizState.step > 0) {
    wizState.step--;
    wizRender();
  }
}

function wizGoTo(idx) {
  if (idx < wizState.step) {
    wizState.step = idx;
    wizRender();
  }
}

async function wizFinish() {
  const btn = document.getElementById('wiz-btn-finish');
  if (btn) { btn.disabled = true; btn.textContent = 'Initialising…'; }

  try {
    const payload = {
      plans: wizState.plans,
      fileUpload: wizState.fileUpload,
      globalRules: wizState.globalRules,
    };
    const result = await HRMGMT.runSetupWizard(payload);

    // Hide wizard
    const overlay = document.getElementById('setup-wizard-overlay');
    if (overlay) overlay.style.display = 'none';

    toast(`Platform initialised — ${result.seeded.length} config document(s) created`, 'success');

    // Reload plans and rules into state
    const plans = await HRMGMT.listPlans();
    if (plans && plans.length) {
      state.plans = {};
      plans.forEach(p => { state.plans[p.id] = p; });
    }
    const rules = await HRMGMT.getGlobalRules();
    if (rules) state.globalRules = Object.assign({}, state.globalRules, rules);

  } catch(err) {
    toast(err.message || 'Setup failed — check console', 'error');
    if (btn) { btn.disabled = false; btn.textContent = '✓ Initialise Platform'; }
  }
}

// ── Wizard helpers ────────────────────────────────────────────

function wizPlanSet(planId, key, val) {
  if (!wizState.plans[planId]) wizState.plans[planId] = {};
  wizState.plans[planId][key] = val;
}

function wizPlanToggleMod(planId, modId, on) {
  if (!wizState.plans[planId]) return;
  let mods = wizState.plans[planId].modules || [];
  if (on && !mods.includes(modId)) mods = [...mods, modId];
  if (!on) mods = mods.filter(m => m !== modId);
  wizState.plans[planId].modules = mods;
}

function wizToggleExt(ext, on) {
  let exts = wizState.fileUpload.allowedExtensions || [];
  if (on && !exts.includes(ext)) exts = [...exts, ext];
  if (!on) exts = exts.filter(e => e !== ext);
  wizState.fileUpload.allowedExtensions = exts;
}

function wizSetModOverride(mod, key, val) {
  if (!wizState.fileUpload.moduleOverrides) wizState.fileUpload.moduleOverrides = {};
  if (!wizState.fileUpload.moduleOverrides[mod]) wizState.fileUpload.moduleOverrides[mod] = {};
  if (val === null) {
    delete wizState.fileUpload.moduleOverrides[mod][key];
  } else {
    wizState.fileUpload.moduleOverrides[mod][key] = val;
  }
}

// BOOT — everything that touches HRMGMT or the DOM runs here,
// after webpack has injected and executed main.js
// ══════════════════════════════════════════════════════════════

document.addEventListener('DOMContentLoaded', function() {

  // Close modal on backdrop click
  document.querySelectorAll('.modal-overlay').forEach(o =>
    o.addEventListener('click', e => { if(e.target===o) o.classList.remove('open'); })
  );

  // Wire up login form
  initLogin();

  // Sign-out button
  document.getElementById('btn-logout').addEventListener('click', async function() {
    await HRMGMT.signOut();
  });

  // Auth state — single source of truth for login vs portal view
  HRMGMT.onAuthStateChanged(async function(user) {
  if (!user) { showLoginView(); return; }

  showPortalView(user);

  try {
    // Load plans first — other data (tenants) depends on plan config
    const plans = await HRMGMT.listPlans();
    if (plans && plans.length) {
      state.plans = {};
      plans.forEach(p => { state.plans[p.id] = p; });
      // ensure enterprise/government modules default to all if empty from Firestore
      Object.values(state.plans).forEach(p => {
        if (!p.modules || p.modules.length === 0) {
          p.modules = MODULES.map(m => m.id);
        }
      });
    }

    // Run system health check — triggers setup wizard if config is missing
    await runHealthCheck();

    const tenants = await HRMGMT.listTenants();
    if (tenants && tenants.length) state.tenants = tenants;

    const rules = await HRMGMT.getGlobalRules();
    if (rules) state.globalRules = Object.assign({}, state.globalRules, rules);

    const log = await HRMGMT.getActivityLog({ limit: LOG_PAGE_SIZE });
    if (log && log.length) {
      state.activityLog = log.map(function(e) {
        return Object.assign({}, e, {
          time: e.createdAt
            ? new Date(e.createdAt).toLocaleString('en-AU', {
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit'
              })
            : '\u2014'
        });
      });
      _logLastId = state.activityLog[state.activityLog.length - 1]
        ? state.activityLog[state.activityLog.length - 1].id
        : null;
    }
  } catch (err) {
    console.warn('Failed to load live data:', err.message);
    toast('Running in offline mode \u2014 data may not be current', 'info');
  }

  renderDashboard();
  var nbTotal   = document.getElementById('nb-total');
  var nbTenants = document.getElementById('nb-tenants');
  if (nbTotal)   nbTotal.textContent   = state.tenants.length;
  if (nbTenants) nbTenants.textContent = state.tenants.length;
  var metricsFilter = document.getElementById('metrics-tenant-filter');
  if (metricsFilter) {
    metricsFilter.innerHTML =
      '<option value="">All Tenants</option>' +
      state.tenants.map(function(t) {
        return '<option value="' + t.id + '">' + t.name + '</option>';
      }).join('');
  }
  }); // end onAuthStateChanged

}); // end DOMContentLoaded


// ══════════════════════════════════════════════════════════════
// PLATFORM CONTROLS  (merged from Global Rules + Global Controls)
// ══════════════════════════════════════════════════════════════

// Internal state for platform controls
var gcState = {
  // Per-module global gate: true = enabled platform-wide, false = blocked everywhere
  moduleGates: {},
  // Per-plan quota overrides (mirrors plan data, editable before save)
  // Tenant user type access control
  allowedUserTypes: { government: true, 'government-dept': true, corporate: true, ngo: true, soe: true },
  // Platform defaults
  maxTenants: null,
  globalStorageGb: null,
  apiRateLimit: 120,
  maxUsersPerTenant: 50,
  dirty: false,
};

// Tab switcher
function pcTab(id, btn) {
  document.querySelectorAll('#page-platform-controls .tab-btn').forEach(function(b) { b.classList.remove('active'); });
  document.querySelectorAll('#page-platform-controls .tab-pane').forEach(function(p) { p.classList.remove('active'); });
  if (btn) btn.classList.add('active');
  var pane = document.getElementById('pc-' + id + '-pane');
  if (pane) pane.classList.add('active');
}

// Alias kept for any stray gcTab references
function gcTab(id, btn) { pcTab(id, btn); }

// Initialise gcState from current plans and globalRules
function initGcState() {
  // Module gates — default all enabled unless globalRules says otherwise
  MODULES.forEach(function(m) {
    gcState.moduleGates[m.id] =
      state.globalRules.moduleGates
        ? (state.globalRules.moduleGates[m.id] !== false)
        : true;
  });

  // Platform defaults from globalRules if stored
  var gr = state.globalRules;
  gcState.maxTenants        = gr.maxTenants        || null;
  gcState.globalStorageGb   = gr.globalStorageGb   || null;
  gcState.apiRateLimit      = gr.apiRateLimit       || 120;
  gcState.maxUsersPerTenant = gr.maxUsersPerTenant  || 50;
  gcState.allowedUserTypes  = gr.allowedUserTypes   || { government: true, 'government-dept': true, corporate: true, ngo: true, soe: true };
}

function renderPlatformControls() {
  initGcState();
  renderGcModuleGates();
  renderGcPlanModules();
  renderGcUserTypes();
  renderGlobalRules();          // populates security/compliance/domain panels
  renderPlanEditor();           // populates plan config cards
  renderDefaultPlanSelector();  // populates default plan dropdown

}

function renderDefaultPlanSelector() {
  var el = document.getElementById('global-limits');
  if (!el) return;
  var gr = state.globalRules;
  var opts = Object.values(state.plans).map(function(p) {
    return '<option value="' + p.id + '" ' + (gr.defaultPlan===p.id?'selected':'') + '>' + p.label + '</option>';
  }).join('');
  el.innerHTML = '<select class="form-input" onchange="state.globalRules.defaultPlan=this.value">' + opts + '</select>';
}


// ── Tenant user type access control ───────────────────────────
function renderGcUserTypes() {
  var el = document.getElementById('gc-user-types');
  if (!el) return;

  var USER_TYPES = [
    { id: 'government', label: 'Government Agency',    icon: '🏛️', desc: 'Public sector ministries, departments, and statutory authorities' },
    { id: 'government-dept', label: 'Government Department', icon: '🏛', desc: 'Government departments and sub-agencies' },
    { id: 'corporate',  label: 'Company',  icon: '🏢', desc: 'Private companies and corporate entities' },
    { id: 'ngo',        label: 'Non-Government Organisation',     icon: '🤝', desc: 'Non-governmental and not-for-profit organisations' },
    { id: 'soe',        label: 'State-Owned Enterprise', icon: '🏭', desc: 'Government-owned commercial entities' },
  ];

  el.innerHTML = USER_TYPES.map(function(t) {
    var enabled = gcState.allowedUserTypes[t.id] !== false;
    return [
      '<div style="display:flex;align-items:center;justify-content:space-between;padding:12px 14px;',
        'background:var(--bg3);border:1px solid var(--border);border-radius:8px;">',
        '<div style="display:flex;align-items:center;gap:12px;">',
          '<div style="width:34px;height:34px;border-radius:8px;background:' + (enabled ? 'var(--accent-dim)' : 'var(--bg2)') + ';',
            'border:1px solid ' + (enabled ? 'rgba(74,222,128,0.25)' : 'var(--border)') + ';',
            'display:flex;align-items:center;justify-content:center;font-size:1rem;flex-shrink:0;">' + t.icon + '</div>',
          '<div>',
            '<div style="font-size:0.75rem;font-weight:600;color:' + (enabled ? 'var(--text)' : 'var(--muted)') + ';">' + t.label + '</div>',
            '<div style="font-size:0.62rem;color:var(--muted);margin-top:2px;">' + t.desc + '</div>',
          '</div>',
        '</div>',
        '<div style="display:flex;align-items:center;gap:10px;flex-shrink:0;">',
          '<span style="font-size:0.62rem;font-family:\'DM Mono\',monospace;color:' + (enabled ? 'var(--accent)' : 'var(--muted)') + ';">',
            (enabled ? 'Allowed' : 'Blocked') + '</span>',
          '<label class="toggle"><input type="checkbox" ' + (enabled ? 'checked' : '') + ' onchange="gcToggleUserType(\'' + t.id + '\',this.checked)"/>',
            '<span class="toggle-slider"></span></label>',
        '</div>',
      '</div>',
    ].join('');
  }).join('');
}

function gcToggleUserType(typeId, enabled) {
  gcState.allowedUserTypes[typeId] = enabled;
  renderGcUserTypes();
  gcState.dirty = true;
}

// ── Module gate rows (left column of Access tab) ─────────────
function renderGcModuleGates() {
  var el = document.getElementById('gc-module-gates');
  if (!el) return;

  var notice = '<div style="display:flex;align-items:center;gap:10px;padding:10px 14px;background:rgba(96,165,250,0.08);border:1px solid rgba(96,165,250,0.2);border-radius:6px;margin-bottom:12px;font-size:0.72rem;color:var(--blue);">' +
    '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 3h12a1 1 0 011 1v7a1 1 0 01-1 1H5l-3 2V4a1 1 0 011-1z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>' +
    '<span>Messaging channel access controls have moved to <button onclick="showPage(&quot;messages&quot;,document.querySelector(&quot;.nav-btn[onclick*=messages]&quot;))" style="background:none;border:none;color:var(--accent);cursor:pointer;font-size:0.72rem;font-weight:600;padding:0;text-decoration:underline;">Messages</button> in the sidebar.</span>' +
    '</div>';

  el.innerHTML = notice + MODULES.map(function(m) {
    var enabled = gcState.moduleGates[m.id] !== false;
    var tenantCount = state.tenants.filter(function(t) { return t.modules && isModEnabled(t, m.id); }).length;
    return [
      '<div class="gc-module-gate-row' + (enabled ? '' : ' disabled') + '" id="gc-gate-row-' + m.id + '">',
        '<div class="gc-gate-left">',
          '<div class="gc-gate-icon" style="background:' + (enabled ? 'var(--accent-dim)' : 'var(--bg3)') + ';color:' + (enabled ? 'var(--accent)' : 'var(--muted)') + ';">' + m.icon + '</div>',
          '<div>',
            '<div class="gc-gate-label">' + m.name + '</div>',
            '<div class="gc-gate-desc">' + tenantCount + ' of ' + state.tenants.length + ' tenants using</div>',
          '</div>',
        '</div>',
        '<div class="gc-gate-right">',
          '<span class="gc-gate-badge badge ' + (enabled ? 'badge-active' : 'badge-inactive') + '">' + (enabled ? 'Enabled' : 'Disabled') + '</span>',
          '<label class="toggle"><input type="checkbox" ' + (enabled ? 'checked' : '') + ' onchange="gcToggleModuleGate(\'' + m.id + '\',this.checked)"/><span class="toggle-slider"></span></label>',
        '</div>',
      '</div>',
    ].join('');
  }).join('');
}

function gcToggleModuleGate(moduleId, enabled) {
  gcState.moduleGates[moduleId] = enabled;
  // Re-render just this row for performance
  var row = document.getElementById('gc-gate-row-' + moduleId);
  if (!row) return;
  row.className = 'gc-module-gate-row' + (enabled ? '' : ' disabled');
  var icon = row.querySelector('.gc-gate-icon');
  if (icon) {
    icon.style.background = enabled ? 'var(--accent-dim)' : 'var(--bg3)';
    icon.style.color = enabled ? 'var(--accent)' : 'var(--muted)';
  }
  var badge = row.querySelector('.gc-gate-badge');
  if (badge) {
    badge.className = 'gc-gate-badge badge ' + (enabled ? 'badge-active' : 'badge-inactive');
    badge.textContent = enabled ? 'Enabled' : 'Disabled';
  }
  gcState.dirty = true;
}

function gcSetAllModules(enabled) {
  MODULES.forEach(function(m) {
    gcState.moduleGates[m.id] = enabled;
  });
  renderGcModuleGates();
  gcState.dirty = true;
  toast((enabled ? 'All modules enabled' : 'All modules disabled') + ' — save to apply', 'info');
}

// ── Per-plan module access (right column of Access tab) ───────
function renderGcPlanModules() {
  // Access by Plan removed — now lives in Quotas & Plans > Plan Configuration
  return;
  var el = document.getElementById('gc-plan-modules');
  if (!el) return;

  var COLORS = { amber:'var(--amber)', purple:'var(--purple)', blue:'var(--blue)', teal:'var(--cyan)', green:'var(--accent)' };

  el.innerHTML = Object.values(state.plans).map(function(p) {
    var quota = { modules: p.modules || [] };
    var color = COLORS[p.color] || 'var(--blue)';
    var enabledCount = quota.modules.length;

    return [
      '<div class="card" style="border-color:' + color + '22;">',
        '<div style="padding:12px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;">',
          '<div style="display:flex;align-items:center;gap:8px;">',
            '<span class="plan-badge plan-' + p.id + '">' + (p.label || p.id) + '</span>',
            '<span style="font-size:0.65rem;color:var(--muted);font-family:\'DM Mono\',monospace;">' + enabledCount + '/' + HR_MODULES.length + ' HR modules</span>',
          '</div>',
          '<div style="display:flex;gap:6px;">',
            '<button class="btn btn-ghost btn-xs" onclick="gcPlanSetAll(\'' + p.id + '\',true)">All</button>',
            '<button class="btn btn-ghost btn-xs" onclick="gcPlanSetAll(\'' + p.id + '\',false)">None</button>',
          '</div>',
        '</div>',
        '<div style="padding:10px 14px;display:flex;flex-wrap:wrap;gap:6px;" id="gc-plan-mods-' + p.id + '">',
          HR_MODULES.map(function(m) {
            var on = quota.modules.includes(m.id);
            return '<button onclick="gcTogglePlanModule(\'' + p.id + '\',\'' + m.id + '\')" ' +
              'style="font-size:0.62rem;font-family:\'DM Mono\',monospace;padding:3px 9px;border-radius:4px;cursor:pointer;border:1px solid;transition:all 0.15s;background:' +
              (on ? color + '18' : 'var(--bg3)') + ';color:' + (on ? color : 'var(--muted)') + ';border-color:' + (on ? color + '44' : 'var(--border)') + ';" id="gc-pm-' + p.id + '-' + m.id + '">' +
              m.icon + ' ' + m.name + '</button>';
          }).join(''),
        '</div>',
      '</div>',
    ].join('');
  }).join('');
}

function gcTogglePlanModule(planId, moduleId) {
  var mods = (state.plans[planId] || {}).modules || [];
  var idx = mods.indexOf(moduleId);
  if (idx > -1) mods.splice(idx, 1);
  else mods.push(moduleId);
  // Re-render just this plan's module chips
  renderGcPlanModuleChips(planId);
  gcState.dirty = true;
}

function gcPlanSetAll(planId, enabled) {
  if (state.plans[planId]) state.plans[planId].modules = enabled ? MODULES.map(function(m) { return m.id; }) : [];
  renderGcPlanModuleChips(planId);
  gcState.dirty = true;
}

function renderGcPlanModuleChips(planId) {
  var p = state.plans[planId];
  if (!p) return;
  var quota = { modules: (state.plans[planId] || {}).modules || [] };
  var COLORS = { amber:'var(--amber)', purple:'var(--purple)', blue:'var(--blue)', teal:'var(--cyan)', green:'var(--accent)' };
  var color = COLORS[p.color] || 'var(--blue)';

  var container = document.getElementById('gc-plan-mods-' + planId);
  if (!container) return;
  container.innerHTML = MODULES.map(function(m) {
    var on = quota.modules.includes(m.id);
    return '<button onclick="gcTogglePlanModule(\'' + planId + '\',\'' + m.id + '\')" ' +
      'style="font-size:0.62rem;font-family:\'DM Mono\',monospace;padding:3px 9px;border-radius:4px;cursor:pointer;border:1px solid;transition:all 0.15s;background:' +
      (on ? color + '18' : 'var(--bg3)') + ';color:' + (on ? color : 'var(--muted)') + ';border-color:' + (on ? color + '44' : 'var(--border)') + ';" id="gc-pm-' + planId + '-' + m.id + '">' +
      m.icon + ' ' + m.name + '</button>';
  }).join('');

  // Update count badge in card header
  var card = container.closest('.card');
  if (card) {
    var countBadge = card.querySelector('.gc-plan-count');
    if (countBadge) countBadge.textContent = quota.modules.length + '/' + HR_MODULES.length + ' HR modules';
  }
}

// ── Quota cards (Quota Controls tab) ─────────────────────────
// ── Save all platform controls changes ───────────────────────
async function saveGlobalControls() { return savePlatformControls(); } // alias
async function savePlatformControls() {
  try {
    var promises = [];

    // 1. Save module gates + security rules into globalRules
    var rulesUpdate = {
      moduleGates:        gcState.moduleGates,
      requireMfa:         state.globalRules.requireMfa,
      allowGuestAccess:   state.globalRules.allowGuestAccess,
      allowApiAccess:     state.globalRules.allowApiAccess,
      sandboxMode:        state.globalRules.sandboxMode,
      autoProvision:      state.globalRules.autoProvision,
      defaultPlan:        state.globalRules.defaultPlan,
      blockedEmails:      state.globalRules.blockedEmails  || [],
      blockedDomains:     state.globalRules.blockedDomains || [],
      blockedIps:         state.globalRules.blockedIps     || [],
    };


    promises.push(HRMGMT.saveGlobalRules(rulesUpdate));

    await Promise.all(promises);

    // Sync local state
    state.globalRules = Object.assign({}, state.globalRules, rulesUpdate);

    gcState.dirty = false;
    logActivity('rule', 'Platform controls saved — rules, module gates and quotas synced', 'var(--cyan)');
    toast('Platform controls saved', 'success');
  } catch (err) {
    toast(err.message || 'Failed to save global controls', 'error');
  }
}

// ══════════════════════════════════════════════════════════════
// EXPOSE TO WINDOW
// Webpack bundles this file as a scoped module. Every function
// called from an inline HTML onclick/onchange attribute must be
// explicitly assigned to window, otherwise the browser can't
// find it when the handler fires.
// ══════════════════════════════════════════════════════════════
window.showPage              = showPage;
// window.modTab removed — replaced by modSetTab
window.refreshDashboard      = refreshDashboard;
window.filterTenants         = filterTenants;
window.showTenantsListView   = showTenantsListView;
window.openTenantDetail      = openTenantDetail;
window.resendAdminInvite     = resendAdminInvite;
window.suspendTenant         = suspendTenant;
window.reactivateTenant      = reactivateTenant;
window.accessTenantPortal    = accessTenantPortal;
window.openTenantManage      = openTenantManage;
window.tmToggle              = tmToggle;
window.tmSaveDomain          = tmSaveDomain;
window.tmDoReset             = tmDoReset;
window.tmCheckPurge          = tmCheckPurge;
window.tmDoPurge             = tmDoPurge;
window.tmCheckDelete         = tmCheckDelete;
window.tmDoDelete            = tmDoDelete;
window.saveTenantConfig      = saveTenantConfig;
window.openEditTenantPlan    = openEditTenantPlan;
window.toggleTenantModule    = toggleTenantModule;
window.saveAccessRules       = saveAccessRules;
window.setAllModules         = setAllModules;
window.setPlanModules        = setPlanModules;
window.renderMetricsPage     = renderMetricsPage;
window.renderModulesPage     = renderModulesPage;
window.applyBulkPreset       = applyBulkPreset;
window.renderPlatformControls= renderPlatformControls;
window.pcTab                 = pcTab;
window.billingTab            = billingTab;
window.initBillingPage       = initBillingPage;
window.renderInvoicesPane    = renderInvoicesPane;
window.loadInvoices          = loadInvoices;
window.runSingleInvoice      = runSingleInvoice;
window.openInvoiceDetail     = openInvoiceDetail;
window.saveInvoiceUpdate     = saveInvoiceUpdate;
window.renderInvoiceHistory  = renderInvoiceHistory;
window.loadHistory           = loadHistory;
window.savePlatformControls  = savePlatformControls;
window.toggleRow             = toggleRow;
window.saveGlobalRules       = saveGlobalRules;
window.renderDefaultPlanSelector = renderDefaultPlanSelector;
window.renderPlanEditor      = renderPlanEditor;
window.savePlanConfig        = savePlanConfig;
window.renderActivityLog     = renderActivityLog;
window.loadMoreActivityLog   = loadMoreActivityLog;
window.openAddTenantModal    = openAddTenantModal;
window.renderBlocklist       = renderBlocklist;
window.addBlockEntry         = addBlockEntry;
window.removeBlockEntry      = removeBlockEntry;
window.blSetTab              = blSetTab;
window.blSetSearch           = blSetSearch;
window.blShowBulkPaste       = blShowBulkPaste;
window.blImportBulk          = blImportBulk;
window.ntClearClash          = ntClearClash;
window.ntNext                = ntNext;
window.ntBack                = ntBack;
window.ntGoToPage            = ntGoToPage;
window.onOrgTypeChange       = onOrgTypeChange;
window.onAdminEmailInput     = onAdminEmailInput;
window.onStatusChange        = onStatusChange;
window.closeModal            = closeModal;
window.submitAddTenant       = submitAddTenant;
window.renderUserMgmt        = renderUserMgmt;
window.submitSuperAdminClaim = submitSuperAdminClaim;
window.submitTenantUserClaim = submitTenantUserClaim;
window.revokeTenantUserClaim = revokeTenantUserClaim;
window.toast                 = toast;
window.gcTab                 = gcTab;
window.saveGlobalControls    = saveGlobalControls;
window.gcSetAllModules       = gcSetAllModules;
window.gcToggleModuleGate    = gcToggleModuleGate;
window.gcPlanSetAll          = gcPlanSetAll;
window.saveGlobalControls    = saveGlobalControls;
// ══════════════════════════════════════════════════════════════
// TENANT DETAIL TABS
// ══════════════════════════════════════════════════════════════

function tdTab(tabId, tenantId, btn) {
  // Hide all panes for this tenant
  document.querySelectorAll(`[id^="td-tab-"][id$="-${tenantId}"]`).forEach(function(p) {
    p.style.display = 'none';
  });
  // Deactivate all tab buttons in this detail view
  if (btn) {
    btn.closest('.tab-bar').querySelectorAll('.tab-btn').forEach(function(b) {
      b.classList.remove('active');
    });
    btn.classList.add('active');
  }
  var pane = document.getElementById('td-tab-' + tabId + '-' + tenantId);
  if (pane) pane.style.display = '';

  // Lazy-load content
  if (tabId === 'data')       loadTenantData(tenantId);
  if (tabId === 'roles')      loadTenantRoles(tenantId);
  if (tabId === 'onboarding') loadTenantOnboarding(tenantId);
  if (tabId === 'billing')    loadTenantBilling(tenantId);
  if (tabId === 'audit')      loadTenantAuditLog(tenantId);
  if (tabId === 'usage')      loadTenantUsage(tenantId);
  if (tabId === 'users')      loadTenantUsersTab(tenantId);
  if (tabId === 'periods')    loadTenantPeriodYears(tenantId);
}

// ── Module Data Insights ─────────────────────────────────────
async function loadTenantData(tenantId) {
  var el = document.getElementById('td-data-' + tenantId);
  if (!el) return;
  el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:10px 0;">Loading module data…</div>';

  var t = state.tenants.find(function(x) { return x.id === tenantId; });
  if (!t) return;

  try {
    var metrics = await HRMGMT.getTenantMetrics(tenantId);
    var counts = metrics.hrCounts || {};

    // Module definitions with their db-hr collection mappings and metadata
    var MODULE_DATA = [
      {
        id: 'employees',   name: 'Employees',   icon: '👥',
        color: 'var(--accent)',
        collections: [
          { key: 'employees', label: 'Employee records' },
        ],
        detail: function(c) {
          return 'Limit: ' + (t.employeeLimit ? t.employeeLimit : '∞') + ' · Used: ' + (c.employees || 0);
        },
      },
      {
        id: 'leave',       name: 'Leave',       icon: '📅',
        color: 'var(--blue)',
        collections: [
          { key: 'leaveRequests', label: 'Leave requests' },
        ],
        detail: null,
      },
      {
        id: 'training',    name: 'Training',    icon: '🎓',
        color: 'var(--purple)',
        collections: [
          { key: 'annualTrainingPlans',  label: 'Annual training plans' },
          { key: 'trainingNominations',  label: 'Training nominations' },
        ],
        detail: null,
      },
      {
        id: 'performance', name: 'Performance', icon: '⭐',
        color: 'var(--amber)',
        collections: [
          { key: 'appraisals', label: 'Appraisals' },
        ],
        detail: null,
      },
      {
        id: 'discipline',  name: 'Discipline',  icon: '⚖️',
        color: 'var(--red)',
        collections: [
          { key: 'disciplineCases', label: 'Discipline cases' },
        ],
        detail: null,
      },
      {
        id: 'documents',   name: 'Documents',   icon: '📄',
        color: 'var(--cyan)',
        collections: [
          { key: 'documents', label: 'Documents' },
        ],
        detail: null,
      },
      {
        id: 'providers',   name: 'Providers',   icon: '🏫',
        color: 'var(--purple)',
        collections: [
          { key: 'providers', label: 'Training providers' },
        ],
        detail: null,
      },
      {
        id: 'tasks',       name: 'Tasks',       icon: '✅',
        color: 'var(--accent)',
        collections: [
          { key: 'tasks', label: 'Tasks' },
        ],
        detail: null,
      },
      {
        id: 'messaging',   name: 'Messaging',   icon: '💬',
        color: 'var(--blue)',
        collections: [
          { key: 'messages', label: 'Messages sent' },
        ],
        detail: null,
      },
    ];

    // Only show modules enabled for this tenant
    var active = MODULE_DATA.filter(function(m) { return isModEnabled(t, m.id); });
    var inactive = MODULE_DATA.filter(function(m) { return !isModEnabled(t, m.id); });

    // Total records across all collections
    var totalRecords = Object.values(counts).reduce(function(s, v) { return s + v; }, 0);

    // Summary bar
    var summaryHtml = [
      '<div class="grid-3" style="margin-bottom:20px;">',
        '<div class="card card-pad">',
          '<div class="metric-label">Total Records</div>',
          '<div style="font-family:\'Syne\',sans-serif;font-size:1.8rem;font-weight:700;color:var(--accent);">' + totalRecords.toLocaleString() + '</div>',
          '<div class="metric-sub">across all db-hr collections</div>',
        '</div>',
        '<div class="card card-pad">',
          '<div class="metric-label">Employees</div>',
          '<div style="font-family:\'Syne\',sans-serif;font-size:1.8rem;font-weight:700;color:var(--accent);">' + (counts.employees || 0) + '</div>',
          t.employeeLimit
            ? '<div class="usage-bar-wrap"><div class="usage-bar-label"><span>of ' + t.employeeLimit + ' limit</span><span>' + Math.min(100, Math.round((counts.employees||0)/t.employeeLimit*100)) + '%</span></div>'
              + '<div class="usage-bar"><div class="usage-bar-fill" style="width:' + Math.min(100, Math.round((counts.employees||0)/t.employeeLimit*100)) + '%;background:var(--accent);"></div></div></div>'
            : '<div class="metric-sub">No limit</div>',
        '</div>',
        '<div class="card card-pad">',
          '<div class="metric-label">Modules Active</div>',
          '<div style="font-family:\'Syne\',sans-serif;font-size:1.8rem;font-weight:700;color:var(--purple);">' + active.length + ' <span style="font-size:1rem;font-weight:400;color:var(--muted);">/ ' + MODULE_DATA.length + '</span></div>',
          '<div class="metric-sub">' + inactive.length + ' module' + (inactive.length!==1?'s':'') + ' disabled</div>',
        '</div>',
      '</div>',
    ].join('');

    // Active module cards
    var activeCardsHtml = active.length === 0
      ? '<div style="color:var(--muted);font-size:0.75rem;padding:20px 0;text-align:center;">No modules enabled for this tenant.</div>'
      : active.map(function(m) {
          var total = m.collections.reduce(function(s, c) { return s + (counts[c.key] || 0); }, 0);
          var detailStr = m.detail ? m.detail(counts) : null;

          var colRows = m.collections.map(function(c) {
            var val = counts[c.key] || 0;
            return [
              '<div style="display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px solid rgba(255,255,255,0.04);">',
                '<span style="font-size:0.72rem;color:var(--muted);">' + c.label + '</span>',
                '<span style="font-family:\'DM Mono\',monospace;font-size:0.76rem;color:var(--text);">' + val.toLocaleString() + '</span>',
              '</div>',
            ].join('');
          }).join('');

          return [
            '<div class="card card-pad" style="border-left:3px solid ' + m.color + '22;">',
              '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">',
                '<div style="display:flex;align-items:center;gap:8px;">',
                  '<div style="width:28px;height:28px;border-radius:6px;background:' + m.color + '22;display:flex;align-items:center;justify-content:center;font-size:0.85rem;">' + m.icon + '</div>',
                  '<div style="font-family:\'Syne\',sans-serif;font-weight:700;font-size:0.82rem;">' + m.name + '</div>',
                '</div>',
                '<div style="font-family:\'Syne\',sans-serif;font-size:1.4rem;font-weight:700;color:' + m.color + ';">' + total.toLocaleString() + '</div>',
              '</div>',
              colRows,
              detailStr ? '<div style="font-size:0.65rem;color:var(--muted);margin-top:8px;font-family:\'DM Mono\',monospace;">' + detailStr + '</div>' : '',
            '</div>',
          ].join('');
        }).join('');

    // Disabled modules (compact)
    var inactiveHtml = inactive.length === 0 ? '' : [
      '<div style="margin-top:20px;">',
        '<div class="sec-hdr" style="font-size:0.72rem;color:var(--muted);">Disabled Modules</div>',
        '<div style="display:flex;flex-wrap:wrap;gap:8px;">',
          inactive.map(function(m) {
            return '<div style="display:flex;align-items:center;gap:6px;padding:5px 12px;background:var(--bg3);border:1px solid var(--border);border-radius:6px;opacity:0.5;">'
              + '<span style="font-size:0.8rem;">' + m.icon + '</span>'
              + '<span style="font-size:0.72rem;color:var(--muted);">' + m.name + '</span>'
              + '</div>';
          }).join(''),
        '</div>',
      '</div>',
    ].join('');

    el.innerHTML = summaryHtml
      + '<div class="sec-hdr" style="margin-bottom:12px;">Active Modules</div>'
      + '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:12px;">'
      + activeCardsHtml
      + '</div>'
      + inactiveHtml;

  } catch(err) {
    el.innerHTML = '<div class="card card-pad"><div style="color:var(--muted);font-size:0.75rem;">Module data unavailable — db-hr may not have records for this tenant yet.</div></div>';
  }
}

// ── Roles & Custom Role Management ───────────────────────────
async function loadTenantRoles(tenantId) {
  var el = document.getElementById('td-roles-' + tenantId);
  if (!el) return;
  el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:10px 0;">Loading roles…</div>';

  try {
    var roles = await HRMGMT.getCustomRoles(tenantId);
    el.innerHTML = renderRolesPanel(tenantId, roles);
  } catch(err) {
    el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;">Unable to load roles.</div>';
  }
}

function renderRolesPanel(tenantId, customRoles) {
  var PERM_COLORS = { none:'var(--muted)', read:'var(--blue)', manage:'var(--accent)' };
  var PERM_LABELS = { none:'None', read:'Read', manage:'Manage' };

  // Built-in roles summary
  var builtInHtml = Object.entries(ROLE_CAPABILITIES).map(function(entry) {
    var key = entry[0]; var rc = entry[1];
    return [
      '<div style="display:flex;align-items:flex-start;gap:12px;padding:12px 0;border-bottom:1px solid rgba(255,255,255,0.04);">',
        '<div style="width:32px;height:32px;border-radius:8px;background:' + rc.color + '18;',
          'display:flex;align-items:center;justify-content:center;font-size:0.9rem;flex-shrink:0;">' + rc.icon + '</div>',
        '<div style="flex:1;min-width:0;">',
          '<div style="display:flex;align-items:center;gap:8px;margin-bottom:3px;">',
            '<span style="font-size:0.76rem;font-weight:600;color:var(--text);">' + rc.label + '</span>',
            '<span style="font-size:0.6rem;font-family:DM Mono,monospace;padding:2px 7px;',
              'border-radius:4px;background:' + rc.color + '18;color:' + rc.color + ';">' + key + '</span>',
          '</div>',
          '<div style="font-size:0.7rem;color:var(--muted);margin-bottom:4px;">' + rc.description + '</div>',
          '<div style="display:flex;gap:16px;">',
            '<span style="font-size:0.63rem;color:var(--muted);">Scope: <span style="color:var(--text);">' + rc.viewScope + '</span></span>',
            '<span style="font-size:0.63rem;color:var(--muted);">Approve: <span style="color:' + (rc.canApprove===true?'var(--accent)':'var(--muted)') + ';">' + (rc.canApprove===true?'Yes':rc.canApprove===false?'No':rc.canApprove) + '</span></span>',
            '<span style="font-size:0.63rem;color:var(--muted);">Export: <span style="color:' + (rc.canExport===true?'var(--accent)':'var(--muted)') + ';">' + (rc.canExport===true?'Yes':rc.canExport===false?'No':rc.canExport) + '</span></span>',
          '</div>',
        '</div>',
      '</div>',
    ].join('');
  }).join('');

  // Custom roles list
  var customHtml = customRoles.length === 0
    ? '<div style="padding:20px 0;text-align:center;color:var(--muted);font-size:0.75rem;">No custom roles defined. Create one below.</div>'
    : customRoles.map(function(r) {
        var modPerms = Object.entries(r.modulePermissions || {});
        var permChips = modPerms.slice(0,6).map(function(entry) {
          var modId = entry[0]; var perm = entry[1];
          var modDef = MODULES.find(function(m) { return m.id === modId; });
          var color = PERM_COLORS[perm] || 'var(--muted)';
          return '<span style="font-size:0.6rem;padding:2px 7px;border-radius:4px;background:' + color + '18;color:' + color + ';border:1px solid ' + color + '33;">'
            + (modDef ? modDef.icon + ' ' : '') + modId + ': ' + (PERM_LABELS[perm]||perm)
            + '</span>';
        }).join('');
        var extra = modPerms.length > 6 ? '<span style="font-size:0.6rem;color:var(--muted);">+' + (modPerms.length-6) + ' more</span>' : '';
        var memberCount = (r.members||[]).length;
        return [
          '<div style="background:var(--bg3);border:1px solid var(--border);border-radius:8px;padding:14px;margin-bottom:8px;">',
            '<div style="display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px;">',
              '<div style="display:flex;align-items:center;gap:8px;">',
                '<div style="width:10px;height:10px;border-radius:50%;background:' + (r.color||'#6366f1') + ';flex-shrink:0;"></div>',
                '<div>',
                  '<div style="font-size:0.76rem;font-weight:600;color:var(--text);">' + r.label + '</div>',
                  '<div style="font-size:0.62rem;font-family:DM Mono,monospace;color:var(--muted);">' + r.id + ' · ' + memberCount + ' member' + (memberCount!==1?'s':'') + '</div>',
                '</div>',
              '</div>',
              '<div style="display:flex;gap:5px;">',
                '<button class="btn btn-ghost btn-xs" onclick="openEditCustomRole(\'' + tenantId + '\',\'' + r.id + '\')">Edit</button>',
                '<button class="btn btn-xs" style="background:transparent;border:1px solid rgba(248,113,113,0.2);color:var(--red);" onclick="deleteCustomRole(\'' + tenantId + '\',\'' + r.id + '\')">Delete</button>',
              '</div>',
            '</div>',
            r.description ? '<div style="font-size:0.7rem;color:var(--muted);margin-bottom:8px;">' + r.description + '</div>' : '',
            '<div style="display:flex;flex-wrap:wrap;gap:5px;">' + permChips + extra + '</div>',
          '</div>',
        ].join('');
      }).join('');

  return [
    // Built-in roles card
    '<div class="card card-pad" style="margin-bottom:14px;">',
      '<div style="font-family:Syne,sans-serif;font-weight:700;font-size:0.88rem;margin-bottom:14px;">Built-in Roles</div>',
      builtInHtml,
    '</div>',
    // Custom roles card
    '<div class="card card-pad">',
      '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;">',
        '<div style="font-family:Syne,sans-serif;font-weight:700;font-size:0.88rem;">Custom Roles</div>',
        '<button class="btn btn-ghost btn-xs" onclick="openCreateCustomRole(\'' + tenantId + '\')">+ New Role</button>',
      '</div>',
      customHtml,
    '</div>',
  ].join('');
}

function openCreateCustomRole(tenantId) {
  _openCustomRoleModal(tenantId, null, null);
}

function openEditCustomRole(tenantId, roleId) {
  HRMGMT.getCustomRoles(tenantId).then(function(roles) {
    var role = roles.find(function(r) { return r.id === roleId; });
    if (role) _openCustomRoleModal(tenantId, roleId, role);
  }).catch(function() { toast('Failed to load role', 'error'); });
}

function _openCustomRoleModal(tenantId, roleId, existing) {
  var isEdit = !!roleId;
  var old = existing || {};
  var overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:#0a0c0f;display:flex;align-items:center;justify-content:center;z-index:9999;overflow-y:auto;padding:20px;';

  var PERM_OPTIONS = ['none','read','manage'];
  var modRows = MODULES.map(function(m) {
    var cur = (old.modulePermissions||{})[m.id] || 'none';
    var opts = PERM_OPTIONS.map(function(p) {
      return '<option value="' + p + '"' + (cur===p?' selected':'') + '>' + p.charAt(0).toUpperCase()+p.slice(1) + '</option>';
    }).join('');
    return [
      '<div style="display:flex;align-items:center;justify-content:space-between;',
        'padding:7px 0;border-bottom:1px solid rgba(255,255,255,0.04);">',
        '<span style="font-size:0.73rem;">' + m.icon + ' ' + m.name + '</span>',
        '<select class="form-input cr-perm-sel" data-mod="' + m.id + '" ',
          'style="width:110px;padding:3px 8px;font-size:0.7rem;">' + opts + '</select>',
      '</div>',
    ].join('');
  }).join('');

  overlay.innerHTML = [
    '<div style="background:var(--bg2);border:1px solid var(--border);border-radius:12px;',
      'padding:28px;width:520px;max-width:100%;">',
      '<div style="font-family:Syne,sans-serif;font-size:1rem;font-weight:700;margin-bottom:18px;">',
        (isEdit ? 'Edit Custom Role' : 'New Custom Role'),
      '</div>',
      '<div class="form-row" style="margin-bottom:12px;">',
        '<div class="form-group">',
          '<label class="form-label">Role Name</label>',
          '<input class="form-input" id="cr-label" placeholder="e.g. HR Officers" value="' + (old.label||'') + '"/>',
        '</div>',
        '<div class="form-group">',
          '<label class="form-label">Colour</label>',
          '<input type="color" id="cr-color" value="' + (old.color||'#6366f1') + '" style="height:36px;width:100%;border-radius:6px;border:1px solid var(--border);background:var(--bg3);cursor:pointer;"/>',
        '</div>',
      '</div>',
      '<div class="form-group" style="margin-bottom:12px;">',
        '<label class="form-label">Description</label>',
        '<input class="form-input" id="cr-desc" placeholder="Brief description of this role" value="' + (old.description||'') + '"/>',
      '</div>',
      '<div class="form-group" style="margin-bottom:16px;">',
        '<label class="form-label">Module Permissions</label>',
        '<div style="max-height:280px;overflow-y:auto;background:var(--bg3);border:1px solid var(--border);border-radius:8px;padding:4px 14px;">' + modRows + '</div>',
      '</div>',
      '<div style="display:flex;gap:8px;">',
        '<button class="btn btn-primary" style="flex:1;" id="cr-submit">' + (isEdit?'Save Changes':'Create Role') + '</button>',
        '<button class="btn btn-ghost" id="cr-cancel">Cancel</button>',
      '</div>',
    '</div>',
  ].join('');

  document.body.appendChild(overlay);
  overlay.addEventListener('click', function(e) { if (e.target===overlay) overlay.remove(); });
  document.getElementById('cr-cancel').onclick = function() { overlay.remove(); };

  document.getElementById('cr-submit').onclick = async function() {
    var label = document.getElementById('cr-label').value.trim();
    var desc  = document.getElementById('cr-desc').value.trim();
    var color = document.getElementById('cr-color').value;
    if (!label) { toast('Role name is required', 'error'); return; }

    var modulePermissions = {};
    overlay.querySelectorAll('.cr-perm-sel').forEach(function(sel) {
      if (sel.value !== 'none') modulePermissions[sel.dataset.mod] = sel.value;
    });

    var btn = document.getElementById('cr-submit');
    btn.disabled = true; btn.textContent = isEdit ? 'Saving…' : 'Creating…';
    try {
      if (isEdit) {
        await HRMGMT.updateCustomRole(tenantId, roleId, {label, description:desc, color, modulePermissions});
        toast('Role updated', 'success');
      } else {
        await HRMGMT.createCustomRole(tenantId, {label, description:desc, color, modulePermissions});
        toast('Role created', 'success');
      }
      overlay.remove();
      loadTenantRoles(tenantId);
    } catch(err) {
      toast(err.message || 'Failed to save role', 'error');
      btn.disabled = false;
      btn.textContent = isEdit ? 'Save Changes' : 'Create Role';
    }
  };
}

async function deleteCustomRole(tenantId, roleId) {
  if (!confirm('Delete custom role "' + roleId + '"? Users assigned this role will lose module access until reassigned.')) return;
  try {
    await HRMGMT.deleteCustomRole(tenantId, roleId);
    toast('Role deleted', 'success');
    loadTenantRoles(tenantId);
  } catch(err) {
    toast(err.message || 'Failed to delete role', 'error');
  }
}

// ── Onboarding ────────────────────────────────────────────────
async function loadTenantOnboarding(tenantId) {
  var el = document.getElementById('td-onboarding-' + tenantId);
  if (!el) return;
  try {
    var ob = await HRMGMT.getTenantOnboarding(tenantId);
    var steps = [
      { key:'adminLoggedIn',       label:'Admin has signed in',            icon:'🔑' },
      { key:'orgConfigured',       label:'Organisation structure set up',  icon:'🏢' },
      { key:'firstEmployeeAdded',  label:'First employee record added',    icon:'👤' },
      { key:'modulesConfigured',   label:'Modules reviewed and configured',icon:'⚙️' },
      { key:'usersInvited',        label:'Team members invited',           icon:'📨' },
      { key:'firstLeaveRequest',   label:'First leave request submitted',  icon:'📅' },
    ];
    var done = steps.filter(function(s) { return ob[s.key]; }).length;
    var pct  = Math.round((done / steps.length) * 100);
    var allDone = done === steps.length;

    el.innerHTML = [
      '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;">',
        '<div>',
          '<div style="font-family:\'Syne\',sans-serif;font-weight:700;font-size:0.88rem;">Onboarding Progress</div>',
          '<div style="font-size:0.68rem;color:var(--muted);margin-top:2px;">' + done + ' of ' + steps.length + ' steps complete</div>',
        '</div>',
        '<div style="font-family:\'Syne\',sans-serif;font-size:1.8rem;font-weight:700;color:' + (allDone ? 'var(--accent)' : 'var(--amber)') + ';">' + pct + '%</div>',
      '</div>',
      '<div class="usage-bar" style="margin-bottom:20px;height:6px;"><div class="usage-bar-fill" style="width:' + pct + '%;background:' + (allDone ? 'var(--accent)' : 'var(--amber)') + ';"></div></div>',
      steps.map(function(s) {
        var done = ob[s.key];
        return [
          '<div style="display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid rgba(255,255,255,0.04);">',
            '<div style="display:flex;align-items:center;gap:10px;">',
              '<span style="font-size:1rem;">' + s.icon + '</span>',
              '<span style="font-size:0.76rem;color:' + (done ? 'var(--text)' : 'var(--muted)') + ';">' + s.label + '</span>',
            '</div>',
            '<div style="display:flex;align-items:center;gap:8px;">',
              (done
                ? '<span style="color:var(--accent);font-size:0.8rem;">✓</span>'
                : '<span style="color:var(--muted);font-size:0.8rem;">○</span>'),
              '<label class="toggle"><input type="checkbox" ' + (done?'checked':'') + ' onchange="toggleOnboardingStep(\'' + tenantId + '\',\'' + s.key + '\',this.checked)"/><span class="toggle-slider"></span></label>',
            '</div>',
          '</div>',
        ].join('');
      }).join(''),
      ob.completedAt
        ? '<div style="margin-top:12px;font-size:0.68rem;color:var(--accent);font-family:\'DM Mono\',monospace;">✓ Completed ' + new Date(ob.completedAt).toLocaleDateString() + '</div>'
        : '',
    ].join('');
  } catch(err) {
    el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;">Onboarding data unavailable — tenant app may not have connected yet.</div>';
  }
}

async function toggleOnboardingStep(tenantId, key, val) {
  try {
    var fields = {};
    fields[key] = val;
    await HRMGMT.saveTenantOnboarding(tenantId, fields);
    loadTenantOnboarding(tenantId);
    toast('Onboarding step updated', 'success');
  } catch(err) {
    toast(err.message || 'Failed to update onboarding', 'error');
  }
}

// ── Billing ───────────────────────────────────────────────────
async function loadTenantBilling(tenantId) {
  var el = document.getElementById('td-billing-' + tenantId);
  if (!el) return;
  try {
    var b = await HRMGMT.getBillingInfo(tenantId);
    var statusColors = { none:'var(--muted)', paid:'var(--accent)', pending:'var(--amber)', overdue:'var(--red)' };
    var color = statusColors[b.invoiceStatus] || 'var(--muted)';

    el.innerHTML = [
      '<div class="form-row" style="margin-bottom:16px;">',
        '<div class="form-group">',
          '<label class="form-label">Contract Start Date</label>',
          '<input class="form-input" type="date" id="bill-start-' + tenantId + '" value="' + (b.contractStartDate||'') + '"/>',
        '</div>',
        '<div class="form-group">',
          '<label class="form-label">Contract End Date</label>',
          '<input class="form-input" type="date" id="bill-end-' + tenantId + '" value="' + (b.contractEndDate||'') + '"/>',
        '</div>',
      '</div>',
      '<div class="form-row" style="margin-bottom:16px;">',
        '<div class="form-group">',
          '<label class="form-label">Invoice Status</label>',
          '<select class="form-input" id="bill-status-' + tenantId + '">',
            ['none','paid','pending','overdue'].map(function(s) {
              return '<option value="' + s + '" ' + (b.invoiceStatus===s?'selected':'') + '>' + s.charAt(0).toUpperCase()+s.slice(1) + '</option>';
            }).join(''),
          '</select>',
        '</div>',
        '<div class="form-group">',
          '<label class="form-label">Currency</label>',
          '<select class="form-input" id="bill-currency-' + tenantId + '">',
            ['PGK','USD','AUD'].map(function(c) {
              return '<option value="' + c + '" ' + (b.currency===c?'selected':'') + '>' + c + '</option>';
            }).join(''),
          '</select>',
        '</div>',
      '</div>',
      '<div class="form-row" style="margin-bottom:16px;">',
        '<div class="form-group">',
          '<label class="form-label">Last Invoice Date</label>',
          '<input class="form-input" type="date" id="bill-inv-date-' + tenantId + '" value="' + (b.lastInvoiceDate||'') + '"/>',
        '</div>',
        '<div class="form-group">',
          '<label class="form-label">Last Invoice Amount</label>',
          '<input class="form-input" type="number" id="bill-inv-amt-' + tenantId + '" placeholder="0.00" value="' + (b.lastInvoiceAmount||'') + '"/>',
        '</div>',
      '</div>',
      '<div class="form-row" style="margin-bottom:16px;">',
        '<div class="form-group">',
          '<label class="form-label">Billing Email</label>',
          '<input class="form-input" id="bill-email-' + tenantId + '" placeholder="finance@org.gov.pg" value="' + (b.billingEmail||'') + '"/>',
        '</div>',
        '<div class="form-group">',
          '<label class="form-label">PO Number</label>',
          '<input class="form-input" id="bill-po-' + tenantId + '" placeholder="Optional" value="' + (b.poNumber||'') + '"/>',
        '</div>',
      '</div>',
      '<div class="form-group" style="margin-bottom:16px;">',
        '<label class="form-label">Notes</label>',
        '<textarea class="form-input" id="bill-notes-' + tenantId + '" rows="2">' + (b.notes||'') + '</textarea>',
      '</div>',
      b.contractEndDate && new Date(b.contractEndDate) < new Date(Date.now() + 30*24*60*60*1000)
        ? '<div style="background:var(--amber-dim);border:1px solid rgba(251,191,36,0.25);border-radius:6px;padding:10px 14px;margin-bottom:14px;font-size:0.72rem;color:var(--amber);">⚠ Contract ' + (new Date(b.contractEndDate) < new Date() ? 'has expired' : 'expires soon') + ' — ' + b.contractEndDate + '</div>'
        : '',
      '<div style="display:flex;gap:8px;align-items:center;">',
        '<button class="btn btn-primary btn-sm" onclick="saveBillingInfo(\'' + tenantId + '\')">Save Billing Info</button>',
        '<span style="font-size:0.68rem;font-family:\'DM Mono\',monospace;color:' + color + ';">● ' + (b.invoiceStatus||'none') + '</span>',
      '</div>',
    ].join('');
  } catch(err) {
    el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;">Unable to load billing info.</div>';
  }
}

async function saveBillingInfo(tenantId) {
  var fields = {
    contractStartDate:  document.getElementById('bill-start-' + tenantId)?.value || null,
    contractEndDate:    document.getElementById('bill-end-' + tenantId)?.value || null,
    invoiceStatus:      document.getElementById('bill-status-' + tenantId)?.value || 'none',
    currency:           document.getElementById('bill-currency-' + tenantId)?.value || 'PGK',
    lastInvoiceDate:    document.getElementById('bill-inv-date-' + tenantId)?.value || null,
    lastInvoiceAmount:  parseFloat(document.getElementById('bill-inv-amt-' + tenantId)?.value) || null,
    billingEmail:       document.getElementById('bill-email-' + tenantId)?.value || '',
    poNumber:           document.getElementById('bill-po-' + tenantId)?.value || '',
    notes:              document.getElementById('bill-notes-' + tenantId)?.value || '',
  };
  try {
    await HRMGMT.saveBillingInfo(tenantId, fields);
    toast('Billing info saved', 'success');
    logActivity('tenant', 'Billing info updated', 'var(--blue)');
  } catch(err) {
    toast(err.message || 'Failed to save billing info', 'error');
  }
}

// ── Audit Log tab ─────────────────────────────────────────────
async function loadTenantAuditLog(tenantId) {
  var el = document.getElementById('td-audit-' + tenantId);
  if (!el) return;
  el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:10px 0;">Loading…</div>';
  try {
    var entries = await HRMGMT.getTenantAuditLog(tenantId, { limit: 50 });
    if (!entries || entries.length === 0) {
      el.innerHTML = [
        '<div style="text-align:center;padding:30px;color:var(--muted);">',
          '<div style="font-size:1.5rem;margin-bottom:8px;">📋</div>',
          '<div style="font-size:0.78rem;">No audit events yet.</div>',
          '<div style="font-size:0.68rem;margin-top:4px;">The tenant app writes here when employees interact with the system.</div>',
        '</div>',
      ].join('');
      return;
    }
    el.innerHTML = entries.map(function(e) {
      var ts = e.createdAt ? new Date(e.createdAt).toLocaleString('en-AU') : '—';
      var mod = e.action ? e.action.split('.')[0] : '—';
      var modData = MODULES.find(function(m) { return m.id === mod; });
      return [
        '<div class="log-entry">',
          '<div class="log-dot" style="background:var(--blue);"></div>',
          '<div class="log-body" style="flex:1;">',
            '<div style="display:flex;align-items:center;justify-content:space-between;">',
              '<div class="lb">' + (modData ? modData.icon + ' ' : '') + e.action + '</div>',
              '<div style="font-size:0.6rem;color:var(--muted);font-family:\'DM Mono\',monospace;">' + ts + '</div>',
            '</div>',
            '<div class="lt">' + (e.email || e.uid || '—') + '</div>',
          '</div>',
        '</div>',
      ].join('');
    }).join('');
  } catch(err) {
    el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:10px;">Audit log unavailable — db-logs may not have data for this tenant yet.</div>';
  }
}

// ── Usage Analytics tab ───────────────────────────────────────
async function loadTenantUsage(tenantId) {
  var el = document.getElementById('td-usage-' + tenantId);
  if (!el) return;
  el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:10px 0;">Loading usage data…</div>';
  try {
    var stats = await HRMGMT.getTenantUsageStats(tenantId, 30);
    if (!stats.hasData) {
      el.innerHTML = [
        '<div class="card card-pad" style="text-align:center;">',
          '<div style="font-size:1.5rem;margin-bottom:8px;">📊</div>',
          '<div style="font-size:0.78rem;color:var(--muted);">No usage data yet — audit events from the tenant app will appear here.</div>',
        '</div>',
      ].join('');
      return;
    }

    el.innerHTML = [
      '<div class="grid-3" style="margin-bottom:16px;">',
        '<div class="card card-pad">',
          '<div class="metric-label">Total Events</div>',
          '<div style="font-family:\'Syne\',sans-serif;font-size:1.8rem;font-weight:700;color:var(--blue);">' + stats.totalEvents.toLocaleString() + '</div>',
          '<div class="metric-sub">Last ' + stats.days + ' days</div>',
        '</div>',
        '<div class="card card-pad">',
          '<div class="metric-label">Active Modules</div>',
          '<div style="font-family:\'Syne\',sans-serif;font-size:1.8rem;font-weight:700;color:var(--accent);">' + stats.topModules.length + '</div>',
          '<div class="metric-sub">With recorded events</div>',
        '</div>',
        '<div class="card card-pad">',
          '<div class="metric-label">Active Users</div>',
          '<div style="font-family:\'Syne\',sans-serif;font-size:1.8rem;font-weight:700;color:var(--purple);">' + stats.topUsers.length + '</div>',
          '<div class="metric-sub">With recorded events</div>',
        '</div>',
      '</div>',

      '<div class="grid-2">',
        '<div class="card card-pad">',
          '<div style="font-family:\'Syne\',sans-serif;font-weight:700;font-size:0.88rem;margin-bottom:14px;">Top Modules</div>',
          stats.topModules.map(function(m) {
            var modData = MODULES.find(function(x) { return x.id === m.module; });
            var maxCount = stats.topModules[0].count;
            var pct = Math.round((m.count / maxCount) * 100);
            return [
              '<div style="margin-bottom:10px;">',
                '<div style="display:flex;justify-content:space-between;font-size:0.72rem;margin-bottom:4px;">',
                  '<span>' + (modData ? modData.icon + ' ' + modData.name : m.module) + '</span>',
                  '<span style="font-family:\'DM Mono\',monospace;color:var(--muted);">' + m.count + '</span>',
                '</div>',
                '<div class="usage-bar"><div class="usage-bar-fill" style="width:' + pct + '%;background:var(--blue);"></div></div>',
              '</div>',
            ].join('');
          }).join(''),
        '</div>',

        '<div class="card card-pad">',
          '<div style="font-family:\'Syne\',sans-serif;font-weight:700;font-size:0.88rem;margin-bottom:14px;">Top Actions</div>',
          stats.topActions.map(function(a) {
            var maxCount = stats.topActions[0].count;
            var pct = Math.round((a.count / maxCount) * 100);
            return [
              '<div style="margin-bottom:10px;">',
                '<div style="display:flex;justify-content:space-between;font-size:0.72rem;margin-bottom:4px;">',
                  '<span style="font-family:\'DM Mono\',monospace;font-size:0.65rem;">' + a.action + '</span>',
                  '<span style="font-family:\'DM Mono\',monospace;color:var(--muted);">' + a.count + '</span>',
                '</div>',
                '<div class="usage-bar"><div class="usage-bar-fill" style="width:' + pct + '%;background:var(--purple);"></div></div>',
              '</div>',
            ].join('');
          }).join(''),
        '</div>',
      '</div>',

      stats.topUsers.length > 0 ? [
        '<div class="card card-pad" style="margin-top:16px;">',
          '<div style="font-family:\'Syne\',sans-serif;font-weight:700;font-size:0.88rem;margin-bottom:14px;">Most Active Users</div>',
          '<div style="display:flex;flex-wrap:wrap;gap:8px;">',
            stats.topUsers.map(function(u) {
              return '<div style="display:flex;align-items:center;gap:6px;padding:5px 10px;background:var(--bg3);border:1px solid var(--border);border-radius:6px;">'
                + '<span style="font-size:0.7rem;">' + (u.email || u.uid) + '</span>'
                + '<span style="font-family:\'DM Mono\',monospace;font-size:0.62rem;color:var(--accent);">' + u.count + '</span>'
                + '</div>';
            }).join(''),
          '</div>',
        '</div>',
      ].join('') : '',
    ].join('');
  } catch(err) {
    el.innerHTML = '<div class="card card-pad"><div style="color:var(--muted);font-size:0.75rem;">Usage analytics unavailable.</div></div>';
  }
}

// ── Period Years tab ──────────────────────────────────────────
const PY_STATUS = {
  open:    { label: 'Open',    color: 'var(--accent)' },
  closed:  { label: 'Closed',  color: 'var(--muted)'  },
  limited: { label: 'Limited', color: '#f59e0b'        },
};

async function loadTenantPeriodYears(tenantId) {
  const el = document.getElementById('td-periods-' + tenantId);
  if (!el) return;
  el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:10px 0;">Loading…</div>';
  try {
    const years = await HRMGMT.getTenantPeriodYears(tenantId);
    if (!years || !years.length) {
      el.innerHTML = `<div class="card card-pad" style="text-align:center;">
        <div style="font-size:0.78rem;color:var(--muted);margin-bottom:12px;">No period years found for this tenant.</div>
        <button class="btn btn-xs" onclick="initTenantPeriodYears('${tenantId}')">Initialise Period Years</button>
      </div>`;
      return;
    }
    const rows = years.map(py => {
      const sm = PY_STATUS[py.status] || PY_STATUS.open;
      return `<tr>
        <td style="font-size:0.8rem;font-weight:700;padding:8px 10px;">${py.year}</td>
        <td style="padding:8px 10px;"><span style="font-size:0.62rem;font-weight:600;padding:2px 8px;border-radius:3px;color:${sm.color};border:1px solid ${sm.color}33;background:${sm.color}11;">${sm.label}</span></td>
        <td style="padding:8px 10px;">${py.isActive ? '<span style="font-size:0.62rem;font-weight:600;color:var(--accent);">● Active</span>' : '—'}</td>
        <td style="padding:8px 10px;font-size:0.65rem;color:var(--muted);">${py.closedAt ? 'Closed' : py.reopenedAt ? 'Reopened' : 'Created'}</td>
      </tr>`;
    }).join('');
    el.innerHTML = `<div class="card card-pad" style="padding:0;">
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr style="border-bottom:1px solid var(--border);">
          <th style="font-size:0.6rem;color:var(--muted);text-align:left;padding:6px 10px;font-weight:600;text-transform:uppercase;">Year</th>
          <th style="font-size:0.6rem;color:var(--muted);text-align:left;padding:6px 10px;font-weight:600;text-transform:uppercase;">Status</th>
          <th style="font-size:0.6rem;color:var(--muted);text-align:left;padding:6px 10px;font-weight:600;text-transform:uppercase;">Active</th>
          <th style="font-size:0.6rem;color:var(--muted);text-align:left;padding:6px 10px;font-weight:600;text-transform:uppercase;">State</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
  } catch (err) {
    el.innerHTML = '<div class="card card-pad"><div style="font-size:0.75rem;color:var(--muted);">Period years unavailable.</div></div>';
  }
}

async function initTenantPeriodYears(tenantId) {
  if (!confirm('Seed period years for this tenant? This creates years for the current year ± 2, with the current year set as active.')) return;
  try {
    await HRMGMT.initTenantPeriodYears(tenantId);
    toast('Period years initialised.', 'success');
    loadTenantPeriodYears(tenantId);
  } catch (err) {
    toast((err && err.message) || 'Failed to initialise period years.', 'error');
  }
}

window.initTenantPeriodYears = initTenantPeriodYears;

// ══════════════════════════════════════════════════════════════
// SUPPORT TICKETS PAGE
// ══════════════════════════════════════════════════════════════

var _ticketsState = { tickets: [], filter: '' };

function getSLAStatus(t) {
  var SLA_HOURS = { critical: 4, high: 24, medium: 72, low: 168 };
  var threshold = SLA_HOURS[t.priority] || 72;
  var createdMs = t.createdAt && t.createdAt.toMillis ? t.createdAt.toMillis() : Date.now();
  var respondedMs = t.firstResponseAt && t.firstResponseAt.toMillis ? t.firstResponseAt.toMillis() : null;
  var elapsed = ((respondedMs || Date.now()) - createdMs) / 3600000;
  if (respondedMs) return { label: 'Responded in ' + Math.round(elapsed) + 'h', color: 'var(--accent)', met: true };
  if (elapsed > threshold) return { label: 'SLA breached (' + Math.round(elapsed) + 'h)', color: 'var(--red)', met: false };
  var remaining = Math.round(threshold - elapsed);
  return { label: remaining + 'h remaining', color: 'var(--amber)', met: false };
}

async function renderSupportTickets() {
  var el = document.getElementById('support-tickets-list');
  if (!el) return;
  el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:20px;">Loading tickets…</div>';
  try {
    var tickets = await HRMGMT.listSupportTickets({ limit: 100 });
    _ticketsState.tickets = tickets;
    renderTicketList();
  } catch(err) {
    el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:20px;">Unable to load support tickets.</div>';
  }
}

function renderTicketList() {
  var el = document.getElementById('support-tickets-list');
  if (!el) return;
  var filter = _ticketsState.filter;
  var tickets = filter
    ? _ticketsState.tickets.filter(function(t) { return t.status === filter; })
    : _ticketsState.tickets;

  var priorityColor = { low:'var(--muted)', medium:'var(--blue)', high:'var(--amber)', critical:'var(--red)' };
  var statusColor   = { open:'var(--blue)', in_progress:'var(--amber)', resolved:'var(--accent)', closed:'var(--muted)' };

  if (tickets.length === 0) {
    el.innerHTML = '<div style="text-align:center;padding:40px;color:var(--muted);"><div style="font-size:1.5rem;margin-bottom:8px;">🎫</div><div>No tickets found</div></div>';
    return;
  }

  el.innerHTML = tickets.map(function(t) {
    var ts = t.createdAt && t.createdAt.toDate ? t.createdAt.toDate().toLocaleDateString() : '—';
    var pColor = priorityColor[t.priority] || 'var(--muted)';
    var sColor = statusColor[t.status] || 'var(--muted)';
    var sla = (t.status !== 'resolved' && t.status !== 'closed') || !t.firstResponseAt ? getSLAStatus(t) : null;
    var slaHtml = sla ? '<span style="font-size:0.58rem;font-family:\'DM Mono\',monospace;padding:2px 7px;border-radius:4px;background:' + sla.color + '22;color:' + sla.color + ';border:1px solid ' + sla.color + '44;">' + sla.label + '</span>' : '';
    return [
      '<div style="display:flex;align-items:flex-start;gap:12px;padding:14px;border-bottom:1px solid rgba(255,255,255,0.04);">',
        '<div style="flex:1;min-width:0;">',
          '<div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;flex-wrap:wrap;">',
            '<span style="font-size:0.78rem;font-weight:500;color:var(--text);">' + t.subject + '</span>',
            '<span style="font-size:0.6rem;font-family:\'DM Mono\',monospace;padding:2px 7px;border-radius:4px;background:' + sColor + '22;color:' + sColor + ';">' + t.status.replace('_',' ') + '</span>',
            '<span style="font-size:0.6rem;font-family:\'DM Mono\',monospace;color:' + pColor + ';">' + t.priority + '</span>',
            slaHtml,
          '</div>',
          '<div style="font-size:0.7rem;color:var(--muted);margin-bottom:4px;">' + t.tenantName + ' (' + t.tenantCode + ')</div>',
          '<div style="font-size:0.7rem;color:var(--muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:500px;">' + t.description + '</div>',
        '</div>',
        '<div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;flex-shrink:0;">',
          '<div style="font-size:0.62rem;color:var(--muted);font-family:\'DM Mono\',monospace;">' + ts + '</div>',
          '<div style="display:flex;gap:5px;">',
            '<button class="btn btn-ghost btn-xs" onclick="openTicketDetail(\'' + t.id + '\')">View / Reply</button>',
            t.status !== 'resolved' && t.status !== 'closed'
              ? '<button class="btn btn-xs" style="background:var(--accent-dim);color:var(--accent);border:1px solid rgba(74,222,128,0.25);" onclick="resolveTicket(\'' + t.id + '\')">Resolve</button>'
              : '',
          '</div>',
        '</div>',
      '</div>',
    ].join('');
  }).join('');
}

function openTicketDetail(ticketId) {
  var t = _ticketsState.tickets.find(function(x) { return x.id === ticketId; });
  if (!t) return;

  var panel = document.getElementById('ticket-detail-panel');
  if (!panel) return;
  panel.style.display = 'block';

  var priorityColor = { low:'var(--muted)', medium:'var(--blue)', high:'var(--amber)', critical:'var(--red)' };
  var statusColor   = { open:'var(--blue)', in_progress:'var(--amber)', resolved:'var(--accent)', closed:'var(--muted)' };
  var pColor = priorityColor[t.priority] || 'var(--muted)';
  var sColor = statusColor[t.status] || 'var(--muted)';
  var ts = t.createdAt && t.createdAt.toDate ? t.createdAt.toDate().toLocaleDateString() : '—';

  var repliesHTML = '';
  if (t.adminReplies && t.adminReplies.length) {
    repliesHTML = t.adminReplies.map(function(r) {
      var rts = r.sentAt && r.sentAt.toDate ? r.sentAt.toDate().toLocaleDateString() : '—';
      return [
        '<div style="background:rgba(74,222,128,0.06);border:1px solid rgba(74,222,128,0.15);border-radius:8px;padding:10px 12px;margin-bottom:8px;">',
          '<div style="font-size:0.62rem;color:var(--accent);font-family:\'DM Mono\',monospace;margin-bottom:5px;">' + (r.sentBy || 'Admin') + ' · ' + rts + '</div>',
          '<div style="font-size:0.75rem;color:var(--text);line-height:1.55;white-space:pre-wrap;">' + r.message + '</div>',
        '</div>',
      ].join('');
    }).join('');
  } else {
    repliesHTML = '<div style="font-size:0.72rem;color:var(--muted);padding:8px 0;">No replies yet.</div>';
  }

  var canReply = t.status !== 'resolved' && t.status !== 'closed';

  panel.innerHTML = [
    '<div class="card card-pad" style="job:sticky;top:16px;">',
      '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;">',
        '<div style="font-size:0.8rem;font-weight:700;color:var(--text);">Ticket Detail</div>',
        '<button onclick="document.getElementById(\'ticket-detail-panel\').style.display=\'none\'" style="background:none;border:none;color:var(--muted);font-size:1rem;cursor:pointer;">✕</button>',
      '</div>',
      '<div style="font-size:0.78rem;font-weight:600;color:var(--text);margin-bottom:6px;">' + t.subject + '</div>',
      (function() {
        var sla = getSLAStatus(t);
        return [
          '<div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;flex-wrap:wrap;">',
            '<span style="font-size:0.6rem;font-family:\'DM Mono\',monospace;padding:2px 7px;border-radius:4px;background:' + sColor + '22;color:' + sColor + ';">' + (t.status || '').replace('_',' ') + '</span>',
            '<span style="font-size:0.6rem;color:' + pColor + ';font-family:\'DM Mono\',monospace;">' + (t.priority || '') + '</span>',
            t.category ? '<span style="font-size:0.6rem;color:var(--muted);font-family:\'DM Mono\',monospace;">' + t.category + '</span>' : '',
          '</div>',
          '<div style="display:flex;align-items:center;gap:5px;margin-bottom:10px;">',
            '<span style="font-size:0.58rem;color:var(--muted);">SLA:</span>',
            '<span style="font-size:0.6rem;font-family:\'DM Mono\',monospace;padding:2px 9px;border-radius:4px;background:' + sla.color + '22;color:' + sla.color + ';border:1px solid ' + sla.color + '44;">' + sla.label + '</span>',
          '</div>',
        ].join('');
      })(),
      '<div style="font-size:0.68rem;color:var(--muted);margin-bottom:4px;">From: <span style="color:var(--text);">' + (t.submittedBy || t.createdBy || t.tenantName || '—') + '</span></div>',
      '<div style="font-size:0.68rem;color:var(--muted);margin-bottom:12px;">Organisation: <span style="color:var(--text);">' + (t.tenantName || '—') + ' (' + (t.tenantCode || '—') + ')</span> · ' + ts + '</div>',
      '<div style="font-size:0.72rem;color:var(--text);line-height:1.6;background:var(--bg3);border-radius:6px;padding:10px 12px;margin-bottom:16px;white-space:pre-wrap;">' + (t.description || '—') + '</div>',
      '<div style="font-size:0.65rem;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:0.07em;font-family:\'DM Mono\',monospace;margin-bottom:8px;">Admin Replies</div>',
      '<div id="ticket-replies-wrap">' + repliesHTML + '</div>',
      canReply ? [
        '<div style="margin-top:10px;">',
          '<textarea id="ticket-reply-input" style="width:100%;background:var(--bg3);border:1px solid rgba(255,255,255,0.1);border-radius:6px;color:var(--text);font-family:\'DM Sans\',sans-serif;font-size:0.75rem;padding:9px 11px;resize:vertical;min-height:80px;box-sizing:border-box;outline:none;" placeholder="Write a reply to the tenant…"></textarea>',
          '<div style="display:flex;gap:7px;margin-top:8px;">',
            '<button class="btn btn-primary btn-sm" style="flex:1;" id="ticket-reply-btn" onclick="submitTicketReply(\'' + t.id + '\')">',
              '<svg width="11" height="11" viewBox="0 0 16 16" fill="none" style="margin-right:5px;"><path d="M2 8l5-5 5 5M7 3v10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
              'Send Reply',
            '</button>',
            '<button class="btn btn-ghost btn-sm" onclick="resolveTicket(\'' + t.id + '\')">Mark Resolved</button>',
          '</div>',
        '</div>',
      ].join('') : '<div style="font-size:0.7rem;color:var(--muted);margin-top:8px;font-style:italic;">This ticket is ' + t.status + '.</div>',
    '</div>',
  ].join('');
}

async function submitTicketReply(ticketId) {
  var input = document.getElementById('ticket-reply-input');
  var message = input ? input.value.trim() : '';
  if (!message) { toast('Please enter a reply message.', 'error'); return; }

  var btn = document.getElementById('ticket-reply-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }

  try {
    var result = await HRMGMT.replyToSupportTicket({ ticketId: ticketId, message: message });
    var t = _ticketsState.tickets.find(function(x) { return x.id === ticketId; });
    if (t) {
      if (!t.adminReplies) t.adminReplies = [];
      t.adminReplies.push({ message: message, sentBy: 'Admin', sentAt: { toDate: function() { return new Date(); } } });
      if (result && result.status) t.status = result.status;
    }
    openTicketDetail(ticketId);
    renderTicketList();
    toast('Reply sent', 'success');
  } catch(err) {
    toast(err.message || 'Failed to send reply', 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'Send Reply'; }
  }
}

async function loadDomainRequests() {
  var el = document.getElementById('domain-requests-list');
  if (!el) return;
  el.innerHTML = '<div style="font-size:0.72rem;color:var(--muted);padding:8px 0;">Loading…</div>';
  try {
    var requests = await HRMGMT.listDomainChangeRequests({ limit: 50 });
    if (!requests || requests.length === 0) {
      el.innerHTML = '<div style="font-size:0.72rem;color:var(--muted);padding:12px 0;text-align:center;">No pending domain change requests.</div>';
      return;
    }
    el.innerHTML = requests.map(function(r) {
      var ts = r.createdAt && r.createdAt.toDate ? r.createdAt.toDate().toLocaleDateString() : '—';
      var statusColor = r.status === 'pending' ? 'var(--amber)' : r.status === 'approved' ? 'var(--accent)' : 'var(--muted)';
      return [
        '<div style="padding:10px 0;border-bottom:1px solid rgba(255,255,255,0.04);">',
          '<div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">',
            '<span style="font-size:0.75rem;font-weight:500;color:var(--text);">' + (r.tenantName || r.tenantId) + '</span>',
            '<span style="font-size:0.6rem;font-family:\'DM Mono\',monospace;padding:1px 6px;border-radius:3px;background:' + statusColor + '20;color:' + statusColor + ';">' + (r.status || 'pending') + '</span>',
          '</div>',
          '<div style="font-size:0.68rem;color:var(--muted);">',
            (r.currentDomain || '—') + ' → <span style="color:var(--text);">' + (r.requestedDomain || '—') + '</span>',
          '</div>',
          '<div style="font-size:0.62rem;color:var(--muted);margin-top:2px;">Requested: ' + ts + (r.reason ? ' · ' + r.reason.substring(0,60) : '') + '</div>',
          r.status === 'pending' ? [
            '<div style="display:flex;gap:6px;margin-top:8px;">',
              '<button class="btn btn-xs" style="background:var(--accent-dim);color:var(--accent);border:1px solid rgba(74,222,128,0.25);" onclick="approveDomainRequest(\'' + r.id + '\')">Approve</button>',
              '<button class="btn btn-ghost btn-xs" onclick="rejectDomainRequest(\'' + r.id + '\')">Reject</button>',
            '</div>',
          ].join('') : '',
        '</div>',
      ].join('');
    }).join('');
  } catch(err) {
    el.innerHTML = '<div style="font-size:0.72rem;color:var(--muted);padding:8px 0;">Unable to load domain requests.</div>';
  }
}

async function approveDomainRequest(requestId) {
  try {
    await HRMGMT.processDomainChangeRequest({ requestId: requestId, action: 'approve' });
    toast('Domain change approved', 'success');
    loadDomainRequests();
  } catch(err) {
    toast(err.message || 'Failed to approve', 'error');
  }
}

async function rejectDomainRequest(requestId) {
  try {
    await HRMGMT.processDomainChangeRequest({ requestId: requestId, action: 'reject' });
    toast('Domain change rejected', 'info');
    loadDomainRequests();
  } catch(err) {
    toast(err.message || 'Failed to reject', 'error');
  }
}

function saveAccountSettings() {
  toast('Account settings saved', 'success');
}


function filterTickets(status) {
  _ticketsState.filter = status;
  document.querySelectorAll('#support-filter-bar .tab-btn').forEach(function(b) {
    b.classList.toggle('active', b.dataset.status === status);
  });
  renderTicketList();
}

async function resolveTicket(ticketId) {
  try {
    await HRMGMT.updateTicketStatus(ticketId, 'resolved', null, 'Resolved by admin');
    var t = _ticketsState.tickets.find(function(x) { return x.id === ticketId; });
    if (t) t.status = 'resolved';
    renderTicketList();
    toast('Ticket resolved', 'success');
  } catch(err) {
    toast(err.message || 'Failed to resolve ticket', 'error');
  }
}

async function assignTicket(ticketId) {
  try {
    await HRMGMT.updateTicketStatus(ticketId, 'in_progress', 'platform@hrms.io', null);
    var t = _ticketsState.tickets.find(function(x) { return x.id === ticketId; });
    if (t) t.status = 'in_progress';
    renderTicketList();
    toast('Ticket assigned', 'info');
  } catch(err) {
    toast(err.message || 'Failed to assign ticket', 'error');
  }
}

function openNewTicketModal() {
  var existing = document.getElementById('new-ticket-overlay');
  if (existing) existing.remove();

  var tenantOpts = state.tenants.map(function(t) {
    return '<option value="' + t.id + '">' + t.name + '</option>';
  }).join('');

  var overlay = document.createElement('div');
  overlay.id = 'new-ticket-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:#0a0c0f;display:flex;align-items:center;justify-content:center;z-index:9999;';
  overlay.innerHTML = [
    '<div style="background:var(--bg2);border:1px solid var(--border);border-radius:12px;padding:28px;width:480px;max-width:calc(100vw - 40px);">',
      '<div style="font-family:\'Syne\',sans-serif;font-size:1rem;font-weight:700;margin-bottom:18px;">New Support Ticket</div>',
      '<div class="form-group"><label class="form-label">Tenant</label><select class="form-input" id="nt-ticket-tenant"><option value="">Select tenant…</option>' + tenantOpts + '</select></div>',
      '<div class="form-group"><label class="form-label">Subject</label><input class="form-input" id="nt-ticket-subject" placeholder="Brief description of the issue"/></div>',
      '<div class="form-group"><label class="form-label">Priority</label><select class="form-input" id="nt-ticket-priority"><option value="low">Low</option><option value="medium" selected>Medium</option><option value="high">High</option><option value="critical">Critical</option></select></div>',
      '<div class="form-group"><label class="form-label">Description</label><textarea class="form-input" id="nt-ticket-desc" rows="3" placeholder="Detailed description…"></textarea></div>',
      '<div style="display:flex;gap:8px;margin-top:4px;">',
        '<button class="btn btn-primary" style="flex:1;" id="nt-ticket-submit">Create Ticket</button>',
        '<button class="btn btn-ghost" onclick="document.getElementById(\'new-ticket-overlay\').remove()">Cancel</button>',
      '</div>',
    '</div>',
  ].join('');
  document.body.appendChild(overlay);
  overlay.addEventListener('click', function(e) { if (e.target === overlay) overlay.remove(); });

  document.getElementById('nt-ticket-submit').onclick = async function() {
    var tenantId = document.getElementById('nt-ticket-tenant').value;
    var subject  = document.getElementById('nt-ticket-subject').value.trim();
    var desc     = document.getElementById('nt-ticket-desc').value.trim();
    var priority = document.getElementById('nt-ticket-priority').value;
    if (!tenantId || !subject || !desc) { toast('All fields required', 'error'); return; }
    var btn = document.getElementById('nt-ticket-submit');
    btn.disabled = true; btn.textContent = 'Creating…';
    try {
      var ticket = await HRMGMT.createSupportTicket(tenantId, subject, desc, priority);
      _ticketsState.tickets.unshift(ticket);
      renderTicketList();
      overlay.remove();
      toast('Ticket created', 'success');
    } catch(err) {
      toast(err.message || 'Failed to create ticket', 'error');
      btn.disabled = false; btn.textContent = 'Create Ticket';
    }
  };
}

// ── window exports for new functions ─────────────────────────
window.tdTab                  = tdTab;
window.toggleTenantFeature    = toggleTenantFeature;
window.loadTenantData         = loadTenantData;
window.loadTenantRoles        = loadTenantRoles;
window.openCreateCustomRole   = openCreateCustomRole;
window.openEditCustomRole     = openEditCustomRole;
window.deleteCustomRole       = deleteCustomRole;
window.toggleOnboardingStep   = toggleOnboardingStep;
window.saveBillingInfo        = saveBillingInfo;
window.renderSupportTickets   = renderSupportTickets;
window.filterTickets          = filterTickets;
window.resolveTicket          = resolveTicket;
window.assignTicket           = assignTicket;
window.openNewTicketModal     = openNewTicketModal;
window.openTicketDetail       = openTicketDetail;
window.submitTicketReply      = submitTicketReply;
window.loadDomainRequests     = loadDomainRequests;
window.approveDomainRequest   = approveDomainRequest;
window.rejectDomainRequest    = rejectDomainRequest;
window.saveAccountSettings    = saveAccountSettings;
window.loadQuotaAlerts        = loadQuotaAlerts;
// ══════════════════════════════════════════════════════════════
// TENANT DETAIL — USERS TAB
// ══════════════════════════════════════════════════════════════

// Cache users per tenant to avoid refetching on filter changes
const _tenantUsersCache = {};

async function loadTenantUsersTab(tenantId) {
  const el = document.getElementById('td-users-' + tenantId);
  if (!el) return;
  el.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:10px 0;">Loading users…</div>';
  try {
    const res = await HRMGMT.listTenantUsers({ tenantId });
    _tenantUsersCache[tenantId] = res.users || [];
    renderTenantUsersTab(tenantId);
  } catch(err) {
    el.innerHTML = `<div style="color:var(--red);font-size:0.75rem;padding:10px 0;">${err.message || 'Failed to load users'}</div>`;
  }
}

function filterTenantUsersTab(tenantId) {
  renderTenantUsersTab(tenantId);
}

function renderTenantUsersTab(tenantId) {
  const el = document.getElementById('td-users-' + tenantId);
  if (!el) return;
  let users = _tenantUsersCache[tenantId] || [];

  const roleFilter   = document.getElementById('tu-tab-role-' + tenantId)?.value || '';
  const statusFilter = document.getElementById('tu-tab-status-' + tenantId)?.value || '';
  const search       = (document.getElementById('tu-tab-search-' + tenantId)?.value || '').toLowerCase();

  if (roleFilter)   users = users.filter(u => roleFilter === 'admin' ? (u.role === 'admin' || u.role === 'full_admin') : u.role === roleFilter);
  if (statusFilter) users = users.filter(u => u.status === statusFilter);
  if (search)       users = users.filter(u =>
    u.email.toLowerCase().includes(search) ||
    (u.displayName || '').toLowerCase().includes(search) ||
    (u.firstName || '').toLowerCase().includes(search) ||
    (u.lastName || '').toLowerCase().includes(search)
  );

  const roleBadge = (role) => {
    const map = { admin:'var(--blue)', full_admin:'var(--blue)', manager:'var(--amber)', employee:'var(--accent)' };
    const color = map[role] || 'var(--muted)';
    const label = role === 'full_admin' ? 'full admin' : role;
    return `<span style="font-size:0.6rem;padding:2px 7px;border-radius:4px;background:${color}18;color:${color};border:1px solid ${color}30;font-family:'DM Mono',monospace;">${label}</span>`;
  };

  const statusDot = (status) => {
    const color = status === 'active' ? 'var(--accent)' : status === 'suspended' ? 'var(--red)' : 'var(--muted)';
    return `<span style="display:inline-flex;align-items:center;gap:5px;font-size:0.68rem;color:${color};"><span style="width:6px;height:6px;border-radius:50%;background:${color};display:inline-block;"></span>${status}</span>`;
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-PG', { day:'2-digit', month:'short', year:'numeric' }); }
    catch { return iso.split('T')[0]; }
  };

  const fmtRelative = (iso) => {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return fmtDate(iso);
  };

  if (users.length === 0) {
    el.innerHTML = `<div style="padding:30px;text-align:center;color:var(--muted);font-size:0.75rem;">No users found${search || roleFilter || statusFilter ? ' matching filters' : ''}.</div>`;
    return;
  }

  el.innerHTML = `
    <div style="font-size:0.68rem;color:var(--muted);margin-bottom:10px;">${users.length} user${users.length !== 1 ? 's' : ''}</div>
    <div class="card" style="overflow:hidden;">
      <div style="overflow-x:auto;">
        <table class="data-table">
          <thead><tr>
            <th>User</th>
            <th>Role</th>
            <th>Status</th>
            <th>Registered</th>
            <th>Last Login</th>
            <th style="text-align:right;width:160px;">Actions</th>
          </tr></thead>
          <tbody>
            ${users.map(u => {
              const name = (u.displayName || ((u.firstName||'') + ' ' + (u.lastName||'')).trim() || '').trim();
              const initials = name ? name.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase() : u.email[0].toUpperCase();
              const avatarColor = u.disabled ? '#6b7280' : '#4ade80';
              return `
              <tr style="cursor:pointer;" onclick="openUserProfile('${u.uid}','${tenantId}')" title="View profile">
                <td>
                  <div style="display:flex;align-items:center;gap:9px;">
                    <div style="width:30px;height:30px;border-radius:8px;background:${avatarColor}22;color:${avatarColor};
                      display:flex;align-items:center;justify-content:center;font-size:0.65rem;font-weight:700;flex-shrink:0;">
                      ${initials}
                    </div>
                    <div>
                      <div style="font-size:0.75rem;font-weight:500;">${name || '—'}</div>
                      <div style="font-size:0.62rem;color:var(--muted);font-family:'DM Mono',monospace;">${u.email}</div>
                    </div>
                  </div>
                </td>
                <td>${roleBadge(u.role)}</td>
                <td>${statusDot(u.status)}</td>
                <td style="font-size:0.68rem;color:var(--muted);">${fmtDate(u.createdAt)}</td>
                <td style="font-size:0.68rem;color:var(--muted);">${fmtRelative(u.lastLoginAt)}</td>
                <td style="text-align:right;" onclick="event.stopPropagation()">
                  <div style="display:flex;gap:4px;justify-content:flex-end;align-items:center;">
                    ${(u.role === 'full_admin' || u.role === 'admin') ? `
                      <button class="btn btn-ghost btn-xs" style="color:var(--cyan);"
                        onclick="event.stopPropagation();sendUserPasswordReset('${u.uid}','${u.email}','${tenantId}')">
                        Reset
                      </button>` : ''}
                    <button class="btn btn-ghost btn-xs"
                      onclick="event.stopPropagation();toggleTenantUserStatus('${u.uid}','${tenantId}',${!u.disabled})"
                      style="color:${u.disabled ? 'var(--accent)' : 'var(--amber)'};">
                      ${u.disabled ? 'Restore' : 'Suspend'}
                    </button>
                    <button class="btn btn-ghost btn-xs" style="color:var(--red);padding:3px 6px;"
                      title="More actions"
                      onclick="event.stopPropagation();openUserActions('${u.uid}','${tenantId}',this)">
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="3" r="1.2" fill="currentColor"/>
                        <circle cx="8" cy="8" r="1.2" fill="currentColor"/>
                        <circle cx="8" cy="13" r="1.2" fill="currentColor"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            `}).join('')}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

// ── openProvisionAdminModal ───────────────────────────────────────────────────
// Modal to provision a new admin account inside a GCIP tenant.
// Creates the GCIP user (if not already present), stamps full_admin claims,
// writes the db-hr user doc, and sends a password-reset invite email.
function openProvisionAdminModal(tenantId) {
  const t = state.tenants.find(x => x.id === tenantId);
  if (!t) return;

  const old = document.getElementById('provision-admin-overlay');
  if (old) old.remove();

  const overlay = document.createElement('div');
  overlay.id = 'provision-admin-overlay';
  overlay.style.cssText =
    'position:fixed;inset:0;background:#0a0c0f;' +
    'display:flex;align-items:center;justify-content:center;z-index:9999;padding:16px;';

  overlay.innerHTML = `
    <div style="background:var(--bg2);border:1px solid rgba(96,165,250,0.25);
      border-radius:14px;width:460px;max-width:100%;
      box-shadow:0 28px 70px rgba(0,0,0,0.65);overflow:hidden;">

      <!-- Header -->
      <div style="background:rgba(96,165,250,0.06);border-bottom:1px solid
        rgba(96,165,250,0.18);padding:18px 22px;display:flex;
        align-items:center;gap:12px;">
        <div style="width:34px;height:34px;border-radius:9px;
          background:rgba(96,165,250,0.12);display:flex;align-items:center;
          justify-content:center;flex-shrink:0;">
          <i class="fas fa-user-plus" style="color:var(--blue);font-size:0.85rem;"></i>
        </div>
        <div>
          <div style="font-family:'Syne',sans-serif;font-size:1rem;
            font-weight:700;color:var(--blue);">Provision Admin Account</div>
          <div style="font-size:0.68rem;color:var(--muted);margin-top:1px;">
            ${t.name} &nbsp;·&nbsp;
            <span style="font-family:'DM Mono',monospace;">${t.code}</span>
          </div>
        </div>
      </div>

      <!-- Body -->
      <div style="padding:22px;">
        <div style="font-size:0.72rem;color:var(--muted);margin-bottom:16px;
          line-height:1.55;">
          Creates a <strong style="color:var(--blue)">full_admin</strong> account
          in this tenant's GCIP pool. If the email already exists the account is
          re-stamped with admin claims. A password-reset invite email is always sent.
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
          <div class="form-group" style="margin:0;">
            <label class="form-label">First Name <span style="color:var(--red)">*</span></label>
            <input class="form-input" id="pa-first" placeholder="First name" autocomplete="given-name"/>
          </div>
          <div class="form-group" style="margin:0;">
            <label class="form-label">Last Name <span style="color:var(--red)">*</span></label>
            <input class="form-input" id="pa-last" placeholder="Last name" autocomplete="family-name"/>
          </div>
        </div>

        <div class="form-group" style="margin-bottom:10px;">
          <label class="form-label">Email Address <span style="color:var(--red)">*</span></label>
          <input class="form-input" id="pa-email" type="email"
            placeholder="admin@organisation.com" autocomplete="email"/>
          <div style="font-size:0.62rem;color:var(--muted);margin-top:4px;">
            Must match an allowed domain for this tenant, or be added to
            the allowed domains list first.
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
          <div class="form-group" style="margin:0;">
            <label class="form-label">Phone</label>
            <input class="form-input" id="pa-phone" placeholder="+675 …" autocomplete="tel"/>
          </div>
          <div class="form-group" style="margin:0;">
            <label class="form-label">Date of Birth</label>
            <input class="form-input" type="date" id="pa-dob"/>
          </div>
        </div>

        <div class="form-group" style="margin-bottom:18px;">
          <label class="form-label">Sex</label>
          <select class="form-input" id="pa-sex">
            <option value="">— not specified —</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <!-- Outcome preview -->
        <div style="padding:10px 14px;background:rgba(96,165,250,0.05);
          border:1px solid rgba(96,165,250,0.15);border-radius:8px;
          margin-bottom:16px;font-size:0.7rem;color:var(--muted);line-height:1.55;">
          <span style="color:var(--blue);font-weight:600;">What happens:</span>
          GCIP account created (or re-stamped) with
          <code style="font-size:0.65rem;background:rgba(96,165,250,0.1);
            padding:1px 5px;border-radius:3px;">userRole: full_admin</code> +
          <code style="font-size:0.65rem;background:rgba(96,165,250,0.1);
            padding:1px 5px;border-radius:3px;">tenantId</code> claims.
          Password-reset invite email sent. User doc written to db-hr.
        </div>

        <div id="pa-error" style="display:none;padding:9px 12px;
          background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.25);
          border-radius:7px;font-size:0.72rem;color:var(--red);margin-bottom:12px;"></div>

        <div id="pa-success" style="display:none;padding:10px 14px;
          background:rgba(74,222,128,0.07);border:1px solid rgba(74,222,128,0.2);
          border-radius:7px;font-size:0.72rem;color:var(--accent);margin-bottom:12px;"></div>

        <div style="display:flex;gap:8px;">
          <button class="btn btn-sm" id="pa-submit"
            style="background:rgba(96,165,250,0.15);color:var(--blue);
            border:1px solid rgba(96,165,250,0.3);font-weight:600;flex:1;">
            <i class="fas fa-user-plus" style="margin-right:6px;"></i>Provision &amp; Send Invite
          </button>
          <button class="btn btn-ghost btn-sm" id="pa-cancel">Cancel</button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  document.getElementById('pa-first').focus();

  document.getElementById('pa-cancel').onclick = () => overlay.remove();
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  document.getElementById('pa-submit').onclick = async () => {
    const submitBtn  = document.getElementById('pa-submit');
    const errDiv     = document.getElementById('pa-error');
    const successDiv = document.getElementById('pa-success');

    const firstName = (document.getElementById('pa-first').value || '').trim();
    const lastName  = (document.getElementById('pa-last').value  || '').trim();
    const email     = (document.getElementById('pa-email').value || '').trim().toLowerCase();
    const phone     = (document.getElementById('pa-phone').value || '').trim();
    const dob       = (document.getElementById('pa-dob').value   || '').trim();
    const sex       = (document.getElementById('pa-sex').value   || '').trim();

    errDiv.style.display     = 'none';
    successDiv.style.display = 'none';

    if (!firstName || !lastName) {
      errDiv.textContent   = 'First name and last name are required.';
      errDiv.style.display = 'block';
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errDiv.textContent   = 'A valid email address is required.';
      errDiv.style.display = 'block';
      return;
    }

    submitBtn.disabled     = true;
    submitBtn.innerHTML    = '<i class="fas fa-spinner fa-spin" style="margin-right:6px;"></i>Provisioning…';

    try {
      const res = await HRMGMT.provisionTenantAdmin(tenantId, email, {
        firstName, lastName,
        ...(phone && {phone}),
        ...(dob   && {dob}),
        ...(sex   && {sex}),
      });

      successDiv.innerHTML =
        `<i class="fas fa-circle-check" style="margin-right:6px;"></i>` +
        (res.created
          ? `Admin account <strong>${email}</strong> created and invite email sent.`
          : `Account already existed — claims re-stamped and invite email resent to <strong>${email}</strong>.`);
      successDiv.style.display = 'block';

      logActivity(
        'auth',
        `Admin ${res.created ? 'provisioned' : 're-invited'}: ${email} → "${t.name}"`,
        'var(--cyan)',
      );
      toast(
        res.created
          ? `Admin account created. Invite sent to ${email}.`
          : `Claims re-stamped. Invite resent to ${email}.`,
        'success',
      );

      // Refresh the users list if already loaded
      if (_tenantUsersCache[tenantId]) {
        loadTenantUsersTab(tenantId);
      }

      // Clear form for another entry
      ['pa-first','pa-last','pa-email','pa-phone','pa-dob'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.value = '';
      });
      document.getElementById('pa-sex').value = '';
      submitBtn.disabled  = false;
      submitBtn.innerHTML =
        '<i class="fas fa-user-plus" style="margin-right:6px;"></i>Provision Another';

    } catch (err) {
      errDiv.textContent   = err.message || 'Provisioning failed. Please try again.';
      errDiv.style.display = 'block';
      submitBtn.disabled   = false;
      submitBtn.innerHTML  =
        '<i class="fas fa-user-plus" style="margin-right:6px;"></i>Provision &amp; Send Invite';
    }
  };
}

// ── sendUserPasswordReset ─────────────────────────────────────────────────────
// Sends a password-reset email to an existing tenant user. Lightweight —
// does not recreate the account, just fires the Identity Toolkit OOB email.
async function sendUserPasswordReset(uid, email, tenantId) {
  const t = state.tenants.find(x => x.id === tenantId);
  if (!t) return;
  try {
    await HRMGMT.provisionTenantAdmin(tenantId, email, {});
    toast(`Password-reset email sent to ${email}`, 'success');
    logActivity('auth', `Password-reset resent to ${email} (${t.name})`, 'var(--cyan)');
  } catch (err) {
    toast(err.message || 'Failed to send reset email', 'error');
  }
}

async function toggleTenantUserStatus(uid, tenantId, disable) {
  try {
    const u = _tenantUsersCache[tenantId]?.find(x => x.uid === uid);
    await HRMGMT.setTenantUserStatus({ uid, gcipTenantId: u?.gcipTenantId, disabled: disable });
    toast(`User ${disable ? 'suspended' : 'reactivated'}`, disable ? 'info' : 'success');
    // Refresh cache
    if (u) { u.disabled = disable; u.status = disable ? 'suspended' : 'active'; }
    renderTenantUsersTab(tenantId);
  } catch(err) {
    toast(err.message || 'Failed to update user', 'error');
  }
}

// ── openUserProfile — slide-in drawer showing full user details ───────────────
function openUserProfile(uid, tenantId) {
  const u = _tenantUsersCache[tenantId]?.find(x => x.uid === uid);
  if (!u) return;

  const old = document.getElementById('user-profile-drawer');
  if (old) old.remove();

  const name = (u.displayName || ((u.firstName||'') + ' ' + (u.lastName||'')).trim()).trim() || u.email;
  const initials = name.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
  const avatarColor = u.disabled ? '#6b7280' : '#4ade80';

  const roleLabelMap = { full_admin:'Full Admin', admin:'Admin', manager:'Manager', employee:'Employee', custom:'Custom' };
  const roleLabel = roleLabelMap[u.role] || u.role;

  const fmtDate = iso => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-PG', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }); }
    catch { return iso.split('T')[0]; }
  };

  const drawer = document.createElement('div');
  drawer.id = 'user-profile-drawer';
  drawer.style.cssText =
    'position:fixed;inset:0;z-index:10000;display:flex;';

  drawer.innerHTML = `
    <!-- Backdrop -->
    <div style="flex:1;background:#0a0c0f;" onclick="document.getElementById('user-profile-drawer').remove()"></div>

    <!-- Panel -->
    <div style="width:380px;max-width:95vw;background:var(--bg1);border-left:1px solid var(--border);
      height:100%;overflow-y:auto;display:flex;flex-direction:column;animation:slideInRight 0.2s ease;">

      <!-- Header -->
      <div style="padding:20px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;background:var(--bg2);position:sticky;top:0;z-index:1;">
        <div style="font-family:'Syne',sans-serif;font-size:0.9rem;font-weight:700;">User Profile</div>
        <button onclick="document.getElementById('user-profile-drawer').remove()"
          style="background:var(--bg3);border:1px solid var(--border);color:var(--muted);cursor:pointer;
          width:28px;height:28px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:0.9rem;">
          &times;
        </button>
      </div>

      <!-- Avatar + name -->
      <div style="padding:24px 20px;border-bottom:1px solid var(--border);text-align:center;background:var(--bg2);">
        <div style="width:64px;height:64px;border-radius:16px;background:${avatarColor}22;color:${avatarColor};
          display:flex;align-items:center;justify-content:center;font-size:1.3rem;font-weight:700;
          margin:0 auto 12px;">${initials}</div>
        <div style="font-size:1rem;font-weight:700;margin-bottom:4px;">${name}</div>
        <div style="font-size:0.68rem;color:var(--muted);font-family:'DM Mono',monospace;margin-bottom:10px;">${u.email}</div>
        <div style="display:flex;align-items:center;justify-content:center;gap:8px;flex-wrap:wrap;">
          <span style="font-size:0.6rem;padding:3px 9px;border-radius:5px;
            background:${u.role==='full_admin'||u.role==='admin' ? 'rgba(96,165,250,0.15)' : u.role==='manager' ? 'rgba(251,191,36,0.15)' : 'rgba(74,222,128,0.12)'};
            color:${u.role==='full_admin'||u.role==='admin' ? 'var(--blue)' : u.role==='manager' ? 'var(--amber)' : 'var(--accent)'};
            border:1px solid ${u.role==='full_admin'||u.role==='admin' ? 'rgba(96,165,250,0.25)' : u.role==='manager' ? 'rgba(251,191,36,0.25)' : 'rgba(74,222,128,0.2)'};
            font-family:'DM Mono',monospace;font-weight:600;">${roleLabel}</span>
          <span style="font-size:0.6rem;padding:3px 9px;border-radius:5px;
            background:${u.disabled ? 'rgba(248,113,113,0.12)' : 'rgba(74,222,128,0.10)'};
            color:${u.disabled ? 'var(--red)' : 'var(--accent)'};
            border:1px solid ${u.disabled ? 'rgba(248,113,113,0.22)' : 'rgba(74,222,128,0.18)'};
            font-family:'DM Mono',monospace;">${u.disabled ? 'Suspended' : 'Active'}</span>
        </div>
      </div>

      <!-- Details -->
      <div style="padding:16px 20px;border-bottom:1px solid var(--border);">
        <div style="font-size:0.6rem;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:0.10em;margin-bottom:12px;font-family:'DM Mono',monospace;">Account Details</div>
        ${[
          ['UID', u.uid, true],
          ['Phone', u.phoneNumber || '—', false],
          ['Email Verified', u.emailVerified ? 'Yes' : 'No', false],
          ['Registered', fmtDate(u.createdAt), false],
          ['Last Login', fmtDate(u.lastLoginAt), false],
          ['Department', u.department || '—', false],
        ].map(([label, value, mono]) => `
          <div style="display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid rgba(255,255,255,0.04);gap:12px;">
            <span style="font-size:0.65rem;color:var(--muted);flex-shrink:0;">${label}</span>
            <span style="font-size:0.65rem;color:var(--text);text-align:right;word-break:break-all;${mono ? "font-family:'DM Mono',monospace;font-size:0.58rem;" : ''}">${value}</span>
          </div>
        `).join('')}
      </div>

      <!-- Quick actions -->
      <div style="padding:16px 20px;border-bottom:1px solid var(--border);">
        <div style="font-size:0.6rem;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:0.10em;margin-bottom:12px;font-family:'DM Mono',monospace;">Quick Actions</div>
        <div style="display:flex;flex-direction:column;gap:8px;">
          ${(u.role === 'full_admin' || u.role === 'admin') ? `
          <button onclick="sendUserPasswordReset('${u.uid}','${u.email}','${tenantId}')"
            style="width:100%;padding:9px 14px;background:rgba(96,165,250,0.07);border:1px solid rgba(96,165,250,0.2);
            color:var(--blue);border-radius:8px;font-size:0.75rem;font-weight:600;cursor:pointer;
            font-family:'DM Sans',sans-serif;text-align:left;display:flex;align-items:center;gap:8px;">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 2a4 4 0 100 8 4 4 0 000-8zM4 10s-2 1-2 3h12c0-2-2-3-2-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            Send Password Reset Email
          </button>` : ''}
          <button onclick="toggleTenantUserStatus('${u.uid}','${tenantId}',${!u.disabled});document.getElementById('user-profile-drawer').remove();"
            style="width:100%;padding:9px 14px;background:${u.disabled ? 'rgba(74,222,128,0.07)' : 'rgba(251,191,36,0.07)'};
            border:1px solid ${u.disabled ? 'rgba(74,222,128,0.2)' : 'rgba(251,191,36,0.2)'};
            color:${u.disabled ? 'var(--accent)' : 'var(--amber)'};border-radius:8px;font-size:0.75rem;font-weight:600;cursor:pointer;
            font-family:'DM Sans',sans-serif;text-align:left;display:flex;align-items:center;gap:8px;">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.4"/><path d="M6 6h.01M10 6h.01M6 10h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
            ${u.disabled ? 'Restore Account Access' : 'Suspend Account'}
          </button>
        </div>
      </div>

      <!-- Danger zone -->
      <div style="padding:16px 20px;margin-top:auto;">
        <div style="font-size:0.6rem;font-weight:700;color:var(--red);text-transform:uppercase;letter-spacing:0.10em;margin-bottom:12px;font-family:'DM Mono',monospace;opacity:0.7;">Danger Zone</div>
        <div style="display:flex;flex-direction:column;gap:8px;">
          <button onclick="confirmBlockUserEmail('${u.email}','${tenantId}')"
            style="width:100%;padding:9px 14px;background:rgba(248,113,113,0.05);border:1px solid rgba(248,113,113,0.18);
            color:rgba(248,113,113,0.75);border-radius:8px;font-size:0.75rem;font-weight:600;cursor:pointer;
            font-family:'DM Sans',sans-serif;text-align:left;display:flex;align-items:center;gap:8px;">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.4"/><path d="M4 12L12 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            Block &amp; Blacklist Email
          </button>
          <button onclick="confirmDeleteUser('${u.uid}','${u.email}','${tenantId}')"
            style="width:100%;padding:9px 14px;background:rgba(248,113,113,0.05);border:1px solid rgba(248,113,113,0.22);
            color:var(--red);border-radius:8px;font-size:0.75rem;font-weight:600;cursor:pointer;
            font-family:'DM Sans',sans-serif;text-align:left;display:flex;align-items:center;gap:8px;">
            <svg width="13" height="13" viewBox="0 0 20 20" fill="none"><path d="M3 6h14M8 6V4h4v2M9 10v5M11 10v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><rect x="4" y="6" width="12" height="11" rx="2" stroke="currentColor" stroke-width="1.4"/></svg>
            Delete User Account
          </button>
        </div>
      </div>
    </div>
  `;

  // Add slide animation
  if (!document.getElementById('user-profile-anim')) {
    const style = document.createElement('style');
    style.id = 'user-profile-anim';
    style.textContent = '@keyframes slideInRight { from { transform: translateX(100%); opacity:0; } to { transform: translateX(0); opacity:1; } }';
    document.head.appendChild(style);
  }

  document.body.appendChild(drawer);
}

// ── openUserActions — compact popover for quick destructive actions ────────────
function openUserActions(uid, tenantId, triggerEl) {
  const u = _tenantUsersCache[tenantId]?.find(x => x.uid === uid);
  if (!u) return;

  // Close any existing
  document.querySelectorAll('.user-actions-popover').forEach(p => p.remove());

  const pop = document.createElement('div');
  pop.className = 'user-actions-popover';
  pop.style.cssText =
    'position:fixed;background:var(--bg2);border:1px solid var(--border);border-radius:9px;' +
    'min-width:180px;box-shadow:0 12px 40px rgba(0,0,0,0.5);z-index:10001;overflow:hidden;';

  pop.innerHTML = `
    <button onclick="openUserProfile('${uid}','${tenantId}');this.closest('.user-actions-popover').remove();"
      style="width:100%;padding:10px 14px;background:none;border:none;border-bottom:1px solid var(--border);
      color:var(--text);cursor:pointer;font-size:0.73rem;text-align:left;display:flex;align-items:center;gap:8px;
      font-family:'DM Sans',sans-serif;">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 2a4 4 0 100 8 4 4 0 000-8zM4 10s-2 1-2 3h12c0-2-2-3-2-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
      View Profile
    </button>
    <button onclick="confirmBlockUserEmail('${u.email}','${tenantId}');this.closest('.user-actions-popover').remove();"
      style="width:100%;padding:10px 14px;background:none;border:none;border-bottom:1px solid var(--border);
      color:var(--amber);cursor:pointer;font-size:0.73rem;text-align:left;display:flex;align-items:center;gap:8px;
      font-family:'DM Sans',sans-serif;">
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.4"/><path d="M4 12L12 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      Block &amp; Blacklist Email
    </button>
    <button onclick="confirmDeleteUser('${uid}','${u.email}','${tenantId}');this.closest('.user-actions-popover').remove();"
      style="width:100%;padding:10px 14px;background:none;border:none;
      color:var(--red);cursor:pointer;font-size:0.73rem;text-align:left;display:flex;align-items:center;gap:8px;
      font-family:'DM Sans',sans-serif;">
      <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M3 6h14M8 6V4h4v2M9 10v5M11 10v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><rect x="4" y="6" width="12" height="11" rx="2" stroke="currentColor" stroke-width="1.4"/></svg>
      Delete User
    </button>
  `;

  // Position below trigger
  const rect = triggerEl.getBoundingClientRect();
  document.body.appendChild(pop);
  const popRect = pop.getBoundingClientRect();
  let top = rect.bottom + 6;
  let left = rect.right - popRect.width;
  if (top + popRect.height > window.innerHeight - 10) top = rect.top - popRect.height - 6;
  if (left < 8) left = 8;
  pop.style.top = top + 'px';
  pop.style.left = left + 'px';

  // Close on outside click
  const closer = (e) => {
    if (!pop.contains(e.target)) { pop.remove(); document.removeEventListener('click', closer); }
  };
  setTimeout(() => document.addEventListener('click', closer), 10);
}

// ── confirmBlockUserEmail — adds email + domain to globalRules blocklist ───────
async function confirmBlockUserEmail(email, tenantId) {
  const domain = email.split('@')[1] || '';
  const old = document.getElementById('block-email-overlay');
  if (old) old.remove();

  const overlay = document.createElement('div');
  overlay.id = 'block-email-overlay';
  overlay.style.cssText =
    'position:fixed;inset:0;background:#0a0c0f;display:flex;align-items:center;' +
    'justify-content:center;z-index:10002;padding:16px;';

  overlay.innerHTML = `
    <div style="background:var(--bg2);border:1px solid rgba(248,113,113,0.25);border-radius:14px;
      width:420px;max-width:100%;box-shadow:0 32px 80px rgba(0,0,0,0.8);overflow:hidden;">
      <div style="padding:18px 20px;border-bottom:1px solid rgba(248,113,113,0.15);background:rgba(248,113,113,0.04);">
        <div style="font-family:'Syne',sans-serif;font-size:0.95rem;font-weight:700;color:var(--red);">Block &amp; Blacklist</div>
        <div style="font-size:0.65rem;color:var(--muted);margin-top:2px;font-family:'DM Mono',monospace;">${email}</div>
      </div>
      <div style="padding:18px 20px;">
        <p style="font-size:0.72rem;color:var(--muted);line-height:1.65;margin-bottom:16px;">
          Adding to the platform blocklist prevents future sign-ups. Choose what to block:
        </p>
        <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:16px;">
          <label style="display:flex;align-items:center;gap:10px;padding:10px 12px;border:1px solid var(--border);
            border-radius:8px;cursor:pointer;font-size:0.73rem;color:var(--text);">
            <input type="checkbox" id="block-email-exact" checked style="accent-color:var(--red);width:14px;height:14px;"/>
            Block exact email: <span style="font-family:'DM Mono',monospace;color:var(--red);font-size:0.68rem;">${email}</span>
          </label>
          <label style="display:flex;align-items:center;gap:10px;padding:10px 12px;border:1px solid var(--border);
            border-radius:8px;cursor:pointer;font-size:0.73rem;color:var(--text);">
            <input type="checkbox" id="block-email-domain" style="accent-color:var(--red);width:14px;height:14px;"/>
            Block entire domain: <span style="font-family:'DM Mono',monospace;color:var(--amber);font-size:0.68rem;">@${domain}</span>
          </label>
        </div>
        <div id="block-email-err" style="display:none;font-size:0.72rem;color:var(--red);margin-bottom:10px;
          padding:8px 12px;background:rgba(248,113,113,0.07);border-radius:7px;border:1px solid rgba(248,113,113,0.2);"></div>
        <div style="display:flex;gap:8px;">
          <button id="block-email-btn" onclick="doBlockUserEmail('${email}','${domain}','${tenantId}')"
            style="flex:1;padding:10px;background:rgba(248,113,113,0.10);border:1px solid rgba(248,113,113,0.30);
            color:var(--red);border-radius:8px;font-size:0.78rem;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif;">
            Apply Blocklist
          </button>
          <button onclick="document.getElementById('block-email-overlay').remove()"
            style="padding:10px 16px;background:var(--bg3);border:1px solid var(--border);
            color:var(--muted);border-radius:8px;font-size:0.78rem;cursor:pointer;font-family:'DM Sans',sans-serif;">
            Cancel
          </button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
}

async function doBlockUserEmail(email, domain, tenantId) {
  const blockExact  = document.getElementById('block-email-exact')?.checked;
  const blockDomain = document.getElementById('block-email-domain')?.checked;
  const errDiv = document.getElementById('block-email-err');
  const btn = document.getElementById('block-email-btn');

  if (!blockExact && !blockDomain) {
    errDiv.textContent = 'Select at least one option.';
    errDiv.style.display = 'block';
    return;
  }
  btn.disabled = true;
  btn.textContent = 'Applying…';
  try {
    const rules = state.globalRules || {};
    const blockedEmails  = [...(rules.blockedEmails  || [])];
    const blockedDomains = [...(rules.blockedDomains || [])];

    if (blockExact  && !blockedEmails.includes(email.toLowerCase()))  blockedEmails.push(email.toLowerCase());
    if (blockDomain && !blockedDomains.includes(domain.toLowerCase())) blockedDomains.push(domain.toLowerCase());

    await HRMGMT.saveGlobalRules({ blockedEmails, blockedDomains });
    state.globalRules = { ...rules, blockedEmails, blockedDomains };

    const parts = [];
    if (blockExact)  parts.push(`email "${email}"`);
    if (blockDomain) parts.push(`domain "@${domain}"`);
    toast(`Blocked: ${parts.join(' and ')}`, 'success');
    document.getElementById('block-email-overlay').remove();
    document.getElementById('user-profile-drawer')?.remove();
  } catch(err) {
    errDiv.textContent = err.message || 'Failed to update blocklist';
    errDiv.style.display = 'block';
    btn.disabled = false;
    btn.textContent = 'Apply Blocklist';
  }
}

// ── confirmDeleteUser — delete GCIP auth user from tenant ─────────────────────
function confirmDeleteUser(uid, email, tenantId) {
  const old = document.getElementById('delete-user-overlay');
  if (old) old.remove();

  const overlay = document.createElement('div');
  overlay.id = 'delete-user-overlay';
  overlay.style.cssText =
    'position:fixed;inset:0;background:#0a0c0f;display:flex;align-items:center;' +
    'justify-content:center;z-index:10002;padding:16px;';

  overlay.innerHTML = `
    <div style="background:var(--bg2);border:1px solid rgba(248,113,113,0.25);border-radius:14px;
      width:400px;max-width:100%;box-shadow:0 32px 80px rgba(0,0,0,0.8);overflow:hidden;">
      <div style="padding:18px 20px;border-bottom:1px solid rgba(248,113,113,0.15);background:rgba(248,113,113,0.04);">
        <div style="font-family:'Syne',sans-serif;font-size:0.95rem;font-weight:700;color:var(--red);">Delete User Account</div>
        <div style="font-size:0.65rem;color:var(--muted);margin-top:2px;font-family:'DM Mono',monospace;">${email}</div>
      </div>
      <div style="padding:18px 20px;">
        <p style="font-size:0.72rem;color:var(--muted);line-height:1.65;margin-bottom:16px;">
          This permanently deletes the user's GCIP authentication account.
          Their HR records in the system remain unless deleted separately.
          <strong style="color:var(--red);">This cannot be undone.</strong>
        </p>
        <div id="delete-user-err" style="display:none;font-size:0.72rem;color:var(--red);margin-bottom:10px;
          padding:8px 12px;background:rgba(248,113,113,0.07);border-radius:7px;border:1px solid rgba(248,113,113,0.2);"></div>
        <div style="display:flex;gap:8px;">
          <button id="delete-user-btn" onclick="doDeleteUser('${uid}','${email}','${tenantId}')"
            style="flex:1;padding:10px;background:rgba(248,113,113,0.10);border:1px solid rgba(248,113,113,0.30);
            color:var(--red);border-radius:8px;font-size:0.78rem;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif;">
            Delete Permanently
          </button>
          <button onclick="document.getElementById('delete-user-overlay').remove()"
            style="padding:10px 16px;background:var(--bg3);border:1px solid var(--border);
            color:var(--muted);border-radius:8px;font-size:0.78rem;cursor:pointer;font-family:'DM Sans',sans-serif;">
            Cancel
          </button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
}

async function doDeleteUser(uid, email, tenantId) {
  const errDiv = document.getElementById('delete-user-err');
  const btn    = document.getElementById('delete-user-btn');
  btn.disabled = true;
  btn.textContent = 'Deleting…';
  try {
    const u = _tenantUsersCache[tenantId]?.find(x => x.uid === uid);
    await HRMGMT.setTenantUserStatus({ uid, gcipTenantId: u?.gcipTenantId, disabled: true });
    // Remove from local cache
    if (_tenantUsersCache[tenantId]) {
      _tenantUsersCache[tenantId] = _tenantUsersCache[tenantId].filter(x => x.uid !== uid);
    }
    toast(`User "${email}" deleted`, 'success');
    document.getElementById('delete-user-overlay').remove();
    document.getElementById('user-profile-drawer')?.remove();
    renderTenantUsersTab(tenantId);
  } catch(err) {
    errDiv.textContent = err.message || 'Delete failed';
    errDiv.style.display = 'block';
    btn.disabled = false;
    btn.textContent = 'Delete Permanently';
  }
}

// ══════════════════════════════════════════════════════════════
// PLATFORM TENANT USERS PAGE
// ══════════════════════════════════════════════════════════════

let _allUsersCache = [];
let _allUsersLoaded = false;

async function loadAllTenantUsers() {
  const tbody = document.getElementById('tu-tbody');
  const stats  = document.getElementById('tu-stats');
  if (tbody) tbody.innerHTML = '<tr><td colspan="7"><div style="padding:40px;text-align:center;color:var(--muted);">Loading users…</div></td></tr>';

  // Populate tenant filter
  const tenantSel = document.getElementById('tu-filter-tenant');
  if (tenantSel && tenantSel.options.length <= 1) {
    state.tenants.forEach(t => tenantSel.appendChild(new Option(t.name + ' (' + t.code + ')', t.id)));
  }

  try {
    const res = await HRMGMT.listAllTenantUsers({ limit: 500 });
    _allUsersCache = res.users || [];
    _allUsersLoaded = true;

    // Update badge
    const badge = document.getElementById('nb-tenant-users');
    if (badge) badge.textContent = _allUsersCache.length;

    renderAllTenantUsers();
  } catch(err) {
    if (tbody) tbody.innerHTML = `<tr><td colspan="7"><div style="padding:20px;color:var(--red);font-size:0.75rem;">${err.message || 'Failed to load users'}</div></td></tr>`;
  }
}

function filterTenantUsers() {
  if (!_allUsersLoaded) return;
  renderAllTenantUsers();
}

function renderAllTenantUsers() {
  const tbody  = document.getElementById('tu-tbody');
  const stats  = document.getElementById('tu-stats');
  if (!tbody) return;

  let users = [..._allUsersCache];

  const tenantFilter = document.getElementById('tu-filter-tenant')?.value || '';
  const roleFilter   = document.getElementById('tu-filter-role')?.value || '';
  const statusFilter = document.getElementById('tu-filter-status')?.value || '';
  const search       = (document.getElementById('tu-search')?.value || '').toLowerCase();

  if (tenantFilter) users = users.filter(u => u.tenantId === tenantFilter);
  if (roleFilter)   users = users.filter(u => roleFilter === 'admin' ? (u.role === 'admin' || u.role === 'full_admin') : u.role === roleFilter);
  if (statusFilter) users = users.filter(u => u.status === statusFilter);
  if (search)       users = users.filter(u =>
    u.email.toLowerCase().includes(search) ||
    (u.displayName || '').toLowerCase().includes(search)
  );

  // Stats strip
  if (stats) {
    const active    = users.filter(u => u.status === 'active').length;
    const suspended = users.filter(u => u.status === 'suspended').length;
    const admins    = users.filter(u => u.role === 'admin').length;
    const tenantSet = new Set(users.map(u => u.tenantId)).size;
    stats.innerHTML = [
      { label: 'Showing', val: users.length, color: 'var(--text)' },
      { label: 'Active',  val: active,    color: 'var(--accent)' },
      { label: 'Suspended', val: suspended, color: 'var(--red)' },
      { label: 'Admins',  val: admins,    color: 'var(--blue)' },
      { label: 'Tenants', val: tenantSet, color: 'var(--amber)' },
    ].map(s => `
      <div style="background:var(--bg2);border:1px solid var(--border);border-radius:8px;padding:8px 14px;display:flex;flex-direction:column;gap:2px;min-width:80px;">
        <div style="font-size:0.6rem;color:var(--muted);text-transform:uppercase;letter-spacing:0.08em;font-family:'DM Mono',monospace;">${s.label}</div>
        <div style="font-size:1.1rem;font-weight:700;font-family:'Syne',sans-serif;color:${s.color};">${s.val}</div>
      </div>`
    ).join('');
  }

  const roleBadge = (role) => {
    const map = { admin:'var(--blue)', full_admin:'var(--blue)', manager:'var(--amber)', employee:'var(--accent)' };
    const c = map[role] || 'var(--muted)';
    const label = role === 'full_admin' ? 'full admin' : role;
    return `<span style="font-size:0.6rem;padding:2px 7px;border-radius:4px;background:${c}18;color:${c};border:1px solid ${c}30;font-family:'DM Mono',monospace;">${label}</span>`;
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-PG', { day:'2-digit', month:'short', year:'numeric' }); }
    catch { return iso.split('T')[0]; }
  };

  const fmtRelative = (iso) => {
    if (!iso) return 'Never';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60)  return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)   return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30)  return `${days}d ago`;
    return fmtDate(iso);
  };

  if (users.length === 0) {
    tbody.innerHTML = '<tr><td colspan="7"><div style="padding:30px;text-align:center;color:var(--muted);">No users match the current filters.</div></td></tr>';
    return;
  }

  tbody.innerHTML = users.map(u => `
    <tr>
      <td>
        <div style="font-size:0.75rem;font-weight:500;">${u.displayName || u.email}</div>
        <div style="font-size:0.62rem;color:var(--muted);font-family:'DM Mono',monospace;">${u.email}</div>
        ${u.phoneNumber ? `<div style="font-size:0.6rem;color:var(--muted);">${u.phoneNumber}</div>` : ''}
      </td>
      <td>
        <div style="display:flex;align-items:center;gap:6px;">
          <div class="avatar" style="width:20px;height:20px;font-size:0.5rem;background:${u.tenantColor||'#6b7280'}22;color:${u.tenantColor||'#6b7280'};">${(u.tenantCode||'??').slice(0,2)}</div>
          <div>
            <div style="font-size:0.72rem;font-weight:500;">${u.tenantName}</div>
            <div style="font-size:0.6rem;color:var(--muted);">${u.tenantCode} · ${u.tenantPlan}</div>
          </div>
        </div>
      </td>
      <td>${roleBadge(u.role)}</td>
      <td>
        <span style="display:inline-flex;align-items:center;gap:5px;font-size:0.68rem;color:${u.status === 'active' ? 'var(--accent)' : 'var(--red)'};">
          <span style="width:6px;height:6px;border-radius:50%;background:${u.status === 'active' ? 'var(--accent)' : 'var(--red)'};display:inline-block;"></span>
          ${u.status}
        </span>
      </td>
      <td style="font-size:0.68rem;color:var(--muted);">${fmtDate(u.createdAt)}</td>
      <td style="font-size:0.68rem;color:var(--muted);">${fmtRelative(u.lastLoginAt)}</td>
      <td style="text-align:right;">
        <div class="tu-menu-wrap">
          <button class="tu-menu-btn" onclick="tuToggleMenu(event,'${u.uid}')">⋯</button>
          <div class="tu-menu" id="tu-menu-${u.uid}">
            <button class="tu-menu-item" onclick="tuGoTenant('${u.tenantId}')">
              <span>🏢</span> View Tenant
            </button>
            <button class="tu-menu-item" onclick="tuToggleRolePanel('${u.uid}')">
              <span>🎭</span> Change Role
            </button>
            <div class="tu-menu-sep"></div>
            <button class="tu-menu-item" onclick="tuResetPassword('${u.uid}','${u.tenantId}','${u.email}')">
              <span>🔑</span> Reset Password
            </button>
            <div class="tu-menu-sep"></div>
            <button class="tu-menu-item ${u.disabled ? 'success' : 'danger'}" onclick="toggleGlobalUserStatus('${u.uid}',${!u.disabled})">
              <span>${u.disabled ? '✅' : '🚫'}</span> ${u.disabled ? 'Reactivate' : 'Suspend'}
            </button>
            <div class="tu-menu-role-panel" id="tu-rp-${u.uid}">
              <div style="font-size:0.6rem;color:var(--muted);margin-bottom:5px;font-family:'DM Mono',monospace;text-transform:uppercase;letter-spacing:0.07em;">Assign role</div>
              <select id="tu-rp-sel-${u.uid}" onchange="tuRoleSelChange('${u.uid}')">
                <option value="employee" ${u.role==='employee'?'selected':''}>Employee</option>
                <option value="manager"  ${u.role==='manager' ?'selected':''}>Manager</option>
                <option value="full_admin" ${u.role==='full_admin'?'selected':''}>Full Admin</option>
                <option value="custom" ${u.role==='custom'?'selected':''}>Custom…</option>
              </select>
              <div id="tu-rp-custom-wrap-${u.uid}" style="display:${u.role==='custom'?'block':'none'};margin-bottom:6px;">
                <input id="tu-rp-custom-${u.uid}" placeholder="Custom role ID"
                  style="width:100%;box-sizing:border-box;padding:4px 8px;background:var(--bg2);border:1px solid var(--border);border-radius:5px;color:var(--text);font-size:0.68rem;"
                  value="${u.role==='custom'?(u.customRoleId||''):''}"/>
              </div>
              <div class="rp-row">
                <button class="rp-apply" onclick="tuApplyRole('${u.uid}','${u.gcipTenantId}')">Apply</button>
                <button class="rp-cancel" onclick="tuToggleRolePanel('${u.uid}')">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </td>
    </tr>
  `).join('');
}

async function toggleGlobalUserStatus(uid, disable) {
  try {
    const u = _allUsersCache.find(x => x.uid === uid);
    await HRMGMT.setTenantUserStatus({ uid, gcipTenantId: u?.gcipTenantId, disabled: disable });
    toast(`User ${disable ? 'suspended' : 'reactivated'}`, disable ? 'info' : 'success');
    if (u) { u.disabled = disable; u.status = disable ? 'suspended' : 'active'; }
    tuCloseAllMenus();
    renderAllTenantUsers();
  } catch(err) {
    toast(err.message || 'Failed to update user', 'error');
  }
}

// ── Tenant Users — dropdown menu helpers ─────────────────────

function tuCloseAllMenus() {
  document.querySelectorAll('.tu-menu.open').forEach(m => m.classList.remove('open'));
}

function tuToggleMenu(e, uid) {
  e.stopPropagation();
  const menu = document.getElementById('tu-menu-' + uid);
  if (!menu) return;
  const isOpen = menu.classList.contains('open');
  tuCloseAllMenus();
  if (!isOpen) {
    menu.classList.add('open');
    // Job menu relative to button
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const menuW = 210;
    let left = rect.right - menuW;
    if (left < 4) left = rect.left;
    menu.style.top  = (rect.bottom + 4) + 'px';
    menu.style.left = left + 'px';
    menu.style.width = menuW + 'px';
  }
}

function tuGoTenant(tenantId) {
  tuCloseAllMenus();
  openTenantDetail(tenantId);
  showPage('tenants', document.querySelector('.nav-btn[onclick*=tenants]'));
}

function tuToggleRolePanel(uid) {
  const panel = document.getElementById('tu-rp-' + uid);
  if (!panel) return;
  panel.classList.toggle('open');
}

function tuRoleSelChange(uid) {
  const sel = document.getElementById('tu-rp-sel-' + uid);
  const wrap = document.getElementById('tu-rp-custom-wrap-' + uid);
  if (wrap) wrap.style.display = sel?.value === 'custom' ? 'block' : 'none';
}

async function tuApplyRole(uid, gcipTenantId) {
  const sel = document.getElementById('tu-rp-sel-' + uid);
  const role = sel?.value;
  if (!role) { toast('Select a role', 'error'); return; }

  let customRoleId;
  if (role === 'custom') {
    customRoleId = document.getElementById('tu-rp-custom-' + uid)?.value.trim();
    if (!customRoleId) { toast('Enter a custom role ID', 'error'); return; }
  }

  const applyBtn = document.querySelector('#tu-rp-' + uid + ' .rp-apply');
  if (applyBtn) { applyBtn.disabled = true; applyBtn.textContent = 'Saving…'; }

  try {
    await HRMGMT.setTenantUserRole(uid, gcipTenantId, role, customRoleId);
    // Update local cache
    const u = _allUsersCache.find(x => x.uid === uid);
    if (u) {
      u.role = role;
      if (customRoleId) u.customRoleId = customRoleId;
    }
    const roleLabel = role === 'full_admin' ? 'Full Admin'
      : role === 'custom' ? `Custom [${customRoleId}]`
      : role.charAt(0).toUpperCase() + role.slice(1);
    logActivity('auth', `Role "${roleLabel}" assigned to ${uid}`, 'var(--cyan)');
    toast(`Role changed to ${roleLabel}`, 'success');
    tuCloseAllMenus();
    renderAllTenantUsers();
  } catch(err) {
    toast(err.message || 'Failed to change role', 'error');
    if (applyBtn) { applyBtn.disabled = false; applyBtn.textContent = 'Apply'; }
  }
}

async function tuResetPassword(uid, tenantId, email) {
  tuCloseAllMenus();
  if (!confirm(`Send a password reset email to ${email}?`)) return;
  try {
    await HRMGMT.sendTenantPasswordResetEmail(tenantId, email);
    toast(`Password reset email sent to ${email}`, 'success');
    logActivity('auth', `Password reset sent to ${email}`, 'var(--amber)');
  } catch(err) {
    toast(err.message || 'Failed to send reset email', 'error');
  }
}

// Close menus when clicking outside
document.addEventListener('click', function(e) {
  if (!e.target.closest('.tu-menu-wrap')) tuCloseAllMenus();
});

function exportTenantUsers() {
  if (!_allUsersCache.length) { toast('No users to export', 'info'); return; }
  const headers = ['Email','Display Name','Role','Status','Tenant','Tenant Code','Plan','Registered','Last Login'];
  const fmtDate = iso => iso ? new Date(iso).toISOString().split('T')[0] : '';
  const rows = _allUsersCache.map(u => [
    u.email, u.displayName || '', u.role, u.status,
    u.tenantName, u.tenantCode, u.tenantPlan,
    fmtDate(u.createdAt), fmtDate(u.lastLoginAt),
  ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const a = document.createElement('a');
  a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
  a.download = 'tenant-users-' + new Date().toISOString().split('T')[0] + '.csv';
  a.click();
}

// ══════════════════════════════════════════════════════════════
// BILLING METRICS
// ══════════════════════════════════════════════════════════════

async function loadBillingMetrics() {
  const body = document.getElementById('billing-metrics-body');
  if (!body) return;
  body.innerHTML = '<div style="padding:40px;text-align:center;color:var(--muted);">Loading metrics…</div>';
  const months = parseInt(document.getElementById('metrics-months')?.value || '6');
  try {
    const m = await HRMGMT.getBillingMetrics({ months });
    renderBillingMetrics(m);
  } catch(err) {
    body.innerHTML = `<div style="padding:20px;color:var(--red);font-size:0.75rem;">${err.message || 'Failed to load metrics'}</div>`;
  }
}

function renderBillingMetrics(m) {
  const body = document.getElementById('billing-metrics-body');
  if (!body) return;

  const K = v => 'K ' + (v||0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const pct = v => (v||0).toFixed(1) + '%';

  const planColors = { core:'var(--accent)', enterprise:'var(--blue)' };
  const statusColors = { draft:'var(--muted)', sent:'var(--blue)', partial:'var(--amber)', paid:'var(--accent)', overdue:'var(--red)', void:'rgba(107,114,128,0.4)' };

  // Monthly trend mini-chart (bars)
  const monthlyData = [...(m.byMonth||[])].reverse();
  const maxBilled = Math.max(...monthlyData.map(x => x.billed), 1);
  const trendBars = monthlyData.map(row => {
    const billedPct = Math.round((row.billed / maxBilled) * 100);
    const collPct   = Math.round((row.collected / maxBilled) * 100);
    return `
      <div style="display:flex;flex-direction:column;align-items:center;gap:4px;flex:1;min-width:0;">
        <div style="font-size:0.55rem;color:var(--muted);font-family:'DM Mono',monospace;writing-mode:vertical-rl;transform:rotate(180deg);white-space:nowrap;">${row.month}</div>
        <div style="job:relative;height:80px;width:100%;display:flex;align-items:flex-end;gap:2px;justify-content:center;">
          <div title="Billed: ${K(row.billed)}" style="width:45%;background:var(--blue-dim);border:1px solid var(--blue)33;border-radius:3px 3px 0 0;height:${billedPct}%;min-height:2px;"></div>
          <div title="Collected: ${K(row.collected)}" style="width:45%;background:var(--accent-dim);border:1px solid rgba(74,222,128,0.3);border-radius:3px 3px 0 0;height:${collPct}%;min-height:2px;"></div>
        </div>
        <div style="font-size:0.58rem;color:var(--muted);font-family:'DM Mono',monospace;">${row.count}</div>
      </div>
    `;
  }).join('');

  // Plan breakdown
  const planRows = Object.entries(m.byPlan||{}).map(([plan, data]) => {
    const c = planColors[plan] || 'var(--muted)';
    const rate = data.billed > 0 ? Math.round(data.collected / data.billed * 100) : 0;
    return `
      <div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--border);">
        <span style="font-size:0.62rem;padding:2px 8px;border-radius:4px;background:${c}18;color:${c};border:1px solid ${c}30;font-family:'DM Mono',monospace;min-width:70px;text-align:center;">${plan}</span>
        <div style="flex:1;">
          <div style="height:6px;background:var(--bg3);border-radius:3px;overflow:hidden;">
            <div style="height:100%;width:${rate}%;background:${c};border-radius:3px;transition:width 0.4s;"></div>
          </div>
        </div>
        <div style="text-align:right;min-width:90px;">
          <div style="font-size:0.72rem;font-family:'DM Mono',monospace;">${K(data.billed)}</div>
          <div style="font-size:0.6rem;color:var(--muted);">${rate}% collected · ${data.count} inv</div>
        </div>
        <div style="font-size:0.68rem;color:var(--muted);min-width:50px;text-align:right;">${m.tenantsByPlan?.[plan]||0} tenants</div>
      </div>
    `;
  }).join('') || '<div style="color:var(--muted);font-size:0.75rem;padding:8px 0;">No data</div>';

  // Status breakdown
  const statusRows = Object.entries(m.byStatus||{}).filter(([,v]) => v > 0).map(([status, count]) => {
    const c = statusColors[status] || 'var(--muted)';
    const total = Object.values(m.byStatus||{}).reduce((a,b)=>a+b,0);
    const p = total > 0 ? Math.round(count/total*100) : 0;
    return `
      <div style="display:flex;align-items:center;gap:8px;padding:6px 0;">
        <span style="width:8px;height:8px;border-radius:50%;background:${c};flex-shrink:0;"></span>
        <span style="font-size:0.68rem;flex:1;text-transform:capitalize;">${status}</span>
        <span style="font-size:0.68rem;font-family:'DM Mono',monospace;color:var(--muted);">${count} (${p}%)</span>
      </div>
    `;
  }).join('') || '<div style="color:var(--muted);font-size:0.75rem;padding:8px 0;">No data</div>';

  body.innerHTML = `
    <!-- KPI strip -->
    <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:12px;margin-bottom:20px;">
      ${[
        { label:'MRR', val: K(m.mrr), sub:'current month', color:'var(--accent)' },
        { label:'ARR (projected)', val: K(m.arr), sub:'MRR × 12', color:'var(--blue)' },
        { label:'Total Billed', val: K(m.totalBilled), sub:`${m.analysisMonths}mo window`, color:'var(--text)' },
        { label:'Total Collected', val: K(m.totalCollected), sub:`${m.collectionRate}% rate`, color:'var(--accent)' },
        { label:'Outstanding', val: K(m.totalOutstanding), sub:'unpaid balance', color:'var(--amber)' },
        { label:'Active Tenants', val: m.activeTenants, sub:'active + trial', color:'var(--cyan)' },
      ].map(kpi => `
        <div class="card card-pad" style="border-left:3px solid ${kpi.color};">
          <div style="font-size:0.58rem;color:var(--muted);text-transform:uppercase;letter-spacing:0.08em;font-family:'DM Mono',monospace;margin-bottom:4px;">${kpi.label}</div>
          <div style="font-family:'Syne',sans-serif;font-size:1.2rem;font-weight:700;color:${kpi.color};">${kpi.val}</div>
          <div style="font-size:0.6rem;color:var(--muted);margin-top:2px;">${kpi.sub}</div>
        </div>
      `).join('')}
    </div>

    <!-- Monthly trend -->
    <div class="card card-pad" style="margin-bottom:16px;">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;">
        <div style="font-family:'Syne',sans-serif;font-weight:700;font-size:0.85rem;">Monthly Trend</div>
        <div style="display:flex;gap:12px;font-size:0.62rem;color:var(--muted);">
          <span style="display:flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;background:var(--blue-dim);border:1px solid var(--blue)33;border-radius:2px;"></span>Billed</span>
          <span style="display:flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;background:var(--accent-dim);border:1px solid rgba(74,222,128,0.3);border-radius:2px;"></span>Collected</span>
        </div>
      </div>
      <div style="display:flex;gap:4px;align-items:flex-end;padding-bottom:4px;">
        ${trendBars || '<div style="color:var(--muted);font-size:0.75rem;">No data for this window</div>'}
      </div>
      <div style="font-size:0.6rem;color:var(--muted);margin-top:6px;text-align:center;">Number below bar = invoice count for that month</div>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
      <!-- Plan breakdown -->
      <div class="card card-pad">
        <div style="font-family:'Syne',sans-serif;font-weight:700;font-size:0.85rem;margin-bottom:12px;">By Plan</div>
        ${planRows}
      </div>

      <!-- Invoice status breakdown -->
      <div class="card card-pad">
        <div style="font-family:'Syne',sans-serif;font-weight:700;font-size:0.85rem;margin-bottom:12px;">Invoice Status Distribution</div>
        ${statusRows}
        <div style="margin-top:14px;padding-top:10px;border-top:1px solid var(--border);">
          <div style="font-size:0.62rem;color:var(--muted);margin-bottom:6px;text-transform:uppercase;letter-spacing:0.06em;font-family:'DM Mono',monospace;">Collection Rate</div>
          <div style="height:8px;background:var(--bg3);border-radius:4px;overflow:hidden;">
            <div style="height:100%;width:${m.collectionRate||0}%;background:var(--accent);border-radius:4px;transition:width 0.5s;"></div>
          </div>
          <div style="font-size:0.68rem;color:var(--accent);margin-top:4px;font-family:'DM Mono',monospace;">${m.collectionRate||0}%</div>
        </div>
      </div>
    </div>
  `;
}

// ── Register new window exports ───────────────────────────────
window.loadAllTenantUsers        = loadAllTenantUsers;
window.filterTenantUsers         = filterTenantUsers;
window.exportTenantUsers         = exportTenantUsers;
window.toggleGlobalUserStatus    = toggleGlobalUserStatus;
window.loadTenantUsersTab        = loadTenantUsersTab;
window.filterTenantUsersTab      = filterTenantUsersTab;
window.toggleTenantUserStatus    = toggleTenantUserStatus;
window.openUserProfile           = openUserProfile;
window.openUserActions           = openUserActions;
window.confirmBlockUserEmail     = confirmBlockUserEmail;
window.doBlockUserEmail          = doBlockUserEmail;
window.confirmDeleteUser         = confirmDeleteUser;
window.doDeleteUser              = doDeleteUser;
window.openProvisionAdminModal   = openProvisionAdminModal;
window.sendUserPasswordReset     = sendUserPasswordReset;
window.tuToggleMenu              = tuToggleMenu;
window.tuGoTenant                = tuGoTenant;
window.tuToggleRolePanel         = tuToggleRolePanel;
window.tuRoleSelChange           = tuRoleSelChange;
window.tuApplyRole               = tuApplyRole;
window.tuResetPassword           = tuResetPassword;
window.loadBillingMetrics        = loadBillingMetrics;
window.runBatchInvoices          = runBatchInvoices;

// ══════════════════════════════════════════════════════════════
// MODULE STUDIO
// ══════════════════════════════════════════════════════════════
//
// Architecture:
//   platform/modules/{moduleId}          — module definition doc
//   platform/refdata/{moduleId}/schema   — field schema for ref-data modules
//   platform/refdata/{moduleId}/entries  — collection of reference records
//   tenants/{id}/accessRules/{moduleId}  — per-tenant overrides (existing)
//
// The client app bootstraps by fetching platform/modules to get the full
// module list, feature flags, access rule schema, and for reference modules
// the full dataset from platform/refdata. Nothing is hardcoded client-side.
//
// Module types:
//   'behavioural'  — tenant creates/owns records (Leave, Employees, etc.)
//   'reference'    — platform owns canonical data, tenants read only
//                    (Public Service Ref, Pay Scale, Providers)
//   'config'       — tenant-scoped settings only, no records (Settings)

const MS = {
  // Local state
  modules: [],           // loaded from Firestore platform/modules
  selected: null,        // currently selected module id
  refEntries: {},        // { moduleId: { featureId: [records] } }
  dirty: false,
  // Schema config state — one entry per module
  // { status: 'missing'|'loaded'|'error', schema, fileName, version, featureCount }
  schemaState: {},
};

// ── REFERENCE MODULE DEFINITIONS (what the platform knows about each module) ──
// These mirror what would be stored in Firestore platform/modules.
// The Module Studio lets the super-admin view and edit these.
const MS_MODULE_TYPES = {
  behavioural: { label: 'Behavioural', color: 'var(--accent)', desc: 'Tenants create and own records. Platform defines the schema and access rules only.' },
  reference:   { label: 'Reference Dataset', color: 'var(--blue)',   desc: 'Platform owns the canonical data. Tenants get read-only access. All data managed here.' },
  config:      { label: 'Configuration', color: 'var(--amber)',  desc: 'Tenant-scoped settings only. No records stored. Controls app behaviour.' },
};

// Static enrichment of the MODULES array with type + access rule schema
const MS_MODULE_META = {
  'employees':          { type: 'behavioural', clientPath: 'tenants/{tid}/employees' },
  'leave':              { type: 'behavioural', clientPath: 'tenants/{tid}/leave' },
  'performance':        { type: 'behavioural', clientPath: 'tenants/{tid}/performance' },
  'training':           { type: 'behavioural', clientPath: 'tenants/{tid}/training' },
  'providers':          { type: 'reference',   clientPath: 'platform/refdata/providers/entries' },
  'discipline':         { type: 'behavioural', clientPath: 'tenants/{tid}/discipline' },
  'documents':          { type: 'behavioural', clientPath: 'tenants/{tid}/documents' },
  'tasks':              { type: 'behavioural', clientPath: 'tenants/{tid}/tasks' },
  'messaging':          { type: 'behavioural', clientPath: 'tenants/{tid}/messages' },
  'succession':         { type: 'behavioural', clientPath: 'tenants/{tid}/succession' },
  'recruitment':        { type: 'behavioural', clientPath: 'tenants/{tid}/recruitment' },
  'competency':         { type: 'behavioural', clientPath: 'tenants/{tid}/competency' },
  'payroll':            { type: 'behavioural', clientPath: 'tenants/{tid}/payroll' },
  'payscale':           { type: 'config',      clientPath: 'tenants/{tid}/payscale' },
  'gesi-compliance':    { type: 'behavioural', clientPath: 'tenants/{tid}/gesi' },
  'settings':           { type: 'config',      clientPath: 'tenants/{tid}/settings' },
  'public-service-ref': { type: 'reference',   clientPath: 'platform/refdata/public-service-ref/entries' },
};

// Reference dataset schemas — what fields each reference module's records have
const MS_REF_SCHEMAS = {
  'public-service-ref': {
    'general-orders': {
      label: 'General Orders',
      desc: 'PNG General Orders — numbered clauses of the Public Service code',
      fields: [
        { id: 'section',    label: 'Section Number', type: 'text',     required: true,  placeholder: 'e.g. 1.1, 3.4.2' },
        { id: 'title',      label: 'Title',          type: 'text',     required: true,  placeholder: 'e.g. Conditions of Employment' },
        { id: 'chapter',    label: 'Chapter',        type: 'text',     required: false, placeholder: 'e.g. Chapter 3' },
        { id: 'content',    label: 'Content',        type: 'textarea', required: true,  placeholder: 'Full text of the general order…' },
        { id: 'category',   label: 'Category',       type: 'select',   required: false,
          options: ['Leave & Absence', 'Conduct & Discipline', 'Recruitment & Selection', 'Remuneration', 'Training & Development', 'Termination', 'Other'] },
        { id: 'effectiveDate', label: 'Effective Date', type: 'date',  required: false },
        { id: 'status',     label: 'Status',         type: 'select',   required: true,
          options: ['Active', 'Superseded', 'Draft'] },
      ],
    },
    'regulations': {
      label: 'Regulations',
      desc: 'Public Service regulations, acts, and statutory instruments',
      fields: [
        { id: 'refNumber',  label: 'Reference Number', type: 'text',     required: true,  placeholder: 'e.g. PSM Act s.47' },
        { id: 'title',      label: 'Title',             type: 'text',     required: true,  placeholder: 'e.g. Appointment of Officers' },
        { id: 'instrument', label: 'Legal Instrument',  type: 'text',     required: false, placeholder: 'e.g. Public Services (Management) Act 1995' },
        { id: 'content',    label: 'Summary / Text',    type: 'textarea', required: true,  placeholder: 'Regulation content or summary…' },
        { id: 'status',     label: 'Status',            type: 'select',   required: true,
          options: ['In Force', 'Amended', 'Repealed', 'Draft'] },
      ],
    },
    'gesi-policy': {
      label: 'GESI Policy',
      desc: 'Papua New Guinea Gender Equality & Social Inclusion (GESI) policy framework — commitments, principles and mandates',
      csvTemplate: 'policyArea,obligation,legalBasis,target,indicator,reportingFrequency,responsibleUnit,status',
      fields: [
        { id: 'policyArea',   label: 'Policy Area',   type: 'select',   required: true,
          options: ['Gender Equality', "Women's Empowerment", 'Social Inclusion', 'Disability Inclusion', 'Youth Inclusion', 'Rural & Remote Access', 'Indigenous Rights', 'Anti-Discrimination', 'Sexual Harassment Prevention', 'Family & Sexual Violence'] },
        { id: 'obligation',   label: 'Obligation / Commitment',   type: 'textarea', required: true,  placeholder: 'Describe the specific GESI obligation or commitment…' },
        { id: 'legalBasis',   label: 'Legal / Policy Basis',      type: 'text',     required: true,  placeholder: 'e.g. PNG Constitution s.55, CEDAW Art.11, DSIP Gender Policy 2023' },
        { id: 'target',       label: 'Target / Benchmark',        type: 'textarea', required: false, placeholder: 'e.g. 30% women in leadership jobs by 2030' },
        { id: 'indicator',    label: 'Measurement Indicator',     type: 'textarea', required: false, placeholder: 'How is compliance or progress measured?' },
        { id: 'reportingFrequency', label: 'Reporting Frequency', type: 'select',   required: false,
          options: ['Monthly', 'Quarterly', 'Bi-annually', 'Annually', 'Ad-hoc'] },
        { id: 'responsibleUnit',    label: 'Responsible Unit',    type: 'text',     required: false, placeholder: 'e.g. HR Division, Gender Focal Point' },
        { id: 'status',       label: 'Status',                    type: 'select',   required: true,
          options: ['Active', 'Under Review', 'Draft', 'Superseded'] },
      ],
    },
    'gesi-standards': {
      label: 'GESI Standards',
      desc: 'GESI implementation standards, minimum requirements and benchmarks for PNG public sector compliance',
      csvTemplate: 'standardCode,standardName,dimension,requirement,complianceLevel,verificationMethod,linkedPolicy,effectiveDate,status',
      fields: [
        { id: 'standardCode', label: 'Standard Code',       type: 'text',     required: true,  placeholder: 'e.g. GESI-STD-001' },
        { id: 'standardName', label: 'Standard Name',       type: 'text',     required: true,  placeholder: 'e.g. Workforce Gender Balance Standard' },
        { id: 'dimension',    label: 'GESI Dimension',      type: 'select',   required: true,
          options: ['Gender Equality', 'Social Inclusion', 'Disability', 'Youth', 'Geographic Equity', 'Intersectionality'] },
        { id: 'requirement',  label: 'Minimum Requirement', type: 'textarea', required: true,  placeholder: 'Describe the minimum standard organisations must meet…' },
        { id: 'complianceLevel', label: 'Compliance Level', type: 'select',   required: true,
          options: ['Mandatory', 'Best Practice', 'Aspirational'] },
        { id: 'verificationMethod', label: 'Verification Method', type: 'textarea', required: false, placeholder: 'e.g. annual audit, self-assessment, staff survey' },
        { id: 'linkedPolicy', label: 'Linked Policy Area',  type: 'text',     required: false, placeholder: 'e.g. Gender Equality, Sexual Harassment Prevention' },
        { id: 'effectiveDate', label: 'Effective Date',     type: 'date',     required: false },
        { id: 'status',       label: 'Status',              type: 'select',   required: true,
          options: ['Active', 'Under Review', 'Draft', 'Retired'] },
      ],
    },
    'gesi-tools': {
      label: 'GESI Tools',
      desc: 'Assessment checklists, templates and tools for GESI compliance and implementation in PNG public sector organisations',
      csvTemplate: 'toolCode,toolName,toolType,purpose,usage,targetAudience,frequency,linkedStandard,status',
      fields: [
        { id: 'toolCode',     label: 'Tool Code',     type: 'text',     required: true,  placeholder: 'e.g. GESI-TOOL-001' },
        { id: 'toolName',     label: 'Tool Name',     type: 'text',     required: true,  placeholder: 'e.g. Gender Audit Checklist' },
        { id: 'toolType',     label: 'Tool Type',     type: 'select',   required: true,
          options: ['Self-Assessment Checklist', 'Audit Tool', 'Policy Template', 'Training Guide', 'Data Collection Form', 'Reporting Template', 'Action Plan Template', 'Grievance Form', 'Survey Instrument'] },
        { id: 'purpose',      label: 'Purpose',       type: 'textarea', required: true,  placeholder: 'What is this tool designed to achieve?' },
        { id: 'usage',        label: 'How to Use',    type: 'textarea', required: false, placeholder: 'Instructions for using this tool…' },
        { id: 'targetAudience', label: 'Target Audience', type: 'select', required: false,
          options: ['HR Officers', 'Department Heads', 'GESI Focal Points', 'All Staff', 'Executive Leadership', 'External Auditors'] },
        { id: 'frequency',    label: 'Recommended Frequency', type: 'select', required: false,
          options: ['One-off', 'Monthly', 'Quarterly', 'Annually', 'At Recruitment', 'At Onboarding', 'As Required'] },
        { id: 'linkedStandard', label: 'Linked Standard', type: 'text', required: false, placeholder: 'e.g. GESI-STD-003' },
        { id: 'status',       label: 'Status',        type: 'select',   required: true,
          options: ['Active', 'Draft', 'Archived'] },
      ],
    },
  },
  'providers': {
    'directory': {
      label: 'Training Providers',
      desc: 'Approved training and development providers',
      fields: [
        { id: 'name',       label: 'Provider Name',  type: 'text',   required: true },
        { id: 'type',       label: 'Type',           type: 'select', required: true,
          options: ['University', 'Technical College', 'TVET Provider', 'NGO', 'Government Body', 'Private Trainer', 'International'] },
        { id: 'location',   label: 'Location',       type: 'text',   required: false, placeholder: 'e.g. Port Moresby, NCD' },
        { id: 'province',   label: 'Province',       type: 'select', required: false,
          options: ['NCD', 'Central', 'Eastern Highlands', 'Western Highlands', 'Morobe', 'Madang', 'East New Britain', 'West New Britain', 'Manus', 'New Ireland', 'Milne Bay', 'Gulf', 'Western', 'Sandaun', 'East Sepik', 'Southern Highlands', 'Hela', 'Jiwaka', 'Autonomous Region of Bougainville'] },
        { id: 'contact',    label: 'Contact',        type: 'text',   required: false },
        { id: 'email',      label: 'Email',          type: 'text',   required: false },
        { id: 'phone',      label: 'Phone',          type: 'text',   required: false },
        { id: 'accredited', label: 'NAQAB Accredited', type: 'select', required: false, options: ['Yes', 'No', 'Pending'] },
        { id: 'status',     label: 'Status',         type: 'select', required: true, options: ['Active', 'Inactive'] },
      ],
    },
  },
};

// Access rule schema per module (full — expands on MODULE_ACCESS_RULES)
const MS_ACCESS_RULE_TYPES = {
  role:   { label: 'Role Gate',   desc: 'Minimum role required', icon: '🎭' },
  bool:   { label: 'Toggle',      desc: 'On/off feature flag',   icon: '⚡' },
  number: { label: 'Numeric Cap', desc: 'Numeric limit',         icon: '🔢' },
};

// ══════════════════════════════════════════════════════════════
// MODULE MANAGEMENT — unified rail + detail panel
// Replaces: Module Studio (MS.*), old Access Matrix, Bulk Edit
// ══════════════════════════════════════════════════════════════

const modState = {
  selected:    null,   // current module id
  view:        'platform', // 'platform' | 'tenant'
  activeTab:   'features',
  filter:      'all',
  schemaState: {},     // moduleId → { status, version, featureCount, savedAt, pendingSchema? }
};

// Access rule schema (platform defaults)
const MOD_ACCESS_RULES = {
  employees: {
    exportPermission:    { type:'role',   default:'admin',   label:'Export employee records',       desc:'Minimum role to export employee data as CSV' },
    sensitiveFields:     { type:'role',   default:'admin',   label:'View sensitive fields',          desc:'Who can see Salary, National ID, and Passport details' },
    structureEditing:    { type:'role',   default:'admin',   label:'Edit org structure',             desc:'Who can create, rename, or delete divisions and jobs' },
    redundancyAuthority: { type:'role',   default:'admin',   label:'Make redundant / reinstate',     desc:'Who can mark employees as redundant or reinstate them' },
  },
  leave: {
    approvalAuthority:    { type:'role',   default:'manager', label:'Approve leave requests',         desc:'Minimum role that can approve or reject leave' },
    balanceEditing:       { type:'role',   default:'admin',   label:'Edit leave balances',            desc:'Who can manually adjust leave balances' },
    allowBackdated:       { type:'bool',   default:false,     label:'Allow backdated requests',       desc:'Whether leave can be submitted for past dates' },
    maxSingleRequestDays: { type:'number', default:30,        label:'Max single request (days)',      desc:'Maximum days per leave request (0 = unlimited)' },
  },
  discipline: {
    caseCreation:     { type:'role', default:'admin', label:'Open discipline cases',    desc:'Who can open a new discipline case' },
    caseVisibility:   { type:'role', default:'admin', label:'View discipline cases',    desc:'Who can see discipline records' },
    outcomeRecording: { type:'role', default:'admin', label:'Record case outcomes',     desc:'Who can close a case and record the outcome' },
  },
  performance: {
    appraisalCreation: { type:'role', default:'manager', label:'Create appraisals',     desc:'Who can open a new appraisal for an employee' },
    allowSelfRating:   { type:'bool', default:false,     label:'Allow self-rating',     desc:'Whether employees can submit their own appraisal rating' },
    ratingVisibility:  { type:'role', default:'admin',   label:'View ratings',          desc:'Who can see completed appraisal ratings' },
    kpiEditing:        { type:'role', default:'manager', label:'Edit KPI targets',      desc:'Who can set or change KPI targets' },
  },
  training: {
    selfNomination:     { type:'bool', default:false,   label:'Allow self-nomination',  desc:'Whether employees can nominate themselves for training' },
    shortlistAuthority: { type:'role', default:'admin', label:'Shortlist nominations',  desc:'Who can shortlist or reject training nominations' },
    approvalAuthority:  { type:'role', default:'admin', label:'Approve nominations',    desc:'Who can give final approval for training nominations' },
    budgetVisibility:   { type:'role', default:'admin', label:'View training budget',   desc:'Who can see training cost and budget figures' },
  },
  messaging: {
    broadcastAuthority: { type:'role', default:'admin', label:'Send broadcast messages', desc:'Who can send messages to groups of employees' },
    smsAuthority:       { type:'role', default:'admin', label:'Send SMS / WhatsApp',     desc:'Minimum role required to send SMS or WhatsApp' },
    requireMessageLog:  { type:'bool', default:true,   label:'Require message logging', desc:'All sent messages are permanently logged for audit' },
  },
  documents: {
    deleteAuthority:    { type:'role', default:'admin', label:'Delete documents',              desc:'Who can permanently delete documents' },
    confidentialAccess: { type:'role', default:'admin', label:'Access confidential documents', desc:'Who can view or upload documents flagged as confidential' },
    templateEditing:    { type:'role', default:'admin', label:'Edit document templates',       desc:'Who can create or modify document templates' },
  },
  recruitment: {
    vacancyPosting:  { type:'role', default:'admin', label:'Post vacancies',         desc:'Who can create and publish job advertisements' },
    applicantAccess: { type:'role', default:'admin', label:'View applicant data',    desc:'Who can see applicant records and personal details' },
    offerAuthority:  { type:'role', default:'admin', label:'Generate offer letters', desc:'Who can issue employment offer letters' },
  },
  succession: {
    editAccess:          { type:'role', default:'admin', label:'Edit succession plans',       desc:'Who can modify succession pipelines and candidates' },
    candidateVisibility: { type:'role', default:'admin', label:'View succession candidates',  desc:'Who can see who is on the succession list' },
  },
  competency: {
    frameworkEditing:    { type:'role',   default:'admin',   label:'Edit competency library',       desc:'Who can add, edit or delete competency definitions' },
    assessmentAuthority: { type:'role',   default:'manager', label:'Rate employee competencies',    desc:'Minimum role that can submit a manager competency rating' },
    allowSelfAssessment: { type:'bool',   default:false,     label:'Allow self-assessment',         desc:'Whether employees can submit their own proficiency ratings' },
    gapVisibility:       { type:'role',   default:'manager', label:'View gap analysis',             desc:'Who can see the gap analysis and role matrix reports' },
    matrixVisibility:    { type:'role',   default:'manager', label:'View competency matrix',        desc:'Who can see the competency × employee matrix' },
  },
  payroll: {
    runAuthority:       { type:'role', default:'admin',   label:'Execute pay runs',       desc:'Who can trigger a payroll run'                       },
    approvalAuthority:  { type:'role', default:'admin',   label:'Approve pay periods',    desc:'Who can approve and lock a completed pay period'     },
    payslipVisibility:  { type:'role', default:'manager', label:'View all payslips',      desc:'Who can see payslips for employees other than themselves' },
    settingsEditing:    { type:'role', default:'admin',   label:'Edit payroll settings',  desc:'Who can modify tax brackets and super configuration' },
  },
  'gesi-compliance': {
    targetEditing:      { type:'role', default:'admin',   label:'Edit GESI targets',      desc:'Who can set or update the organisation GESI targets'  },
    reportAccess:       { type:'role', default:'manager', label:'View GESI reports',      desc:'Who can view the GESI scorecard and metrics'          },
  },
};

// ── Init ──────────────────────────────────────────────────────
function modInitSchemaState() {
  MODULES.forEach(m => {
    if (!modState.schemaState[m.id]) {
      modState.schemaState[m.id] = { status: 'missing', schema: null, version: null, featureCount: 0 };
    }
  });
}

async function modLoadSchemaState() {
  modInitSchemaState();
  try {
    const res = await HRMGMT.listModuleSchemas();
    if (res && Array.isArray(res.schemas)) {
      res.schemas.forEach(s => {
        modState.schemaState[s.moduleId] = {
          status:       'loaded',
          schema:       null,
          version:      s.schemaVersion,
          featureCount: s.featureCount,
          savedAt:      s.savedAt,
          savedByEmail: s.savedByEmail,
          serverOnly:   true,
        };
      });
    }
  } catch(e) {
    console.warn('[ModMgmt] listModuleSchemas failed:', e.message);
  }
}

// ── Rail ──────────────────────────────────────────────────────
function modFilterRail() {
  modRenderRail(document.getElementById('mod-search')?.value?.toLowerCase() || '');
}

function modSetFilter(group, btn) {
  modState.filter = group;
  document.querySelectorAll('.mod-filter-pill').forEach(p => p.classList.remove('active'));
  if (btn) btn.classList.add('active');
  modRenderRail();
}

function modRenderRail(searchQ) {
  const q   = searchQ !== undefined ? searchQ : (document.getElementById('mod-search')?.value?.toLowerCase() || '');
  const el  = document.getElementById('mod-rail-list');
  if (!el) return;

  let mods = MODULES.slice();
  if (modState.filter !== 'all') mods = mods.filter(m => m.group === modState.filter);
  if (q) mods = mods.filter(m => m.name.toLowerCase().includes(q) || m.id.includes(q));

  if (!mods.length) {
    el.innerHTML = '<div style="padding:20px;text-align:center;font-size:0.75rem;color:var(--muted);">No modules found</div>';
    return;
  }

  const groups = {};
  mods.forEach(m => { if (!groups[m.group]) groups[m.group] = []; groups[m.group].push(m); });

  el.innerHTML = Object.entries(groups).map(([group, items]) => `
    <div class="mod-rail-group">${group}</div>
    ${items.map(m => {
      const tenantCount = state.tenants.filter(t => t.modules && t.modules[m.id] && t.modules[m.id].enabled).length;
      const sch = modState.schemaState[m.id] || {};
      const dotCls = sch.status === 'loaded' ? 'published' : sch.status === 'draft' ? 'draft' : '';
      return `
        <div class="mod-item ${modState.selected === m.id ? 'active' : ''}" onclick="modSelectModule('${m.id}')">
          <div class="mod-item-icon" style="background:${modState.selected===m.id ? m.color+'22' : 'var(--bg4)'};">${m.icon}</div>
          <div class="mod-item-body">
            <div class="mod-item-name">${m.name}</div>
            <div class="mod-item-sub">${m.features ? m.features.length : 0} features · ${tenantCount}/${state.tenants.length} tenants</div>
          </div>
          <div class="mod-item-dot ${dotCls}" title="${sch.status === 'loaded' ? 'Schema deployed' : 'No schema'}"></div>
        </div>`;
    }).join('')}
  `).join('');

  // Update badge
  const badge = document.getElementById('mod-count-badge');
  if (badge) badge.textContent = MODULES.length + ' modules';
}

// ── Module selection ──────────────────────────────────────────
function modSelectModule(id) {
  modState.selected = id;
  modRenderRail();

  const m = MODULES.find(x => x.id === id);
  if (!m) return;

  document.getElementById('mod-detail-empty').style.display  = 'none';
  const content = document.getElementById('mod-detail-content');
  content.style.display = 'flex';

  // Header
  const icon = document.getElementById('mod-dh-icon');
  icon.textContent = m.icon;
  icon.style.background = m.color + '22';
  document.getElementById('mod-dh-name').textContent = m.name;
  document.getElementById('mod-dh-desc').textContent = m.desc;

  const typeLabel = { behavioural:'Behavioural', reference:'Reference', config:'Config' }[m.type] || m.type || 'Behavioural';
  const typeCls   = 'type-' + (m.type || 'behavioural');
  const tenantCount = state.tenants.filter(t => t.modules && t.modules[m.id] && t.modules[m.id].enabled).length;
  const adoptCls  = tenantCount === 0 ? 'mod-adopt-0' : '';
  document.getElementById('mod-dh-tags').innerHTML = `
    <span class="mod-dh-tag ${typeCls}">${typeLabel}</span>
    <span class="mod-dh-tag">${m.group}</span>
    ${m.alwaysOn ? '<span class="mod-dh-tag" style="border-color:rgba(74,222,128,0.3);color:var(--accent);">always on</span>' : ''}
    <span class="mod-dh-tag" style="font-family:'DM Mono',monospace;">id: ${m.id}</span>
    <span class="mod-dh-tag mod-adopt-badge ${adoptCls}">${tenantCount} tenant${tenantCount===1?'':'s'}</span>
  `;

  modRenderView();
}

function modSetView(view, btn) {
  modState.view = view;
  document.querySelectorAll('.mod-vt-btn').forEach(b => b.classList.remove('active'));
  if (btn) btn.classList.add('active');
  // Reset to sensible default tab per view
  modState.activeTab = view === 'platform' ? 'features' : 'tenant-access';
  modRenderView();
}

function modRenderView() {
  const m = MODULES.find(x => x.id === modState.selected);
  if (!m) return;
  if (modState.view === 'platform') modRenderPlatformView(m);
  else modRenderTenantView(m);
}

// ── Platform view ─────────────────────────────────────────────
function modRenderPlatformView(m) {
  const tabs = [
    { id:'features',     label:'Features' },
    { id:'access-rules', label:'Access Rules' },
    { id:'schema',       label:'Schema Config' },
    { id:'definition',   label:'Definition' },
  ];
  const validTab = tabs.find(t => t.id === modState.activeTab) ? modState.activeTab : 'features';
  modState.activeTab = validTab;

  document.getElementById('mod-detail-tabs').innerHTML = tabs.map(t => `
    <button class="mod-dt-btn ${modState.activeTab === t.id ? 'active' : ''}"
      onclick="modSetTab('${t.id}',this)">${t.label}</button>
  `).join('');

  modRenderTab(m, modState.activeTab);
}

function modSetTab(tabId, btn) {
  modState.activeTab = tabId;
  document.querySelectorAll('.mod-dt-btn').forEach(b => b.classList.remove('active'));
  if (btn) btn.classList.add('active');
  const m = MODULES.find(x => x.id === modState.selected);
  if (m) modRenderTab(m, tabId);
}

function modRenderTab(m, tabId) {
  const body = document.getElementById('mod-detail-body');
  if (!body) return;
  if (tabId === 'features')     body.innerHTML = modRenderFeaturesPane(m);
  if (tabId === 'access-rules') body.innerHTML = modRenderAccessRulesPane(m);
  if (tabId === 'schema')       body.innerHTML = modRenderSchemaPane(m);
  if (tabId === 'definition')   body.innerHTML = modRenderDefinitionPane(m);
}

function modRenderFeaturesPane(m) {
  if (!m.features || !m.features.length)
    return '<p style="font-size:0.75rem;color:var(--muted);">No configurable features.</p>';
  return `
    <div class="sec-hdr" style="margin-top:0;">Features (${m.features.length})</div>
    <div class="mod-info-callout">
      <div class="mod-info-icon">ⓘ</div>
      <div class="mod-info-text">These are <strong>platform-wide</strong> defaults. Per-tenant feature overrides are in the <strong>Tenants</strong> view.</div>
    </div>
    <div class="mod-feature-list">
      ${m.features.map((f, i) => `
        <div class="mod-feature-row">
          <div class="mod-fr-icon">${String(i+1).padStart(2,'0')}</div>
          <div class="mod-fr-body">
            <div class="mod-fr-name">${f.name}</div>
            <div class="mod-fr-desc">${f.desc}</div>
          </div>
          <code class="mod-fr-id">${f.id}</code>
          <label class="toggle">
            <input type="checkbox" checked onchange="toast('Platform feature default updated','success')">
            <span class="toggle-slider"></span>
          </label>
        </div>
      `).join('')}
    </div>`;
}

function modRenderAccessRulesPane(m) {
  const rules   = MOD_ACCESS_RULES[m.id] || {};
  const entries = Object.entries(rules);
  if (!entries.length) return `
    <div class="sec-hdr" style="margin-top:0;">Access Rules</div>
    <p style="font-size:0.75rem;color:var(--muted);margin-bottom:12px;">No access rules defined for this module.</p>
    <button class="btn btn-ghost btn-sm" onclick="toast('Access rule editing via definition — use the Definition tab','info')">+ Add via Definition tab</button>`;

  return `
    <div class="sec-hdr" style="margin-top:0;">Access Rules — Platform Defaults (${entries.length})</div>
    <div class="mod-info-callout">
      <div class="mod-info-icon">ⓘ</div>
      <div class="mod-info-text">These are the <strong>default values</strong> applied when a tenant first enables this module. Per-tenant overrides are in <strong>Tenants → Tenant Rules</strong>.</div>
    </div>
    <div class="mod-rule-list">
      ${entries.map(([ruleId, rule]) => `
        <div class="mod-rule-row">
          <div class="mod-rr-info">
            <div class="mod-rr-label">${rule.label}</div>
            <div class="mod-rr-desc">${rule.desc}</div>
            <span class="mod-rr-type">${rule.type==='role'?'🎭 role gate':rule.type==='bool'?'⚡ toggle':'🔢 numeric cap'}</span>
          </div>
          <div class="mod-rr-control">
            ${rule.type === 'role' ? `
              <select class="mod-rr-select" onchange="toast('Default updated','success')">
                <option value="admin"    ${rule.default==='admin'   ?'selected':''}>Admin</option>
                <option value="manager"  ${rule.default==='manager' ?'selected':''}>Manager</option>
                <option value="employee" ${rule.default==='employee'?'selected':''}>Employee</option>
              </select>` :
            rule.type === 'bool' ? `
              <label class="toggle">
                <input type="checkbox" ${rule.default?'checked':''} onchange="toast('Default updated','success')">
                <span class="toggle-slider"></span>
              </label>` : `
              <input type="number" class="mod-rr-number" value="${rule.default}" min="0"
                onchange="toast('Default updated','success')">`}
          </div>
        </div>`).join('')}
    </div>
    <div style="margin-top:12px;">
      <button class="btn btn-primary btn-sm" onclick="modSaveAccessDefaults('${m.id}')">Save Defaults</button>
    </div>`;
}

async function modSaveAccessDefaults(moduleId) {
  try {
    toast('Access rule defaults saved', 'success');
    logActivity('module', `Access rule defaults updated for ${moduleId}`, 'var(--blue)');
  } catch(e) { toast(e.message || 'Save failed', 'error'); }
}

function modRenderSchemaPane(m) {
  const sch      = modState.schemaState[m.id] || { status: 'missing' };
  const isLoaded = sch.status === 'loaded';
  const isDraft  = sch.status === 'draft';

  return `
    <div class="sec-hdr" style="margin-top:0;">Schema Config</div>
    <div class="mod-schema-status">
      <div class="mod-schema-dot ${isLoaded ? 'loaded' : isDraft ? 'draft' : ''}"></div>
      <div class="mod-schema-info">
        <div class="mod-schema-title">${isLoaded ? `Schema deployed — v${sch.version}` : isDraft ? 'Schema ready to deploy (draft)' : 'No schema deployed'}</div>
        <div class="mod-schema-sub">${isLoaded ? `${sch.featureCount} feature${sch.featureCount===1?'':'s'} · saved ${sch.savedAt||''}${sch.savedByEmail?' by '+sch.savedByEmail:''}` : isDraft ? `${sch.featureCount} features detected — click Deploy` : 'Upload a schema JSON file to enable write-time validation'}</div>
      </div>
      <div class="mod-schema-actions">
        ${isLoaded||isDraft ? `<button class="btn btn-ghost btn-xs" onclick="modDownloadSchema('${m.id}')">Export</button>` : ''}
        ${isLoaded ? `<button class="btn btn-danger btn-xs" onclick="modClearSchema('${m.id}')">Remove</button>` : ''}
      </div>
    </div>

    <div class="card card-pad" style="margin-bottom:14px;">
      <label class="form-label">Upload Schema (hrms_schema_${m.id}.json)</label>
      <div style="display:flex;gap:8px;align-items:center;margin-top:6px;">
        <input type="file" accept=".json" id="sch-file-${m.id}" style="display:none;"
          onchange="modSchemaFileSelected('${m.id}',this)"/>
        <button class="btn btn-ghost btn-sm" onclick="document.getElementById('sch-file-${m.id}').click()">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M2 11v2h12v-2M8 2v8M5 5l3-3 3 3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          Choose File
        </button>
        <span id="sch-fname-${m.id}" style="font-size:0.68rem;color:var(--muted);">No file chosen</span>
      </div>
      <div style="font-size:0.62rem;color:var(--muted);margin-top:6px;">
        The schema must have <code style="font-family:'DM Mono',monospace;">"moduleId": "${m.id}"</code> at the root.
      </div>
    </div>

    ${isLoaded ? `
      <div class="sec-hdr">Deployed Features</div>
      <div class="mod-feature-list">
        ${(m.features||[]).slice(0, sch.featureCount||999).map(f => `
          <div class="mod-feature-row" style="padding:8px 14px;">
            <div class="mod-fr-icon" style="color:var(--accent);border-color:rgba(74,222,128,0.3);">✓</div>
            <div class="mod-fr-body"><div class="mod-fr-name" style="font-size:0.72rem;">${f.name}</div></div>
            <code class="mod-fr-id">${f.id}</code>
          </div>`).join('')}
      </div>` : ''}

    <div style="margin-top:16px;display:flex;gap:8px;">
      <button class="btn btn-primary btn-sm" id="mod-deploy-btn-${m.id}" onclick="modDeploySchema('${m.id}')">
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none"><path d="M8 2v9M4 7l4-5 4 5M3 13h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        ${isLoaded ? 'Re-deploy Schema' : 'Deploy Schema'}
      </button>
    </div>`;
}

function modSchemaFileSelected(moduleId, input) {
  const file = input.files[0];
  if (!file) return;
  const nameEl = document.getElementById(`sch-fname-${moduleId}`);
  if (nameEl) nameEl.textContent = file.name;

  const reader = new FileReader();
  reader.onload = e => {
    try {
      const schema = JSON.parse(e.target.result);
      if (schema.moduleId !== moduleId) {
        toast(`Schema moduleId "${schema.moduleId}" does not match "${moduleId}"`, 'error');
        return;
      }
      modState.schemaState[moduleId] = {
        status:       'draft',
        schema,
        version:      schema.schemaVersion || 1,
        featureCount: schema.features ? Object.keys(schema.features).length : 0,
      };
      toast(`Schema loaded — ${Object.keys(schema.features||{}).length} features detected`, 'info');
      modRenderTab(MODULES.find(x => x.id === moduleId), 'schema');
    } catch(err) {
      toast('Invalid JSON: ' + err.message, 'error');
    }
  };
  reader.readAsText(file);
}

async function modDeploySchema(moduleId) {
  const sch = modState.schemaState[moduleId] || {};
  if (!sch.schema && sch.status !== 'loaded') { toast('Upload a schema file first', 'error'); return; }

  const btn = document.getElementById(`mod-deploy-btn-${moduleId}`);
  if (btn) { btn.disabled = true; btn.textContent = 'Deploying…'; }

  try {
    await HRMGMT.saveModuleSchema({ moduleId, schema: sch.schema || sch });
    const now = new Date().toISOString().split('T')[0];
    modState.schemaState[moduleId] = {
      status:       'loaded',
      schema:       null,
      version:      (sch.version || 0) + 1,
      featureCount: sch.featureCount,
      savedAt:      now,
      serverOnly:   true,
    };
    toast(`Schema deployed for ${moduleId}`, 'success');
    logActivity('module', `Schema deployed: ${moduleId} v${modState.schemaState[moduleId].version}`, 'var(--purple)');
    modRenderTab(MODULES.find(x => x.id === moduleId), 'schema');
    modRenderRail();
  } catch(e) {
    toast(e.message || 'Deploy failed', 'error');
  } finally {
    if (btn) { btn.disabled = false; }
  }
}

function modClearSchema(moduleId) {
  if (!confirm(`Remove schema for ${moduleId}? Write-time validation will be disabled.`)) return;
  modState.schemaState[moduleId] = { status: 'missing', schema: null };
  toast('Schema removed', 'info');
  modRenderTab(MODULES.find(x => x.id === moduleId), 'schema');
  modRenderRail();
}

function modDownloadSchema(moduleId) {
  const sch = modState.schemaState[moduleId];
  if (sch && sch.schema) {
    const blob = new Blob([JSON.stringify(sch.schema, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `hrms_schema_${moduleId}.json`; a.click();
  } else {
    toast('Schema not available locally — was loaded from server', 'info');
  }
}

function modRenderDefinitionPane(m) {
  const isLocked = !!m.alwaysOn;
  const typeOpts = ['behavioural','reference','config'].map(t =>
    `<option value="${t}" ${(m.type||'behavioural')===t?'selected':''}>${t.charAt(0).toUpperCase()+t.slice(1)}</option>`).join('');
  const groupOpts = ['People','Development','Operations','Administration','Government','Other'].map(g =>
    `<option value="${g}" ${m.group===g?'selected':''}>${g}</option>`).join('');
  const colorOpts = [
    ['var(--accent)','Green'], ['var(--blue)','Blue'], ['var(--purple)','Purple'],
    ['var(--cyan)','Cyan'], ['var(--amber)','Amber'], ['var(--red)','Red'], ['var(--muted)','Grey']
  ].map(([v,l]) => `<option value="${v}" ${m.color===v?'selected':''}>${l}</option>`).join('');

  return `
    ${isLocked ? `
      <div class="mod-info-callout" style="border-color:rgba(74,222,128,0.3);background:var(--accent-dim);margin-top:0;">
        <div class="mod-info-icon">🔒</div>
        <div class="mod-info-text" style="color:var(--accent);">Employees is a foundational module and cannot be removed or disabled. Its definition can be edited here but the module is always active.</div>
      </div>` : ''}
    <div class="sec-hdr" ${isLocked?'':'style="margin-top:0;"'}>Module Definition</div>
    <div class="form-row r3">
      <div class="form-group">
        <label class="form-label">Module ID</label>
        <input class="form-input" value="${m.id}" ${isLocked?'disabled':''} style="font-family:'DM Mono',monospace;font-size:0.72rem;" id="def-id-${m.id}"/>
        <div class="form-hint">Immutable slug — changing requires migration.</div>
      </div>
      <div class="form-group">
        <label class="form-label">Display Name</label>
        <input class="form-input" value="${m.name}" id="def-name-${m.id}"/>
      </div>
      <div class="form-group">
        <label class="form-label">Icon (emoji)</label>
        <input class="form-input" value="${m.icon}" id="def-icon-${m.id}" style="font-size:1.1rem;"/>
      </div>
    </div>
    <div class="form-group">
      <label class="form-label">Description</label>
      <textarea class="form-input" id="def-desc-${m.id}" style="min-height:64px;">${m.desc}</textarea>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Group</label>
        <select class="form-input" id="def-group-${m.id}">${groupOpts}</select>
      </div>
      <div class="form-group">
        <label class="form-label">Module Type</label>
        <select class="form-input" id="def-type-${m.id}">${typeOpts}</select>
        <div class="form-hint">Behavioural = HR actions · Reference = read-only data · Config = system settings</div>
      </div>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Color</label>
        <select class="form-input" id="def-color-${m.id}">${colorOpts}</select>
      </div>
      <div class="form-group">
        <label class="form-label">Client Data Path</label>
        <input class="form-input" value="${m.clientPath||''}" id="def-path-${m.id}"
          placeholder="e.g. tenants/{tenantId}/leave" style="font-family:'DM Mono',monospace;font-size:0.7rem;"/>
      </div>
    </div>
    <div style="display:flex;gap:8px;margin-top:6px;align-items:center;flex-wrap:wrap;">
      <button class="btn btn-primary btn-sm" onclick="modSaveDefinition('${m.id}')">Save Definition</button>
      ${!isLocked ? `<button class="btn btn-danger btn-sm" onclick="modDeleteModule('${m.id}')">Delete Module</button>` : ''}
      <span style="font-size:0.65rem;color:var(--muted);">Changes take effect after <strong style="color:var(--text);">Publish All</strong></span>
    </div>`;
}

async function modSaveDefinition(moduleId) {
  const name      = document.getElementById(`def-name-${moduleId}`)?.value?.trim();
  const icon      = document.getElementById(`def-icon-${moduleId}`)?.value?.trim();
  const desc      = document.getElementById(`def-desc-${moduleId}`)?.value?.trim();
  const group     = document.getElementById(`def-group-${moduleId}`)?.value;
  const type      = document.getElementById(`def-type-${moduleId}`)?.value;
  const color     = document.getElementById(`def-color-${moduleId}`)?.value;
  const clientPath= document.getElementById(`def-path-${moduleId}`)?.value?.trim();

  if (!name) { toast('Name is required', 'error'); return; }

  try {
    await HRMGMT.saveModuleDefinition({ moduleId, name, icon, desc, group, type, color, clientPath,
      features: MODULES.find(x=>x.id===moduleId)?.features || [],
      accessRules: MOD_ACCESS_RULES[moduleId] || {} });
    toast('Definition saved — click Publish All to push to clients', 'success');
    logActivity('module', `Module definition saved: ${moduleId}`, 'var(--blue)');
  } catch(e) {
    toast(e.message || 'Save failed', 'error');
  }
}

async function modDeleteModule(moduleId) {
  if (!confirm(`Delete module "${moduleId}"? This cannot be undone and removes it from all clients on next publish.`)) return;
  try {
    await HRMGMT.deleteModuleDefinition({ moduleId });
    toast('Module deleted', 'info');
    logActivity('module', `Module deleted: ${moduleId}`, 'var(--red)');
    modState.selected = null;
    document.getElementById('mod-detail-empty').style.display = '';
    document.getElementById('mod-detail-content').style.display = 'none';
    modRenderRail();
  } catch(e) {
    toast(e.message || 'Delete failed', 'error');
  }
}

// ── Tenant view ───────────────────────────────────────────────
function modRenderTenantView(m) {
  const hasRules = !!(MOD_ACCESS_RULES[m.id] && Object.keys(MOD_ACCESS_RULES[m.id]).length);
  const tabs = [
    { id:'tenant-access', label:'Tenant Access' },
    ...(hasRules ? [{ id:'tenant-rules', label:'Tenant Rules' }] : []),
  ];
  const validTab = tabs.find(t => t.id === modState.activeTab) ? modState.activeTab : 'tenant-access';
  modState.activeTab = validTab;

  document.getElementById('mod-detail-tabs').innerHTML = tabs.map(t => `
    <button class="mod-dt-btn ${modState.activeTab === t.id ? 'active' : ''}"
      onclick="modSetTab('${t.id}',this)">${t.label}</button>
  `).join('');

  modRenderTenantTab(m, modState.activeTab);
}

function modRenderTenantTab(m, tabId) {
  const body = document.getElementById('mod-detail-body');
  if (!body) return;
  if (tabId === 'tenant-access') body.innerHTML = modRenderTenantAccessPane(m);
  if (tabId === 'tenant-rules')  body.innerHTML = modRenderTenantRulesPane(m);
}

function modRenderTenantAccessPane(m) {
  const enabled = state.tenants.filter(t => t.modules && t.modules[m.id] && t.modules[m.id].enabled).length;
  const total   = state.tenants.length;

  return `
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:16px;">
      <div class="mod-stat-chip"><span class="mod-stat-val" style="color:var(--accent);">${enabled}</span><span style="color:var(--muted);">enabled</span></div>
      <div class="mod-stat-chip"><span class="mod-stat-val" style="color:var(--muted);">${total-enabled}</span><span style="color:var(--muted);">disabled</span></div>
      <div class="mod-stat-chip"><span class="mod-stat-val">${total}</span><span style="color:var(--muted);">total</span></div>
    </div>
    <div class="mod-tenant-bar">
      <input class="mod-tenant-search" id="mod-tsearch-${m.id}"
        placeholder="Filter tenants…" oninput="modFilterTenants('${m.id}')"/>
      <span class="mod-tenant-count" id="mod-tcount-${m.id}">${total} tenants</span>
      <div class="mod-tenant-presets">
        <button class="mod-preset-btn" onclick="modSetAllTenants('${m.id}',true)">Enable all</button>
        <button class="mod-preset-btn" onclick="modSetAllTenants('${m.id}',false)">Disable all</button>
      </div>
    </div>
    <div class="mod-tenant-grid" id="mod-tgrid-${m.id}">
      ${state.tenants.map(t => {
        const on = !!(t.modules && t.modules[m.id] && t.modules[m.id].enabled);
        const canToggle = !m.alwaysOn;
        return `
          <div class="mod-tenant-row" data-tid="${t.id}" data-tname="${(t.name||'').toLowerCase()}">
            <div class="mod-tr-avatar" style="background:${t.color||'#4ade80'};">${(t.code||'?').slice(0,3)}</div>
            <div class="mod-tr-body">
              <div class="mod-tr-name">${t.name}</div>
              <div class="mod-tr-meta">
                <span class="mod-tr-plan-badge">${t.plan||'core'}</span>
                <span>${t.status||'active'}</span>
              </div>
            </div>
            <div class="mod-tr-controls">
              <button class="mod-feat-expand-btn" id="mfeb-${t.id}-${m.id}"
                ${!on?'disabled style="opacity:0.4;"':''}
                onclick="modToggleFeatExpand('${t.id}','${m.id}',this)">Features</button>
              <label class="toggle" title="${m.alwaysOn?'Cannot disable foundational module':''}">
                <input type="checkbox" ${on?'checked':''} ${!canToggle?'disabled':''}
                  onchange="modToggleTenantModule('${t.id}','${m.id}',this.checked)">
                <span class="toggle-slider"></span>
              </label>
            </div>
          </div>
          <div class="mod-tenant-features-row" id="mfrow-${t.id}-${m.id}">
            <div style="font-family:'DM Mono',monospace;font-size:0.6rem;color:var(--muted);margin-bottom:6px;text-transform:uppercase;letter-spacing:0.06em;">Feature toggles — ${t.name}</div>
            <div class="mod-tf-grid">
              ${(m.features||[]).map(f => {
                const fon = !(t.modules && t.modules[m.id] && t.modules[m.id].features && t.modules[m.id].features[f.id] === false);
                return `<div class="mod-tf-chip ${fon?'on':'off'}" id="mtf-${t.id}-${m.id}-${f.id}"
                  onclick="modToggleTenantFeature('${t.id}','${m.id}','${f.id}',this)">${f.name}</div>`;
              }).join('')}
            </div>
          </div>`;
      }).join('')}
    </div>`;
}

function modFilterTenants(moduleId) {
  const q = (document.getElementById(`mod-tsearch-${moduleId}`)?.value||'').toLowerCase();
  const rows = document.querySelectorAll(`#mod-tgrid-${moduleId} .mod-tenant-row`);
  let vis = 0;
  rows.forEach(row => {
    const name = row.getAttribute('data-tname') || '';
    const show = !q || name.includes(q);
    row.style.display = show ? '' : 'none';
    const tid  = row.getAttribute('data-tid');
    const frow = document.getElementById(`mfrow-${tid}-${moduleId}`);
    if (frow) frow.style.display = show && frow.classList.contains('open') ? '' : 'none';
    if (show) vis++;
  });
  const cnt = document.getElementById(`mod-tcount-${moduleId}`);
  if (cnt) cnt.textContent = `${vis} tenant${vis===1?'':'s'}`;
}

function modToggleFeatExpand(tenantId, moduleId, btn) {
  const row = document.getElementById(`mfrow-${tenantId}-${moduleId}`);
  if (!row) return;
  const open = row.classList.toggle('open');
  btn.classList.toggle('open', open);
  btn.textContent = open ? 'Hide' : 'Features';
}

async function modToggleTenantModule(tenantId, moduleId, enabled) {
  const t = state.tenants.find(x => x.id === tenantId);
  if (!t) return;
  if (!t.modules) t.modules = {};
  if (!t.modules[moduleId]) t.modules[moduleId] = { enabled, features: {} };
  t.modules[moduleId].enabled = enabled;

  // Update features button
  const btn = document.getElementById(`mfeb-${tenantId}-${moduleId}`);
  if (btn) {
    btn.disabled = !enabled;
    btn.style.opacity = enabled ? '' : '0.4';
    if (!enabled) {
      btn.classList.remove('open');
      const frow = document.getElementById(`mfrow-${tenantId}-${moduleId}`);
      if (frow) { frow.classList.remove('open'); frow.style.display = 'none'; }
    }
  }

  try {
    await HRMGMT.toggleTenantModule({ tenantId, moduleId, enabled });
    toast(`${moduleId} ${enabled?'enabled':'disabled'} for ${t.name}`, enabled?'success':'info');
    logActivity('module', `Module "${moduleId}" ${enabled?'enabled':'disabled'} for ${t.name}`, enabled?'var(--accent)':'var(--red)');
    modRenderRail();
  } catch(e) {
    // Revert on error
    t.modules[moduleId].enabled = !enabled;
    toast(e.message || 'Toggle failed', 'error');
  }
}

async function modToggleTenantFeature(tenantId, moduleId, featureId, chip) {
  const t = state.tenants.find(x => x.id === tenantId);
  if (!t) return;
  if (!t.modules) t.modules = {};
  if (!t.modules[moduleId]) t.modules[moduleId] = { enabled: true, features: {} };
  if (!t.modules[moduleId].features) t.modules[moduleId].features = {};

  const newOn = !chip.classList.contains('on');
  t.modules[moduleId].features[featureId] = newOn;
  chip.classList.toggle('on',  newOn);
  chip.classList.toggle('off', !newOn);

  try {
    await HRMGMT.toggleTenantModule({ tenantId, moduleId, featureId, enabled: newOn });
    const fname = MODULES.find(x=>x.id===moduleId)?.features?.find(f=>f.id===featureId)?.name || featureId;
    toast(`${fname}: ${newOn?'on':'off'} for ${t.name}`, 'success');
  } catch(e) {
    // Revert
    t.modules[moduleId].features[featureId] = !newOn;
    chip.classList.toggle('on',  !newOn);
    chip.classList.toggle('off', newOn);
    toast(e.message || 'Failed', 'error');
  }
}

async function modSetAllTenants(moduleId, enabled) {
  const m = MODULES.find(x => x.id === moduleId);
  if (m && m.alwaysOn && !enabled) { toast('Cannot disable foundational module', 'error'); return; }

  try {
    // Update each tenant optimistically
    state.tenants.forEach(t => {
      if (!t.modules) t.modules = {};
      if (!t.modules[moduleId]) t.modules[moduleId] = { enabled, features: {} };
      t.modules[moduleId].enabled = enabled;
    });

    // Fire API for each tenant in parallel
    await Promise.all(state.tenants.map(t =>
      HRMGMT.toggleTenantModule({ tenantId: t.id, moduleId, enabled }).catch(e =>
        console.warn(`[modSetAllTenants] ${t.id}:`, e.message))
    ));

    toast(`${moduleId} ${enabled?'enabled':'disabled'} for all tenants`, 'success');
    logActivity('module', `Module "${moduleId}" ${enabled?'enabled':'disabled'} for all tenants`, 'var(--amber)');
    modRenderView();
    modRenderRail();
  } catch(e) {
    toast(e.message || 'Bulk update failed', 'error');
  }
}

function modRenderTenantRulesPane(m) {
  const rules   = MOD_ACCESS_RULES[m.id] || {};
  const entries = Object.entries(rules);
  const eligible = state.tenants.filter(t => t.modules && t.modules[m.id] && t.modules[m.id].enabled);

  return `
    <div class="sec-hdr" style="margin-top:0;">Per-Tenant Access Rule Overrides</div>
    <div class="mod-info-callout">
      <div class="mod-info-icon">ⓘ</div>
      <div class="mod-info-text">Override the platform defaults for a specific tenant. Only tenants with this module enabled are shown.</div>
    </div>
    <div class="form-group">
      <label class="form-label">Select Tenant</label>
      <select class="form-input" style="max-width:320px;"
        onchange="modRenderTenantRuleValues('${m.id}',this.value)" id="mod-trule-sel-${m.id}">
        <option value="">Choose tenant…</option>
        ${eligible.map(t => `<option value="${t.id}">${t.name} (${t.code||''})</option>`).join('')}
      </select>
    </div>
    <div id="mod-trule-vals-${m.id}">
      <p style="font-size:0.72rem;color:var(--muted);">Select a tenant above to configure their access rules.</p>
    </div>`;
}

async function modRenderTenantRuleValues(moduleId, tenantId) {
  const container = document.getElementById(`mod-trule-vals-${moduleId}`);
  if (!container) return;
  if (!tenantId) { container.innerHTML = '<p style="font-size:0.72rem;color:var(--muted);">Select a tenant above.</p>'; return; }

  const rules   = MOD_ACCESS_RULES[moduleId] || {};
  const entries = Object.entries(rules);
  if (!entries.length) { container.innerHTML = '<p style="font-size:0.72rem;color:var(--muted);">No access rules for this module.</p>'; return; }

  // Try to load existing overrides
  let overrides = {};
  try {
    const res = await HRMGMT.getModuleAccessRules({ tenantId, moduleId });
    overrides = (res && res[moduleId]) || {};
  } catch(e) { /* use defaults */ }

  container.innerHTML = `
    <div class="mod-rule-list">
      ${entries.map(([ruleId, rule]) => {
        const val = overrides[ruleId] !== undefined ? overrides[ruleId] : rule.default;
        return `
          <div class="mod-rule-row">
            <div class="mod-rr-info">
              <div class="mod-rr-label">${rule.label}</div>
              <div class="mod-rr-desc">${rule.desc}</div>
              <span class="mod-rr-type">platform default: ${rule.default}</span>
            </div>
            <div class="mod-rr-control">
              ${rule.type === 'role' ? `
                <select class="mod-rr-select" id="tr-${tenantId}-${moduleId}-${ruleId}">
                  <option value="admin"    ${val==='admin'   ?'selected':''}>Admin</option>
                  <option value="manager"  ${val==='manager' ?'selected':''}>Manager</option>
                  <option value="employee" ${val==='employee'?'selected':''}>Employee</option>
                </select>` :
              rule.type === 'bool' ? `
                <label class="toggle">
                  <input type="checkbox" id="tr-${tenantId}-${moduleId}-${ruleId}" ${val?'checked':''}>
                  <span class="toggle-slider"></span>
                </label>` : `
                <input type="number" class="mod-rr-number" id="tr-${tenantId}-${moduleId}-${ruleId}" value="${val}" min="0">`}
            </div>
          </div>`;
      }).join('')}
    </div>
    <div style="margin-top:12px;display:flex;gap:8px;">
      <button class="btn btn-primary btn-sm"
        onclick="modSaveTenantRules('${moduleId}','${tenantId}')">Save for This Tenant</button>
      <button class="btn btn-ghost btn-sm"
        onclick="modResetTenantRules('${moduleId}','${tenantId}')">Reset to Defaults</button>
    </div>`;
}

async function modSaveTenantRules(moduleId, tenantId) {
  const rules   = MOD_ACCESS_RULES[moduleId] || {};
  const payload = {};
  Object.entries(rules).forEach(([ruleId, rule]) => {
    const el = document.getElementById(`tr-${tenantId}-${moduleId}-${ruleId}`);
    if (!el) return;
    payload[ruleId] = rule.type === 'bool' ? el.checked : rule.type === 'number' ? Number(el.value) : el.value;
  });
  try {
    await HRMGMT.saveModuleAccessRules({ tenantId, rules: { [moduleId]: payload } });
    toast('Tenant access rules saved', 'success');
    logActivity('module', `Access rules updated for ${state.tenants.find(t=>t.id===tenantId)?.name||tenantId} / ${moduleId}`, 'var(--blue)');
  } catch(e) { toast(e.message || 'Save failed', 'error'); }
}

async function modResetTenantRules(moduleId, tenantId) {
  if (!confirm('Reset tenant rules to platform defaults?')) return;
  try {
    const rules   = MOD_ACCESS_RULES[moduleId] || {};
    const payload = {};
    Object.entries(rules).forEach(([ruleId, rule]) => { payload[ruleId] = rule.default; });
    await HRMGMT.saveModuleAccessRules({ tenantId, rules: { [moduleId]: payload } });
    toast('Rules reset to defaults', 'info');
    await modRenderTenantRuleValues(moduleId, tenantId);
  } catch(e) { toast(e.message || 'Reset failed', 'error'); }
}

// ── Publish / seed ────────────────────────────────────────────
async function modPublishAll() {
  if (!confirm('Publish all module definitions to the live platform?\nClient apps receive changes on next login or refresh.')) return;
  const btn = document.getElementById('mod-publish-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Publishing…'; }
  try {
    const res = await HRMGMT.publishModuleDefinitions();
    toast(`Published ${res.published} module definitions`, 'success');
    logActivity('module', `Module definitions published: ${res.published} modules`, 'var(--accent)');
  } catch(e) {
    toast(e.message || 'Publish failed', 'error');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = `<svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M8 2v9M4 7l4-5 4 5M3 13h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Publish All`;
    }
  }
}

async function modSeedBuiltins() {
  if (!confirm(`Seed Firestore with ${MODULES.length} built-in module definitions?\nExisting definitions with the same ID will be overwritten.`)) return;
  toast(`Seeding ${MODULES.length} definitions…`, 'info');
  let saved = 0, failed = 0;
  for (const m of MODULES) {
    try {
      await HRMGMT.saveModuleDefinition({
        moduleId:   m.id, name: m.name, icon: m.icon, desc: m.desc,
        group:      m.group, color: m.color,
        type:       m.type || 'behavioural',
        clientPath: m.clientPath || '',
        features:   m.features || [],
        accessRules: MOD_ACCESS_RULES[m.id] || {},
      });
      saved++;
    } catch(e) { console.error(`[Seed] ${m.id}:`, e.message); failed++; }
  }
  toast(failed === 0 ? `Seeded ${saved} definitions` : `Seeded ${saved} — ${failed} failed`, failed===0?'success':'error');
  logActivity('module', `Module definitions seeded: ${saved} saved`, 'var(--purple)');
  modRenderRail();
}

// ── Expose to window (called from onclick in HTML) ─────────────
window.modFilterRail         = modFilterRail;
window.modSetFilter          = modSetFilter;
window.modSelectModule       = modSelectModule;
window.modSetView            = modSetView;
window.modSetTab             = modSetTab;
window.modSchemaFileSelected = modSchemaFileSelected;
window.modDeploySchema       = modDeploySchema;
window.modClearSchema        = modClearSchema;
window.modDownloadSchema     = modDownloadSchema;
window.modSaveDefinition     = modSaveDefinition;
window.modDeleteModule       = modDeleteModule;
window.modSaveAccessDefaults = modSaveAccessDefaults;
window.modFilterTenants      = modFilterTenants;
window.modToggleFeatExpand   = modToggleFeatExpand;
window.modToggleTenantModule = modToggleTenantModule;
window.modToggleTenantFeature= modToggleTenantFeature;
window.modSetAllTenants      = modSetAllTenants;
window.modRenderTenantRuleValues = modRenderTenantRuleValues;
window.modSaveTenantRules    = modSaveTenantRules;
window.modResetTenantRules   = modResetTenantRules;
window.modPublishAll         = modPublishAll;
window.modSeedBuiltins       = modSeedBuiltins;
window.modRenderRail         = modRenderRail;
window.modLoadSchemaState    = modLoadSchemaState;


// ══════════════════════════════════════════════════════════════
// MESSAGES PAGE
// ══════════════════════════════════════════════════════════════

function msgTab(id, btn) {
  document.querySelectorAll('#page-messages .tab-btn').forEach(function(b) { b.classList.remove('active'); });
  document.querySelectorAll('#page-messages .tab-pane').forEach(function(p) { p.classList.remove('active'); });
  document.getElementById('msg-' + id + '-pane').classList.add('active');
  btn.classList.add('active');
  if (id === 'tenants') renderMsgTenantTable();
  if (id === 'metrics') renderMsgMetrics();
}

function renderMessagesPage() {
  renderMsgMetricsSummaryBadge();
  renderMsgMetrics();
  // Load global config from state if available
  var svc = state.messagingService || {};
  if (svc.emailGlobalLimit != null) document.getElementById('msg-email-global-limit').value = svc.emailGlobalLimit;
  if (svc.emailTenantDefault != null) document.getElementById('msg-email-tenant-default').value = svc.emailTenantDefault;
  if (svc.smsGlobalLimit != null) document.getElementById('msg-sms-global-limit').value = svc.smsGlobalLimit;
  if (svc.smsTenantDefault != null) document.getElementById('msg-sms-tenant-default').value = svc.smsTenantDefault;
}

function renderMsgMetricsSummaryBadge() {
  // Build aggregate channel stats from tenants
  var totalEmail = 0, totalSms = 0, totalWa = 0;
  state.tenants.forEach(function(t) {
    var ml = t.messagingLimits || {};
    totalEmail += (ml.email || 0);
    totalSms += (ml.sms || 0);
    totalWa += (ml.whatsapp || 0);
  });
  var total = totalEmail + totalSms + totalWa;
  var badge = document.getElementById('nb-messages');
  if (badge) badge.textContent = total > 0 ? total.toLocaleString() : state.tenants.length;
}

function renderMsgTenantTable() {
  var wrap = document.getElementById('msg-tenant-table-wrap');
  if (!wrap) return;
  var search = (document.getElementById('msg-tenant-search') || {}).value || '';
  var tenants = state.tenants.filter(function(t) {
    return !search || t.name.toLowerCase().indexOf(search.toLowerCase()) !== -1;
  });

  if (!tenants.length) {
    wrap.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:20px 0;text-align:center;">No tenants found.</div>';
    return;
  }

  var rows = tenants.map(function(t) {
    var ml = t.messagingLimits || {};
    var emailLim = ml.email != null ? ml.email : '';
    var smsLim = ml.sms != null ? ml.sms : '';
    var waLim = ml.whatsapp != null ? ml.whatsapp : '';
    return '<tr style="border-top:1px solid var(--border);">' +
      '<td style="padding:9px 14px;font-size:0.78rem;font-weight:500;">' + t.name + '</td>' +
      '<td style="padding:9px 14px;"><span style="font-size:0.65rem;padding:2px 7px;border-radius:4px;background:' + (t.status==='active'?'rgba(52,211,153,0.12)':t.status==='trial'?'rgba(251,191,36,0.12)':'rgba(248,113,113,0.12)') + ';color:' + (t.status==='active'?'var(--green)':t.status==='trial'?'var(--amber)':'var(--red)') + ';">' + t.status + '</span></td>' +
      '<td style="padding:9px 14px;"><input type="number" class="form-input" style="width:90px;font-size:0.72rem;padding:4px 8px;" placeholder="inherit" value="' + emailLim + '" id="mt-email-' + t.id + '" min="0"/></td>' +
      '<td style="padding:9px 14px;"><input type="number" class="form-input" style="width:90px;font-size:0.72rem;padding:4px 8px;" placeholder="inherit" value="' + smsLim + '" id="mt-sms-' + t.id + '" min="0"/></td>' +
      '<td style="padding:9px 14px;"><input type="number" class="form-input" style="width:90px;font-size:0.72rem;padding:4px 8px;" placeholder="inherit" value="' + waLim + '" id="mt-wa-' + t.id + '" min="0"/></td>' +
      '<td style="padding:9px 14px;"><label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:0.7rem;"><input type="checkbox" ' + (ml.messagingEnabled !== false ? 'checked' : '') + ' id="mt-enabled-' + t.id + '" style="accent-color:var(--accent);"/> On</label></td>' +
      '<td style="padding:9px 14px;"><button class="btn btn-ghost btn-xs" onclick="saveMsgTenantOverride(\'' + t.id + '\')">Save</button></td>' +
      '</tr>';
  }).join('');

  wrap.innerHTML = '<table style="width:100%;border-collapse:collapse;">' +
    '<thead><tr>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--muted);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">Tenant</th>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--muted);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">Status</th>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--blue);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">Email / mo</th>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--green);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">SMS / mo</th>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--purple);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">WhatsApp / mo</th>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--muted);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">Enabled</th>' +
    '<th style="padding:8px 14px;"></th>' +
    '</tr></thead>' +
    '<tbody>' + rows + '</tbody></table>';
}

function saveMsgTenantOverride(tenantId) {
  var emailEl = document.getElementById('mt-email-' + tenantId);
  var smsEl = document.getElementById('mt-sms-' + tenantId);
  var waEl = document.getElementById('mt-wa-' + tenantId);
  var enabledEl = document.getElementById('mt-enabled-' + tenantId);
  var t = state.tenants.find(function(x) { return x.id === tenantId; });
  if (!t) return;
  t.messagingLimits = t.messagingLimits || {};
  t.messagingLimits.email = emailEl && emailEl.value !== '' ? parseInt(emailEl.value) : null;
  t.messagingLimits.sms = smsEl && smsEl.value !== '' ? parseInt(smsEl.value) : null;
  t.messagingLimits.whatsapp = waEl && waEl.value !== '' ? parseInt(waEl.value) : null;
  t.messagingLimits.messagingEnabled = enabledEl ? enabledEl.checked : true;
  showToast('Messaging overrides saved for ' + t.name, 'success');
}

function saveGlobalMessagingConfig() {
  var config = {
    emailEnabled: document.getElementById('msg-email-enabled') ? document.getElementById('msg-email-enabled').checked : true,
    smsEnabled: document.getElementById('msg-sms-enabled') ? document.getElementById('msg-sms-enabled').checked : true,
    waEnabled: document.getElementById('msg-wa-enabled') ? document.getElementById('msg-wa-enabled').checked : false,
    requireLog: document.getElementById('msg-require-log').checked,
    rateLimiting: document.getElementById('msg-rate-limit').checked,
    broadcastAdminOnly: document.getElementById('msg-broadcast-admin').checked,
  };
  state.messagingService = Object.assign(state.messagingService || {}, config);
  showToast('Global messaging config saved', 'success');
}

function renderMsgMetrics() {
  var totalEmail = 0, totalSms = 0, totalWa = 0;
  state.tenants.forEach(function(t) {
    var ml = t.messagingLimits || {};
    totalEmail += ml.email || 0;
    totalSms += ml.sms || 0;
    totalWa += ml.whatsapp || 0;
  });
  var grand = totalEmail + totalSms + totalWa;
  var summary = document.getElementById('msg-metrics-summary');
  var fmtQ = function(v) { return v > 0 ? v.toLocaleString() : '<span style="font-size:1.1rem;color:var(--muted);">Not set</span>'; };
  if (summary) summary.innerHTML =
    '<div class="metric-card metric-blue"><div class="metric-label">Email Quota (total)</div><div class="metric-val">' + fmtQ(totalEmail) + '</div><div class="metric-sub">Across all tenants / mo</div></div>' +
    '<div class="metric-card metric-accent"><div class="metric-label">SMS Quota (total)</div><div class="metric-val">' + fmtQ(totalSms) + '</div><div class="metric-sub">Across all tenants / mo</div></div>' +
    '<div class="metric-card"><div class="metric-label">WhatsApp Quota (total)</div><div class="metric-val">' + fmtQ(totalWa) + '</div><div class="metric-sub">Across all tenants / mo</div></div>' +
    '<div class="metric-card metric-amber"><div class="metric-label">Channels Active</div><div class="metric-val">2</div><div class="metric-sub">Email + SMS enabled</div></div>';

  var breakdown = document.getElementById('msg-channel-breakdown');
  if (breakdown && grand > 0) {
    var channels = [
      { name: 'Email', val: totalEmail, color: 'var(--blue)' },
      { name: 'SMS', val: totalSms, color: 'var(--green)' },
      { name: 'WhatsApp', val: totalWa, color: 'var(--purple)' },
    ];
    breakdown.innerHTML = channels.map(function(c) {
      var pct = grand > 0 ? Math.round(c.val / grand * 100) : 0;
      return '<div style="margin-bottom:14px;">' +
        '<div style="display:flex;justify-content:space-between;font-size:0.72rem;margin-bottom:4px;"><span>' + c.name + '</span><span style="color:var(--muted);">' + c.val.toLocaleString() + ' (' + pct + '%)</span></div>' +
        '<div class="usage-bar"><div class="usage-bar-fill" style="width:' + pct + '%;background:' + c.color + ';"></div></div>' +
        '</div>';
    }).join('');
  } else if (breakdown) {
    breakdown.innerHTML = '<div style="color:var(--muted);font-size:0.73rem;">No quota data configured yet.</div>';
  }

  var topTenants = document.getElementById('msg-top-tenants');
  if (topTenants) {
    var ranked = state.tenants.slice().sort(function(a, b) {
      var av = ((a.messagingLimits||{}).email||0) + ((a.messagingLimits||{}).sms||0) + ((a.messagingLimits||{}).whatsapp||0);
      var bv = ((b.messagingLimits||{}).email||0) + ((b.messagingLimits||{}).sms||0) + ((b.messagingLimits||{}).whatsapp||0);
      return bv - av;
    }).slice(0, 6);
    var maxVol = ranked.length ? (((ranked[0].messagingLimits||{}).email||0)+((ranked[0].messagingLimits||{}).sms||0)+((ranked[0].messagingLimits||{}).whatsapp||0)) : 1;
    topTenants.innerHTML = ranked.map(function(t) {
      var vol = ((t.messagingLimits||{}).email||0)+((t.messagingLimits||{}).sms||0)+((t.messagingLimits||{}).whatsapp||0);
      var pct = maxVol > 0 ? Math.round(vol / maxVol * 100) : 0;
      return '<div style="margin-bottom:12px;">' +
        '<div style="display:flex;justify-content:space-between;font-size:0.72rem;margin-bottom:4px;"><span>' + t.name + '</span><span style="color:var(--muted);">' + vol.toLocaleString() + '</span></div>' +
        '<div class="usage-bar"><div class="usage-bar-fill" style="width:' + pct + '%;"></div></div>' +
        '</div>';
    }).join('') || '<div style="color:var(--muted);font-size:0.73rem;">No messaging limits set.</div>';
  }
}

window.msgTab               = msgTab;
window.renderMessagesPage   = renderMessagesPage;
window.renderMsgTenantTable = renderMsgTenantTable;
window.saveMsgTenantOverride= saveMsgTenantOverride;
window.saveGlobalMessagingConfig = saveGlobalMessagingConfig;
window.renderMsgMetrics     = renderMsgMetrics;

// ══════════════════════════════════════════════════════════════
// STORAGE PAGE
// ══════════════════════════════════════════════════════════════

function stgTab(id, btn) {
  document.querySelectorAll('#page-storage .tab-btn').forEach(function(b) { b.classList.remove('active'); });
  document.querySelectorAll('#page-storage .tab-pane').forEach(function(p) { p.classList.remove('active'); });
  document.getElementById('stg-' + id + '-pane').classList.add('active');
  btn.classList.add('active');
  if (id === 'tenants') renderStgTenantTable();
  if (id === 'metrics') renderStgMetrics();
}

function renderStoragePage() {
  renderStgMetricsBadge();
  // Load saved config
  var cfg = state.storageConfig || {};
  if (cfg.globalCap != null && document.getElementById('stg-global-cap')) document.getElementById('stg-global-cap').value = cfg.globalCap;
  if (cfg.tenantDefaultQuota != null && document.getElementById('stg-tenant-default-quota')) document.getElementById('stg-tenant-default-quota').value = cfg.tenantDefaultQuota;
  if (cfg.maxFileSize != null && document.getElementById('stg-max-file-size')) document.getElementById('stg-max-file-size').value = cfg.maxFileSize;
}

function renderStgMetricsBadge() {
  var badge = document.getElementById('nb-storage');
  if (!badge) return;
  var totalQuota = state.tenants.reduce(function(s, t) { return s + (t.storageQuotaMB || 0); }, 0);
  badge.textContent = totalQuota > 0 ? (Math.round(totalQuota / 1024) + ' GB') : state.tenants.length;
}

function renderStgTenantTable() {
  var wrap = document.getElementById('stg-tenant-table-wrap');
  if (!wrap) return;
  var search = (document.getElementById('stg-tenant-search') || {}).value || '';
  var tenants = state.tenants.filter(function(t) {
    return !search || t.name.toLowerCase().indexOf(search.toLowerCase()) !== -1;
  });

  if (!tenants.length) {
    wrap.innerHTML = '<div style="color:var(--muted);font-size:0.75rem;padding:20px 0;text-align:center;">No tenants found.</div>';
    return;
  }

  var defQuota = (state.storageConfig || {}).tenantDefaultQuota || 5000;

  var rows = tenants.map(function(t) {
    var quota = t.storageQuotaMB != null ? t.storageQuotaMB : '';
    var used = t.storageUsedMB || 0;
    var cap = t.storageQuotaMB || defQuota;
    var pct = cap > 0 ? Math.min(100, Math.round(used / cap * 100)) : 0;
    var barColor = pct >= 90 ? 'var(--red)' : pct >= 70 ? 'var(--amber)' : 'var(--accent)';
    return '<tr style="border-top:1px solid var(--border);">' +
      '<td style="padding:9px 14px;font-size:0.78rem;font-weight:500;">' + t.name + '</td>' +
      '<td style="padding:9px 14px;"><span style="font-size:0.65rem;padding:2px 7px;border-radius:4px;background:' + (t.status==='active'?'rgba(52,211,153,0.12)':t.status==='trial'?'rgba(251,191,36,0.12)':'rgba(248,113,113,0.12)') + ';color:' + (t.status==='active'?'var(--green)':t.status==='trial'?'var(--amber)':'var(--red)') + ';">' + t.status + '</span></td>' +
      '<td style="padding:9px 14px;"><input type="number" class="form-input" style="width:100px;font-size:0.72rem;padding:4px 8px;" placeholder="default (' + defQuota + ')" value="' + quota + '" id="st-quota-' + t.id + '" min="0"/></td>' +
      '<td style="padding:9px 14px;min-width:140px;">' +
        '<div style="display:flex;justify-content:space-between;font-size:0.65rem;color:var(--muted);margin-bottom:3px;"><span>' + used + ' MB used</span><span>' + pct + '%</span></div>' +
        '<div class="usage-bar"><div class="usage-bar-fill" style="width:' + pct + '%;background:' + barColor + ';"></div></div>' +
      '</td>' +
      '<td style="padding:9px 14px;"><label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:0.7rem;"><input type="checkbox" ' + (t.storageEnabled !== false ? 'checked' : '') + ' id="st-enabled-' + t.id + '" style="accent-color:var(--accent);"/> Access</label></td>' +
      '<td style="padding:9px 14px;"><button class="btn btn-ghost btn-xs" onclick="saveStgTenantQuota(\'' + t.id + '\')">Save</button></td>' +
      '</tr>';
  }).join('');

  wrap.innerHTML = '<table style="width:100%;border-collapse:collapse;">' +
    '<thead><tr>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--muted);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">Tenant</th>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--muted);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">Status</th>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--muted);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">Quota (MB)</th>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--muted);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">Usage</th>' +
    '<th style="padding:8px 14px;text-align:left;font-size:0.65rem;color:var(--muted);font-family:\'DM Mono\',monospace;text-transform:uppercase;letter-spacing:0.05em;">Storage</th>' +
    '<th style="padding:8px 14px;"></th>' +
    '</tr></thead>' +
    '<tbody>' + rows + '</tbody></table>';
}

function saveStgTenantQuota(tenantId) {
  var quotaEl = document.getElementById('st-quota-' + tenantId);
  var enabledEl = document.getElementById('st-enabled-' + tenantId);
  var t = state.tenants.find(function(x) { return x.id === tenantId; });
  if (!t) return;
  t.storageQuotaMB = quotaEl && quotaEl.value !== '' ? parseInt(quotaEl.value) : null;
  t.storageEnabled = enabledEl ? enabledEl.checked : true;
  showToast('Storage quota saved for ' + t.name, 'success');
  renderStgMetricsBadge();
}

function saveGlobalStorageConfig() {
  var cap = document.getElementById('stg-global-cap');
  var def = document.getElementById('stg-tenant-default-quota');
  var maxFile = document.getElementById('stg-max-file-size');
  state.storageConfig = state.storageConfig || {};
  if (cap && cap.value !== '') state.storageConfig.globalCap = parseInt(cap.value);
  if (def && def.value !== '') state.storageConfig.tenantDefaultQuota = parseInt(def.value);
  if (maxFile && maxFile.value !== '') state.storageConfig.maxFileSize = parseInt(maxFile.value);
  state.storageConfig.requireAuth = document.getElementById('stg-require-auth').checked;
  state.storageConfig.tenantIsolation = document.getElementById('stg-tenant-isolation').checked;
  state.storageConfig.confidentialAdmin = document.getElementById('stg-confidential-admin').checked;
  state.storageConfig.avatarSelfWrite = document.getElementById('stg-avatar-self-write').checked;
  showToast('Global storage rules saved', 'success');
  renderStgMetricsBadge();
}

function renderStgMetrics() {
  var totalQuota = state.tenants.reduce(function(s, t) { return s + (t.storageQuotaMB || 0); }, 0);
  var totalUsed = state.tenants.reduce(function(s, t) { return s + (t.storageUsedMB || 0); }, 0);
  var overThreshold = state.tenants.filter(function(t) {
    var q = t.storageQuotaMB || 5000;
    return (t.storageUsedMB || 0) / q > 0.8;
  }).length;

  var summary = document.getElementById('stg-metrics-summary');
  if (summary) summary.innerHTML =
    '<div class="metric-card metric-accent"><div class="metric-label">Total Quota Allocated</div><div class="metric-val">' + (totalQuota >= 1024 ? (Math.round(totalQuota/1024*10)/10) + ' GB' : totalQuota + ' MB') + '</div><div class="metric-sub">Across ' + state.tenants.length + ' tenants</div></div>' +
    '<div class="metric-card metric-blue"><div class="metric-label">Total Used</div><div class="metric-val">' + (totalUsed >= 1024 ? (Math.round(totalUsed/1024*10)/10) + ' GB' : totalUsed + ' MB') + '</div><div class="metric-sub">' + (totalQuota > 0 ? Math.round(totalUsed/totalQuota*100) : 0) + '% of allocated quota</div></div>' +
    '<div class="metric-card metric-amber"><div class="metric-label">Over 80% Capacity</div><div class="metric-val">' + overThreshold + '</div><div class="metric-sub">Tenant' + (overThreshold!==1?'s':'')+' approaching limit</div></div>' +
    '<div class="metric-card"><div class="metric-label">Bucket</div><div class="metric-val" style="font-size:0.9rem;">1</div><div class="metric-sub">png-hr.firebasestorage.app</div></div>';

  var byTenant = document.getElementById('stg-by-tenant-list');
  if (byTenant) {
    var ranked = state.tenants.slice().sort(function(a, b) { return (b.storageUsedMB||0) - (a.storageUsedMB||0); }).slice(0, 8);
    var maxUsed = ranked.length ? (ranked[0].storageUsedMB || 1) : 1;
    byTenant.innerHTML = ranked.map(function(t) {
      var used = t.storageUsedMB || 0;
      var quota = t.storageQuotaMB || (state.storageConfig || {}).tenantDefaultQuota || 5000;
      var pct = quota > 0 ? Math.min(100, Math.round(used / quota * 100)) : 0;
      var barColor = pct >= 90 ? 'var(--red)' : pct >= 70 ? 'var(--amber)' : 'var(--accent)';
      return '<div style="margin-bottom:12px;">' +
        '<div style="display:flex;justify-content:space-between;font-size:0.72rem;margin-bottom:3px;"><span>' + t.name + '</span><span style="color:var(--muted);">' + used + ' / ' + quota + ' MB (' + pct + '%)</span></div>' +
        '<div class="usage-bar"><div class="usage-bar-fill" style="width:' + pct + '%;background:' + barColor + ';"></div></div>' +
        '</div>';
    }).join('') || '<div style="color:var(--muted);font-size:0.73rem;">No storage usage data.</div>';
  }

  var byType = document.getElementById('stg-by-type-list');
  if (byType) {
    var types = [
      { label: 'Documents (PDF, DOCX)', pct: 48, color: 'var(--blue)' },
      { label: 'Images (JPG, PNG)', pct: 27, color: 'var(--accent)' },
      { label: 'Spreadsheets (XLSX, CSV)', pct: 15, color: 'var(--green)' },
      { label: 'Avatars', pct: 7, color: 'var(--purple)' },
      { label: 'Other', pct: 3, color: 'var(--muted)' },
    ];
    byType.innerHTML = types.map(function(ty) {
      return '<div style="margin-bottom:12px;">' +
        '<div style="display:flex;justify-content:space-between;font-size:0.72rem;margin-bottom:3px;"><span>' + ty.label + '</span><span style="color:var(--muted);">' + ty.pct + '%</span></div>' +
        '<div class="usage-bar"><div class="usage-bar-fill" style="width:' + ty.pct + '%;background:' + ty.color + ';"></div></div>' +
        '</div>';
    }).join('');
  }
}

window.stgTab               = stgTab;
window.renderStoragePage    = renderStoragePage;
window.renderStgTenantTable = renderStgTenantTable;
window.saveStgTenantQuota   = saveStgTenantQuota;
window.saveGlobalStorageConfig = saveGlobalStorageConfig;
window.renderStgMetrics     = renderStgMetrics;
// Setup wizard
window.wizNext              = wizNext;
window.wizBack              = wizBack;
window.wizGoTo              = wizGoTo;
window.wizFinish            = wizFinish;
window.wizPlanSet           = wizPlanSet;
window.wizPlanToggleMod     = wizPlanToggleMod;
window.wizToggleExt         = wizToggleExt;
window.wizSetModOverride    = wizSetModOverride;
// ─────────────────────────────────────────────────────────────────────────────
// PROVINCE GEOJSON — upload and status management
// ─────────────────────────────────────────────────────────────────────────────

let _pendingGeoJSON = null;

/**
 * Load and display the current GeoJSON upload status.
 * Called when the system-settings page is shown.
 */
async function loadGeoJSONStatus() {
  const statusEl = document.getElementById('geojson-status-text');
  const statusWrap = document.getElementById('geojson-current-status');
  if (!statusEl) return;

  statusEl.textContent = 'Loading…';
  try {
    const res = await HRMGMT.getProvinceGeoJSON();
    if (res && res.featureCount > 0) {
      statusWrap.style.borderColor = 'rgba(74,222,128,0.3)';
      statusEl.style.color = 'var(--accent)';
      const when = res.updatedAt
        ? new Date(res.updatedAt).toLocaleString('en-PG', { timeZone: 'Pacific/Port_Moresby' })
        : 'unknown time';
      statusEl.innerHTML =
        '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" style="flex-shrink:0"><path d="M3 8.5l3.5 3.5L13 5" stroke="var(--accent)" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '&nbsp;GeoJSON active — ' + res.featureCount + ' provinces · last updated ' + when;
    } else {
      statusWrap.style.borderColor = 'rgba(251,191,36,0.3)';
      statusEl.style.color = 'var(--amber)';
      statusEl.innerHTML =
        '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" style="flex-shrink:0"><path d="M8 5v4M8 11v.5" stroke="var(--amber)" stroke-width="1.6" stroke-linecap="round"/><path d="M1.5 13L8 2l6.5 11H1.5z" stroke="var(--amber)" stroke-width="1.4" stroke-linejoin="round"/></svg>' +
        '&nbsp;No GeoJSON uploaded yet — the HR workforce map will not be available until a file is uploaded.';
    }
  } catch (e) {
    statusEl.style.color = 'var(--red)';
    statusEl.textContent = 'Error loading status: ' + (e.message || 'unknown error');
  }
}

/**
 * Called when a .geojson file is selected in the file picker.
 * Reads and validates the file, then enables the upload button.
 */
function onGeoJSONFileSelected(input) {
  const file = input.files && input.files[0];
  const nameEl = document.getElementById('geojson-file-name');
  const uploadBtn = document.getElementById('geojson-upload-btn');
  const resultEl = document.getElementById('geojson-upload-result');

  _pendingGeoJSON = null;
  if (uploadBtn) { uploadBtn.disabled = true; uploadBtn.style.opacity = '0.5'; }
  if (resultEl) resultEl.style.display = 'none';

  if (!file) return;

  if (nameEl) nameEl.textContent = file.name;

  const reader = new FileReader();
  reader.onload = function(e) {
    try {
      const parsed = JSON.parse(e.target.result);
      if (parsed.type !== 'FeatureCollection') {
        showGeoJSONResult('error', 'File must be a GeoJSON FeatureCollection.');
        return;
      }
      if (!Array.isArray(parsed.features) || parsed.features.length === 0) {
        showGeoJSONResult('error', 'FeatureCollection has no features.');
        return;
      }
      const missing = parsed.features.filter(f => !(f.properties && f.properties.adm1_name));
      if (missing.length > 0) {
        showGeoJSONResult('error',
          missing.length + ' feature(s) are missing the adm1_name property. ' +
          'All features must include adm1_name.');
        return;
      }
      _pendingGeoJSON = parsed;
      if (uploadBtn) { uploadBtn.disabled = false; uploadBtn.style.opacity = '1'; }
      showGeoJSONResult('info',
        'File validated — ' + parsed.features.length + ' province features ready to upload.');
    } catch (err) {
      showGeoJSONResult('error', 'Invalid JSON: ' + (err.message || 'parse error'));
    }
  };
  reader.readAsText(file);
}

/**
 * Show a result message below the upload section.
 */
function showGeoJSONResult(type, msg) {
  const el = document.getElementById('geojson-upload-result');
  if (!el) return;
  const colors = {
    info:    { bg: 'var(--blue-dim)',   border: 'rgba(96,165,250,0.3)',   text: 'var(--blue)'   },
    success: { bg: 'var(--accent-dim)', border: 'rgba(74,222,128,0.3)',   text: 'var(--accent)' },
    error:   { bg: 'var(--red-dim)',    border: 'rgba(248,113,113,0.3)',   text: 'var(--red)'    },
  };
  const c = colors[type] || colors.info;
  el.style.display = 'block';
  el.style.cssText += ';padding:10px 14px;border-radius:6px;font-size:0.73rem;line-height:1.5;' +
    'background:' + c.bg + ';border:1px solid ' + c.border + ';color:' + c.text + ';';
  el.textContent = msg;
}

/**
 * Upload the validated GeoJSON to the platform.
 */
async function uploadProvinceGeoJSON() {
  if (!_pendingGeoJSON) return;

  const uploadBtn = document.getElementById('geojson-upload-btn');
  const origText = uploadBtn ? uploadBtn.innerHTML : '';
  if (uploadBtn) {
    uploadBtn.disabled = true;
    uploadBtn.innerHTML = '<span style="font-size:0.72rem;">Uploading…</span>';
  }

  try {
    const res = await HRMGMT.saveProvinceGeoJSON(JSON.stringify(_pendingGeoJSON));
    if (res && res.success) {
      showGeoJSONResult('success',
        '✓ GeoJSON uploaded successfully — ' + res.featureCount + ' provinces stored. ' +
        'The HR workforce map will now display province boundaries.');
      _pendingGeoJSON = null;
      if (uploadBtn) { uploadBtn.disabled = true; uploadBtn.style.opacity = '0.5'; }
      // Reset file input
      const fileInput = document.getElementById('geojson-file-input');
      if (fileInput) fileInput.value = '';
      const nameEl = document.getElementById('geojson-file-name');
      if (nameEl) nameEl.textContent = 'Choose .geojson file';
      // Refresh status
      await loadGeoJSONStatus();
    }
  } catch (err) {
    showGeoJSONResult('error', 'Upload failed: ' + (err.message || 'unknown error'));
    if (uploadBtn) {
      uploadBtn.disabled = false;
      uploadBtn.style.opacity = '1';
      uploadBtn.innerHTML = origText;
    }
  }
}

// Load GeoJSON status when the system-settings page is opened.
// Hooks into the nav button's onclick rather than wrapping showPage,
// so there is zero risk of interfering with auth or other page routing.
document.addEventListener('DOMContentLoaded', function() {
  var ssBtn = document.querySelector('.nav-btn[onclick*="system-settings"]');
  if (ssBtn) {
    ssBtn.addEventListener('click', function() {
      // Defer slightly so showPage() runs first
      setTimeout(loadGeoJSONStatus, 80);
    });
  }
});

window.loadGeoJSONStatus      = loadGeoJSONStatus;

// ══════════════════════════════════════════════════════════════
// PS REFERENCE LIBRARY
// ══════════════════════════════════════════════════════════════

const PS_REF_CAT_LABELS = {
  'general-orders': 'General Orders',
  'regulations':    'Regulations & Legislation',
  'gesi-policy':    'GESI Policy',
  'gesi-standards': 'GESI Standards',
  'gesi-tools':     'GESI Tools & Templates',
};

const PS_REF_TYPE_LABELS = {
  link:    'Link',
  pdf:     'PDF',
  content: 'Text',
};

let _psRefItems     = [];    // local cache
let _psRefLoading   = false;
let _psRefFilter    = 'all'; // active category tab

// ── Load & render ─────────────────────────────────────────────

async function renderPSRefLibrary() {
  if (_psRefLoading) return;
  _psRefLoading = true;
  _psRefFilter = 'all';

  // Reset tab buttons
  document.querySelectorAll('#page-ps-reference .tab-btn').forEach((b, i) => {
    b.classList.toggle('active', i === 0);
  });

  _setPSRefBanner('');
  _renderPSRefTable([]);
  const tbody = document.getElementById('psref-tbody');
  if (tbody) {
    tbody.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:30px;color:var(--muted);font-size:0.75rem;">Loading…</td></tr>';
  }

  try {
    const res = await HRMGMT.listPlatformPSRefItems();
    _psRefItems = res.items || [];
    _psRefLoading = false;

    if (_psRefItems.length === 0) {
      _setPSRefBanner(
        'Library is empty. Click <strong>Seed Defaults</strong> to populate with the 12 built-in PNG Public Service items, then edit them with actual URLs and content.',
        'amber'
      );
    }
    _renderPSRefTable(_psRefItems);
  } catch (err) {
    _psRefLoading = false;
    if (tbody) {
      tbody.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:30px;color:var(--red);font-size:0.75rem;">Failed to load: ' + (err.message || 'unknown error') + '</td></tr>';
    }
  }
}

function _setPSRefBanner(html, type) {
  const el = document.getElementById('psref-status-banner');
  if (!el) return;
  if (!html) { el.style.display = 'none'; el.innerHTML = ''; return; }
  const colors = {
    amber:   { bg: 'var(--amber-dim)',  border: 'rgba(251,191,36,0.3)',   text: 'var(--amber)'  },
    green:   { bg: 'var(--accent-dim)', border: 'rgba(74,222,128,0.3)',   text: 'var(--accent)' },
    red:     { bg: 'var(--red-dim)',    border: 'rgba(248,113,113,0.3)',   text: 'var(--red)'    },
  };
  const c = colors[type] || colors.amber;
  el.style.cssText = 'display:block;margin-bottom:16px;padding:10px 14px;border-radius:7px;' +
    'font-size:0.73rem;background:' + c.bg + ';border:1px solid ' + c.border + ';color:' + c.text + ';';
  el.innerHTML = html;
}

function _renderPSRefTable(items) {
  const tbody = document.getElementById('psref-tbody');
  if (!tbody) return;

  const filtered = _psRefFilter === 'all'
    ? items
    : items.filter(i => i.category === _psRefFilter);

  if (filtered.length === 0) {
    tbody.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:30px;color:var(--muted);">' +
      (items.length === 0 ? 'No items — use Seed Defaults or Add Item.' : 'No items in this category.') +
      '</td></tr>';
    return;
  }

  tbody.innerHTML = filtered.map((item, idx) => {
    const catLabel  = PS_REF_CAT_LABELS[item.category] || item.category;
    const typeLabel = PS_REF_TYPE_LABELS[item.docType] || item.docType;
    const typeColor = item.docType === 'content' ? 'var(--purple)' : item.docType === 'pdf' ? 'var(--amber)' : 'var(--blue)';

    let urlCell;
    if (item.docType === 'content') {
      urlCell = item.content
        ? '<button class="btn btn-xs btn-ghost" onclick="viewPSRefContent(\'' + item.id + '\')">View text</button>'
        : '<span style="color:var(--muted);font-size:0.68rem;">No content</span>';
    } else {
      urlCell = item.url
        ? '<a href="' + _esc(item.url) + '" target="_blank" rel="noopener" style="font-size:0.68rem;color:var(--blue);font-family:\'DM Mono\',monospace;word-break:break-all;">' +
          item.url.replace(/^https?:\/\//, '') + '</a>'
        : '<span style="color:var(--muted);font-size:0.68rem;">—</span>';
    }

    const statusBadge = item.status === 'draft'
      ? '<span class="badge badge-trial">draft</span>'
      : '<span class="badge badge-active">active</span>';

    return '<tr>' +
      '<td style="font-family:\'DM Mono\',monospace;font-size:0.62rem;color:var(--muted);">' + (item.sortOrder ?? idx) + '</td>' +
      '<td>' +
        '<div style="font-weight:500;font-size:0.75rem;">' + _esc(item.title) + '</div>' +
        (item.description ? '<div style="font-size:0.63rem;color:var(--muted);margin-top:2px;">' + _esc(item.description).slice(0, 90) + (item.description.length > 90 ? '…' : '') + '</div>' : '') +
      '</td>' +
      '<td><span style="font-size:0.68rem;color:var(--text);">' + catLabel + '</span></td>' +
      '<td><span style="font-size:0.64rem;font-family:\'DM Mono\',monospace;color:' + typeColor + ';background:' + typeColor + '18;padding:2px 7px;border-radius:4px;">' + typeLabel + '</span></td>' +
      '<td>' + urlCell + '</td>' +
      '<td>' + statusBadge + '</td>' +
      '<td>' +
        '<div style="display:flex;gap:5px;">' +
          '<button class="btn btn-xs btn-ghost" onclick="openPSRefItemModal(\'' + item.id + '\')">Edit</button>' +
          '<button class="btn btn-xs btn-danger" onclick="deletePSRefItem(\'' + item.id + '\')">Del</button>' +
        '</div>' +
      '</td>' +
    '</tr>';
  }).join('');
}

function _esc(str) {
  return String(str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

// ── Category filter tab ───────────────────────────────────────

function psRefFilterTab(cat, btn) {
  _psRefFilter = cat;
  document.querySelectorAll('#page-ps-reference .tab-btn').forEach(b => b.classList.remove('active'));
  if (btn) btn.classList.add('active');
  _renderPSRefTable(_psRefItems);
}

// ── Add / Edit modal ──────────────────────────────────────────

function openPSRefItemModal(itemId) {
  const item = itemId ? _psRefItems.find(i => i.id === itemId) : null;

  document.getElementById('psref-modal-title').textContent = item ? 'Edit Reference Item' : 'Add Reference Item';
  document.getElementById('psref-edit-id').value           = item ? item.id : '';
  document.getElementById('psref-title').value             = item ? (item.title || '') : '';
  document.getElementById('psref-category').value          = item ? (item.category || 'general-orders') : 'general-orders';
  document.getElementById('psref-doctype').value           = item ? (item.docType || 'link') : 'link';
  document.getElementById('psref-description').value       = item ? (item.description || '') : '';
  document.getElementById('psref-url').value               = item ? (item.url || '') : '';
  document.getElementById('psref-content').value           = item ? (item.content || '') : '';
  document.getElementById('psref-sort').value              = item ? (item.sortOrder ?? '') : '';
  document.getElementById('psref-status').value            = item ? (item.status || 'active') : 'active';

  // Reset PDF upload state
  const fileInput    = document.getElementById('psref-pdf-file');
  const nameEl       = document.getElementById('psref-pdf-filename');
  const dropEl       = document.getElementById('psref-pdf-drop');
  const progressWrap = document.getElementById('psref-pdf-progress-wrap');
  const progressBar  = document.getElementById('psref-pdf-progress-bar');
  if (fileInput)    fileInput.value = '';
  if (nameEl)       { nameEl.textContent = 'Click to choose a PDF file'; nameEl.style.color = ''; }
  if (dropEl)       dropEl.style.borderColor = '';
  if (progressWrap) progressWrap.style.display = 'none';
  if (progressBar)  progressBar.style.width = '0%';

  // Show current file link when editing an uploaded PDF
  const currentDiv  = document.getElementById('psref-pdf-current');
  const currentLink = document.getElementById('psref-pdf-current-link');
  if (currentDiv && currentLink) {
    if (item && item.docType === 'pdf' && item.url) {
      const label = item.storagePath
        ? decodeURIComponent(item.storagePath.split('/').pop().replace(/^\d+_/, ''))
        : item.url.split('/').pop().split('?')[0] || 'current file';
      currentLink.href        = item.url;
      currentLink.textContent = label;
      currentDiv.style.display = 'flex';
    } else {
      currentDiv.style.display = 'none';
    }
  }

  psRefDocTypeChange(document.getElementById('psref-doctype').value);

  document.getElementById('modal-ps-ref-item').classList.add('open');
}

function psRefDocTypeChange(val) {
  const urlGrp     = document.getElementById('psref-url-group');
  const pdfGrp     = document.getElementById('psref-pdf-group');
  const contentGrp = document.getElementById('psref-content-group');
  if (!urlGrp || !contentGrp) return;
  urlGrp.style.display     = val === 'link'    ? '' : 'none';
  pdfGrp.style.display     = val === 'pdf'     ? '' : 'none';
  contentGrp.style.display = val === 'content' ? '' : 'none';
}

function psRefPDFFileSelected(input) {
  const file   = input.files[0];
  const nameEl = document.getElementById('psref-pdf-filename');
  const dropEl = document.getElementById('psref-pdf-drop');
  if (file) {
    nameEl.textContent  = file.name + ' (' + (file.size / 1024).toFixed(0) + ' KB)';
    nameEl.style.color  = 'var(--text)';
    if (dropEl) dropEl.style.borderColor = 'var(--accent)';
  } else {
    nameEl.textContent  = 'Click to choose a PDF file';
    nameEl.style.color  = '';
    if (dropEl) dropEl.style.borderColor = '';
  }
}

async function savePSRefItem() {
  const itemId      = document.getElementById('psref-edit-id').value || null;
  const title       = (document.getElementById('psref-title').value || '').trim();
  const category    = document.getElementById('psref-category').value;
  const docType     = document.getElementById('psref-doctype').value;
  const description = (document.getElementById('psref-description').value || '').trim();
  const content     = (document.getElementById('psref-content').value || '').trim();
  const sortOrder   = parseInt(document.getElementById('psref-sort').value, 10);
  const status      = document.getElementById('psref-status').value;

  if (!title) { toast('Title is required', 'error'); return; }

  const btn          = document.querySelector('#modal-ps-ref-item .btn-primary');
  const progressWrap = document.getElementById('psref-pdf-progress-wrap');
  const progressBar  = document.getElementById('psref-pdf-progress-bar');
  const progressLbl  = document.getElementById('psref-pdf-progress-label');

  let url         = (document.getElementById('psref-url').value || '').trim();
  let storagePath = null;

  // ── PDF upload ────────────────────────────────────────────────
  if (docType === 'pdf') {
    const fileInput = document.getElementById('psref-pdf-file');
    const file      = fileInput && fileInput.files[0];

    if (file) {
      if (file.size > 20 * 1024 * 1024) { toast('PDF must be under 20 MB', 'error'); return; }

      if (btn) { btn.disabled = true; btn.textContent = 'Uploading…'; }
      if (progressWrap) { progressWrap.style.display = ''; }

      try {
        const result = await HRMGMT.uploadPSRefPDF(file, (pct) => {
          if (progressBar) progressBar.style.width = pct + '%';
          if (progressLbl) progressLbl.textContent = 'Uploading… ' + pct + '%';
        });
        url         = result.downloadURL;
        storagePath = result.storagePath;
        if (progressLbl) progressLbl.textContent = 'Upload complete';
      } catch (err) {
        toast('Upload failed: ' + (err.message || 'unknown error'), 'error');
        if (btn) { btn.disabled = false; btn.textContent = 'Save Item'; }
        if (progressWrap) progressWrap.style.display = 'none';
        return;
      }
    } else if (itemId) {
      // Editing existing PDF item with no new file — keep existing url/storagePath
      const cached = _psRefItems.find(i => i.id === itemId);
      if (cached) { url = cached.url || ''; storagePath = cached.storagePath || null; }
    }

    if (!url) { toast('Please upload a PDF file', 'error'); if (btn) { btn.disabled = false; btn.textContent = 'Save Item'; } return; }
  }

  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

  try {
    const payload = {
      title, category, docType, description, url, content, status,
      ...(storagePath ? { storagePath } : {}),
      ...(isNaN(sortOrder) ? {} : { sortOrder }),
      ...(itemId ? { itemId } : {}),
    };
    const res     = await HRMGMT.savePlatformPSRefItem(payload);
    const savedId = res.itemId || itemId;

    const updated = {
      id: savedId, title, category, docType, description, url, content,
      sortOrder: isNaN(sortOrder) ? 999 : sortOrder, status,
      ...(storagePath ? { storagePath } : {}),
    };
    const existingIdx = _psRefItems.findIndex(i => i.id === savedId);
    if (existingIdx >= 0) {
      _psRefItems[existingIdx] = { ..._psRefItems[existingIdx], ...updated };
    } else {
      _psRefItems.push(updated);
      _psRefItems.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
    }

    closeModal('modal-ps-ref-item');
    toast(itemId ? 'Item updated.' : 'Item added.', 'success');
    logActivity('module', 'PS Ref Library: ' + (itemId ? 'updated' : 'added') + ' "' + title + '"', 'var(--blue)');
    _renderPSRefTable(_psRefItems);
  } catch (err) {
    toast(err.message || 'Failed to save item', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Save Item'; }
    if (progressWrap) progressWrap.style.display = 'none';
  }
}

// ── Delete ────────────────────────────────────────────────────

async function deletePSRefItem(itemId) {
  const item = _psRefItems.find(i => i.id === itemId);
  if (!confirm('Delete "' + (item ? item.title : itemId) + '"? This removes it from all tenant portals.')) return;

  try {
    await HRMGMT.deletePlatformPSRefItem(itemId);
    // Best-effort: delete uploaded PDF from storage (backend also attempts this)
    if (item && item.storagePath) {
      HRMGMT.deletePSRefPDF(item.storagePath).catch(() => {});
    }
    _psRefItems = _psRefItems.filter(i => i.id !== itemId);
    toast('Item deleted.', 'success');
    logActivity('module', 'PS Ref Library: deleted "' + (item ? item.title : itemId) + '"', 'var(--red)');
    _renderPSRefTable(_psRefItems);
    if (_psRefItems.length === 0) {
      _setPSRefBanner('Library is now empty. Click <strong>Seed Defaults</strong> or <strong>Add Item</strong>.', 'amber');
    }
  } catch (err) {
    toast(err.message || 'Failed to delete item', 'error');
  }
}

// ── Seed defaults ─────────────────────────────────────────────

async function seedPSRefDefaults() {
  const hasItems = _psRefItems.length > 0;
  const msg = hasItems
    ? 'Seed the 12 default items? Existing items with the same ID will be updated (merge) but your edits are preserved.'
    : 'Seed the 12 default PNG Public Service reference items?';
  if (!confirm(msg)) return;

  const btn = document.getElementById('psref-seed-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span style="font-size:0.72rem;">Seeding…</span>'; }

  try {
    const res = await HRMGMT.seedPlatformPSRefItems(false);
    toast('Seeded ' + (res.count || 12) + ' default items — edit them to add accurate URLs and content.', 'success');
    logActivity('module', 'PS Ref Library: seeded ' + (res.count || 12) + ' defaults', 'var(--blue)');
    _psRefItems = [];
    _psRefLoading = false;
    await renderPSRefLibrary();
  } catch (err) {
    toast(err.message || 'Seed failed', 'error');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<svg width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.4"/><path d="M8 5v3l2 2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg> Seed Defaults';
    }
  }
}

// ── Inline content viewer ─────────────────────────────────────

function viewPSRefContent(itemId) {
  const item = _psRefItems.find(i => i.id === itemId);
  if (!item) return;
  const titleEl = document.getElementById('psref-view-title');
  const bodyEl  = document.getElementById('psref-view-body');
  if (titleEl) titleEl.textContent = item.title;
  if (bodyEl)  bodyEl.textContent  = item.content || '(no content)';
  document.getElementById('modal-ps-ref-content').classList.add('open');
}
window.psRefFilterTab      = psRefFilterTab;
window.openPSRefItemModal  = openPSRefItemModal;
window.savePSRefItem       = savePSRefItem;
window.deletePSRefItem     = deletePSRefItem;
window.viewPSRefContent    = viewPSRefContent;
window.seedPSRefDefaults   = seedPSRefDefaults;
window.psRefDocTypeChange  = psRefDocTypeChange;
window.psRefPDFFileSelected = psRefPDFFileSelected;
window.onGeoJSONFileSelected  = onGeoJSONFileSelected;
window.uploadProvinceGeoJSON  = uploadProvinceGeoJSON;

// ══════════════════════════════════════════════════════════════
// AI KNOWLEDGE BASE — global docs management
// Super-admin managed. Documents are fed into all tenant AI
// assistant sessions alongside each tenant's private docs.
// ══════════════════════════════════════════════════════════════

let _aikbDocs   = [];
let _aikbFilter = 'all';

const AIKB_CAT_LABELS = {
  general: 'General', leave: 'Leave', payroll: 'Payroll',
  performance: 'Performance', conduct: 'Conduct & Discipline',
  recruitment: 'Recruitment', training: 'Training',
  benefits: 'Benefits', safety: 'Safety',
};

async function aiKbLoad() {
  const tbody = document.getElementById('aikb-tbody');
  if (tbody) tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:30px;color:var(--muted);">Loading…</td></tr>';
  try {
    const res = await HRMGMT.listGlobalAiDocs();
    _aikbDocs = (res && res.docs) || [];
    _renderAikbTable(_aikbDocs);
    const badge = document.getElementById('nb-ai-docs');
    if (badge) badge.textContent = _aikbDocs.length;
  } catch (e) {
    if (tbody) tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:30px;color:var(--danger);">Failed to load documents: ' + _esc(e.message) + '</td></tr>';
  }
}

function _renderAikbTable(docs) {
  const tbody = document.getElementById('aikb-tbody');
  if (!tbody) return;

  const filtered = _aikbFilter === 'all' ? docs : docs.filter(d => d.category === _aikbFilter);

  if (!filtered.length) {
    tbody.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:30px;color:var(--muted);">' +
      (_aikbFilter === 'all' ? 'No documents yet. Click <strong>Add Document</strong> to get started.' : 'No documents in this category.') +
      '</td></tr>';
    return;
  }

  tbody.innerHTML = filtered.map(doc => {
    const catLabel = AIKB_CAT_LABELS[doc.category] || doc.category || 'General';
    const isFile   = !!doc.storagePath;
    const typeTag  = isFile
      ? '<span style="font-size:0.64rem;font-family:\'DM Mono\',monospace;color:var(--amber);background:var(--amber)18;padding:2px 7px;border-radius:4px;">file</span>'
      : '<span style="font-size:0.64rem;font-family:\'DM Mono\',monospace;color:var(--blue);background:var(--blue)18;padding:2px 7px;border-radius:4px;">text</span>';

    let preview;
    if (isFile) {
      preview = doc.fileName
        ? '<a href="' + _esc(doc.downloadUrl || '#') + '" target="_blank" rel="noopener" style="font-size:0.68rem;color:var(--blue);">' + _esc(doc.fileName) + '</a>'
        : '<span style="color:var(--muted);font-size:0.68rem;">—</span>';
    } else {
      const snippet = (doc.content || '').slice(0, 100);
      preview = '<span style="font-size:0.68rem;color:var(--muted);">' + _esc(snippet) + (doc.content && doc.content.length > 100 ? '…' : '') + '</span>';
    }

    const updatedAt = doc.updatedAt
      ? (doc.updatedAt.toDate ? doc.updatedAt.toDate().toLocaleDateString() : new Date(doc.updatedAt._seconds * 1000).toLocaleDateString())
      : '—';

    return '<tr>' +
      '<td><div style="font-weight:500;font-size:0.75rem;">' + _esc(doc.title) + '</div></td>' +
      '<td><span style="font-size:0.68rem;">' + _esc(catLabel) + '</span></td>' +
      '<td>' + typeTag + '</td>' +
      '<td>' + preview + '</td>' +
      '<td style="font-size:0.68rem;color:var(--muted);">' + updatedAt + '</td>' +
      '<td><div style="display:flex;gap:5px;">' +
        '<button class="btn btn-xs btn-ghost" onclick="openAiKbDocModal(\'' + doc.id + '\')">Edit</button>' +
        '<button class="btn btn-xs btn-danger" onclick="deleteAiKbDoc(\'' + doc.id + '\',\'' + _esc(doc.title) + '\')">Del</button>' +
      '</div></td>' +
    '</tr>';
  }).join('');
}

function aiKbFilterTab(cat, btn) {
  _aikbFilter = cat;
  document.querySelectorAll('#page-ai-knowledge .tab-btn').forEach(b => b.classList.remove('active'));
  if (btn) btn.classList.add('active');
  _renderAikbTable(_aikbDocs);
}

function aiKbDocTypeChange(val) {
  const textGrp = document.getElementById('aikb-text-group');
  const fileGrp = document.getElementById('aikb-file-group');
  if (textGrp) textGrp.style.display = val === 'text' ? '' : 'none';
  if (fileGrp) fileGrp.style.display = val === 'file' ? '' : 'none';
}

function aiKbFileSelected(input) {
  const file   = input.files[0];
  const nameEl = document.getElementById('aikb-file-name');
  const dropEl = document.getElementById('aikb-file-drop');
  if (file) {
    nameEl.textContent = file.name + ' (' + (file.size / 1024).toFixed(0) + ' KB)';
    nameEl.style.color = 'var(--text)';
    if (dropEl) dropEl.style.borderColor = 'var(--accent)';
  } else {
    nameEl.textContent = 'Click to choose a PDF or image file';
    nameEl.style.color = '';
    if (dropEl) dropEl.style.borderColor = '';
  }
}

async function openAiKbDocModal(docId) {
  const titleEl   = document.getElementById('aikb-modal-title');
  const idEl      = document.getElementById('aikb-edit-id');
  const titleInp  = document.getElementById('aikb-title');
  const catSel    = document.getElementById('aikb-category');
  const typeSel   = document.getElementById('aikb-doctype');
  const contentEl = document.getElementById('aikb-content');
  const fileInput = document.getElementById('aikb-file-input');
  const nameEl    = document.getElementById('aikb-file-name');
  const dropEl    = document.getElementById('aikb-file-drop');
  const progressWrap = document.getElementById('aikb-file-progress-wrap');
  const currentDiv   = document.getElementById('aikb-file-current');
  const currentLink  = document.getElementById('aikb-file-current-link');

  // Reset state
  if (fileInput) fileInput.value = '';
  if (nameEl)    { nameEl.textContent = 'Click to choose a PDF or image file'; nameEl.style.color = ''; }
  if (dropEl)    dropEl.style.borderColor = '';
  if (progressWrap) progressWrap.style.display = 'none';

  if (!docId) {
    titleEl.textContent  = 'Add Global Document';
    idEl.value           = '';
    titleInp.value       = '';
    catSel.value         = 'general';
    typeSel.value        = 'text';
    contentEl.value      = '';
    if (currentDiv) currentDiv.style.display = 'none';
    aiKbDocTypeChange('text');
    document.getElementById('modal-ai-kb-doc').classList.add('open');
    return;
  }

  titleEl.textContent = 'Edit Global Document';
  idEl.value = docId;

  try {
    const res = await HRMGMT.getGlobalAiDoc(docId);
    const doc = res && res.doc;
    if (!doc) throw new Error('Document not found');
    titleInp.value  = doc.title || '';
    catSel.value    = doc.category || 'general';
    contentEl.value = doc.content || '';

    const isFile = !!doc.storagePath;
    typeSel.value = isFile ? 'file' : 'text';

    if (currentDiv && currentLink) {
      if (isFile && doc.downloadUrl) {
        const label = doc.fileName || doc.storagePath.split('/').pop().replace(/^\d+_/, '') || 'current file';
        currentLink.href        = doc.downloadUrl;
        currentLink.textContent = label;
        currentDiv.style.display = 'block';
      } else {
        currentDiv.style.display = 'none';
      }
    }

    aiKbDocTypeChange(typeSel.value);
    document.getElementById('modal-ai-kb-doc').classList.add('open');
  } catch (e) {
    toast('Failed to load document: ' + e.message, 'error');
  }
}

async function saveAiKbDoc() {
  const docId    = document.getElementById('aikb-edit-id').value || null;
  const title    = (document.getElementById('aikb-title').value || '').trim();
  const category = document.getElementById('aikb-category').value;
  const docType  = document.getElementById('aikb-doctype').value;
  const content  = (document.getElementById('aikb-content').value || '').trim();

  if (!title) { toast('Title is required', 'error'); return; }

  const btn          = document.querySelector('#modal-ai-kb-doc .btn-primary');
  const progressWrap = document.getElementById('aikb-file-progress-wrap');
  const progressBar  = document.getElementById('aikb-file-progress-bar');
  const progressLbl  = document.getElementById('aikb-file-progress-label');

  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }

  try {
    let payload = { docId, title, category };

    if (docType === 'text') {
      if (!content) { toast('Content is required for text documents', 'error'); return; }
      payload.content = content;
    } else {
      // File upload path — upload to Storage first, pass base64 to function
      const fileInput = document.getElementById('aikb-file-input');
      const file      = fileInput && fileInput.files[0];

      if (file) {
        if (file.size > 20 * 1024 * 1024) { toast('File must be under 20 MB', 'error'); return; }

        if (progressWrap) progressWrap.style.display = 'block';
        if (progressBar)  progressBar.style.width = '10%';
        if (progressLbl)  progressLbl.textContent  = 'Uploading file…';

        // Read as base64 for the callable function
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload  = e => resolve(e.target.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        if (progressBar) progressBar.style.width = '50%';

        const safe        = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const storagePath = `aiDocs/_global/${Date.now()}_${safe}`;

        payload.file        = { data: base64, mimetype: file.type || 'application/octet-stream' };
        payload.storagePath = storagePath;
        payload.fileName    = file.name;

        if (progressBar) progressBar.style.width = '80%';
      } else if (!docId) {
        toast('Please select a file to upload', 'error'); return;
      }
    }

    await HRMGMT.saveGlobalAiDoc(payload);

    if (progressBar) progressBar.style.width = '100%';
    document.getElementById('modal-ai-kb-doc').classList.remove('open');
    toast(docId ? 'Document updated' : 'Document added', 'success');
    await aiKbLoad();
  } catch (e) {
    toast('Failed to save: ' + e.message, 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Save Document'; }
    if (progressWrap) progressWrap.style.display = 'none';
    if (progressBar)  progressBar.style.width = '0%';
  }
}

async function deleteAiKbDoc(docId, title) {
  if (!confirm(`Delete "${title}"? This cannot be undone.`)) return;
  try {
    await HRMGMT.deleteGlobalAiDoc(docId);
    toast('Document deleted', 'success');
    await aiKbLoad();
  } catch (e) {
    toast('Failed to delete: ' + e.message, 'error');
  }
}

window.aiKbLoad         = aiKbLoad;
window.aiKbFilterTab    = aiKbFilterTab;
window.aiKbDocTypeChange = aiKbDocTypeChange;
window.aiKbFileSelected = aiKbFileSelected;
window.openAiKbDocModal = openAiKbDocModal;
window.saveAiKbDoc      = saveAiKbDoc;
window.deleteAiKbDoc    = deleteAiKbDoc;

// ══════════════════════════════════════════════════════════════
// EMAIL TEMPLATES — platform transactional emails
// Rendering lives server-side (HR functions/emailTemplates.js); the
// preview pane shows exactly what will be sent, with unsaved edits.
// ══════════════════════════════════════════════════════════════

let _etId = null;
let _etPreviewTimer = null;
let _etPreviewSeq = 0;
let _etLastField = null;

function _etFields() {
  const out = {};
  document.querySelectorAll('#page-email-templates .et-f').forEach(el => { out[el.dataset.f] = el.value; });
  return out;
}

function _etFill(fields) {
  document.querySelectorAll('#page-email-templates .et-f').forEach(el => {
    el.value = fields[el.dataset.f] ?? '';
  });
  const picker = document.getElementById('et-color-picker');
  if (picker && /^#[0-9a-fA-F]{6}$/.test(fields.brandColor || '')) picker.value = fields.brandColor;
}

function _etRenderImages(images) {
  document.querySelectorAll('#page-email-templates .et-img').forEach(wrap => {
    const slot = wrap.dataset.slot;
    const box = wrap.querySelector('.et-img-box');
    const src = images && images[slot];
    box.innerHTML = src
      ? '<img src="' + src + '" alt="" style="max-width:100%;max-height:120px;display:block;"/>'
      : '<span style="font-size:0.66rem;color:#7b8794;">' + (slot === 'header' ? 'No image — app name shown' : 'No image') + '</span>';
  });
}

async function renderEmailTemplatesPage() {
  const sel = document.getElementById('et-template');
  try {
    if (!sel.options.length) {
      const res = await HRMGMT.listEmailTemplates();
      sel.innerHTML = (res.templates || []).map(t =>
        '<option value="' + _esc(t.id) + '">' + _esc(t.label) + '</option>').join('');
      // Wire inputs once: track last-focused field and live preview.
      document.querySelectorAll('#page-email-templates .et-f').forEach(el => {
        el.addEventListener('focus', () => { _etLastField = el; });
        el.addEventListener('input', () => {
          if (el.dataset.f === 'brandColor' && /^#[0-9a-fA-F]{6}$/.test(el.value)) {
            document.getElementById('et-color-picker').value = el.value;
          }
          etQueuePreview();
        });
      });
    }
    await etLoad(_etId || sel.value);
  } catch (e) {
    toast('Failed to load email templates: ' + _esc(e.message), 'error');
  }
}

async function etLoad(templateId) {
  if (!templateId) return;
  _etId = templateId;
  document.getElementById('et-template').value = templateId;
  try {
    const t = await HRMGMT.getEmailTemplate(templateId);
    document.getElementById('et-desc').textContent = t.description || '';
    document.getElementById('et-updated').textContent = t.updatedAt
      ? 'Last saved ' + new Date(t.updatedAt).toLocaleString('en-PG', { timeZone: 'Pacific/Port_Moresby' }) +
        (t.updatedBy ? ' by ' + t.updatedBy : '')
      : 'Using built-in default text (never saved).';
    document.getElementById('et-placeholders').innerHTML = Object.entries(t.placeholders || {}).map(([k, d]) =>
      '<span class="et-ph" title="' + _esc(d) + '" onclick="etInsertPlaceholder(\'' + _esc(k) + '\')">{{' + _esc(k) + '}}</span>'
    ).join('');
    _etFill(t.fields || {});
    _etRenderImages(t.images);
    etRefreshPreview();
  } catch (e) {
    toast('Failed to load template: ' + _esc(e.message), 'error');
  }
}

function etInsertPlaceholder(key) {
  const el = _etLastField;
  if (!el || !['subject', 'heading', 'body', 'ctaLabel', 'afterCta', 'footerText'].includes(el.dataset.f)) {
    toast('Click into a text field first, then choose a placeholder.', 'info');
    return;
  }
  const token = '{{' + key + '}}';
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  el.value = el.value.slice(0, start) + token + el.value.slice(end);
  el.focus();
  el.selectionStart = el.selectionEnd = start + token.length;
  etQueuePreview();
}

function etQueuePreview() {
  clearTimeout(_etPreviewTimer);
  _etPreviewTimer = setTimeout(etRefreshPreview, 600);
}

async function etRefreshPreview() {
  if (!_etId) return;
  const seq = ++_etPreviewSeq;
  const subjEl = document.getElementById('et-preview-subject');
  try {
    const r = await HRMGMT.previewEmailTemplate(_etId, _etFields());
    if (seq !== _etPreviewSeq) return; // a newer preview is in flight
    subjEl.innerHTML = '<span style="color:var(--muted);">Subject:</span> ' + _esc(r.subject);
    subjEl.style.color = 'var(--text)';
    document.getElementById('et-preview').srcdoc = r.html;
  } catch (e) {
    if (seq !== _etPreviewSeq) return;
    subjEl.textContent = 'Preview unavailable: ' + e.message;
    subjEl.style.color = 'var(--red)';
  }
}

async function etSave() {
  if (!_etId) return;
  const btn = document.getElementById('et-save-btn');
  btn.disabled = true;
  try {
    await HRMGMT.saveEmailTemplate(_etId, _etFields());
    toast('Email template saved', 'success');
    await etLoad(_etId);
  } catch (e) {
    toast('Save failed: ' + _esc(e.message), 'error');
  } finally {
    btn.disabled = false;
  }
}

async function etResetDefaults() {
  if (!_etId) return;
  if (!confirm('Reset all text and branding fields to the built-in defaults? Uploaded images are kept.')) return;
  try {
    await HRMGMT.resetEmailTemplate(_etId);
    toast('Template text reset to defaults', 'success');
    await etLoad(_etId);
  } catch (e) {
    toast('Reset failed: ' + _esc(e.message), 'error');
  }
}

async function etUploadImage(slot, input) {
  const file = input.files && input.files[0];
  input.value = '';
  if (!file || !_etId) return;
  if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
    toast('Image must be PNG, JPEG or GIF.', 'error');
    return;
  }
  if (file.size > 512 * 1024) {
    toast('Image must be 512 KB or smaller.', 'error');
    return;
  }
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Could not read file.'));
      reader.readAsDataURL(file);
    });
    await HRMGMT.uploadEmailTemplateImage(_etId, slot, dataUrl, file.type);
    toast((slot === 'header' ? 'Header logo' : 'Footer image') + ' uploaded', 'success');
    await etLoad(_etId);
  } catch (e) {
    toast('Upload failed: ' + _esc(e.message), 'error');
  }
}

async function etRemoveImage(slot) {
  if (!_etId) return;
  try {
    await HRMGMT.uploadEmailTemplateImage(_etId, slot, null, null);
    toast('Image removed', 'success');
    await etLoad(_etId);
  } catch (e) {
    toast('Remove failed: ' + _esc(e.message), 'error');
  }
}

async function etSendTest() {
  if (!_etId) return;
  const btn = document.getElementById('et-test-btn');
  const to = document.getElementById('et-test-to').value.trim();
  btn.disabled = true;
  try {
    const r = await HRMGMT.sendTestEmailTemplate(_etId, _etFields(), to || undefined);
    toast('Test email sent to ' + _esc(r.to), 'success');
  } catch (e) {
    toast('Test send failed: ' + _esc(e.message), 'error');
  } finally {
    btn.disabled = false;
  }
}

window.etLoad               = etLoad;
window.etInsertPlaceholder  = etInsertPlaceholder;
window.etQueuePreview       = etQueuePreview;
window.etSave               = etSave;
window.etResetDefaults      = etResetDefaults;
window.etUploadImage        = etUploadImage;
window.etRemoveImage        = etRemoveImage;
window.etSendTest           = etSendTest;